/**
 * OpenSCAD Assistive Forge - Main Entry Point
 * @license GPL-3.0-or-later
 */

import './styles/main.css';
import { extractParameters } from './js/parser.js';
import {
  reconcileParameters,
  collectWithheldDefineKeys,
} from './js/parameter-reconciler.js';
import {
  renderParameterUI,
  commitPendingParameterEdit,
  setLimitsUnlocked,
  getAllDefaults,
  focusParameter,
  locateParameterKey,
  setParameterValue as _setParameterValue,
  setStarterParameters,
  setDraftRenderer,
  setDxfRenderProvider,
  setDesignFitBoxMm,
} from './js/ui-generator.js';
import {
  normalizeStarterList,
  resolveStarterParameters,
  unknownStarterMessage,
  describeUnknownStarter,
} from './js/starter-parameters.js';
import { stateManager } from './js/state.js';
import {
  downloadSTL,
  downloadFile,
  generateFilename,
  resolveDownloadFilename,
  projectStem,
  todayStamp,
  formatFileSize,
  formatPreviewStats,
  sanitizeFilename,
  OUTPUT_FORMATS,
} from './js/download.js';
import { getBrailleDownloadName } from './js/braille-panel.js';
import {
  RenderController,
  RENDER_QUALITY,
  PREVIEW_QUALITY_DEFAULT,
  estimateRenderTime,
} from './js/render-controller.js';
import {
  loadProjectManifest,
  getBuiltinManifest,
  applyPreviewOverrides,
} from './js/project-manifest.js';
import { build2DPreviewStyleTag } from './js/state-colors.js';
import {
  DROP_KIND,
  classifyDrop,
  describeAccepted,
} from './js/upload-router.js';
import { setStlViewActive, isStlViewActive } from './js/stl-view-mode.js';
import {
  escapeHtml,
  isValidServiceWorkerMessage,
  SERVICE_WORKER_MESSAGE_TYPES,
} from './js/html-utils.js';
import { getQualityPreset, COMPLEXITY_TIER } from './js/quality-tiers.js';
import {
  getThreeModule,
  CAMERA_ZOOM_STEP,
  VIEWPORT_SCHEMES,
  PREVIEW_COLORS,
} from './js/preview.js';
import { normalizeHexColor } from './js/color-utils.js';
import { buildDefineArgs as formatBuildDefineArgs } from './js/scad-param-formatter.js';
import {
  AutoPreviewController,
  PREVIEW_STATE,
} from './js/auto-preview-controller.js';
import { isEnabled as isFlagEnabled } from './js/feature-flags.js';
import {
  propose2DExportAdjustments,
  strip2DGenerateForFallback,
  isNonPreviewable,
} from './js/render-intent.js';
import {
  applyCompanionAliases,
  getOverlaySvgTarget,
} from './js/zip-handler.js';
import {
  fetchProjectBlob,
  loadManifest,
  ManifestError,
  validateManifest,
} from './js/manifest-loader.js';
import { buildProjectManifest } from './js/publish-manifest.js';
import { buildProvenance, buildProjectZipEntries } from './js/project-zip.js';
import { getConsolePanel } from './js/console-panel.js';
import {
  getErrorLogPanel,
  initAddStructuredError,
} from './js/error-log-panel.js';
import { themeManager, initThemeToggle } from './js/theme-manager.js';
import {
  presetManager,
  extractScadVersion,
  checkMigrationAvailable,
  migrateFromLegacyStorage,
  dismissMigrationOffer,
  coercePresetValues,
} from './js/preset-manager.js';
import { libraryManager, LIBRARY_DEFINITIONS } from './js/library-manager.js';
import {
  openModal,
  closeModal,
  initStaticModals,
  isAnyModalOpen,
  setupModalCloseHandlers,
} from './js/modal-manager.js';
import {
  joyConKind,
  joyConLayout,
  loadJoyConChoice,
  saveJoyConChoice,
} from './js/joycon.js';
import { createGamepadPresetStore } from './js/gamepad-presets.js';
import {
  disableKeepingFocus,
  firstParameterControl,
  getFocusableElements,
  setBusy,
  isBusy,
} from './js/focus-trap.js';
import {
  readAutoPreviewChoice,
  writeAutoPreviewChoice,
} from './js/auto-preview-choice.js';
import {
  translateError,
  previewFailureSentence,
  findFailedCheck,
  findMissingLibrary,
  showErrorModal,
  showErrorToast,
} from './js/error-translator.js';
import { reserveSpaceForActionsBar } from './js/actions-bar-reserve.js';
import { engineStartFailureNotice } from './js/engine-start-notice.js';
import {
  getStorageEstimate,
  clearCachedData as _clearCachedData,
  isFirstVisit,
  markFirstVisitComplete,
  updateStoragePrefs,
  shouldDeferLargeDownloads,
  rememberEngineDownload,
  engineFilesCached,
  formatBytes as _formatBytes,
  // Persistence and backup
  checkPersistentStorage as _checkPersistentStorage,
  requestPersistentStorage as _requestPersistentStorage,
  clearCacheWithOptions,
  getDetailedStorageInfo,
  exportProjectsBackup,
  exportSingleProject,
  importProjectsBackup,
  linkProjectFromFiles,
  readProjectFilesFromList,
  findLinkedProjectForHandle,
} from './js/storage-manager.js';
import { startTutorial } from './js/tutorial-sandbox.js';
import { initWelcomeSpotlight } from './js/welcome-spotlight.js';
import { initTourNudge } from './js/tour-nudge.js';
import { initDrawerController } from './js/drawer-controller.js';
import {
  registerLookDrawer,
  sidePanelParts,
  slideDrawer,
  stripWidth,
} from './js/drawer-motion.js';
import { initPreviewSettingsDrawer } from './js/preview-settings-drawer.js';
import { initLensDrawer } from './js/lens-drawer.js';
import { initCameraPanelController } from './js/camera-panel-controller.js';
import { initSequenceDetector } from './js/_seq.js';
import {
  createGamepadController,
  createGamepadMapping,
  createWalkMapping,
  isGamepadSupported,
  GAMEPAD_ACTIONS,
} from './js/gamepad-controller.js';
import {
  CLASSIC_REGIONS,
  DEFAULT_REGIONS,
  createGamepadNavigator,
} from './js/gamepad-navigator.js';
import { requestSwUpdate, staleShellAction } from './js/sw-update.js';
import {
  initKeyboardShortcuts,
  keyboardConfig,
  initShortcutsModal,
} from './js/keyboard-config.js';
import { applyAriaKeyshortcuts } from './js/keyboard-shortcuts-binder.js';
import {
  isEnabled as _isEnabled,
  debugFlags,
  FLAGS as _FLAGS,
} from './js/feature-flags.js';
import { initSearchableCombobox } from './js/searchable-combobox.js';
import { initCompanionFilesController } from './js/companion-files-controller.js';
import { initCSPReporter } from './js/csp-reporter.js';
import {
  migrateStorageKeys,
  DEBUG_PREFS,
  isDebugPrefEnabled,
  safeGetItem,
  safeSetItem,
  STORAGE_KEY_AUTO_PREVIEW_ENABLED,
  STORAGE_KEY_PREVIEW_QUALITY,
  STORAGE_KEY_RECOVERY_SOURCE,
  STORAGE_KEY_RECOVERY_TIMESTAMP,
  STORAGE_KEY_STATUS_BAR,
  STORAGE_KEY_GRID,
  STORAGE_KEY_GRID_SIZE,
  STORAGE_KEY_VIEWPORT_SCHEME,
  STORAGE_KEY_MODEL_COLOR,
  STORAGE_KEY_MODEL_COLOR_ENABLED,
  STORAGE_KEY_MODEL_OPACITY,
  STORAGE_KEY_BRIGHTNESS,
  STORAGE_KEY_CONTRAST,
  STORAGE_KEY_MODEL_APPEARANCE_ENABLED,
  STORAGE_KEY_PARAM_PANEL_COLLAPSED,
  STORAGE_KEY_LAYOUT_SIZES,
  STORAGE_KEY_MANIFOLD_ENGINE,
  STORAGE_KEY_SOUNDS,
  STORAGE_KEY_WASM_INIT_STARTED,
  STORAGE_KEY_WASM_INIT_COMPLETED,
  PRESET_SORT_KEY,
} from './js/storage-keys.js';
import { isPhoneLayout, isSidewaysPhone } from './js/phone-drawers.js';
import {
  ensureScopedPrefsSeeded,
  readScopedPref,
  writeScopedPref,
  removeScopedPref,
} from './js/ui-scoped-prefs.js';
import {
  initImageMeasurement,
  openFullscreen as measureOpenFullscreen,
  closeFullscreen as measureCloseFullscreen,
  loadImageFromDataURL,
  setMeasureMode,
  clearRulerPoints,
  getCalibDistancePx,
} from './js/image-measurement.js';
import * as SharedImageStore from './js/shared-image-store.js';
import {
  getUnit,
  setUnit,
  getScaleFactor,
  setScaleFactor,
  onUnitChange,
  onScaleChange,
} from './js/unit-sync.js';

// Storage keys are centralized in ./js/storage-keys.js
import {
  initPreferencesDialog,
  openPreferencesDialog,
} from './js/preferences-dialog.js';
import { createTabList, selectTabIn } from './js/tablist.js';
import {
  initControllerPanel,
  syncControllerPanel,
} from './js/controller-panel.js';
import {
  DEFAULT_ENGINE_PATH,
  clearDownloads as clearEngineDownloads,
  download as downloadEngine,
  getChosenEngineId,
  isDownloaded as isEngineDownloaded,
  loadEnginesManifest,
  offerEngineAfterFault,
  remove as removeEngine,
  resetEngineOffer,
  setChosenEngineId,
  switchTo as switchEngineTo,
} from './js/engine-choice.js';
import {
  announceImmediate,
  announceCameraAction,
  announceError as _announceError,
  POLITENESS as _POLITENESS,
} from './js/announcer.js';
// Expert Mode - Code editor integration
import { getModeManager } from './js/mode-manager.js';
import { loadEditorPrefs, saveEditorPref } from './js/editor-prefs.js';
// UI Mode Controller - Simplified/Standard/Classic interface layout switching
import { getUIModeController } from './js/ui-mode-controller.js';
// Classic is desktop-only for now — the viewport half of the gate
import {
  isViewportDesktopShaped,
  subscribeViewportShape,
} from './js/classic-availability.js';
// The four app-chrome controls join the Customizer row on a phone
import { initMobileToolbar } from './js/mobile-toolbar.js';
import {
  initClassicLayoutController,
  getClassicLayoutController,
  collapseCustomizerGroups,
} from './js/classic-layout-controller.js';
import { tabIdFor, DOCK_PANELS } from './js/classic-dock-model.js';
import { initFontListPanel } from './js/font-list-panel.js';
import { initViewportControlPanel } from './js/viewport-control-panel.js';
import { initAnimatePanel, getAnimatePanel } from './js/animate-panel.js';
import { initClassicStatusBar } from './js/classic-status-bar.js';
import {
  initClassicEditorToolbar,
  getClassicEditorToolbar,
  REASON_NEEDS_RENDER as CLASSIC_STL_NEEDS_RENDER_REASON,
} from './js/classic-editor-toolbar.js';
import { FolderChangeWatcher } from './js/folder-change-watcher.js';
import { FolderWriteBack } from './js/folder-write-back.js';
import { createFolderSaveActions } from './js/folder-save-actions.js';
// Toolbar Menu Controller - File|Edit|Design|View|Window|Help menu bar
import {
  getToolbarMenuController,
  applyToolbarModeVisibility,
} from './js/toolbar-menu-controller.js';
import { setAppSurface } from './js/app-surface.js';
import { installBackGuard } from './js/back-guard.js';
import { initParamDetailController } from './js/param-detail-controller.js';
import { initOverlayGridController } from './js/overlay-grid-controller.js';
import { initSavedProjectsUI } from './js/saved-projects-ui.js';
import {
  getFileActionsController,
  exportFormatFromMenu,
  setExportDependencies,
} from './js/file-actions-controller.js';
import { getEditActionsController } from './js/edit-actions-controller.js';
import { copyPresetName } from './js/copy-preset-name.js';
import { getDesignPanelController } from './js/design-panel-controller.js';
import { getDisplayOptionsController } from './js/display-options-controller.js';
import { getEditorStateManager } from './js/editor-state-manager.js';
import { TextareaEditor } from './js/textarea-editor.js';
import { CodeMirrorEditor } from './js/codemirror-editor.js';
import { showConfirmDialog, showTextToCopyDialog } from './js/dialogs.js';
import { showLibraryInfoDialog } from './js/library-info-dialog.js';
import {
  initHfmController,
  noteControllerMove,
  playPadKey,
  setSoundsOn,
  setSoundKindOff,
  soundKindsOff,
} from './js/hfm-controller.js';
import {
  EXAMPLE_DEFINITIONS,
  PROGRAM_DEFINITIONS,
  showProcessingOverlay,
  initFileHandler,
} from './js/file-handler.js';
import { getFolderSyncController } from './js/folder-sync-controller.js';
import {
  initMemoryMonitor,
  getMemoryMonitor as _getMemoryMonitor,
  MemoryState as _MemoryState,
  MemoryRecovery as _MemoryRecovery,
} from './js/memory-monitor.js';
import {
  initSavedProjectsDB,
  updateProject,
  touchProject,
  getProject,
  listSavedProjects,
  deleteProject,
  getSavedProjectsSummary as _getSavedProjectsSummary,
  clearAllSavedProjects as _clearAllSavedProjects,
  getStorageDiagnostics,
  // Folder operations
  createFolder as _createFolder,
  moveFolder as _moveFolder,
} from './js/saved-projects-manager.js';
import {
  loadFolderHandle,
  saveFolderHandle,
  clearFolderHandle,
} from './js/folder-handle-store.js';
import { createLinkedFoldersUi } from './js/linked-folders-ui.js';
import Split from 'split.js';

/**
 * Resolve parameters for 2D export (SVG/DXF) using the parsed parameter schema.
 * For each parameter that has an enum with a 2D-compatible value, overrides the
 * current value so the model produces 2D geometry for the export.
 *
 * @param {Object} parameters - Current UI parameter values
 * @param {Object|null} schema - Parsed schema from extractParameters() (schema.parameters)
 * @param {string} format - Output format ('svg' or 'dxf')
 * @returns {Object} Parameter object with 2D-compatible overrides applied
 */
function propose2DExportChanges(parameters, schema, format, projectFiles) {
  const manifest = loadProjectManifest(projectFiles) ?? getBuiltinManifest();
  const proposal = propose2DExportAdjustments(
    parameters,
    schema,
    format,
    manifest.export2D ?? null
  );
  if (proposal.changes.length > 0) {
    console.debug(
      '[resolve2D] Proposed 2D-export adjustments (applied only with user consent):',
      proposal.changes
    );
  }
  return proposal;
}

/**
 * Whether the user has consented to applying the proposed 2D-export
 * parameter changes. The checkbox ships checked (the proposals are almost
 * always what the user wants); unchecking exports with parameters exactly
 * as configured.
 */
function is2DAdjustmentsConsented() {
  const checkbox = document.getElementById('format2dAutoAdjustApply');
  return checkbox ? checkbox.checked : true;
}

/**
 * Ask the user before running the approximate projection fallback for a
 * 2D export whose direct render produced 3D geometry (MODEL_NOT_2D).
 * The fallback is a real geometry approximation, so it never runs
 * silently.
 *
 * @param {string} format - 'svg' or 'dxf'
 * @returns {Promise<boolean>} True when the user accepts
 */
function confirmProjectionFallback(format) {
  const formatName = format.toUpperCase();
  return showConfirmDialog(
    'This model produced 3D geometry instead of a 2D profile. ' +
      'An approximate outline can be generated by rendering the model in ' +
      'its default 3D mode and slicing the mesh at its base plane. ' +
      'Curves become straight line segments. Verify all dimensions ' +
      'before laser cutting.',
    `Generate approximate ${formatName}?`,
    `Generate approximate ${formatName}`,
    'Cancel'
  );
}

/**
 * Detect OFF/COFF geometry bytes masquerading as another format.
 * Guards the STL download path: OFF starts with an ASCII "OFF"/"COFF"
 * header line, while binary STL has an arbitrary 80-byte header and
 * ASCII STL starts with "solid".
 *
 * @param {ArrayBuffer|Uint8Array|string} data - Rendered output bytes
 * @returns {boolean} True if the data looks like an OFF/COFF file
 */
function looksLikeOffGeometry(data) {
  if (!data) return false;
  if (typeof data === 'string') return /^C?OFF\b/.test(data.slice(0, 5));
  let bytes;
  if (data instanceof Uint8Array) {
    bytes = data.subarray(0, 5);
  } else if (data instanceof ArrayBuffer) {
    bytes = new Uint8Array(data, 0, Math.min(5, data.byteLength));
  } else {
    return false;
  }
  return /^C?OFF\b/.test(String.fromCharCode(...bytes));
}

// Feature detection
function checkBrowserSupport() {
  const checks = {
    wasm: typeof WebAssembly !== 'undefined',
    worker: typeof Worker !== 'undefined',
    fileApi: typeof FileReader !== 'undefined',
    modules: 'noModule' in HTMLScriptElement.prototype,
  };

  const missing = Object.entries(checks)
    .filter(([_, supported]) => !supported)
    .map(([feature]) => feature);

  return { supported: missing.length === 0, missing };
}

// Show unsupported browser message
function showUnsupportedBrowser(missing) {
  const app = document.getElementById('app');
  app.innerHTML = `
    <div class="unsupported-browser" role="alert" style="padding: 2rem; max-width: 600px; margin: 2rem auto;">
      <h2>Browser Not Supported</h2>
      <p>This application requires a modern browser with WebAssembly support.</p>
      <p>Please use one of the following:</p>
      <ul>
        <li>Chrome 67 or newer</li>
        <li>Firefox 79 or newer</li>
        <li>Safari 15.2 or newer</li>
        <li>Edge 79 or newer</li>
      </ul>
      <p><strong>Missing features:</strong> ${missing.join(', ')}</p>
    </div>
  `;
}

// Global render controller, preview manager, and auto-preview controller
let renderController = null;
let previewManager = null;
let autoPreviewController = null;

/**
 * Export quality mode. Module-scope like previewManager so the __forgeDebug
 * hook can read it: File > Export Quality is its only control and
 * there is no DOM element to ask. Session-only on purpose: every boot
 * starts at 'model'.
 */
let exportQualityMode = 'model';

/**
 * The expert block's ▶ Preview handler, published for the Classic editor
 * toolbar. Null until the expert block initializes.
 * @type {Function|null}
 */
let editorPreviewTrigger = null;

/**
 * Whether a full-quality STL for the given parameters is available right now —
 * i.e. Generate has been pressed and the result still matches the parameters.
 *
 * This is the single source for render-state enablement. It gates the File
 * menu's export items, the Generate button's Download state, and the Classic
 * editor toolbar's Export STL button, so the three cannot drift apart.
 *
 * @param {Object} parameters - current parameter values
 * @returns {boolean}
 */
function hasFullQualitySTLFor(parameters) {
  return Boolean(
    autoPreviewController?.getCurrentFullSTL(parameters) &&
    !autoPreviewController?.needsFullRender(parameters)
  );
}

const BUTTON_ICONS = { download: '📥', wait: '⏳', spin: '⟳' };

/**
 * A button's words after an icon in a span of its own, hidden from assistive
 * tech: the alternative look draws the icon as text (variant.css). The
 * preview badge takes its hourglass and spinner the same way.
 * @param {HTMLElement} button
 * @param {keyof typeof BUTTON_ICONS} icon
 * @param {string} text
 */
function setButtonTextWithIcon(button, icon, text) {
  const glyph = document.createElement('span');
  glyph.className = 'btn-emoji';
  glyph.dataset.icon = icon;
  glyph.setAttribute('aria-hidden', 'true');
  glyph.textContent = BUTTON_ICONS[icon];
  button.replaceChildren(glyph, ` ${text}`);
}

/**
 * Escape in an open list closes the list alone. A list the page draws (the
 * alternative look's on a touch screen, variant.css) is inside the page, so
 * its Escape would otherwise reach the sheet's and the dialogs' own Escape
 * first and close them around it. Left uncancelled, the browser still
 * closes the list.
 */
function keepEscapeForOpenLists() {
  if (!CSS.supports('selector(:open)')) return;
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      if (event.target?.closest?.('select')?.matches(':open')) {
        event.stopPropagation();
      }
    },
    true
  );
}

/* ── Error Log: reaching it by keyboard and by menu ───────────────────────────
 *
 * The Error-Log lives in two different places depending on the host, so every
 * entry point below resolves WHERE it is from the DOM rather than from a mode
 * flag that could disagree with it:
 *
 *   Forge    inside the console's Structured tabpanel, behind a tab
 *   Classic  its own pane in the bottom strip, always on screen
 *
 * The two hosts therefore mean different things by "show the Error Log". In
 * Forge it is genuinely hidden and has to be revealed; in Classic it is
 * already there and what the user wants is to GET to it. Classic must never
 * click the Structured tab — that tablist is hidden there, so it would
 * be an invisible control changing an invisible selection.
 */

/** The console <details> was closed and we opened it, so toggling off can undo that. */
let errorLogOpenedConsole = false;

/** Where focus was before Classic sent it into the Error-Log pane. */
let errorLogReturnFocus = null;

/**
 * Which host currently holds the Error-Log, resolved from the element itself.
 * @returns {'classic'|'forge'|null} null when the markup is not present at all
 */
function errorLogHost() {
  const host = document
    .getElementById('error-log-output')
    ?.closest('#classicErrorLogSlot, #console-view-structured');
  if (!host) return null;
  return host.id === 'classicErrorLogSlot' ? 'classic' : 'forge';
}

/**
 * Whether the Error-Log is on screen right now — what the Window menu item's
 * tick reports, and what decides which way the shortcut toggles.
 * @returns {boolean}
 */
function isErrorLogShowing() {
  const host = errorLogHost();
  if (host === 'forge') {
    return (
      Boolean(document.getElementById('consolePanel')?.open) &&
      document
        .getElementById('console-tab-structured')
        ?.getAttribute('aria-selected') === 'true'
    );
  }
  if (host === 'classic') {
    // Simplified drops the whole bottom strip, and folding takes it with it.
    if (getUIModeController().getClassicDensity() === 'simplified')
      return false;
    if (getClassicLayoutController()?.isConsoleCollapsed()) return false;
    // Merged into a tab group and not the selected tab: the model sets
    // `hidden` on the panels that are not showing.
    return !document.getElementById('classicErrorLogSlot')?.hidden;
  }
  return false;
}

/**
 * Forge: open the console if it is closed, select the Structured tab through
 * the console panel's own wiring (console-panel.js), and put focus on the tab
 * so the move announces itself.
 * @returns {boolean}
 */
function showErrorLogForge() {
  const panel = document.getElementById('consolePanel');
  const tab = document.getElementById('console-tab-structured');
  if (!panel || !tab) return false;

  if (!panel.open) {
    panel.open = true;
    errorLogOpenedConsole = true;
  }
  tab.click();
  tab.focus();
  return true;
}

/**
 * Forge: back to the Log view, and back to a closed console if that is how we
 * found it — toggling off should leave the panel as it was, not half-open.
 */
function hideErrorLogForge() {
  const logTab = document.getElementById('console-tab-log');
  logTab?.click();
  if (errorLogOpenedConsole) {
    const panel = document.getElementById('consolePanel');
    if (panel) panel.open = false;
    errorLogOpenedConsole = false;
  } else {
    logTab?.focus();
  }
}

/**
 * Classic: the pane is already in the bottom strip, so this is about reaching
 * it — unfold the strip if it is folded, select its tab if it has been
 * merged into a group, then focus the title bar's menu button, which is
 * the focusable control the title bar carries (the same landing point a
 * panel move uses).
 * @returns {boolean}
 */
function showErrorLogClassic() {
  const slot = document.getElementById('classicErrorLogSlot');
  if (!slot) return false;

  if (getUIModeController().getClassicDensity() === 'simplified') {
    // Nothing else speaks here, so this announcement is the only feedback.
    announceImmediate('Error-Log is not available in the Simplified view');
    return false;
  }

  const layout = getClassicLayoutController();
  if (layout?.isConsoleCollapsed()) layout.setConsoleCollapsed(false);

  const priorFocus = document.activeElement;

  // A merged group's tab lives in the shared bar, outside the panel; a solo
  // panel keeps its own title bar. tabIdFor comes from the dock model so the
  // id scheme has one definition.
  const tab = document.getElementById(tabIdFor('errorLog'));
  if (tab) {
    tab.click();
    tab.focus();
  } else {
    const target =
      slot.querySelector('.classic-panel-menu-btn') ||
      slot.querySelector('button');
    if (!target) return false;
    target.focus();
  }

  errorLogReturnFocus = priorFocus;
  return true;
}

/** Classic: hand focus back to wherever the user was before. */
function returnFocusFromErrorLog() {
  const prior = errorLogReturnFocus;
  errorLogReturnFocus = null;
  if (prior?.isConnected && typeof prior.focus === 'function') prior.focus();
}

/**
 * Ctrl+Alt+2 / Window > Error-Log. Forge opens and closes the Structured view;
 * Classic, where the pane cannot be closed, sends focus into it and back out
 * again — the honest per-host reading of "toggle" for a panel that is always
 * present in one host and hidden behind a tab in the other.
 */
function toggleErrorLog() {
  const host = errorLogHost();

  if (host === 'forge') {
    if (isErrorLogShowing()) hideErrorLogForge();
    else showErrorLogForge();
    return;
  }

  if (host === 'classic') {
    const slot = document.getElementById('classicErrorLogSlot');
    const active = document.activeElement;
    const alreadyInside =
      Boolean(slot?.contains(active)) || active?.id === tabIdFor('errorLog');
    if (alreadyInside) returnFocusFromErrorLog();
    else showErrorLogClassic();
  }
}

/** Where focus was before Window > Console sent it into the Classic dock. */
let consoleReturnFocus = null;

/** Classic: is the Console dock on screen right now? */
function isConsoleShowingClassic() {
  if (getUIModeController().getClassicDensity() === 'simplified') return false;
  if (getClassicLayoutController()?.isConsoleCollapsed()) return false;
  return !document.getElementById('classicConsoleSlot')?.hidden;
}

/**
 * Ctrl+Alt+1 / Window > Console. Forge opens and closes the console. In
 * Classic the Console is a dock pane that stays open (a closed one is an
 * empty frame), so, like the Error-Log beside it, the toggle sends focus into
 * the pane and back out again. Classic's Simplified view drops the strip;
 * there the Forge behavior stands.
 */
function toggleConsole() {
  const layout = getClassicLayoutController();
  const docked =
    document.body.dataset.uiMode === 'classic' &&
    Boolean(layout) &&
    getUIModeController().getClassicDensity() !== 'simplified';
  if (!docked) {
    getUIModeController().togglePanelVisibility('consoleOutput');
    return;
  }

  const slot = document.getElementById('classicConsoleSlot');
  const active = document.activeElement;
  if (slot?.contains(active) || active?.id === tabIdFor('console')) {
    const prior = consoleReturnFocus;
    consoleReturnFocus = null;
    if (prior?.isConnected && typeof prior.focus === 'function') prior.focus();
    return;
  }
  if (layout.isConsoleCollapsed()) layout.setConsoleCollapsed(false);
  if (focusDockPanel('console')) consoleReturnFocus = active;
}

/**
 * The toolbars Classic can hide, each with the body attribute `classic.css`
 * keys off and the preference key it persists under.
 *
 * Upstream's View menu hides the editor toolbar and the 3D view toolbar
 * separately, so "Hide Toolbar" splits in two. The icon toolbar is this
 * app's own third bar; it keeps its item and its stored preference so nothing
 * on screen becomes unhideable, renamed so a screen reader can tell the three
 * apart. One table, so the menu and the startup restore cannot drift.
 */
const CLASSIC_HIDEABLE_TOOLBARS = {
  editor: {
    label: 'Hide Editor toolbar',
    name: 'Editor toolbar',
    datasetKey: 'classicEditorToolbarHidden',
    storageKey: 'openscad-forge-classic-editor-toolbar-hidden',
  },
  view: {
    label: 'Hide 3D View toolbar',
    name: '3D view toolbar',
    datasetKey: 'classicCameraBarHidden',
    storageKey: 'openscad-forge-classic-camera-bar-hidden',
  },
  icon: {
    label: 'Hide Classic Toolbar',
    name: 'Classic toolbar',
    datasetKey: 'classicToolbarHidden',
    storageKey: 'openscad-forge-classic-toolbar-hidden',
  },
};

/** @param {keyof CLASSIC_HIDEABLE_TOOLBARS} bar */
function isClassicToolbarHidden(bar) {
  return (
    document.body.dataset[CLASSIC_HIDEABLE_TOOLBARS[bar].datasetKey] === 'true'
  );
}

/**
 * Hide or show one Classic toolbar and remember the choice.
 * @param {keyof CLASSIC_HIDEABLE_TOOLBARS} bar
 */
function toggleClassicToolbar(bar) {
  const def = CLASSIC_HIDEABLE_TOOLBARS[bar];
  const hidden = !isClassicToolbarHidden(bar);
  document.body.dataset[def.datasetKey] = String(hidden);
  try {
    localStorage.setItem(def.storageKey, String(hidden));
  } catch {
    // Preference persistence is best-effort.
  }
  // Same swallow as Jump To: a discrete menu action's only feedback must not
  // sit in a 350ms debounce that the next announcement cancels.
  announceImmediate(`${def.name} ${hidden ? 'hidden' : 'shown'}`);
}

const KEYS_TAB_IDS = ['keys-tab-keyboard', 'keys-tab-controller'];
const keysTabs = () =>
  KEYS_TAB_IDS.map((id) => document.getElementById(id)).filter(Boolean);

/**
 * Open Keys, the keyboard shortcuts and the controller, on one of its tabs,
 * wiring it on first use. Focus starts on the chosen tab.
 *
 * One copy for every way in (Edit ▸ Preferences, Help ▸ Keyboard
 * Shortcuts, the header button, Ctrl+Shift+K, and the controller's Keys
 * button): a fix applied to some of the copies looks done and is not.
 *
 * @param {{tab?: 'keyboard'|'controller', onClose?: () => void}} [options]
 *   `onClose` runs when this opening of the dialog closes
 */
function openShortcutsDialog({ tab = 'keyboard', onClose } = {}) {
  const modal = document.getElementById('shortcutsModal');
  const modalBody = document.getElementById('shortcutsModalBody');
  if (!modal || !modalBody) return;
  // Wire once; a second call would stack duplicate listeners.
  if (!modal.dataset.initialized) {
    initShortcutsModal(modalBody, () => closeModal(modal));
    const tablist = document.getElementById('keysTablist');
    if (tablist) {
      createTabList({
        tablist,
        tabs: keysTabs,
        onSelect: () => syncControllerPanel(),
      });
    }
    modal.dataset.initialized = 'true';
  }
  const target =
    document.getElementById(`keys-tab-${tab}`) ?? keysTabs()[0] ?? null;
  if (target) selectTabIn(keysTabs(), target, { focus: false });
  openModal(modal, {
    ...(target ? { focusTarget: target } : {}),
    ...(onClose ? { onClose } : {}),
  });
  syncControllerPanel();
}

// Edit ▸ Insert Template. Nothing in this repository transcribes what
// upstream's template actually inserts, so building it would mean
// inventing it.
const INSERT_TEMPLATE_REASON =
  "Insert Template is not built yet: nothing in this project records what the desktop's template inserts, and guessing would put code you did not write into your file.";

// Help ▸ Offline …: both are disabled with a reason rather than hidden.
// Offline documentation is not bundled, so nothing third-party is
// fetched, pinned or vendored here.
const OFFLINE_DOCUMENTATION_REASON =
  'Offline documentation is not bundled yet. Use Documentation, which opens the OpenSCAD manual in a new window while you are online.';
const OFFLINE_CHEAT_SHEET_REASON =
  'The offline cheat sheet is not bundled yet. Use Cheat Sheet, which opens it in a new window while you are online.';

/**
 * Help ▸ About. Stamps the build's version into the dialog before opening it,
 * so the number can never drift from what was actually shipped.
 */
function openAboutModal() {
  const modal = document.getElementById('aboutModal');
  if (!modal) return;

  const versionLine = document.getElementById('aboutVersion');
  if (versionLine) {
    // The version alone cannot tell two builds of the same version
    // apart, which is exactly what somebody reporting a bug needs to do. The
    // stamp is the string the service worker names its cache after, so what a
    // person reads here and what they see in DevTools are the same thing.
    versionLine.textContent = `OpenSCAD Assistive Forge, version ${__APP_VERSION__} (${__BUILD_STAMP__})`;
  }

  openModal(modal, {
    focusTarget: document.getElementById('aboutModalDone'),
  });
}

/** Window ▸ Jump To…, the web reading of upstream's jump-to-dock popup. */
const JUMP_TO_LABEL = 'Jump To…';
const JUMP_TO_EMPTY_REASON =
  'No panels are open, so there is nowhere to jump to. Turn one on from this menu first.';
const JUMP_TO_UNAVAILABLE_REASON =
  'Jump To is not available in the Simplified view';
const CODE_EDITOR_UNAVAILABLE_REASON =
  'The Code Editor feature is turned off, so there is no editor to open.';
const EDITOR_IN_STANDARD_REASON =
  'The Editor is in the Standard view. Switch to Standard to open it.';

/**
 * Can a Classic dock panel be reached right now? A merged panel that is not
 * the selected tab counts — its tab reaches it — but one the Window menu has
 * hidden, or a whole strip that Simplified drops, does not.
 * @param {{id: string, elementId: string}} panel
 */
function dockPanelReachable(panel) {
  const el = document.getElementById(panel.elementId);
  if (!el) return false;
  if (document.getElementById(tabIdFor(panel.id))) return true;
  return el.getBoundingClientRect().height > 0;
}

/**
 * Put focus on a Classic dock panel: its tab when it is merged into a
 * group, otherwise its title bar's menu button — the same landing point a
 * panel move and the Error-Log shortcut use, so a jump feels like every
 * other way of arriving at a panel.
 * @param {string} panelId
 * @returns {boolean}
 */
function focusDockPanel(panelId) {
  const tab = document.getElementById(tabIdFor(panelId));
  if (tab) {
    tab.click();
    tab.focus();
    return true;
  }
  const panel = DOCK_PANELS.find((p) => p.id === panelId);
  const el = panel && document.getElementById(panel.elementId);
  const target =
    el?.querySelector('.classic-panel-menu-btn') || el?.querySelector('button');
  if (!target) return false;
  target.focus();
  return true;
}

/**
 * Where Jump To can send you, per host: Classic's dock panels, or the
 * disclosure panels Ctrl+Alt+] and Ctrl+Alt+[ already cycle through.
 * @returns {{label: string, focus: () => boolean}[]}
 */
function jumpTargets() {
  const layout = getClassicLayoutController();
  if (document.body.dataset.uiMode === 'classic' && layout) {
    return DOCK_PANELS.filter(dockPanelReachable).map((panel) => ({
      label: panel.label,
      focus: () => focusDockPanel(panel.id),
    }));
  }
  return getUIModeController()
    .listFocusablePanels()
    .map((panel) => ({
      label: panel.label,
      focus: () => {
        if (!panel.el.open) panel.el.open = true;
        const summary = panel.el.querySelector('summary');
        if (!summary) return false;
        summary.focus();
        summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return true;
      },
    }));
}

/**
 * Ctrl+J. Upstream opens the jump-to-dock popup itself, so this opens the
 * Window menu and expands Jump To… rather than leaving the user to find it.
 */
function openJumpToPicker() {
  const btn = document.getElementById('windowMenuBtn');
  if (!btn || btn.offsetParent === null) {
    // Simplified hides the menu bar entirely; nothing else would speak here.
    announceImmediate(JUMP_TO_UNAVAILABLE_REASON);
    return;
  }
  getToolbarMenuController().openMenu('window');
  const trigger = [
    ...document.querySelectorAll('#windowMenuItems .menu-submenu-trigger'),
  ].find(
    (el) => el.querySelector('.menu-item-label')?.textContent === JUMP_TO_LABEL
  );
  trigger?.click();
}

/**
 * Window ▸ Customizer, Ctrl+Alt+4 and Ctrl+B are one command.
 */
function toggleCustomizerPanel() {
  const layout = getClassicLayoutController();
  if (document.body.dataset.uiMode === 'classic' && layout) {
    layout.toggleCustomizer(); // announces for itself
    return;
  }
  const btn = document.getElementById('collapseParamPanelBtn');
  if (!btn) return;
  btn.click();
  // announceImmediate, not the debounced announce: every sibling panel toggle
  // announces immediately, and the debounce CANCELS a pending message, so two
  // quick presses of this one alone would have spoken once.
  announceImmediate(
    btn.getAttribute('aria-expanded') === 'true'
      ? 'Customizer shown'
      : 'Customizer hidden'
  );
}

/** Window ▸ Editor, Ctrl+Alt+3 and Ctrl+E are one command per host. */
function toggleEditorPanel() {
  const layout = getClassicLayoutController();
  if (document.body.dataset.uiMode === 'classic' && layout) {
    layout.toggleEditor(); // announces for itself
    return;
  }
  // Simplified leaves the Editor out (its toolbar toggle is hidden there), so
  // a key says where the Editor is rather than opening it out of sight.
  if (getUIModeController().getMode() === 'simplified') {
    announceImmediate(EDITOR_IN_STANDARD_REASON);
    return;
  }
  // Forge: open or close the Editor itself — the same command as its
  // toolbar toggle, announced by the mode manager ("Editor opened…"). Not
  // togglePanelVisibility('codeEditor'), whose primary element is the
  // toggle button: that would hide the editor's entry point from the
  // toolbar while the editor stayed shut. Hiding the button is the
  // hidden-panels preference's job.
  if (!_isEnabled('expert_mode')) {
    announceImmediate(CODE_EDITOR_UNAVAILABLE_REASON);
    return;
  }
  getModeManager()?.toggleMode?.();
}

/** Restore the View ▸ Hide … preferences on startup. */
function restoreClassicToolbarPrefs() {
  for (const def of Object.values(CLASSIC_HIDEABLE_TOOLBARS)) {
    try {
      if (localStorage.getItem(def.storageKey) === 'true') {
        document.body.dataset[def.datasetKey] = 'true';
      }
    } catch {
      // Preference read is best-effort.
    }
  }
}

// Track which saved project is currently loaded (for auto-saving companion files)
let currentSavedProjectId = null;

// Initialize app
async function initApp() {
  console.log(`OpenSCAD Assistive Forge v${__APP_VERSION__}`);
  console.log('Initializing...');

  // Initialize the foundation systems early
  // Feature flags: Enable controlled rollout of new features
  if (import.meta.env.DEV) {
    debugFlags(); // Log flag states for debugging (dev only)
  }

  // CSP Reporter: Monitor Content-Security-Policy violations
  initCSPReporter();

  // Before any Escape handler of the page's own.
  keepEscapeForOpenLists();

  // Storage key migration: One-time migration of localStorage keys to standardized naming
  // Must run before any localStorage reads to ensure consistent key access
  migrateStorageKeys();

  // Split the per-UI viewing preferences into per-interface namespaces.
  // After the migration so it copies migrated values; before any controller
  // init so every scoped read finds its namespace ready. Marker-gated — a
  // no-op on every boot after the first.
  ensureScopedPrefsSeeded();

  // Recovery Mode: Detect if we're recovering from a memory-related crash
  const urlParams = new URLSearchParams(window.location.search);
  const isRecoveryMode = urlParams.get('recovery') === 'true';

  // Crash detection: If WASM init started but never completed, we may have crashed.
  // The flag is set before WASM init and cleared after success.
  const wasmCrashDetected =
    localStorage.getItem(STORAGE_KEY_WASM_INIT_STARTED) === 'true' &&
    localStorage.getItem(STORAGE_KEY_WASM_INIT_COMPLETED) !== 'true';

  if (wasmCrashDetected && !isRecoveryMode) {
    console.warn(
      '[Recovery] Detected unclean WASM shutdown — offering recovery mode'
    );
    // Clear the flags so we don't loop
    localStorage.removeItem(STORAGE_KEY_WASM_INIT_STARTED);
    localStorage.removeItem(STORAGE_KEY_WASM_INIT_COMPLETED);
    // Auto-enter recovery mode
    window.location.href = window.location.pathname + '?recovery=true';
    return; // stop initialization
  }

  if (isRecoveryMode) {
    console.log('[Recovery] Recovery mode activated');

    // Apply conservative settings for recovery mode:
    // - Auto-preview OFF (no automatic renders)
    // - Quality set to fast (minimum quality settings)
    localStorage.setItem(STORAGE_KEY_AUTO_PREVIEW_ENABLED, 'false');
    localStorage.setItem(STORAGE_KEY_PREVIEW_QUALITY, 'fast');

    // Clean up crash detection flags
    localStorage.removeItem(STORAGE_KEY_WASM_INIT_STARTED);
    localStorage.removeItem(STORAGE_KEY_WASM_INIT_COMPLETED);

    // Check for recovery data
    const recoverySource = localStorage.getItem(STORAGE_KEY_RECOVERY_SOURCE);
    const recoveryTimestamp = localStorage.getItem(
      STORAGE_KEY_RECOVERY_TIMESTAMP
    );

    if (recoverySource && recoveryTimestamp) {
      const elapsed = Date.now() - parseInt(recoveryTimestamp, 10);
      // Only restore if recovery data is less than 1 hour old
      if (elapsed < 3600000) {
        console.log('[Recovery] Found recovery data, will restore after init');
        // Store for later restoration after UI is ready
        window._recoverySource = recoverySource;
      }
      // Clear recovery data
      localStorage.removeItem(STORAGE_KEY_RECOVERY_SOURCE);
      localStorage.removeItem(STORAGE_KEY_RECOVERY_TIMESTAMP);
    }

    // Remove recovery param from URL without reloading
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('recovery');
    window.history.replaceState({}, document.title, cleanUrl.toString());
  }

  // Memory Monitor: Track memory usage and graceful degradation
  // Will be connected to render controller after WASM init
  // Note: Monitor is initialized here and callbacks fire on state changes

  /**
   * Update memory UI elements based on current state
   * @param {string} state - Memory state (normal, warning, critical, emergency)
   * @param {Object} usage - Memory usage info
   */
  function updateMemoryUI(state, usage) {
    const badge = document.getElementById('memoryStatusBadge');
    const badgeText = document.getElementById('memoryStatusText');
    const banner = document.getElementById('memoryBanner');
    const bannerText = document.getElementById('memoryBannerText');

    if (!badge || !banner) return;

    // Update badge
    badge.dataset.state = state;
    if (badgeText) {
      badgeText.textContent = `${usage.heapMB} MB`;
    }

    // Show badge when not normal (or always show if user prefers)
    if (state === 'normal') {
      badge.classList.add('hidden');
    } else {
      badge.classList.remove('hidden');
    }

    // Update banner for critical/emergency states
    if (state === 'critical' || state === 'emergency') {
      banner.dataset.state = state;
      banner.dataset.visible = 'true';

      if (bannerText) {
        if (state === 'emergency') {
          bannerText.textContent =
            'Critical memory usage! Auto-preview disabled. Please save your work immediately.';
        } else {
          bannerText.textContent =
            'High memory usage detected. Consider reducing model complexity or saving your work.';
        }
      }
    } else {
      banner.dataset.visible = 'false';
    }
  }

  const _memoryMonitor = initMemoryMonitor({
    onWarning: (usage) => {
      console.log(`[Memory] Warning state: ${usage.heapMB}MB`);
      updateMemoryUI('warning', usage);
    },
    onCritical: (usage) => {
      console.log(`[Memory] Critical state: ${usage.heapMB}MB`);
      updateMemoryUI('critical', usage);
    },
    onEmergency: (usage) => {
      console.log(`[Memory] Emergency state: ${usage.heapMB}MB`);
      updateMemoryUI('emergency', usage);
      _announceError(
        `Memory emergency: usage at ${usage.heapMB} megabytes. Auto-preview has been disabled.`
      );
      // Disable auto-preview at emergency level
      if (typeof autoPreviewUserEnabled !== 'undefined') {
        autoPreviewUserEnabled = false;
        const autoPreviewToggle = document.getElementById('autoPreviewToggle');
        if (autoPreviewToggle) {
          autoPreviewToggle.checked = false;
        }
      }
    },
    onRecovery: (usage) => {
      console.log(`[Memory] Recovered to normal: ${usage.heapMB}MB`);
      updateMemoryUI('normal', usage);
    },
  });

  // Memory banner action handlers
  document
    .getElementById('memoryBannerDismiss')
    ?.addEventListener('click', () => {
      const banner = document.getElementById('memoryBanner');
      if (banner) banner.dataset.visible = 'false';
    });

  document.getElementById('memoryBannerSave')?.addEventListener('click', () => {
    // This banner tells the user to save immediately, so the button has to
    // reach a real save.
    getFileActionsController().onSave();
  });

  document
    .getElementById('memoryBannerReduceFn')
    ?.addEventListener('click', () => {
      // Reduce export quality to low. The mode lives in main.js (File > Export
      // Quality); there is no select to poke.
      setExportQualityMode('low');
      // Also reduce preview quality to fast. Dispatching 'change' here would
      // start renders, because that handler kicks auto-preview — and rendering
      // is the memory-hungry operation this banner is warning about. Do exactly
      // what the handler does, minus the kick.
      const previewQuality = document.getElementById('previewQualitySelect');
      if (previewQuality) {
        previewQuality.value = 'fast';
        try {
          localStorage.setItem(STORAGE_KEY_PREVIEW_QUALITY, 'fast');
        } catch {
          // Persistence is best-effort; the in-session mode still applies.
        }
        applyPreviewQualityMode();
      }
      // Both selects live in panels the user may not have open, so without
      // this the button changed nothing they could see or hear. updateStatus
      // already speaks through stateManager.announceChange — pairing it with
      // announceImmediate says everything twice.
      updateStatus(
        'Quality reduced: preview set to Fast, export set to Low.',
        'info'
      );
    });

  document
    .getElementById('memoryBannerDisableAuto')
    ?.addEventListener('click', () => {
      // Disable auto-preview
      const autoPreviewToggle = document.getElementById('autoPreviewToggle');
      if (autoPreviewToggle && autoPreviewToggle.checked) {
        autoPreviewToggle.checked = false;
        autoPreviewToggle.dispatchEvent(new Event('change'));
      }
      updateStatus(
        'Automatic preview turned off. Use Preview or Render when you are ready.',
        'info'
      );
    });

  document
    .getElementById('memoryBannerExport')
    ?.addEventListener('click', () => {
      // Downloads the render that already exists, and says so plainly when
      // there is none. Deliberately does not start a fresh render: rendering
      // is the memory-hungry operation this banner is warning about, which
      // is why this is the one export path that opts out of render-on-demand.
      exportFormatFromMenu('stl', { renderIfNeeded: false });
      console.log('[Memory] STL export triggered for emergency save');
    });

  document
    .getElementById('memoryBannerReload')
    ?.addEventListener('click', () => {
      // Recovery mode boots with the code editor disabled to cut memory. It
      // does not carry work across the reload, so the button only reloads,
      // and its tooltip promises no save. The beforeunload dirty guard still
      // stops an unsaved buffer here.
      window.location.href = window.location.pathname + '?recovery=true';
    });

  // Listen for storage-quota-exceeded events dispatched by preset-manager and
  // saved-projects-manager when persistence fails, so the user gets visible +
  // audible feedback instead of a console-only error.
  window.addEventListener('storage-quota-exceeded', (e) => {
    const msg =
      e.detail?.message || 'Storage is full. Data could not be saved.';
    // One announcement, not three: updateStatus would say it politely,
    // _announceError assertively, and the toast assertively again. The
    // toast's is the one worth keeping: a failed save is an error, so it
    // belongs in the assertive region, and the toast says "Storage Problem"
    // first so the announcement names its own subject.
    updateStatus(msg, 'error', { announce: false });
    showErrorToast({ title: 'Storage Problem', message: msg });
  });

  let statusArea = null;
  let cameraPanelController = null; // Declared here, initialized later
  // The person's choice, read back at every start (recovery mode writes it off).
  let autoPreviewEnabled = readAutoPreviewChoice();
  // Runtime mapping from preset name to companion file paths (built on ZIP load).
  // Stores path references only — content is resolved lazily on preset activation.
  let presetCompanionMap = null;
  // Canonical project files snapshot used as the clean base when applying presets.
  // This prevents alias-mounted companion files from one preset bleeding into the next.
  let canonicalProjectFiles = null;
  // Preset tracking state — must be declared before handleFile (which calls
  // forceClearPresetSelection) to avoid a TDZ error during draft restoration.
  let isLoadingPreset = false;
  let currentPresetSignature = null;
  let isPresetDirty = false;
  let autoPreviewUserEnabled = autoPreviewEnabled;
  let previewQuality = RENDER_QUALITY.DESKTOP_DEFAULT;

  // CRITICAL: Declare DOM element variables early to avoid Temporal Dead Zone errors
  // These will be assigned actual values later when DOM queries are performed
  let previewStatusBar = null;
  let previewStatusText = null;
  let previewStatusStats = null;

  // CRITICAL: Declare memoryPollInterval early to avoid TDZ in startMemoryPolling()
  let memoryPollInterval = null;

  // CRITICAL: Import validation constants early to avoid TDZ in handleFile()
  let FILE_SIZE_LIMITS = null;
  let validateFileUpload = null;
  try {
    const validationModule = await import('./js/validation-constants.js');
    FILE_SIZE_LIMITS = validationModule.FILE_SIZE_LIMITS;
  } catch (e) {
    console.error('Failed to import validation constants:', e);
  }

  // File handler controller -- declared early so wrappers can reference it;
  // assigned after all const deps are available (see initFileHandler call below).
  let fileHandler; // eslint-disable-line prefer-const
  // Built when folder sync initializes; null until then and on browsers with
  // no File System Access API at all.
  let folderWriteBack = null;

  // Explicit saves into the connected folder. Every write is asked for,
  // goes through FolderWriteBack's self-trigger contract, and is announced.
  const folderSaveActions = createFolderSaveActions({
    getWriteBack: () => folderWriteBack,
    isEnabled: () => isFlagEnabled('folder_sync_writeback'),
    announce: (message) => announceImmediate(message),
    onStatus: (message, level) => updateStatus(message, level),
  });

  /**
   * Show or hide the folder-saving affordances. Called wherever the answer
   * could have changed: a folder connects or disconnects, a render finishes,
   * a project loads.
   *
   * The button does not exist unless all three conditions hold, rather than
   * existing and failing when pressed.
   */
  function refreshFolderSaveAffordances() {
    const state = stateManager.getState();
    const possible = folderSaveActions.canSave();

    const saveBtn = document.getElementById('saveToFolderBtn');
    if (saveBtn) {
      saveBtn.classList.toggle('hidden', !(possible && Boolean(state.stl)));
    }

    const companionBtn = document.getElementById('companionSaveToFolderBtn');
    if (companionBtn) {
      const hasCompanions = Boolean(
        state.projectFiles && state.projectFiles.size > 1
      );
      companionBtn.classList.toggle('hidden', !(possible && hasCompanions));
    }
  }

  function cloneProjectFiles(files) {
    return files ? new Map(files) : null;
  }

  function setCanonicalProjectFiles(files) {
    canonicalProjectFiles = cloneProjectFiles(files);
  }
  let previewQualityMode = PREVIEW_QUALITY_DEFAULT;
  /**
   * True while a drawing-editor session is open.
   *
   * The charm behind the editor is a thing somebody is GLANCING at while they
   * work on the drawing in front of it, not the thing they are judging, and
   * on the traced Bathroom icon the charm's own `$fn = 64` puts the
   * app's default quality at 65,288 triangles and 0.31 s a render. DRAFT is
   * 26,120 and 0.16 s for a charm that is identical except for the clip's
   * rounded edges.
   *
   * It never UPGRADES anybody: DRAFT is the cheapest of the fixed presets, so
   * taking it can only cost less. The one mode it leaves alone is 'auto',
   * which chooses per model and can legitimately pick something coarser than
   * DRAFT for a complex one - forcing DRAFT there would be making somebody's
   * preview slower in the name of speed.
   */
  let editorDraftQuality = false;
  // The parameter hash of the charm drawn as a draft of the drawing
  // being edited, while that draft stands; null once anything else is drawn
  // or committed. The badge reads from it.
  let draftPreviewHash = null;

  const AUTO_PREVIEW_FORCE_FAST_MS = 2 * 60 * 1000;
  // With Manifold, renders are fast enough that only one over 15 s counts
  // as slow; a lower bar switches to fast mode when it is not needed.
  const AUTO_PREVIEW_SLOW_RENDER_MS = 15000;
  // Manifold handles high polygon counts efficiently, so the bar is 300K
  // triangles.
  const AUTO_PREVIEW_TRIANGLE_THRESHOLD = 300000;
  const autoPreviewHints = {
    forceFastUntil: 0,
    lastPreviewDurationMs: null,
    lastPreviewTriangles: null,
  };
  let adaptivePreviewMemo = { key: null, info: null };

  const updateBanner = document.getElementById('updateBanner');
  const updateBannerRefreshBtn = document.getElementById('updateBannerRefresh');
  const updateBannerDismissBtn = document.getElementById('updateBannerDismiss');

  // Register Service Worker for PWA support
  // In development, avoid Service Worker caching/stale assets which can break testing.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });
      console.log('[PWA] Service Worker registered:', registration.scope);

      let waitingWorker = registration.waiting || null;
      let cacheClearPending = false;

      const showUpdateBanner = (worker) => {
        if (!updateBanner) return;
        waitingWorker = worker;
        updateBanner.classList.remove('hidden');
      };

      const hideUpdateBanner = () => {
        if (!updateBanner) return;
        updateBanner.classList.add('hidden');
      };

      const requestUpdate = () => {
        updateStatus('Updating app... Reloading soon.');
        requestSwUpdate({
          worker: waitingWorker,
          button: updateBannerRefreshBtn,
        });
      };

      const CACHE_CLEAR_TIMEOUT = 10000; // 10 seconds before showing recovery dialog
      const CACHE_CLEAR_EXPECTED = 3000; // Expected time for cache clear

      const requestCacheClear = async () => {
        if (!navigator.serviceWorker?.controller) {
          updateStatus('Cache clear unavailable', 'error');
          return;
        }
        if (cacheClearPending) return;
        cacheClearPending = true;

        // Update button to show progress
        const clearCacheBtn = document.getElementById('clearCacheBtn');
        const originalBtnText = clearCacheBtn?.textContent;
        if (clearCacheBtn) {
          setBusy(clearCacheBtn, true);
          clearCacheBtn.textContent = 'Clearing...';
          clearCacheBtn.setAttribute('aria-busy', 'true');
        }

        updateStatus('Clearing cache...');
        // The downloaded engines go with everything else, and the Forge
        // engine is chosen again.
        await clearEngineDownloads();
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });

        // Track completion via message event
        let cacheCleared = false;
        const onCacheCleared = (event) => {
          // Validate message type against allowlist
          if (!isValidServiceWorkerMessage(event, ['CACHE_CLEARED'])) {
            return; // Ignore invalid messages
          }
          if (event.data.type === 'CACHE_CLEARED') {
            cacheCleared = true;
            navigator.serviceWorker.removeEventListener(
              'message',
              onCacheCleared
            );
          }
        };
        navigator.serviceWorker.addEventListener('message', onCacheCleared);

        // Wait for expected time, then check progress
        await new Promise((resolve) =>
          setTimeout(resolve, CACHE_CLEAR_EXPECTED)
        );

        if (cacheCleared || !cacheClearPending) {
          // Cache was cleared successfully
          cacheClearPending = false;
          updateStatus('Cache cleared. Reloading...', 'success');
          window.location.reload();
          return;
        }

        // Cache clear is taking longer - wait until timeout
        const remainingTime = CACHE_CLEAR_TIMEOUT - CACHE_CLEAR_EXPECTED;
        await new Promise((resolve) => setTimeout(resolve, remainingTime));

        if (cacheCleared || !cacheClearPending) {
          // Cleared during extended wait
          cacheClearPending = false;
          updateStatus('Cache cleared. Reloading...', 'success');
          window.location.reload();
          return;
        }

        // Show recovery dialog - cache clear is hanging
        navigator.serviceWorker.removeEventListener('message', onCacheCleared);

        if (clearCacheBtn) {
          setBusy(clearCacheBtn, false);
          clearCacheBtn.textContent = originalBtnText || 'Clear Cache';
          clearCacheBtn.removeAttribute('aria-busy');
        }

        const action = await showCacheRecoveryDialog();
        cacheClearPending = false;

        if (action === 'force') {
          updateStatus('Force reloading...', 'success');
          window.location.reload();
        } else if (action === 'wait') {
          // User chose to wait - just reset state
          updateStatus('Cache clear may still be in progress');
        }
      };

      /**
       * Show recovery dialog when cache clear takes too long
       * @returns {Promise<string>} 'force', 'wait', or null
       */
      function showCacheRecoveryDialog() {
        return new Promise((resolve) => {
          const modal = document.createElement('div');
          modal.className = 'preset-modal confirm-modal';
          modal.setAttribute('role', 'alertdialog');
          modal.setAttribute('aria-labelledby', 'cacheRecoveryTitle');
          modal.setAttribute('aria-describedby', 'cacheRecoveryMessage');
          modal.setAttribute('aria-modal', 'true');

          modal.innerHTML = `
            <div class="preset-modal-content confirm-modal-content">
              <div class="preset-modal-header">
                <h3 id="cacheRecoveryTitle">Cache Clear Taking Longer Than Expected</h3>
              </div>
              <div class="modal-body">
                <p id="cacheRecoveryMessage">
                  The cache clearing operation is taking longer than usual. This can happen 
                  if there are many cached files or if the browser is busy.
                </p>
                <p>What would you like to do?</p>
              </div>
              <div class="preset-modal-footer">
                <button type="button" class="btn btn-outline" data-action="wait">Continue Waiting</button>
                <button type="button" class="btn btn-primary" data-action="force">Force Reload</button>
              </div>
            </div>
          `;

          const handleAction = (action) => {
            document.body.removeChild(modal);
            resolve(action);
          };

          modal.addEventListener('click', (e) => {
            const btn = e.target.closest('button[data-action]');
            if (btn) {
              handleAction(btn.dataset.action);
            } else if (e.target === modal) {
              handleAction(null);
            }
          });

          modal.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
              handleAction(null);
            }
          });

          document.body.appendChild(modal);
          modal.querySelector('button[data-action="force"]')?.focus();
        });
      }

      if (updateBannerRefreshBtn) {
        updateBannerRefreshBtn.addEventListener('click', requestUpdate);
      }
      if (updateBannerDismissBtn) {
        updateBannerDismissBtn.addEventListener('click', hideUpdateBanner);
      }

      const clearCacheBtn = document.getElementById('clearCacheBtn');
      if (clearCacheBtn) {
        clearCacheBtn.addEventListener('click', requestCacheClear);
        if (!navigator.serviceWorker.controller) {
          clearCacheBtn.disabled = true;
          clearCacheBtn.title =
            'Cache clearing is available after the service worker activates.';
        }
      }

      if (registration.waiting && navigator.serviceWorker.controller) {
        showUpdateBanner(registration.waiting);
      }

      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        console.log('[PWA] Update found, installing new service worker');

        newWorker.addEventListener('statechange', () => {
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            console.log('[PWA] New version available - waiting to activate');
            showUpdateBanner(newWorker);
          }
        });
      });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (clearCacheBtn) {
          clearCacheBtn.disabled = false;
          clearCacheBtn.title = '';
        }
      });

      navigator.serviceWorker.addEventListener('message', (event) => {
        // Validate message type against allowlist
        if (!isValidServiceWorkerMessage(event, SERVICE_WORKER_MESSAGE_TYPES)) {
          console.warn(
            '[SW] Ignoring invalid or unexpected message:',
            event.data
          );
          return;
        }

        if (event.data.type === 'SW_UPDATED') {
          console.log(`[SW] Service worker ${event.data.version} is active`);
          // A worker of a newer build in charge of this page: the host has
          // moved on from this page's files, so it reloads on the Main Page
          // and offers the banner anywhere else.
          const action = staleShellAction({
            pageStamp: __BUILD_STAMP__,
            workerVersion: event.data.version,
            surface: document.body.getAttribute('data-app-surface'),
          });
          if (action === 'reload') window.location.reload();
          else if (action === 'banner')
            showUpdateBanner(navigator.serviceWorker.controller);
          return;
        }

        if (event.data.type === 'CACHE_CLEARED') {
          cacheClearPending = false;
          updateStatus('Cache cleared. Reloading...', 'success');
          window.location.reload();
        }
      });

      // Check for updates periodically (every hour)
      setInterval(
        () => {
          registration.update();
        },
        60 * 60 * 1000
      );
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
      const clearCacheBtn = document.getElementById('clearCacheBtn');
      if (clearCacheBtn) {
        clearCacheBtn.disabled = true;
        clearCacheBtn.title = 'Cache clearing is unavailable right now.';
      }
    }
  } else {
    console.log('[PWA] Service Worker disabled (dev) or not supported');
    const clearCacheBtn = document.getElementById('clearCacheBtn');
    if (clearCacheBtn) {
      clearCacheBtn.disabled = true;
      clearCacheBtn.title = 'Cache clearing is available in the installed app.';
    }
  }

  // Note: App is installable via browser-native prompts (Chrome address bar, iOS Share menu)
  // No custom install UI needed

  // Show success message for native installation
  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed successfully via browser');

    const installed = '✅ App installed! You can now use it offline.';
    // The status area is not live; the announcer speaks.
    announceImmediate(installed);
    const statusArea = document.getElementById('statusArea');
    if (statusArea) {
      const originalText = statusArea.textContent;
      statusArea.textContent = installed;
      setTimeout(() => {
        statusArea.textContent = originalText;
      }, 5000);
    }
  });

  // Terminate WASM worker before page unload to prevent browser freeze.
  // Without this, a mid-render worker blocks the unload sequence on some browsers.
  window.addEventListener('beforeunload', () => {
    if (renderController) {
      renderController.terminate();
    }
  });

  // The Back button gets an answer instead of the door. Installed
  // before any surface can flip, so the very first project opened is guarded.
  installBackGuard();

  // Initialize theme (before any UI rendering)
  themeManager.init();

  // Initialize static modal focus management (WCAG 2.2 SC 2.4.11 Focus Not Obscured)
  initStaticModals();

  // Initialize configurable keyboard shortcuts
  initKeyboardShortcuts();

  // Advertise shortcuts to assistive technology and keep the
  // attributes current when the user re-maps a shortcut.
  applyAriaKeyshortcuts(keyboardConfig.getAllShortcuts());
  keyboardConfig.addChangeListener((shortcuts) =>
    applyAriaKeyshortcuts(shortcuts)
  );

  // Initialize saved projects UI controller
  // updateCompanionSaveButton is wrapped because companionFilesCtrl is
  // created later (after DOM element queries); the wrapper defers safely
  // since savedProjectsUI only invokes it after user interaction.
  // Set by the folder-sync section when folder_sync_watch is enabled; lets
  // the folder-link card-load path start watching after it loads state.
  let resumeFolderWatch = null;
  const savedProjectsUI = initSavedProjectsUI({
    showConfirmDialog,
    showProcessingOverlay,
    // A card pressed in the first seconds, before the file handler exists,
    // waits for it instead of failing on nothing.
    handleFile: async (...args) => {
      while (!fileHandler) await new Promise((r) => setTimeout(r, 50));
      return fileHandler.handleFile(...args);
    },
    updateStatus,
    updateCompanionSaveButton: (...args) =>
      companionFilesCtrl.updateCompanionSaveButton(...args),
    downloadSingleProject,
    setCurrentSavedProjectId: (id) => {
      currentSavedProjectId = id;
    },
    // Pointer-model load path: read a folder-link project's contents from
    // disk via its stored handle. Defined here as a closure because the
    // folder-sync controller/watcher live later in initApp — they are
    // initialized long before any card's Load click can invoke this.
    readLinkedFolder: async (project) => {
      const syncCtrl = getFolderSyncController();
      if (!syncCtrl.isSupported()) {
        return {
          ok: false,
          reason: 'no-handle',
          message: 'This browser cannot access local folders.',
        };
      }
      if (!project.folderRef) {
        return { ok: false, reason: 'no-handle' };
      }
      const handle = await loadFolderHandle({ key: project.folderRef });
      if (!handle) {
        return { ok: false, reason: 'no-handle' };
      }
      try {
        let perm =
          typeof handle.queryPermission === 'function'
            ? await handle.queryPermission({ mode: 'readwrite' })
            : 'prompt';
        if (perm !== 'granted') {
          if (typeof handle.requestPermission !== 'function') {
            return { ok: false, reason: 'permission-denied' };
          }
          perm = await handle.requestPermission({ mode: 'readwrite' });
        }
        if (perm !== 'granted') {
          return { ok: false, reason: 'permission-denied' };
        }
      } catch (permErr) {
        return {
          ok: false,
          reason: 'permission-denied',
          message: permErr?.message,
        };
      }
      try {
        const files = [];
        await fileHandler.collectFilesFromDir(handle, handle.name, files);
        if (files.length === 0) {
          return {
            ok: false,
            reason: 'read-error',
            message: 'The linked folder is empty.',
          };
        }
        // The record stores a root-relative main path; disk paths carry the
        // (possibly renamed) root folder prefix, so match by suffix and fall
        // back to the selection prompt if the file moved.
        let mainAbs =
          files.find((f) =>
            (f.webkitRelativePath || '').endsWith(`/${project.mainFilePath}`)
          )?.webkitRelativePath || null;
        if (!mainAbs) {
          const selection = await fileHandler.prepareFolderSelection(files);
          if (!selection) {
            return {
              ok: false,
              reason: 'read-error',
              message: 'No main .scad file was selected.',
            };
          }
          mainAbs = selection.mainFilePath;
        }
        const read = await readProjectFilesFromList(files, mainAbs);
        const mainRel = read.rootDir
          ? mainAbs.replace(`${read.rootDir}/`, '')
          : mainAbs;
        if (mainRel !== project.mainFilePath) {
          await updateProject({ id: project.id, mainFilePath: mainRel });
        }
        await syncCtrl.adoptHandle(handle);
        const projectFilesMap = new Map(Object.entries(read.projectFiles));
        return {
          ok: true,
          content: read.mainContent,
          projectFiles: projectFilesMap.size > 0 ? projectFilesMap : null,
          mainFilePath: projectFilesMap.size > 0 ? mainRel : null,
          fileName: read.mainFile.name,
          // Called by the UI after handleFile so the watcher snapshots the
          // freshly-loaded state, not the pre-load one.
          finishConnect: async () => {
            if (resumeFolderWatch) await resumeFolderWatch();
          },
        };
      } catch (readErr) {
        return {
          ok: false,
          reason: 'read-error',
          message: readErr?.message ?? String(readErr),
        };
      }
    },
  });

  // Initialize saved projects database
  try {
    const { type } = await initSavedProjectsDB();
    console.log(`[Saved Projects] Initialized with ${type}`);

    // Log diagnostics in development mode or if there are potential issues
    const diagnostics = await getStorageDiagnostics();
    if (
      diagnostics.indexedDbProjectCount !== diagnostics.localStorageProjectCount
    ) {
      console.warn('[Saved Projects] Storage mismatch detected:', {
        indexedDb: diagnostics.indexedDbProjectCount,
        localStorage: diagnostics.localStorageProjectCount,
      });
    }

    // Render saved projects list on welcome screen
    await savedProjectsUI.renderSavedProjectsList();
  } catch (error) {
    console.error('[Saved Projects] Initialization failed:', error);
    // Still try to render from localStorage as fallback
    try {
      await savedProjectsUI.renderSavedProjectsList();
    } catch (renderError) {
      console.error(
        '[Saved Projects] Render fallback also failed:',
        renderError
      );
    }
  }

  // How the person holds one Joy-Con and uses the two (joycon.js): the
  // app's controller reads a Joy-Con through that layout. Kept, asked once.
  let joyConChoice = loadJoyConChoice();
  const setJoyConChoice = (part) => {
    joyConChoice = { ...joyConChoice, ...part };
    saveJoyConChoice(joyConChoice);
    syncControllerPanel();
  };

  // Initialize gamepad controller (if supported)
  let gamepadController = null;
  if (isGamepadSupported()) {
    gamepadController = createGamepadController({
      cameraSensitivity: 1.0,
      parameterSensitivity: 1.0,
      deadzone: 0.15,
      layout: (pad) => joyConLayout(pad, joyConChoice),
    });
    console.log('[Input] Gamepad controller initialized');
  }
  // The person's controller assignments (Preferences > Controller); the
  // layout follows the plugged-in pad until one is chosen. Their own named
  // presets follow the built-in ones.
  const gamepadPresets = createGamepadPresetStore();
  const gamepadMapping = createGamepadMapping({
    getPadId: () => gamepadController?.getGamepadInfo()?.id ?? '',
    userPresets: gamepadPresets,
  });
  // The game's own buttons, named by the layout the app's table follows.
  const walkMapping = createWalkMapping({
    getPadId: () => gamepadController?.getGamepadInfo()?.id ?? '',
    getFamily: () => gamepadMapping.get().family,
  });
  // Keys ▸ Controller: the status line, the assignments and the test.
  initControllerPanel({
    walkMapping,
    getGamepadStatus: () => ({
      supported: !!gamepadController,
      // The same display-name trim the connection status line uses, so the
      // two surfaces name one device the same way.
      padName:
        gamepadController?.getGamepadInfo?.()?.id?.split(' (')[0] ?? null,
    }),
    gamepad: {
      mapping: gamepadMapping,
      capture: (ms) =>
        gamepadController
          ? gamepadController.captureNextInput(ms)
          : Promise.resolve(null),
      cancelCapture: () => gamepadController?.cancelCapture(),
      announce: (text) => announceImmediate(text),
      pad: () => gamepadController?.activePad() ?? null,
      presets: gamepadPresets,
    },
    joyCon: {
      get: () => joyConChoice,
      set: setJoyConChoice,
    },
  });

  // Somewhere to act, not the main region a screen reader reads whole: the
  // open project's first parameter, its Generate button where the
  // Customizer is closed, or on the Main Page the file upload.
  const somewhereToAct = () =>
    [
      firstParameterControl(document.getElementById('parametersContainer')),
      document.getElementById('primaryActionBtn'),
      document.getElementById('fileInput'),
    ].find((el) => el?.checkVisibility?.()) ?? null;

  // The first time one Joy-Con, or the two, connect: how is it held, or how
  // are the two used. Closing without a choice asks again the next time.
  // A pad connecting finds nothing focused, so focus goes somewhere to act.
  const joyConModals = {
    hold: document.getElementById('joyConHoldModal'),
    pair: document.getElementById('joyConPairModal'),
  };
  for (const [part, modal] of Object.entries(joyConModals)) {
    if (!modal) continue;
    setupModalCloseHandlers(modal);
    modal.querySelectorAll(`[data-${part}]`).forEach((button) => {
      button.addEventListener('click', () => {
        setJoyConChoice({ [part]: button.dataset[part] });
        closeModal(modal);
      });
    });
  }
  const askJoyCon = (info) => {
    const kind = joyConKind(info);
    if (!kind) return;
    const part = kind === 'pair' ? 'pair' : 'hold';
    if (joyConChoice[part] || isAnyModalOpen()) return;
    if (joyConModals[part]) {
      openModal(joyConModals[part], { fallbackFocus: somewhereToAct });
    }
  };
  if (gamepadController) {
    gamepadController.on('connected', askJoyCon);
    // A pad the browser already listed at start connected before this.
    const listed = gamepadController.getGamepadInfo();
    if (listed) askJoyCon(listed);
  }

  // Storage UI - Update storage display
  const formatStorageUsage = (usage) => {
    if (typeof usage !== 'number' || !Number.isFinite(usage) || usage < 0) {
      return 'Unknown';
    }
    if (usage === 0) {
      return '0 MB';
    }

    const gb = 1024 * 1024 * 1024;
    const mb = 1024 * 1024;
    const useGb = usage >= gb;
    const value = useGb ? usage / gb : usage / mb;
    const unit = useGb ? 'GB' : 'MB';
    const decimals = useGb ? 1 : value < 1 ? 3 : value < 10 ? 2 : 1;

    return `${parseFloat(value.toFixed(decimals))} ${unit}`;
  };

  async function updateStorageDisplay() {
    const estimate = await getStorageEstimate();

    if (!estimate.supported) {
      // Hide storage panel if not supported
      const storagePanel = document.querySelector('.storage-panel');
      const notSupported = document.getElementById('storageNotSupported');
      if (storagePanel) storagePanel.style.display = 'none';
      if (notSupported) notSupported.classList.remove('hidden');
      return;
    }

    const meterFill = document.querySelector('.storage-meter-fill');
    const usedEl = document.getElementById('storage-used');
    const meter = document.querySelector('.storage-meter');

    if (meterFill && meter) {
      meterFill.style.width = `${estimate.percentUsed}%`;
      meter.setAttribute('aria-valuenow', estimate.percentUsed);

      // Set warning level
      if (estimate.percentUsed > 90) {
        meterFill.setAttribute('data-warning', 'high');
      } else if (estimate.percentUsed > 75) {
        meterFill.setAttribute('data-warning', 'medium');
      } else {
        meterFill.removeAttribute('data-warning');
      }
    }

    const usageText = formatStorageUsage(estimate.usage);

    // Add context about what's being measured
    let displayText = `${usageText} used`;
    const isDevMode = import.meta.env.DEV;
    const hasServiceWorker =
      'serviceWorker' in navigator && navigator.serviceWorker.controller;

    // Show helpful context when storage is minimal
    if (estimate.usage < 1024 * 1024 && isDevMode && !hasServiceWorker) {
      displayText += ' (dev mode: assets not cached)';
    } else if (estimate.usage < 1024 * 1024 && !hasServiceWorker) {
      displayText += ' (offline copy not stored yet)';
    }

    if (usedEl) usedEl.textContent = displayText;
  }

  // Smart Cache Clear Dialog
  async function showSmartCacheClearDialog() {
    try {
      const storageInfo = await getDetailedStorageInfo();

      const modal = document.createElement('div');
      modal.className = 'preset-modal cache-clear-dialog';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-labelledby', 'cacheClearTitle');
      modal.setAttribute('aria-describedby', 'cacheClearWarningText');
      modal.setAttribute('aria-modal', 'true');

      const hasProjects = storageInfo.savedDesignsCount > 0;

      modal.innerHTML = `
        <div class="preset-modal-content">
          <div class="preset-modal-header">
            <h3 id="cacheClearTitle" class="preset-modal-title">Clear Cache</h3>
          </div>

          <div class="preset-modal-body">
            <div class="cache-clear-warning">
              <span class="cache-clear-warning-icon" aria-hidden="true">⚠️</span>
              <div class="cache-clear-warning-text" id="cacheClearWarningText">
                <strong>Warning:</strong> This clears the app's cached data. Your saved projects
                are kept unless you untick Keep my Saved Projects below.
              </div>
            </div>

            <div class="cache-clear-sizes">
              <div class="cache-size-item">
                <div class="cache-size-label">App Cache</div>
                <div class="cache-size-value">${storageInfo.appCacheFormatted}</div>
              </div>
              <div class="cache-size-item">
                <div class="cache-size-label">Saved Projects</div>
                <div class="cache-size-value">${storageInfo.savedDesignsCount} project${storageInfo.savedDesignsCount !== 1 ? 's' : ''}</div>
              </div>
            </div>

            <div class="cache-clear-options">
              <label class="cache-clear-option">
                <input type="checkbox" id="clearAppCaches" checked />
                <div class="cache-clear-option-content">
                  <div class="cache-clear-option-label">Clear app caches (recommended)</div>
                  <div class="cache-clear-option-desc">Remove outdated app versions and cached resources</div>
                </div>
              </label>

              <label class="cache-clear-option preservation-on" id="preserveOption">
                <input type="checkbox" id="preserveSavedDesigns" checked />
                <div class="cache-clear-option-content">
                  <div class="cache-clear-option-label">
                    Keep my Saved Projects
                    <span class="preservation-indicator safe" id="preserveIndicator">
                      <span aria-hidden="true">✓</span> Will be kept
                    </span>
                  </div>
                  <div class="cache-clear-option-desc">
                    ${hasProjects ? `Preserve ${storageInfo.savedDesignsCount} project${storageInfo.savedDesignsCount !== 1 ? 's' : ''} and ${storageInfo.foldersCount} folder${storageInfo.foldersCount !== 1 ? 's' : ''}` : 'No projects to preserve'}
                  </div>
                </div>
              </label>
            </div>

            ${
              hasProjects
                ? `
              <div class="cache-clear-backup-prompt">
                <span>💾</span>
                <span>Export a backup before clearing?</span>
                <button type="button" class="btn btn-sm btn-outline" id="exportBeforeClearBtn">
                  Export Backup
                </button>
              </div>
            `
                : ''
            }
          </div>

          <div class="preset-modal-footer">
            <button class="btn btn-secondary" id="cacheClearCancelBtn">Cancel</button>
            <button class="btn btn-danger" id="cacheClearConfirmBtn">Clear Cache</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      // Wire up preserve checkbox visual feedback
      const preserveCheckbox = modal.querySelector('#preserveSavedDesigns');
      const preserveOption = modal.querySelector('#preserveOption');
      const preserveIndicator = modal.querySelector('#preserveIndicator');

      preserveCheckbox.addEventListener('change', () => {
        if (preserveCheckbox.checked) {
          preserveOption.classList.remove('preservation-off');
          preserveOption.classList.add('preservation-on');
          preserveIndicator.className = 'preservation-indicator safe';
          preserveIndicator.innerHTML =
            '<span aria-hidden="true">✓</span> Will be kept';
        } else {
          preserveOption.classList.remove('preservation-on');
          preserveOption.classList.add('preservation-off');
          preserveIndicator.className = 'preservation-indicator danger';
          preserveIndicator.innerHTML =
            '<span aria-hidden="true">⚠️</span> Will be deleted';
        }
      });

      // Export backup button
      const exportBtn = modal.querySelector('#exportBeforeClearBtn');
      if (exportBtn) {
        exportBtn.addEventListener('click', async () => {
          if (isBusy(exportBtn)) return;
          setBusy(exportBtn, true);
          exportBtn.textContent = 'Exporting...';
          try {
            await handleExportBackup();
          } finally {
            setBusy(exportBtn, false);
            exportBtn.textContent = 'Export Backup';
          }
        });
      }

      // Wait for user action
      return new Promise((resolve) => {
        const cancelBtn = modal.querySelector('#cacheClearCancelBtn');
        const confirmBtn = modal.querySelector('#cacheClearConfirmBtn');

        const cancel = () => {
          closeModal(modal);
          document.body.removeChild(modal);
          resolve(false);
        };

        cancelBtn.addEventListener('click', cancel);

        confirmBtn.addEventListener('click', async () => {
          const clearAppCaches = modal.querySelector('#clearAppCaches').checked;
          const preserveDesigns = modal.querySelector(
            '#preserveSavedDesigns'
          ).checked;

          confirmBtn.disabled = true;
          confirmBtn.textContent = 'Clearing...';
          confirmBtn.setAttribute('aria-busy', 'true');

          // Add timeout to prevent freeze during cache clearing
          const CACHE_CLEAR_TIMEOUT = 8000; // 8 seconds max before force reload

          try {
            // Race between cache clear and timeout
            const result = await Promise.race([
              clearCacheWithOptions({
                clearAppCaches,
                preserveSavedDesigns: preserveDesigns,
              }),
              new Promise((_, reject) =>
                setTimeout(
                  () => reject(new Error('Cache clear timeout')),
                  CACHE_CLEAR_TIMEOUT
                )
              ),
            ]);

            closeModal(modal);
            document.body.removeChild(modal);

            if (result.appCachesCleared || result.userDataCleared) {
              const msg = preserveDesigns
                ? 'App cache cleared. Your saved designs are preserved. Reloading...'
                : 'All data cleared. Reloading...';
              updateStatus(msg, 'success');
              await updateStorageDisplay();
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }

            resolve(true);
          } catch (error) {
            // Timeout or error occurred
            console.warn(
              '[Cache Clear] Operation timed out or failed:',
              error.message
            );

            // Force reload anyway - the cache clear may have partially succeeded
            // and reloading is the safest recovery action
            closeModal(modal);
            document.body.removeChild(modal);
            updateStatus(
              'Cache clear taking too long, forcing reload...',
              'warning'
            );
            setTimeout(() => {
              window.location.reload();
            }, 300);
            resolve(true);
          }
        });

        // Close on escape
        modal.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        });

        // Through the modal manager: Tab stays inside, and Cancel or Escape
        // gives focus back to the button that opened it.
        openModal(modal, { focusTarget: cancelBtn });
      });
    } catch (error) {
      console.error('[Storage] Smart cache clear error:', error);
      updateStatus('Error showing cache dialog', 'error');
    }
  }

  // Export backup handler
  async function handleExportBackup() {
    const dismissOverlay = showProcessingOverlay(
      'Exporting projects backup...',
      {
        hint: 'Packaging all projects. Please do not close or refresh the page.',
      }
    );
    try {
      updateStatus('Creating backup...', 'info');
      const result = await exportProjectsBackup();
      dismissOverlay();

      if (result.success && result.blob) {
        // Download the file
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        updateStatus(`Backup exported: ${result.fileName}`, 'success');
        stateManager.announceChange('Backup exported successfully');
      } else {
        updateStatus(`Export failed: ${result.error}`, 'error');
      }
    } catch (error) {
      dismissOverlay();
      console.error('[Storage] Export error:', error);
      updateStatus('Failed to export backup', 'error');
    }
  }

  // Download a single project as a ZIP
  async function downloadSingleProject(projectId) {
    const dismissOverlay = showProcessingOverlay('Preparing download...', {
      hint: 'Packaging project files. Please do not close or refresh the page.',
    });
    try {
      const result = await exportSingleProject(projectId);
      dismissOverlay();

      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        updateStatus(`Project downloaded: ${result.fileName}`, 'success');
        stateManager.announceChange('Project downloaded successfully');
      } else {
        updateStatus(`Download failed: ${result.error}`, 'error');
      }
    } catch (error) {
      dismissOverlay();
      console.error('[Storage] Single-project download error:', error);
      updateStatus('Failed to download project', 'error');
    }
  }

  // Import backup handler
  async function handleImportBackup(file) {
    try {
      updateStatus('Importing backup...', 'info');
      const result = await importProjectsBackup(file);

      if (result.success) {
        await savedProjectsUI.renderSavedProjectsList();
        const msg = `Imported ${result.imported} project${result.imported !== 1 ? 's' : ''}`;
        updateStatus(msg, 'success');
        stateManager.announceChange(msg);

        if (result.errors.length > 0) {
          console.warn('[Storage] Import errors:', result.errors);
        }
      } else {
        updateStatus(`Import failed: ${result.errors.join(', ')}`, 'error');
      }
    } catch (error) {
      console.error('[Storage] Import error:', error);
      updateStatus('Failed to import backup', 'error');
    }
  }

  // Wire up storage clear button
  const clearStorageBtn = document.getElementById('clearStorageBtn');
  if (clearStorageBtn) {
    clearStorageBtn.addEventListener('click', showSmartCacheClearDialog);
  }

  // Wire up export button
  const exportAllProjectsBtn = document.getElementById('exportAllProjectsBtn');
  if (exportAllProjectsBtn) {
    exportAllProjectsBtn.addEventListener('click', handleExportBackup);
  }

  // Wire up import button and hidden file input
  const importProjectsBtn = document.getElementById('importProjectsBtn');
  const importBackupInput = document.getElementById('importBackupInput');
  if (importProjectsBtn && importBackupInput) {
    importProjectsBtn.addEventListener('click', () => {
      importBackupInput.click();
    });

    importBackupInput.addEventListener('change', async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        await handleImportBackup(file);
        importBackupInput.value = ''; // Reset for next import
      }
    });
  }

  // Folder import — gated behind feature flag and webkitdirectory feature detection
  if (
    _isEnabled('folder_import') &&
    'webkitdirectory' in document.createElement('input')
  ) {
    const importFolderBtn = document.getElementById('importFolderBtn');
    const importFolderInput = document.getElementById('importFolderInput');

    if (importFolderBtn) importFolderBtn.hidden = false;

    // Welcome-zone folder affordance delegates to the same flow.
    const uploadZoneFolderBtn = document.getElementById('uploadZoneFolderBtn');
    if (uploadZoneFolderBtn && importFolderBtn) {
      uploadZoneFolderBtn.addEventListener('click', () =>
        importFolderBtn.click()
      );
    }
    // webkitdirectory is non-standard, so it is applied here (behind the
    // feature detection above) instead of in the static HTML.
    if (importFolderInput) {
      importFolderInput.setAttribute('webkitdirectory', '');
    }

    if (importFolderBtn && importFolderInput) {
      importFolderBtn.addEventListener('click', async () => {
        // Use the modern File System Access API if available (more reliable than webkitdirectory)
        if ('showDirectoryPicker' in window) {
          let dismissOverlay = () => {};
          try {
            const dirHandle = await window.showDirectoryPicker();
            dismissOverlay = showProcessingOverlay(
              `Reading folder "${dirHandle.name}"…`,
              'Scanning files and subfolders. Please do not close or refresh the page.'
            );
            const files = [];
            try {
              await fileHandler.collectFilesFromDir(
                dirHandle,
                dirHandle.name,
                files
              );
            } catch (collectErr) {
              dismissOverlay();
              showErrorToast({
                title: 'Folder Read Error',
                message: collectErr.message,
              });
              return;
            }
            if (files.length === 0) {
              dismissOverlay();
              showErrorToast({
                title: 'Empty Folder',
                message:
                  'No files found in the selected folder. The folder may be empty.',
              });
              return;
            }
            dismissOverlay();
            await fileHandler.handleFolderImport(files);
          } catch (err) {
            dismissOverlay();
            if (err.name === 'AbortError') {
              updateStatus('Folder selection canceled');
              return;
            }
            showErrorToast({
              title: 'Folder Import Error',
              message: err.message,
            });
          }
        } else {
          // Fallback to webkitdirectory input
          importFolderInput.value = '';
          importFolderInput.click();
        }
      });

      // Fallback: webkitdirectory input change handler
      importFolderInput.addEventListener('change', async (e) => {
        try {
          const files = e.target.files;
          if (!files || files.length === 0) {
            updateStatus('No files in selected folder');
            return;
          }
          await fileHandler.handleFolderImport(files);
          importFolderInput.value = '';
        } catch (err) {
          showErrorToast({
            title: 'Folder Import Error',
            message: err.message,
          });
        }
      });
    }
  }

  // ── Persistent local-folder sync (Chromium only) ────────────────────────
  //
  // Hidden by default. Reveals only when:
  //   1. The local_folder_sync feature flag is on, AND
  //   2. The runtime exposes showDirectoryPicker (Chromium today).
  //
  // The connect / restore flows reuse the existing file-handler folder
  // walker so the loaded files behave identically to a snapshot import;
  // this part adds persistence of the directory handle, and the watcher and
  // the write-back below build on it.
  const folderSyncCtrl = getFolderSyncController();
  // Exposed to the File menu; stays null on unsupported browsers so
  // the menu item can render disabled with an honest tooltip.
  let connectToLocalFolder = null;
  if (_isEnabled('local_folder_sync') && folderSyncCtrl.isSupported()) {
    const connectBtn = document.getElementById('connectFolderBtn');
    const statusEl = document.getElementById('folderSyncStatus');
    const statusText = document.getElementById('folderSyncStatusText');
    const restoreBtn = document.getElementById('folderSyncRestoreBtn');
    const disconnectBtn = document.getElementById('folderSyncDisconnectBtn');

    if (connectBtn) connectBtn.hidden = false;

    // ── Every linked folder listed, one connected ───────────────────────
    // Created here so the section only ever exists on a browser that can
    // actually hold folder handles.
    const linkedFoldersUi = createLinkedFoldersUi({
      listEl: document.getElementById('linkedFoldersList'),
      sectionEl: document.getElementById('linkedFolders'),
      listProjects: listSavedProjects,
      getActiveHandle: () => folderSyncCtrl.getHandle(),
      getActiveState: () => folderSyncCtrl.getState(),
      onOpen: (entry) => _openLinkedFolder(entry),
      onRemove: (entry) => _removeLinkedFolder(entry),
      onEmptyFocus: () => connectBtn?.focus(),
    });

    /**
     * Open a listed folder. A folder with a project card goes through the
     * normal card-load path, which already re-grants permission, re-reads
     * the folder from disk and adopts the handle as the active one.
     *
     * A folder with no card (the pre-multi-folder root slot, or one whose
     * card was deleted) has nothing to load, so it re-grants permission on
     * ITS OWN handle and goes through the connect-load path, which creates
     * the card. `restoreFromStored()` cannot serve here: it only ever reads
     * the root slot, so it would re-grant the wrong folder.
     */
    async function _openLinkedFolder(entry) {
      if (!entry?.handle) return;

      if (entry.projectId) {
        await savedProjectsUI.loadSavedProject(entry.projectId);
        return;
      }

      let granted = false;
      try {
        const queried =
          typeof entry.handle.queryPermission === 'function'
            ? await entry.handle.queryPermission({ mode: 'readwrite' })
            : 'prompt';
        granted =
          queried === 'granted' ||
          (typeof entry.handle.requestPermission === 'function' &&
            (await entry.handle.requestPermission({ mode: 'readwrite' })) ===
              'granted');
      } catch (err) {
        console.warn('[LinkedFolders] Permission request failed:', err);
        granted = false;
      }
      if (!granted) {
        updateStatus(
          'Folder connection denied: permission required',
          'warning'
        );
        return;
      }

      await folderSyncCtrl.adoptHandle(entry.handle);
      await _loadFromConnectedFolder(entry.handle);
    }

    /**
     * Remove a folder's link. Never touches the disk: it drops the stored
     * handle and the pointer record, which is all this browser holds.
     *
     * @returns {Promise<boolean>} True when the row should disappear.
     */
    async function _removeLinkedFolder(entry) {
      if (!entry) return false;

      const confirmed = await showConfirmDialog(
        `Remove the link to "${entry.name}"?\n\nYour files on disk are not touched. This removes the folder's link and its project card from this browser only.`,
        'Remove folder link',
        'Remove link',
        'Cancel'
      );
      if (!confirmed) return false;

      // Deleting the record clears its fh-* handle; a folder with no record
      // owns nothing but the handle itself.
      if (entry.projectId) {
        const result = await deleteProject(entry.projectId);
        if (!result.success) {
          showErrorToast({ title: 'Remove Failed', message: result.error });
          return false;
        }
      } else {
        await clearFolderHandle({ key: entry.key });
      }

      // The root slot mirrors whichever folder is active, so removing the
      // active one has to disconnect too — otherwise the next reload
      // hydrates a folder that is no longer listed.
      if (entry.activeState) {
        await folderSyncCtrl.disconnect();
      }

      announceImmediate(`Removed folder link: ${entry.name}`);
      updateStatus(`Removed folder link: ${entry.name}`);
      await savedProjectsUI.renderSavedProjectsList();
      return true;
    }

    /** Re-read the store and repaint the list. Safe to call at any time. */
    function refreshLinkedFolders() {
      return linkedFoldersUi.refresh();
    }

    // Deleting a folder-link card also clears that folder's handle, so the
    // list must follow the project list.
    document.addEventListener('saved-projects-rendered', () => {
      void refreshLinkedFolders();
    });

    /**
     * Reflect controller state into the status pill + buttons. Called
     * via `subscribe()` so this stays the single source of truth.
     *
     * The pill describes the ONE connected folder; the linked-folders
     * list below it shows every folder this browser knows. Connect Folder
     * therefore stays visible in every state — it is how a second folder
     * gets linked.
     */
    function _syncFolderUi(state, handle) {
      if (!statusEl || !statusText) return;
      const name = handle?.name ?? '';
      switch (state) {
        case 'connected':
          statusEl.hidden = false;
          statusEl.dataset.state = 'connected';
          statusText.textContent = `Connected to "${name}"`;
          if (restoreBtn) restoreBtn.hidden = true;
          if (disconnectBtn) disconnectBtn.hidden = false;
          break;
        case 'pending-restore':
          statusEl.hidden = false;
          statusEl.dataset.state = 'pending-restore';
          statusText.textContent = `"${name}": click Reconnect to re-grant permission for this session`;
          if (restoreBtn) restoreBtn.hidden = false;
          if (disconnectBtn) disconnectBtn.hidden = false;
          break;
        case 'denied':
          statusEl.hidden = false;
          statusEl.dataset.state = 'denied';
          statusText.textContent = `"${name}": permission was denied. Click Reconnect to try again, or Disconnect to forget.`;
          if (restoreBtn) restoreBtn.hidden = false;
          if (disconnectBtn) disconnectBtn.hidden = false;
          break;
        case 'idle':
        default:
          statusEl.hidden = true;
          statusEl.dataset.state = 'idle';
          statusText.textContent = '';
          if (restoreBtn) restoreBtn.hidden = true;
          if (disconnectBtn) disconnectBtn.hidden = true;
          break;
      }
      // Which row wears the Connected badge follows the pill.
      void refreshLinkedFolders();
    }

    folderSyncCtrl.subscribe(_syncFolderUi);

    /**
     * After connect / restore succeeds, walk the folder and hand the
     * collected files to the existing snapshot loader. Keeps the
     * file-loading semantics identical to the cross-browser flow so
     * everything downstream (parser, schema, presets, render) is
     * unchanged.
     */
    // Created below when folder_sync_watch is enabled
    let folderWatcher = null;

    async function _loadFromConnectedFolder(handle) {
      const dismissOverlay = showProcessingOverlay(
        `Reading folder "${handle.name}"\u2026`,
        'Scanning files and subfolders. Please do not close or refresh the page.'
      );
      const files = [];
      try {
        await fileHandler.collectFilesFromDir(handle, handle.name, files);
        if (files.length === 0) {
          dismissOverlay();
          showErrorToast({
            title: 'Empty Folder',
            message:
              'No files found in the connected folder. The folder may be empty.',
          });
          return;
        }
        dismissOverlay();

        const selection = await fileHandler.prepareFolderSelection(files);
        if (!selection) return;

        // Pointer model: persist the handle + a contentless folder-link
        // record; the disk stays the source of truth (contents are re-read
        // on every load, so browser storage caps no longer apply).
        const existing = await findLinkedProjectForHandle(handle);
        const folderRef =
          existing?.folderRef ||
          `fh-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        await saveFolderHandle(handle, { key: folderRef });

        const linkResult = await linkProjectFromFiles(
          files,
          selection.mainFilePath,
          { folderRef, existingId: existing?.id || null }
        );
        if (!linkResult.success) {
          showErrorToast({
            title: 'Folder Link Failed',
            message: linkResult.error,
          });
          return;
        }

        await touchProject(linkResult.id);
        currentSavedProjectId = linkResult.id;

        const projectFilesMap =
          linkResult.projectFiles &&
          Object.keys(linkResult.projectFiles).length > 0
            ? new Map(Object.entries(linkResult.projectFiles))
            : null;
        await fileHandler.handleFile(
          { name: linkResult.originalName },
          linkResult.mainContent,
          projectFilesMap,
          projectFilesMap ? linkResult.mainRelPath : null,
          'saved',
          linkResult.projectName
        );

        // Apply per-project UI preferences from the record (same contract as
        // loadSavedProject): cheap, because folder-link records are tiny.
        try {
          const record = await getProject(linkResult.id);
          if (record?.uiPreferences != null) {
            getUIModeController().importPreferences(record.uiPreferences, {
              applyImmediately: true,
            });
          }
        } catch (prefsErr) {
          console.warn(
            '[FolderSync] Could not apply project UI preferences:',
            prefsErr
          );
        }

        await savedProjectsUI.renderSavedProjectsList();
        updateStatus(`Connected folder loaded: ${linkResult.projectName}`);

        if (folderWatcher) {
          await folderWatcher.primeSnapshot();
          folderWatcher.start();
        }
      } catch (err) {
        dismissOverlay();
        showErrorToast({
          title: 'Folder Read Error',
          message: err?.message ?? String(err),
        });
      }
    }

    connectToLocalFolder = async () => {
      const result = await folderSyncCtrl.connect();
      if (result.ok && result.handle) {
        announceImmediate(`Connected to folder ${result.folderName}`);
        await _loadFromConnectedFolder(result.handle);
      } else if (result.reason === 'cancelled') {
        updateStatus('Folder connection canceled');
      } else if (result.reason === 'permission-denied') {
        updateStatus(
          'Folder connection denied: permission required',
          'warning'
        );
      } else if (result.reason === 'unsupported') {
        // Defensive: should not happen because the button is hidden.
        updateStatus(
          'This browser does not support persistent folder access',
          'warning'
        );
      } else {
        updateStatus(`Could not connect to folder: ${result.reason}`, 'error');
      }
    };

    connectBtn?.addEventListener('click', () => connectToLocalFolder());

    restoreBtn?.addEventListener('click', async () => {
      const result = await folderSyncCtrl.restoreFromStored();
      if (result.ok && result.handle) {
        announceImmediate(`Reconnected to folder ${result.folderName}`);
        await _loadFromConnectedFolder(result.handle);
      } else if (result.reason === 'permission-denied') {
        updateStatus(
          'Reconnect denied: folder remains pending until permission is granted',
          'warning'
        );
      } else if (result.reason === 'no-stored-handle') {
        updateStatus('No stored folder to reconnect to');
      } else {
        updateStatus(`Could not reconnect: ${result.reason}`, 'error');
      }
    });

    disconnectBtn?.addEventListener('click', async () => {
      await folderSyncCtrl.disconnect();
      announceImmediate('Disconnected from folder');
      updateStatus('Disconnected from folder');
    });

    // ── Watch the connected folder for external edits ──
    if (_isEnabled('folder_sync_watch')) {
      resumeFolderWatch = async () => {
        if (!folderWatcher) return;
        await folderWatcher.primeSnapshot();
        folderWatcher.start();
      };
      folderWatcher = new FolderChangeWatcher({
        getHandle: () => folderSyncCtrl.getHandle(),
        getWatchPaths: () => {
          const watchState = stateManager.getState();
          if (!watchState.projectFiles || watchState.projectFiles.size === 0) {
            return watchState.mainFilePath ? [watchState.mainFilePath] : [];
          }
          return Array.from(watchState.projectFiles.entries())
            .filter(
              ([, content]) =>
                typeof content === 'string' && !content.startsWith('data:')
            )
            .map(([path]) => path);
        },
        isRenderInFlight: () =>
          autoPreviewController?.state === PREVIEW_STATE.RENDERING,
        onPermissionLost: () => {
          updateStatus(
            'Lost permission to the connected folder. Click Reconnect to re-grant',
            'warning'
          );
          announceImmediate('Lost permission to the connected folder');
        },
        onChange: async (changes) => {
          const changeState = stateManager.getState();
          if (!changeState.uploadedFile) return;

          const nextProjectFiles = changeState.projectFiles
            ? new Map(changeState.projectFiles)
            : new Map();
          let mainContent = null;
          const changedPaths = [];
          for (const { path, file } of changes) {
            let text;
            try {
              text = await file.text();
            } catch (err) {
              console.warn('[FolderWatch] Could not read changed file:', err);
              continue;
            }
            nextProjectFiles.set(path, text);
            changedPaths.push(path);
            if (path === changeState.mainFilePath) {
              mainContent = text;
            }
            consolePanel.addSystemLine(
              `Detected change in ${path}: re-rendering`
            );
          }
          if (changedPaths.length === 0) return;

          const statePatch = { projectFiles: nextProjectFiles };
          if (mainContent !== null) {
            statePatch.uploadedFile = {
              ...changeState.uploadedFile,
              content: mainContent,
            };
          }
          stateManager.setState(statePatch);

          if (autoPreviewController) {
            if (mainContent !== null) {
              autoPreviewController.setScadContent(mainContent);
            }
            autoPreviewController.setProjectFiles(
              nextProjectFiles,
              changeState.mainFilePath
            );
            autoPreviewController.clearPreviewCache();
            autoPreviewController.onParameterChange(
              stateManager.getState().parameters
            );
          }

          announceImmediate(
            changedPaths.length === 1
              ? `Detected change in ${changedPaths[0]}, re-rendering`
              : `Detected changes in ${changedPaths.length} files, re-rendering`
          );
        },
      });

      folderSyncCtrl.subscribe((syncState) => {
        if (syncState !== 'connected') {
          folderWatcher.stop();
        }
        // Connecting or disconnecting a folder changes whether saving into one
        // is possible at all.
        refreshFolderSaveAffordances();
      });
    }

    // ── Writing back into the folder (off by default) ────────────────────
    //
    // The instance is built regardless and the flag is checked at every use.
    // The export and companion paths use it too, and they live far from this
    // block; hoisting the object is what lets them share the one self-trigger
    // contract instead of writing bytes of their own.
    folderWriteBack = new FolderWriteBack({
      getHandle: () => folderSyncCtrl.getHandle(),
      getWatcher: () => folderWatcher,
    });

    if (_isEnabled('folder_sync_writeback')) {
      // OpenSCAD desktop convention: presets live in <design>.json next to
      // the .scad. Case- and space-preserving (raw path, no slugging).
      presetManager.subscribe((event, _preset, modelName) => {
        if (!['save', 'delete', 'rename'].includes(event)) return;
        if (!folderWriteBack.isAvailable()) return;
        const wbState = stateManager.getState();
        if (!wbState.uploadedFile || presetModelKey(wbState) !== modelName) {
          return;
        }
        const mainPath = presetModelKey(wbState);
        const sidecarPath = mainPath.replace(/\.scad$/i, '.json');
        if (sidecarPath === mainPath) return;
        const hiddenParams = wbState.schema?.hiddenParameters || {};
        const json = presetManager.exportOpenSCADNativeFormat(
          modelName,
          hiddenParams
        );
        if (!json) return;
        folderWriteBack
          .writeFile(sidecarPath, json)
          .then(() => {
            consolePanel.addSystemLine(`Saved presets to ${sidecarPath}`);
          })
          .catch((err) => {
            console.warn('[FolderWriteBack] Sidecar write failed:', err);
            updateStatus(
              `Could not write presets to folder: ${err.message}`,
              'warning'
            );
          });
      });
    }

    // Probe IDB for a previously-stored handle. Does NOT call
    // requestPermission (no user gesture yet); just transitions to
    // `pending-restore` so the UI shows the Reconnect prompt.
    folderSyncCtrl.hydrateFromStorage().catch((err) => {
      console.warn('[App] folder-sync hydrate failed:', err);
    });
  }

  let storageUpdateTimeout = null;
  const scheduleStorageUpdate = (delayMs = 2500) => {
    if (storageUpdateTimeout) {
      clearTimeout(storageUpdateTimeout);
    }
    storageUpdateTimeout = setTimeout(() => {
      updateStorageDisplay();
    }, delayMs);
  };

  // Update storage display on init
  updateStorageDisplay();

  // Keep storage usage fresh after state changes (localStorage saves are debounced)
  stateManager.subscribe((state, prevState) => {
    if (
      state.uploadedFile !== prevState.uploadedFile ||
      state.parameters !== prevState.parameters ||
      state.defaults !== prevState.defaults
    ) {
      scheduleStorageUpdate();
    }
  });

  // The Main Page button mentions unsaved changes only when there are some:
  // its name warned on every read, before a single value had changed.
  const mainPageBtn = document.getElementById('clearFileBtn');
  const syncMainPageName = () => {
    if (!mainPageBtn) return;
    const changes = Boolean(
      stateManager.getState().uploadedFile && stateManager.canUndo()
    );
    mainPageBtn.setAttribute(
      'aria-label',
      changes ? 'Main Page. Unsaved changes will be lost.' : 'Main Page'
    );
    mainPageBtn.title = changes
      ? 'Back to the Main Page (unsaved changes will be lost)'
      : 'Back to the Main Page';
  };
  stateManager.subscribe((state, prevState) => {
    if (
      state.uploadedFile !== prevState.uploadedFile ||
      state.parameters !== prevState.parameters
    ) {
      syncMainPageName();
    }
  });

  // ═══════════════════════════════════════════════════════════════════════
  // First-visit gate: the critical initialization barrier
  //
  // On the very first visit the app shows a blocking disclosure modal that
  // the user must accept before any downloads (WASM, manifest files, etc.)
  // can begin. Several subsystems depend on this gate:
  //
  //   ┌──────────────────────────────────────────────────────────────────┐
  //   │  Z-index stack (highest on top)                                 │
  //   │                                                                 │
  //   │  z: 10009  Tour card over the Features Guide                    │
  //   │  z: 10008  The tour's own dialogs (resume / error / mode)       │
  //   │  z: 10007  Tutorial panel + pill (--z-index-tutorial-panel)     │
  //   │  z: 10006  Tutorial veil       (--z-index-tutorial-spotlight)   │
  //   │  z: 10005  Tutorial overlay    (--z-index-tutorial-backdrop)    │
  //   │  z: 10001..10004  Tutorial highlight family (tokens)            │
  //   │  z: 10000  Processing overlay  (.processing-overlay)            │
  //   │  z: 10000  Memory banner / WASM overlay (--z-index-app-overlay) │
  //   │  z:  9999  Skip-link                                            │
  //   │  z:  1000  Modals              (--z-index-modal)                │
  //   │  z:   950  Modal backdrop      (--z-index-modal-backdrop)       │
  //   │  z:   900  Drawers             (--z-index-drawer)               │
  //   └──────────────────────────────────────────────────────────────────┘
  //
  // A tutorial layer above a modal is not a license to paint over one: while
  // a user-opened dialog is on screen the tour stands down entirely. See
  // applyDialogStandDown in tutorial-sandbox.js.
  //
  // Invariant: the processing overlay (z: 10000) must never be shown while
  // the first-visit modal (z: 1000) is open. Because the overlay sits
  // above the modal, it would cover the "Download & Continue" button and
  // trap the user in an infinite spinner. All code paths that call
  // showProcessingOverlay() must first await waitForFirstVisitAcceptance().
  //
  // Subsystems that respect this gate:
  //   - Manifest deep-link handler  (?manifest=<url>)
  //   - Project deep-link handler   (?project=<url>, ?scad=<url>)
  //   - WASM initialization         (ensureWasmInitialized)
  //   - Draft restoration           (pendingDraft)
  //   - Save-copy modal             (showManifestSaveCopyModal)
  //
  // See also: the per-step lifecycle comments in the manifest deep-link
  // handler below for the exact required ordering of overlay → download →
  // process → dismiss → save-copy.
  // ═══════════════════════════════════════════════════════════════════════
  const appRoot = document.getElementById('app');
  let firstVisitBlocking = false;
  let hasUserAcceptedDownload = !isFirstVisit();
  let pendingWasmInit = false;
  let pendingDraft = null;
  // Settles once a kept draft has been asked about and the answer carried
  // out, or once start-up finds none to ask about. The app's own question
  // leaves the page running, as the browser's did not, so the Main Page's
  // tip waits for this rather than speak over the question.
  let settleDraftQuestion;
  const draftQuestionSettled = new Promise((resolve) => {
    settleDraftQuestion = resolve;
  });
  // The end of initApp: until then parts of the page that loading a project
  // reaches (the preview drawer's state among them) do not exist yet.
  let startupDone = false;
  const firstVisitReadyResolvers = [];

  /**
   * Ask about a saved draft and open it if wanted. One path for both roads in,
   * straight after start-up or after the welcome dialog, and never before
   * start-up has finished: a restore run mid-way reached a variable declared
   * further down and lost the project.
   */
  async function restorePendingDraft() {
    if (!startupDone) return;
    if (!pendingDraft) {
      settleDraftQuestion();
      return;
    }
    const draftToRestore = pendingDraft;
    pendingDraft = null;

    try {
      // Opens on Restore: the other answer deletes the draft, and Enter on
      // the browser's own question used to restore. Discard sends focus
      // somewhere to act in one move: a screen reader reads a stop on the way.
      const opener = document.activeElement;
      const shouldRestore = await showConfirmDialog(
        `A draft of "${draftToRestore.fileName}" from ${new Date(draftToRestore.timestamp).toLocaleString()} is kept in this browser. Restore it, or discard it?`,
        'Restore a saved draft?',
        'Restore draft',
        'Discard draft',
        {
          focusConfirm: true,
          returnFocus: (restore) => (restore ? opener : somewhereToAct()),
        }
      );

      if (shouldRestore) {
        console.log('Restoring draft...');
        await fileHandler.handleFile(
          { name: draftToRestore.fileName },
          draftToRestore.fileContent,
          null,
          null,
          'saved',
          null,
          { startingValues: draftToRestore.parameters }
        );
        updateStatus('Draft restored');
      } else {
        stateManager.clearLocalStorage();
      }
    } finally {
      settleDraftQuestion();
    }
  }

  const setFirstVisitBlocking = (blocked) => {
    firstVisitBlocking = blocked;
    // The guard lives in the shortcut registry, so it covers every
    // registered shortcut.
    keyboardConfig.setEnabled(!blocked);
    if (appRoot) {
      if (blocked) {
        appRoot.setAttribute('aria-hidden', 'true');
      } else {
        appRoot.removeAttribute('aria-hidden');
      }
      if ('inert' in appRoot) {
        appRoot.inert = blocked;
      }
    }
    document.body.classList.toggle('first-visit-blocking', blocked);
  };

  const waitForFirstVisitAcceptance = () => {
    if (!firstVisitBlocking && hasUserAcceptedDownload) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      firstVisitReadyResolvers.push(resolve);
    });
  };

  // The modal's Classic card is genuinely disabled while the viewport is
  // mobile-shaped, with the reason visible in the card (a real disabled
  // attribute cannot snap back under the user's hand), and re-enables live
  // if the window turns desktop-shaped while the modal is open. A checked
  // Classic choice is cleared when the gate closes over it so it cannot be
  // submitted stale.
  const updateFirstVisitClassicGate = () => {
    const radio = document.getElementById('firstVisitChoiceClassic');
    const note = document.getElementById('firstVisitClassicGate');
    if (!radio || !note) return;
    const gated = !isViewportDesktopShaped();
    radio.disabled = gated;
    note.classList.toggle('hidden', !gated);
    radio.setAttribute(
      'aria-describedby',
      gated
        ? 'firstVisitClassicGate firstVisitClassicShotDesc firstVisitClassicGuide'
        : 'firstVisitClassicShotDesc firstVisitClassicGuide'
    );
    if (gated && radio.checked) {
      radio.checked = false;
    }
    // The modal's mobile layout — stowed concept rows instead
    // of the four bullets, no screenshots, no desktop-switching line — rides
    // this same predicate rather than a second breakpoint of its own, so the
    // pictures disappear exactly where the choice they illustrate is not
    // offered. A media query cannot express "at least 1024 wide AND not
    // portrait", and two definitions of "mobile" in one modal is the
    // cross-file drift this project keeps paying for.
    document
      .getElementById('first-visit-modal')
      ?.classList.toggle('first-visit-mobile-shaped', gated);
  };

  // First-visit modal check
  const firstVisitModal = document.getElementById('first-visit-modal');
  const firstVisitCheck = isFirstVisit();

  // The Forge card image and the backdrop's Forge half follow the
  // app's active theme, resolved exactly the way detectTheme() resolves
  // dark-vs-light outside Classic (data-theme attribute, else the system
  // preference). High contrast resolves through the same two values;
  // Classic's side stays the light capture always - Classic is
  // light by design. The backdrop swap rides a class so the image URL
  // stays in the stylesheet (CSP: no inline styles).
  const applyFirstVisitThemeAssets = () => {
    const dataTheme = document.documentElement.getAttribute('data-theme');
    const dark =
      dataTheme === 'dark' ||
      (dataTheme !== 'light' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches);
    const forgeShot = document.getElementById('firstVisitForgeShot');
    if (forgeShot) {
      forgeShot.src = dark
        ? '/screenshots/forge-standard-dark.webp'
        : '/screenshots/forge-standard.webp';
    }
    firstVisitModal?.classList.toggle('first-visit-forge-dark', dark);
  };

  // The modal's body scrolls on the sizes where the content still cannot
  // fit (360x640 and 375x667 are arithmetically out of reach), and
  // `overflow: auto` alone gives no affordance of any kind with Android's
  // overlay scrollbars, which fade out on their own. The cue is the fade;
  // the class is the only thing driving it, so it can never be shown at the
  // scroll end.
  const firstVisitBody = firstVisitModal?.querySelector('.modal-body');
  const updateFirstVisitScrollCue = () => {
    const box = firstVisitModal?.querySelector('.modal-first-visit');
    if (!box || !firstVisitBody) return;
    const hidden =
      firstVisitBody.scrollHeight -
      firstVisitBody.scrollTop -
      firstVisitBody.clientHeight;
    box.classList.toggle('first-visit-has-more', hidden > 2);
  };

  if (firstVisitCheck && firstVisitModal) {
    setFirstVisitBlocking(true);
    updateFirstVisitClassicGate();
    subscribeViewportShape(() => {
      updateFirstVisitClassicGate();
      updateFirstVisitScrollCue();
    });
    firstVisitBody?.addEventListener('scroll', updateFirstVisitScrollCue, {
      passive: true,
    });
    // Opening a note row changes how much is below the fold, and `toggle`
    // is the only event a native <details> fires for it.
    firstVisitModal
      .querySelectorAll('.first-visit-note-row')
      .forEach((row) =>
        row.addEventListener('toggle', updateFirstVisitScrollCue)
      );
    // subscribeViewportShape only fires when the desktop/mobile ANSWER
    // changes, so it cannot carry an ordinary resize or an on-screen
    // keyboard opening. This can.
    window.addEventListener('resize', updateFirstVisitScrollCue);
    // And this catches everything neither of those can see: a web font
    // arriving, high contrast resolving late, a user's own text-size
    // setting. Without it, booting into high contrast at
    // 1280x800 leaves 22px below the fold and no cue at all, because
    // nothing the other listeners watch for has happened. The scroller
    // itself is watched for the viewport half; its children are watched
    // because content growing INSIDE a fixed-height scroller never changes
    // that scroller's own box.
    if (typeof ResizeObserver !== 'undefined' && firstVisitBody) {
      const cueObserver = new ResizeObserver(updateFirstVisitScrollCue);
      cueObserver.observe(firstVisitBody);
      [...firstVisitBody.children].forEach((child) =>
        cueObserver.observe(child)
      );
    }
    // Delay slightly to ensure DOM is ready
    setTimeout(() => {
      applyFirstVisitThemeAssets();
      openModal(firstVisitModal);
      updateFirstVisitScrollCue();
    }, 500);
  }

  // First-visit modal handlers
  if (!isFirstVisit()) {
    setFirstVisitBlocking(false);
  }

  const firstVisitContinue = document.getElementById('first-visit-continue');

  const getFirstVisitChoice = () =>
    document.querySelector('input[name="first-visit-ui"]:checked')?.value ||
    null;

  // Visible + announced from inside the modal: #srAnnouncer sits in the
  // inert, aria-hidden #app subtree while this modal blocks, so the global
  // announcer cannot speak here.
  const showFirstVisitChoiceError = () => {
    const error = document.getElementById('firstVisitChoiceError');
    if (error) {
      error.classList.remove('hidden');
      // Re-insert the text so role="alert" re-announces on repeat presses.
      error.textContent = '';
      setTimeout(() => {
        error.textContent = 'Choose an interface to continue.';
      }, 50);
    }
    document.getElementById('firstVisitChoiceForge')?.focus();
  };

  document.querySelectorAll('input[name="first-visit-ui"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      document.getElementById('firstVisitChoiceError')?.classList.add('hidden');
    });
  });

  const handleFirstVisitClose = async (_source = 'unknown') => {
    const uiChoice = getFirstVisitChoice();
    if (!uiChoice) {
      showFirstVisitChoiceError();
      return;
    }
    // Belt and braces: if the window turned mobile-shaped inside the
    // gate's debounce window, a checked Classic radio can race the Continue
    // press. Clear it and fall into the ordinary no-choice flow instead of
    // silently submitting a gated choice.
    if (uiChoice === 'classic' && !isViewportDesktopShaped()) {
      updateFirstVisitClassicGate();
      showFirstVisitChoiceError();
      return;
    }
    hasUserAcceptedDownload = true;
    updateStoragePrefs({ allowLargeDownloads: true, seenDisclosure: true });
    // Unchecked "remember" = proceed this session only; the modal returns
    // next visit because the first-visit marker is never written.
    //
    // The box ships unchecked, so the test has to be null-safe toward not
    // remembering: `?.checked !== false` would remember whenever the element
    // goes missing, which is remember-by-default surviving as DOM drift.
    if (document.getElementById('firstVisitRemember')?.checked === true) {
      markFirstVisitComplete();
    }
    // Unblock first: while the app is inert and aria-hidden, closeModal finds
    // nothing it may give focus to (the page opened with nothing focused, and
    // everything else is inside the app), and focus fell to the page body.
    // It lands on the Main Page's heading, as a return to the Main Page does.
    setFirstVisitBlocking(false);
    closeModal(firstVisitModal, {
      fallbackFocus: document.getElementById('features-heading'),
    });
    // After the unblock, so the mode switch's announcement is not silenced
    // by the aria-hidden #app subtree. The controller persists the choice
    // through its own preference storage.
    if (uiChoice === 'classic') {
      getUIModeController().switchMode('classic');
    }
    if (firstVisitReadyResolvers.length > 0) {
      const resolvers = firstVisitReadyResolvers.splice(0);
      resolvers.forEach((resolve) => resolve());
    }
    if (pendingWasmInit) {
      pendingWasmInit = false;
      await ensureWasmInitialized();
    }

    await previewOpenFileAfterStart();

    // Restore pending draft if one was deferred
    restorePendingDraft();
  };

  /**
   * A project loaded while the engine was still starting (a manifest
   * deep-link on a first visit, or an engine that failed and was started
   * again) has no auto-preview controller: handleFile could not make one.
   * Once the engine is ready, make it and preview the open file.
   */
  async function previewOpenFileAfterStart() {
    const postInitState = stateManager.getState();
    if (
      postInitState.uploadedFile &&
      !autoPreviewController &&
      renderController
    ) {
      await initAutoPreviewController(false);
      if (autoPreviewController) {
        const colorParamNames = Object.values(
          postInitState.schema?.parameters || {}
        )
          .filter((p) => p.uiType === 'color')
          .map((p) => p.name);
        autoPreviewController.setColorParamNames(colorParamNames);
        autoPreviewController.setParamTypes(postInitState.paramTypes || {});
        autoPreviewController.setSchema(postInitState.schema || null);
        autoPreviewController.setScadContent(
          postInitState.uploadedFile.content
        );
        autoPreviewController.setProjectFiles(
          postInitState.projectFiles || null,
          postInitState.mainFilePath || postInitState.uploadedFile.name
        );
        const libsForRender = getEnabledLibrariesForRender();
        autoPreviewController.setEnabledLibraries(libsForRender);
        if (autoPreviewEnabled) {
          autoPreviewController
            .forcePreview(postInitState.parameters)
            .then((initiated) => {
              if (initiated) {
                console.log('[Preview] Deferred initial preview started');
              }
            })
            .catch((error) => {
              console.error(
                '[Preview] Deferred initial preview failed:',
                error
              );
            });
        }
      }
    }
  }

  if (firstVisitContinue && firstVisitModal) {
    firstVisitContinue.addEventListener('click', () =>
      handleFirstVisitClose('continue')
    );
  }

  // Initialize UI mode controller (Basic/Advanced interface layout)
  getUIModeController().init();

  // One dismissible banner, two notices. The boot notice says a saved
  // Classic preference was deferred by the viewport gate (the preference
  // stays saved — the controller's deferral flag protects it until an
  // explicit mode switch). The live notice says a Classic session whose
  // window turned phone-shaped stays alive.
  const classicGateBanner = document.getElementById('classicGateBanner');
  const showClassicGateNotice = (kind) => {
    if (!classicGateBanner) return null;
    const bootText = document.getElementById('classicGateBannerText');
    const liveText = document.getElementById('classicGateLiveText');
    bootText?.classList.toggle('hidden', kind !== 'boot');
    liveText?.classList.toggle('hidden', kind !== 'live');
    classicGateBanner.classList.remove('hidden');
    return kind === 'boot' ? bootText : liveText;
  };
  const isClassicLiveNoticeShowing = () => {
    const liveText = document.getElementById('classicGateLiveText');
    return Boolean(
      classicGateBanner &&
      !classicGateBanner.classList.contains('hidden') &&
      liveText &&
      !liveText.classList.contains('hidden')
    );
  };
  document
    .getElementById('classicGateBannerDismiss')
    ?.addEventListener('click', () =>
      classicGateBanner?.classList.add('hidden')
    );

  if (getUIModeController().isClassicDeferredByViewport()) {
    const bootText = showClassicGateNotice('boot');
    if (bootText) {
      announceImmediate(bootText.textContent.replace(/\s+/g, ' ').trim());
    }
  }

  // The live notice shows on each crossing into narrowed Classic and
  // heals itself when the window widens again; the announcement fires
  // once per session so repeated resizes cannot nag a screen reader.
  let classicNarrowAnnounced = false;
  subscribeViewportShape((desktopShaped) => {
    if (!desktopShaped && getUIModeController().getMode() === 'classic') {
      const liveText = showClassicGateNotice('live');
      if (liveText && !classicNarrowAnnounced) {
        classicNarrowAnnounced = true;
        announceImmediate(liveText.textContent.replace(/\s+/g, ' ').trim());
      }
    } else if (isClassicLiveNoticeShowing()) {
      classicGateBanner?.classList.add('hidden');
    }
  });

  // Any real mode switch ends the state either notice describes: an
  // explicit switch retires the boot deferral, and leaving Classic
  // retires the live notice.
  getUIModeController().subscribe(() => {
    classicGateBanner?.classList.add('hidden');
  });

  // Initialize toolbar menu bar (File|Edit|Design|View|Window|Help)
  getToolbarMenuController().init();
  applyToolbarModeVisibility(getUIModeController().getMode());
  getUIModeController().subscribe((newMode) => {
    applyToolbarModeVisibility(newMode);
  });

  // On a mobile-shaped project surface the four app-chrome
  // controls move out of their own row and into the Customizer row. Wired
  // after applyToolbarModeVisibility so the row it collapses has already
  // settled into the density it is going to hold.
  initMobileToolbar();

  // Initialize HFM/Alt View controller (hidden feature mode)
  const hfmCtrl = initHfmController({
    getPreviewManager: () => previewManager,
    getDisplayOptionsController,
  });

  // Initialize parameter detail level controller (Show/Inline/Hide/Desc-only)
  initParamDetailController();

  async function _saveCurrentProject(successMessage) {
    // Save what the user can see. Without this, saving inside the editor's
    // write-back window persists the pre-edit source and then reports success.
    publishEditorEdits();
    const state = stateManager.getState();
    if (!state.uploadedFile?.content) return;
    if (currentSavedProjectId) {
      const { projectFiles } = state;
      const projectFilesObj = projectFiles
        ? Object.fromEntries(projectFiles)
        : null;
      const result = await updateProject({
        id: currentSavedProjectId,
        content: state.uploadedFile.content,
        projectFiles:
          projectFilesObj !== null
            ? JSON.stringify(projectFilesObj)
            : undefined,
      });
      if (result.success) {
        // The dirty flag means "differs from the saved project", so
        // saving — and only saving — clears it.
        getEditorStateManager().markClean();
        companionFilesCtrl.updateCompanionSaveButton();
        stateManager.announceChange(successMessage);
        updateStatus(successMessage);
        await savedProjectsUI.renderSavedProjectsList();
      } else {
        showErrorToast({
          title: 'Save Failed',
          message: `Failed to save: ${result.error}`,
        });
      }
    } else {
      await savedProjectsUI.showSaveProjectPrompt(state);
    }
  }

  /**
   * One-click 2D export (SVG / DXF).
   *
   * Mirrors desktop OpenSCAD's File > Export > Export as SVG/DXF:
   * switches the output format, auto-adjusts parameters for 2D geometry,
   * runs the full render, and downloads the result.
   *
   * @param {string} format - 'svg' or 'dxf'
   */
  async function _export2DOneClick(format) {
    // As on the 3D export path, this is reached both through
    // _renderForExport and directly from the Classic toolbar's DXF button,
    // so it publishes on its own account rather than trusting its caller.
    publishEditorEdits();
    const state = stateManager.getState();
    if (!state.uploadedFile) {
      showErrorToast({
        title: 'No File Open',
        message: 'Open a .scad file first.',
      });
      return;
    }
    if (!renderController) {
      showErrorToast({
        title: 'Engine Not Ready',
        message:
          'The OpenSCAD engine has not initialized yet. Please wait or refresh the page.',
      });
      return;
    }

    const formatName = OUTPUT_FORMATS[format]?.name || format.toUpperCase();

    const outputFormatSelect = document.getElementById('outputFormat');
    if (outputFormatSelect) {
      outputFormatSelect.value = format;
      outputFormatSelect.dispatchEvent(new Event('change'));
    }

    getToolbarMenuController().closeAll();

    // The format change above populated the consent panel; honor its
    // checkbox (checked by default) rather than applying silently.
    const proposal = propose2DExportChanges(
      state.parameters,
      state.schema,
      format,
      state.projectFiles
    );
    const renderParameters =
      proposal.changes.length > 0 && is2DAdjustmentsConsented()
        ? proposal.resolvedParameters
        : state.parameters;

    updateStatus(`Generating ${formatName}\u2026`);

    if (autoPreviewController) {
      autoPreviewController.cancelPending();
    }

    try {
      const libsForRender = getEnabledLibrariesForRender();
      const startTime = Date.now();

      const oneClickOpts = {
        outputFormat: format,
        paramTypes: state.paramTypes || {},
        files: state.projectFiles,
        mainFile: state.mainFilePath,
        libraries: libsForRender,
        // Said once, above; progress only keeps the words on screen.
        onProgress: () =>
          updateStatus(`Generating ${formatName}\u2026`, 'default', {
            announce: false,
          }),
      };
      let result;
      try {
        result = await renderController.renderFull(
          state.uploadedFile.content,
          renderParameters,
          oneClickOpts
        );
      } catch (renderErr) {
        if (renderErr.code === 'MODEL_NOT_2D') {
          const proceed = await confirmProjectionFallback(format);
          if (!proceed) {
            updateStatus(`${formatName} export canceled`);
            return;
          }
          updateStatus(`Projecting 3D mesh to approximate ${formatName}...`);
          result = await renderController.render2DFallback(
            state.uploadedFile.content,
            strip2DGenerateForFallback(renderParameters),
            oneClickOpts
          );
        } else {
          throw renderErr;
        }
      }

      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      const data = result.data || result.stl;
      const resolvedFormat = result.format || format;

      stateManager.setState({
        generatedOutput: {
          data,
          format: resolvedFormat,
          stats: result.stats,
          paramsHash: hashParams(state.parameters),
        },
        stl: data,
        outputFormat: resolvedFormat,
        stlStats: result.stats,
        lastRenderTime: duration,
      });

      // Show rendered 2D preview for SVG output
      if (resolvedFormat === 'svg' && previewManager) {
        try {
          let svgText =
            typeof data === 'string' ? data : new TextDecoder().decode(data);

          svgText = svgText.replace(
            /(<svg[^>]*>)/i,
            '$1' + build2DPreviewStyleTag('rendered')
          );

          if (typeof previewManager.show2DPreviewAs3DPlane === 'function') {
            await previewManager.show2DPreviewAs3DPlane(svgText, {
              mode: 'rendered',
            });
          } else {
            previewManager.show2DPreview(svgText, { mode: 'rendered' });
          }
        } catch (previewErr) {
          console.warn('[Export2D] Failed to show 2D preview:', previewErr);
        }
      }

      const filename = generateFilename(
        state.uploadedFile.name,
        state.parameters,
        format
      );
      downloadFile(data, filename, format);
      updateStatus(`${formatName} exported (${duration}s): ${filename}`);
      announceImmediate(`${formatName} file exported and downloaded.`);
    } catch (error) {
      console.error(`[Export2D] ${formatName} export failed:`, error);
      updateStatus(`${formatName} export failed: ${error.message || error}`);
      announceImmediate(`${formatName} export failed.`);
    }
  }

  /**
   * Whether the render already in hand is this exact format, for these exact
   * parameters — the one question that decides whether an export downloads
   * immediately or has to render first. STL additionally consults the app's
   * shared render-state function rather than repeating its recipe.
   *
   * @param {string} format
   * @param {Object} parameters
   * @param {Object} [options]
   * @param {boolean} [options.stlBinary=true]
   * @returns {boolean}
   */
  function hasCurrentRenderFor(format, parameters, { stlBinary = true } = {}) {
    const output = stateManager.getState().generatedOutput;
    const matches = Boolean(
      output?.data &&
      (output.format || 'stl').toLowerCase() === format &&
      output.paramsHash === hashParams(parameters)
    );
    if (!matches) return false;
    if (format !== 'stl') return true;
    // ascii and binary are different bytes, so asking for the other encoding
    // is a different render even when the geometry is unchanged.
    if ((output.stlBinary ?? true) !== stlBinary) return false;
    return hasFullQualitySTLFor(parameters);
  }

  /**
   * Render the model in the requested format so an export can proceed.
   * @returns {Promise<'ready'|'downloaded'|false>}
   */
  async function _renderForExport(format, { stlBinary = true } = {}) {
    // File > Export renders straight from state, so it needs the
    // editor published first for the same reason Render does.
    publishEditorEdits();

    // SVG and DXF keep the one-click path: it asks consent for the 2D
    // parameter changes and handles the projection fallback, neither of
    // which a plain render in that format would do.
    if (format === 'svg' || format === 'dxf') {
      await _export2DOneClick(format);
      return 'downloaded';
    }

    const state = stateManager.getState();
    if (!renderController) {
      showErrorToast({
        title: 'Engine Not Ready',
        message:
          'The OpenSCAD engine has not initialized yet. Please wait or refresh the page.',
      });
      return false;
    }

    const formatName = OUTPUT_FORMATS[format]?.name || format.toUpperCase();
    getToolbarMenuController().closeAll();
    updateStatus(`Generating ${formatName}\u2026`, 'default', {
      announce: false,
    });
    announceImmediate(`Generating ${formatName}. This may take a moment.`);
    autoPreviewController?.cancelPending();

    try {
      const startTime = Date.now();
      const result = await renderController.renderFull(
        state.uploadedFile.content,
        state.parameters,
        {
          outputFormat: format,
          stlBinary,
          paramTypes: state.paramTypes || {},
          files: state.projectFiles,
          mainFile: state.mainFilePath,
          libraries: getEnabledLibrariesForRender(),
          onProgress: () =>
            updateStatus(`Generating ${formatName}\u2026`, 'default', {
              announce: false,
            }),
        }
      );
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      const data = result.data || result.stl;
      const resolvedFormat = (result.format || format).toLowerCase();

      stateManager.setState({
        generatedOutput: {
          data,
          format: resolvedFormat,
          stats: result.stats,
          paramsHash: hashParams(state.parameters),
          stlBinary: resolvedFormat === 'stl' ? stlBinary : undefined,
        },
        stl: data,
        outputFormat: resolvedFormat,
        stlStats: result.stats,
        lastRenderTime: duration,
      });
      updateStatus(`${formatName} ready (${duration}s)`);
      return 'ready';
    } catch (error) {
      console.error(`[Export] ${formatName} render failed:`, error);
      // A PDF has no approximate outline to offer, unlike SVG and DXF, and
      // the engine's refusal named a setting this model may not have.
      const message =
        format === 'pdf' && error?.code === 'MODEL_NOT_2D'
          ? 'PDF export needs a 2D model. For this 3D model, export SVG or DXF, which offer an approximate outline.'
          : error.message || String(error);
      updateStatus(`${formatName} export failed: ${message}`, 'error', {
        announce: false,
      });
      // The toast is an alert and says it; a second voice said it twice.
      showErrorToast({
        title: `${formatName} Export Failed`,
        message,
      });
      return false;
    }
  }

  setExportDependencies({
    hasCurrentRender: (format, options) =>
      hasCurrentRenderFor(format, stateManager.getState().parameters, options),
    renderForExport: _renderForExport,
  });

  // Initialize file actions controller (New, Reload, Save, Save As, Export Image, Recent)
  const fileActionsController = getFileActionsController({
    onNew: () => void startNewProject(),
    onReload: () => {
      const state = stateManager.getState();
      if (state.uploadedFile) {
        // 'reload': the same file again, so the camera stays and the
        // quick-access question is not asked a second time.
        fileHandler.handleFile(
          null,
          state.uploadedFile.content,
          state.projectFiles || null,
          state.mainFilePath || null,
          'reload',
          state.uploadedFile.name
        );
      }
    },
    onSave: () => _saveCurrentProject('Project saved'),
    onSaveAs: async () => {
      const state = stateManager.getState();
      if (!state.uploadedFile?.content) return;
      await savedProjectsUI.showSaveProjectPrompt(state, {
        title: 'Save As',
        purpose:
          'Saves this file as a new project. You go on working in the new one, and Save writes there.',
      });
    },
    onSaveAll: () => _saveCurrentProject('All changes saved'),
    onExportImage: () => {
      const canvas = document.querySelector('#previewContainer canvas');
      if (!canvas) return;
      if (previewManager?.renderer && previewManager?.scene) {
        const cam = previewManager.getActiveCamera?.() ?? previewManager.camera;
        if (cam) previewManager.renderer.render(previewManager.scene, cam);
      }
      const dataUrl = canvas.toDataURL('image/png');
      if (!dataUrl || dataUrl.length < 100) return;
      const link = document.createElement('a');
      // Named for the project, so a picture found later says which one.
      link.download = `${projectStem(stateManager.getState().uploadedFile?.name)}-preview.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onExport2D: (format) => _export2DOneClick(format),
    onOpenRecent: (entry) => _openRecentEntry(entry),
  });
  fileActionsController.init();

  const THREEMF_UNAVAILABLE_REASON =
    '3MF export is not available in this browser build. Export as STL or OBJ instead. Most slicers accept both.';
  const RECENT_UNAVAILABLE_REASON =
    'Not saved in this browser. Open the file again to reload it';
  const OPEN_IN_NEW_WINDOW_REASON =
    'A new browser tab cannot be handed a file from this one. Use New Window, then open the file there.';

  // A recent entry is only a file name, so re-opening works when this browser
  // still holds the content: a saved project, or a bundled example. A one-off
  // upload leaves nothing behind, and that entry renders disabled with a
  // reason rather than failing on click. Menu builders are synchronous,
  // so the lookup is cached and rebuilt whenever the project list changes.
  const recentResolution = new Map();

  async function refreshRecentResolution() {
    const projects = await listSavedProjects();
    const resolved = new Map();
    for (const project of projects) {
      if (project.originalName && !resolved.has(project.originalName)) {
        resolved.set(project.originalName, { kind: 'project', id: project.id });
      }
    }
    for (const project of projects) {
      if (project.name && !resolved.has(project.name)) {
        resolved.set(project.name, { kind: 'project', id: project.id });
      }
    }
    for (const [key, def] of Object.entries(EXAMPLE_DEFINITIONS)) {
      if (def.name && !resolved.has(def.name)) {
        resolved.set(def.name, { kind: 'example', key });
      }
    }
    recentResolution.clear();
    for (const [name, target] of resolved) recentResolution.set(name, target);
  }

  function _openRecentEntry(entry) {
    const target = recentResolution.get(entry?.name);
    if (!target) return;
    if (target.kind === 'project') {
      void savedProjectsUI.loadSavedProject(target.id);
    } else {
      void fileHandler.loadExampleByKey(target.key);
    }
  }

  document.addEventListener('saved-projects-rendered', () => {
    void refreshRecentResolution();
  });
  void refreshRecentResolution();

  /**
   * Save a Copy: the open document stays attached to the project it came
   * from, so a later Save still overwrites the original rather than the copy.
   */
  async function _saveProjectCopy() {
    publishEditorEdits();
    const state = stateManager.getState();
    if (!state.uploadedFile?.content) return;
    const previousProjectId = currentSavedProjectId;
    await savedProjectsUI.showSaveProjectPrompt(state, {
      title: 'Save a Copy',
      purpose:
        'Saves a copy as a new project. Save keeps writing to what you have open, not to the copy.',
    });
    currentSavedProjectId = previousProjectId;
  }

  /**
   * File > Close Project. The Back button asks its own question, so a dirty
   * editor is warned here and then closed directly — two dialogs in a row
   * would be worse than one.
   */
  async function _closeProjectFromMenu() {
    if (getEditorStateManager().getIsDirty()) {
      const confirmed = await showConfirmDialog(
        'You have unsaved edits in the code editor. Closing this project will discard them.',
        'Unsaved code edits',
        'Discard edits and close',
        'Keep editing',
        { destructive: true }
      );
      if (!confirmed) return;
      await closeProjectToWelcome();
      return;
    }
    document.getElementById('clearFileBtn')?.click();
  }

  /**
   * File > Show Library Folder…: a browser has no library folder on disk, so
   * this reveals the library bundles this build actually mounts.
   */
  function _showLibraryBundles() {
    const controls = document.getElementById('libraryControls');
    if (!controls) return;
    controls.classList.remove('hidden');
    // Simplified hides this panel through the mode controller's own class, so
    // clearing `hidden` alone left the command doing nothing at all — and a
    // display:none summary cannot take focus, which dropped focus on <body>
    // as the menu closed. Route through the controller so its class and the
    // Window menu's tick keep reading one state.
    const uiCtrl = getUIModeController();
    if (uiCtrl && !uiCtrl.isPanelShowing('libraries')) {
      uiCtrl.togglePanelVisibility('libraries');
    }
    const details = controls.querySelector('.library-details');
    if (details) details.open = true;
    controls.scrollIntoView({ block: 'nearest' });
    controls.querySelector('.library-summary')?.focus();
  }

  // ── Toolbar: File menu ──────────────────────────────────────────────────
  // Order, labels and separators transcribed from upstream MainWindow.ui at
  // tag openscad-2026.01.01-TEST2. Omitted: Save All and the Python submenu
  // (one document, no Python in the WASM build). Quit has no browser
  // meaning, so its slot is dropped and the single Close carries the clearer
  // name "Close Project". "Open Local Folder…" is a Forge extra, kept beside
  // Open File.
  getToolbarMenuController().registerMenuBuilder('file', () => {
    const state = stateManager.getState();
    const hasFile = Boolean(state.uploadedFile);
    const hasRender = Boolean(state.stl);
    // state.stl is only set by a full Generate. Commands that act on WHAT IS
    // ON SCREEN are available as soon as a preview has put a mesh there.
    const hasViewportModel = hasRender || Boolean(previewManager?.mesh);

    // Recent Files submenu: entries this browser can still re-open, then
    // Clear Recent. Unreachable entries stay listed but disabled.
    const hasRecent = fileActionsController.recentFiles.length > 0;
    const recentItems = [
      ...(hasRecent
        ? fileActionsController.recentFiles.map((entry) => {
            const resolvable = recentResolution.has(entry.name);
            return {
              type: 'action',
              label: entry.name,
              disabled: !resolvable,
              tooltip: resolvable ? undefined : RECENT_UNAVAILABLE_REASON,
              handler: resolvable
                ? () => fileActionsController.onOpenRecent(entry)
                : undefined,
            };
          })
        : [{ type: 'action', label: 'No recent files', disabled: true }]),
      { type: 'separator' },
      {
        type: 'action',
        label: 'Clear Recent',
        disabled: !hasRecent,
        tooltip: hasRecent ? undefined : 'The recent files list is empty',
        handler: hasRecent
          ? () => {
              fileActionsController.clearRecent();
              announceImmediate('Recent files list cleared');
            }
          : undefined,
      },
    ];

    // Export submenu, in upstream order. POV is omitted: the WASM build has
    // no POV writer. Every entry renders on demand when the render in hand is
    // not already that format, so no export is a dead end.
    const exportFormats = [
      ['stl', 'Export as STL (ascii)\u2026', { stlBinary: false }],
      ['stl', 'Export as STL (binary)\u2026', { stlBinary: true }],
      ['obj', 'Export as OBJ\u2026', {}],
      ['off', 'Export as OFF\u2026', {}],
      ['wrl', 'Export as WRL\u2026', {}],
      ['amf', 'Export as AMF\u2026', {}],
      // This build's renderer traps on 3MF with
      // "function signature mismatch", so the item says so instead of
      // failing every time it is pressed.
      ['3mf', 'Export as 3MF\u2026', {}, THREEMF_UNAVAILABLE_REASON],
      ['dxf', 'Export as DXF\u2026', {}],
      ['svg', 'Export as SVG\u2026', {}],
      ['csg', 'Export as CSG\u2026', {}],
      ['pdf', 'Export as PDF\u2026', {}],
    ];

    const exportItems = [
      ...exportFormats.map(([format, label, options, unavailableReason]) => ({
        type: 'action',
        label,
        enabled: hasFile && !unavailableReason,
        tooltip: unavailableReason
          ? unavailableReason
          : hasFile
            ? OUTPUT_FORMATS[format]?.description
            : 'Open a file first',
        handler: unavailableReason
          ? undefined
          : () => void exportFormatFromMenu(format, options),
      })),
      { type: 'separator' },
      {
        type: 'action',
        label: 'Export as Image\u2026',
        shortcutAction: 'exportImage',
        // It photographs the canvas, so it needs something on screen and
        // nothing else.
        enabled: hasViewportModel,
        tooltip: hasViewportModel
          ? 'Save the current viewport as a PNG image'
          : 'Preview or render a model first',
        handler: () => fileActionsController.onExportImage(),
      },
    ];

    return [
      {
        type: 'action',
        label: 'New File',
        shortcutAction: 'newFile',
        handler: () => fileActionsController.onNew(),
      },
      {
        type: 'action',
        label: 'Open File\u2026',
        handler: () => document.getElementById('fileInput')?.click(),
      },
      {
        type: 'action',
        label: 'Open Local Folder\u2026',
        enabled: Boolean(connectToLocalFolder),
        tooltip: connectToLocalFolder
          ? 'Connect to a folder on disk; the connection persists across visits'
          : 'Persistent folder access requires Chrome or Edge',
        handler: () => connectToLocalFolder?.(),
      },
      { type: 'submenu', label: 'Recent Files', items: recentItems },
      {
        type: 'submenu',
        label: 'Examples',
        items: (() => {
          const programmedKeys = new Set();
          const grouped = [];

          for (const prog of Object.values(PROGRAM_DEFINITIONS)) {
            const subItems = prog.examples
              .filter((k) => EXAMPLE_DEFINITIONS[k])
              .map((k) => ({
                type: 'action',
                label:
                  EXAMPLE_DEFINITIONS[k].description ||
                  EXAMPLE_DEFINITIONS[k].name,
                handler: () => fileHandler.loadExampleByKey(k),
              }));
            if (subItems.length > 0) {
              grouped.push({
                type: 'submenu',
                label: prog.label,
                items: subItems,
              });
              prog.examples.forEach((k) => programmedKeys.add(k));
            }
          }

          const ungrouped = Object.entries(EXAMPLE_DEFINITIONS)
            .filter(([k]) => !programmedKeys.has(k))
            .map(([k, def]) => ({
              type: 'action',
              label: def.description || def.name,
              handler: () => fileHandler.loadExampleByKey(k),
            }));

          if (grouped.length > 0 && ungrouped.length > 0) {
            return [...ungrouped, { type: 'separator' }, ...grouped];
          }
          return [...ungrouped, ...grouped];
        })(),
      },
      {
        type: 'action',
        label: 'Reload',
        shortcutAction: 'reloadFile',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => fileActionsController.onReload(),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'New Window',
        tooltip: 'Opens a second copy of the app in a new browser tab',
        handler: () =>
          window.open(
            window.location.origin + window.location.pathname,
            '_blank',
            'noopener,noreferrer'
          ),
      },
      {
        type: 'action',
        label: 'Open in New Window',
        disabled: true,
        tooltip: OPEN_IN_NEW_WINDOW_REASON,
      },
      {
        type: 'action',
        label: 'Close Project',
        enabled: hasFile,
        tooltip: hasFile
          ? 'Close the project and return to the start screen'
          : 'Open a file first',
        handler: () => void _closeProjectFromMenu(),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'Save',
        shortcutAction: 'saveFile',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => fileActionsController.onSave(),
      },
      {
        type: 'action',
        label: 'Save As\u2026',
        shortcutAction: 'saveFileAs',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => fileActionsController.onSaveAs(),
      },
      {
        type: 'action',
        label: 'Save a Copy',
        enabled: hasFile,
        tooltip: hasFile
          ? 'Save another copy, leaving this file attached to the project it came from'
          : 'Open a file first',
        handler: () => void _saveProjectCopy(),
      },
      // Forge-only, like Export Quality; and a Simplified view hides it
      // unless the person chose to show it there.
      ...(document.body.dataset.uiMode !== 'classic' &&
      getUIModeController().isFeatureShown('publish')
        ? [
            {
              type: 'action',
              label: 'Publish\u2026',
              enabled: hasFile,
              tooltip: hasFile
                ? 'A manifest to share this project from your own hosting'
                : 'Open a file first',
              handler: () => openPublishDialog(),
            },
          ]
        : []),
      { type: 'separator' },
      { type: 'submenu', label: 'Export', items: exportItems },
      // The export-quality mode: this list is the setting's one home.
      // Forge-only, like the other menu homes for drawer settings - Classic's
      // File menu keeps its upstream shape.
      ...(document.body.dataset.uiMode !== 'classic'
        ? [
            {
              type: 'submenu',
              label: 'Export Quality',
              items: [
                { label: 'Model default', value: 'model' },
                { label: 'Low (fast)', value: 'low' },
                { label: 'Medium (balanced)', value: 'medium' },
                { label: 'High (smooth)', value: 'high' },
              ].map((opt) => ({
                type: 'radio',
                label: opt.label,
                group: 'exportQuality',
                value: opt.value,
                checked: exportQualityMode === opt.value,
                onChange: () => setExportQualityMode(opt.value),
              })),
            },
          ]
        : []),
      { type: 'separator' },
      {
        type: 'action',
        label: 'Show Library Folder\u2026',
        tooltip: 'Shows the library bundles this app can mount',
        handler: () => _showLibraryBundles(),
      },
    ];
  });

  /**
   * Is there a code editor the user can see right now? ModeManager's expert
   * flag stays false in Classic even while the dock's Editor pane is mounted
   * and visible, so the mode alone cannot answer this. The Edit-menu gates,
   * the live font-size apply and jump-to-line all ask this one question, so
   * the three cannot disagree.
   */
  function isEditorOnScreen() {
    const box = document
      .getElementById('expertModePanel')
      ?.getBoundingClientRect();
    return Boolean(box && box.width > 0 && box.height > 0);
  }

  // Initialize edit actions controller (Copy viewport, camera values, error nav, font size)
  const editActionsController = getEditActionsController({
    getPreviewManager: () => previewManager,
    getErrorLogPanel: () => errorLogPanel,
    onJumpToLine: (file, line) => {
      const modeManager = getModeManager();
      const editor = modeManager?.getEditorInstance?.();
      if (editor && (modeManager.isExpertMode?.() || isEditorOnScreen())) {
        if (editor.revealLineInCenter) editor.revealLineInCenter(line);
        if (editor.setPosition)
          editor.setPosition({ lineNumber: line, column: 1 });
        if (editor.focus) editor.focus();
      }
    },
    onFontSizeChange: (size) => {
      const modeManager = getModeManager();
      const editor = modeManager?.getEditorInstance?.();
      if (editor && (modeManager.isExpertMode?.() || isEditorOnScreen())) {
        // setFontSize, not updateOptions: the latter does not exist on either
        // editor, and calling it would save and announce sizes without changing
        // anything on screen.
        editor.setFontSize?.(size);
      }
    },
  });
  editActionsController.init();

  // Preferences ▸ Advanced ▸ Engine: the engine choice, with the render
  // controller and the preview it reruns. Before the engine has started
  // there is nothing to restart: the choice is kept for the first start.
  const rerunPreview = async () => {
    const state = stateManager.getState();
    if (!autoPreviewController || !state.uploadedFile) return;
    autoPreviewController.clearPreviewCache();
    await autoPreviewController.forcePreview(state.parameters);
  };
  const engineHandlers = {
    manifest: () => loadEnginesManifest(),
    chosenId: () => getChosenEngineId(),
    isDownloaded: async (id) =>
      isEngineDownloaded(await loadEnginesManifest(), id),
    download: async (id, onProgress) =>
      downloadEngine(await loadEnginesManifest(), id, { onProgress }),
    remove: async (id) => removeEngine(await loadEnginesManifest(), id),
    switchTo: async (id) => {
      if (!renderController) {
        setChosenEngineId(id);
        return;
      }
      await switchEngineTo(await loadEnginesManifest(), id, {
        renderController,
        rerun: rerunPreview,
      });
    },
    status: (text, { announce = false } = {}) =>
      updateStatus(text, 'info', { announce }),
    announce: (text) => announceImmediate(text),
    confirm: (message, title, confirmLabel, cancelLabel) =>
      showConfirmDialog(message, title, confirmLabel, cancelLabel),
    showError: ({ title, message, technical }) =>
      showErrorModal({ title, message, technical }),
  };
  // For specs, the pattern of window.__forgeDebug: the engine the worker
  // runs and the same handlers Preferences uses.
  window.__forgeEngineChoice = {
    ...engineHandlers,
    enginePath: () => renderController?.getEnginePath() ?? null,
  };
  // The engine offer for a failure's dialog, or nothing. The failure itself
  // is still shown when the engine list cannot be read: only the offer is
  // missing from its dialog.
  const engineOffer = (code, run) =>
    offerEngineAfterFault({ code, handlers: engineHandlers, run }).catch(
      (error) => {
        console.warn(
          '[Engine] The engine list could not be read; no engine offer:',
          error
        );
        return null;
      }
    );

  /**
   * The dialog for an engine that did not start, with the engine offer when
   * the failure may be the engine's.
   * @param {Error & {code?: string, details?: string}} error
   * @param {(id: string) => Promise<void>} run - start that engine and do
   *   again what failed
   */
  async function showEngineStartFailure(error, run) {
    const details = error?.details ? ` Details: ${error.details}` : '';
    showErrorModal({
      title: 'Engine Initialization Failed',
      ...engineStartFailureNotice(error?.code),
      technical: error.message + details,
      ...(await engineOffer(error?.code, run)),
    });
  }

  // -- Toolbar: Edit menu --------------------------------------------------
  // Order and labels transcribed from upstream MainWindow.ui. Omitted: Show
  // Next/Previous Tab (one document, no editor tabs). Preferences is
  // relabelled honestly. Jump to previous error is a Forge extra, kept
  // beside its upstream sibling.
  getToolbarMenuController().registerMenuBuilder('edit', () => {
    const state = stateManager.getState();
    const hasFile = Boolean(state.uploadedFile);

    const modeManager = getModeManager();
    const editor = modeManager?.getEditorInstance?.();
    const expertMode = modeManager?.isExpertMode?.();
    const canEdit = Boolean(editor) && (expertMode || isEditorOnScreen());
    const editorTip = 'Available when the Editor is open';

    // Undo and Redo follow the focus the menu bar just took: from the code
    // editor they undo text, from anywhere else a parameter change. The
    // parameter history keeps its own toolbar buttons, which say so by name.
    const focusInEditor = Boolean(
      getToolbarMenuController()
        .getLastExternalFocus()
        ?.closest?.('#expertModePanel')
    );
    const undoTargetsEditor = Boolean(canEdit && focusInEditor);
    const canUndo = undoTargetsEditor
      ? Boolean(editor.canUndo?.())
      : stateManager.canUndo();
    const canRedo = undoTargetsEditor
      ? Boolean(editor.canRedo?.())
      : stateManager.canRedo();

    function historyTooltip(verb, available) {
      const what = undoTargetsEditor
        ? 'change in the code editor'
        : 'parameter change';
      return available
        ? `${verb}es the last ${what}`
        : `Nothing to ${verb.toLowerCase()}: no ${what} yet`;
    }

    /**
     * @param {string} label
     * @param {string} actionId
     * @param {true|string} [gate] True when allowed, or the reason it is not.
     */
    function editorAction(label, actionId, gate = true) {
      const supported = canEdit && editor.supportsAction?.(actionId) === true;
      const available = supported && gate === true;
      return {
        type: 'action',
        label,
        disabled: !available,
        tooltip: available
          ? undefined
          : !canEdit
            ? editorTip
            : !supported
              ? 'Not available in the basic text editor'
              : gate,
        handler: available ? () => editor.performAction(actionId) : undefined,
      };
    }

    return [
      {
        type: 'action',
        label: 'Undo',
        enabled: canUndo,
        tooltip: historyTooltip('Undo', canUndo),
        restoreFocus: true,
        handler: () =>
          undoTargetsEditor ? editor.performAction('undo') : performUndo(),
      },
      {
        type: 'action',
        label: 'Redo',
        enabled: canRedo,
        tooltip: historyTooltip('Redo', canRedo),
        restoreFocus: true,
        handler: () =>
          undoTargetsEditor ? editor.performAction('redo') : performRedo(),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'Cut',
        disabled: !canEdit,
        tooltip: canEdit ? undefined : editorTip,
        // Opening the menu bar takes focus, which collapses the editor's
        // selection. restoreFocus puts it back before the command runs.
        restoreFocus: true,
        handler: canEdit ? () => document.execCommand('cut') : undefined,
      },
      {
        type: 'action',
        label: 'Copy',
        disabled: !canEdit,
        tooltip: canEdit ? undefined : editorTip,
        restoreFocus: true,
        handler: canEdit ? () => document.execCommand('copy') : undefined,
      },
      {
        type: 'action',
        label: 'Paste',
        disabled: !canEdit,
        tooltip: canEdit ? undefined : editorTip,
        restoreFocus: true,
        // execCommand('paste') is blocked by every modern browser; read
        // the async Clipboard API instead, with an honest fallback.
        handler: canEdit
          ? async () => {
              try {
                const text = await navigator.clipboard.readText();
                editor.replaceSelection?.(text);
              } catch {
                updateStatus(
                  'Clipboard access was blocked. Press Ctrl+V in the editor to paste'
                );
                announceImmediate(
                  'Clipboard access was blocked. Press Control V in the editor to paste.'
                );
              }
            }
          : undefined,
      },
      { type: 'separator' },
      editorAction('Indent', 'indent'),
      editorAction('Unindent', 'unindent'),
      editorAction('Comment', 'comment'),
      editorAction('Uncomment', 'uncomment'),
      editorAction('Convert Tabs to Spaces', 'convertTabsToSpaces'),
      {
        // Upstream has no menu entry for this — it is Alt+Ins only — but a
        // keyboard-only action that does nothing tells the user nothing.
        // Disabled here so it can at least say why.
        type: 'action',
        label: 'Insert Template',
        disabled: true,
        tooltip: INSERT_TEMPLATE_REASON,
      },
      editorAction('Toggle Bookmark', 'toggleBookmark'),
      editorAction('Jump to next bookmark', 'nextBookmark'),
      editorAction('Jump to previous bookmark', 'previousBookmark'),
      { type: 'separator' },
      {
        type: 'action',
        label: 'Copy viewport image',
        shortcutAction: 'copyViewportImage',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => editActionsController.copyViewportImage(),
      },
      {
        type: 'action',
        label: 'Copy viewport translation',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => editActionsController.copyTranslation(),
      },
      {
        type: 'action',
        label: 'Copy viewport rotation',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => editActionsController.copyRotation(),
      },
      {
        type: 'action',
        label: 'Copy viewport distance',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => editActionsController.copyDistance(),
      },
      {
        type: 'action',
        label: 'Copy viewport field of view',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => editActionsController.copyFov(),
      },
      { type: 'separator' },
      editorAction('Find…', 'find'),
      editorAction('Find and Replace…', 'findReplace'),
      editorAction('Find Next', 'findNext'),
      editorAction('Find Previous', 'findPrevious'),
      editorAction(
        'Use Selection for Find',
        'useSelectionForFind',
        canEdit && editor.hasSelection?.()
          ? true
          : 'Select some text in the code editor first'
      ),
      { type: 'separator' },
      {
        type: 'action',
        label: 'Jump to next error',
        shortcutAction: 'jumpNextError',
        handler: () => editActionsController.jumpToNextError(),
      },
      {
        type: 'action',
        label: 'Jump to previous error',
        handler: () => editActionsController.jumpToPrevError(),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'Increase Font Size',
        shortcutAction: 'increaseFontSize',
        handler: () => editActionsController.increaseFontSize(),
      },
      {
        type: 'action',
        label: 'Decrease Font Size',
        shortcutAction: 'decreaseFontSize',
        handler: () => editActionsController.decreaseFontSize(),
      },
      {
        type: 'action',
        label: 'Preferences…',
        handler: () => {
          initPreferencesDialog({
            onOpenShortcuts: () => openShortcutsDialog(),
            onOpenController: () => openShortcutsDialog({ tab: 'controller' }),
            engine: engineHandlers,
            getColorScheme: () => {
              if (previewManager) return previewManager.getViewportScheme();
              // Before the first preview the manager does not exist, but a
              // saved choice does — the dialog must show it, not the default
              // (the multi-copy rule: control and effect must not disagree).
              const saved = readScopedPref(STORAGE_KEY_VIEWPORT_SCHEME);
              return VIEWPORT_SCHEMES.some((s) => s.id === saved)
                ? saved
                : 'cornfield';
            },
            onColorSchemeChange: (id) => {
              if (previewManager) {
                if (!previewManager.setViewportScheme(id)) return;
              } else {
                // No preview yet (the manager is created by the first
                // geometry): persist the choice so the first paint honors
                // it instead of silently dropping it.
                if (!VIEWPORT_SCHEMES.some((s) => s.id === id)) return;
                writeScopedPref(STORAGE_KEY_VIEWPORT_SCHEME, id);
              }
              const label =
                VIEWPORT_SCHEMES.find((s) => s.id === id)?.label ?? id;
              // Instant-apply is silent for a screen-reader user otherwise:
              // the only feedback is a repaint they cannot see.
              announceImmediate(`Color scheme ${label}`);
            },
            getEditorPrefs: () => loadEditorPrefs(),
            onEditorPrefChange: (name, value) => {
              // The preference owner clamps and persists; whatever it stored
              // is what gets applied, so the control cannot show one number
              // while the editor uses another.
              const stored = saveEditorPref(name, value);
              const editor = getModeManager()?.getEditorInstance?.();
              const apply = {
                fontSize: () => editor?.setFontSize?.(stored),
                indentWidth: () => editor?.setIndentWidth?.(stored),
                tabWidth: () => editor?.setTabWidth?.(stored),
                lineWrapping: () => editor?.setLineWrapping?.(stored),
                highlightActiveLine: () =>
                  editor?.setHighlightActiveLine?.(stored),
                wrapIndent: () => editor?.setWrapIndent?.(stored),
                wrapArrow: () => editor?.setWrapArrow?.(stored),
                braceMatching: () => editor?.setBraceMatching?.(stored),
                autocomplete: () => editor?.setAutocomplete?.(stored),
              };
              apply[name]?.();

              const spoken = {
                fontSize: `Font size: ${stored}px`,
                indentWidth: `Indentation width: ${stored} spaces`,
                tabWidth: `Tab width: ${stored} columns`,
                lineWrapping: `Wrap long lines, ${stored ? 'on' : 'off'}`,
                highlightActiveLine: `Highlight the current line, ${
                  stored ? 'on' : 'off'
                }`,
                wrapIndent: `Indent wrapped continuation lines, ${
                  stored ? 'on' : 'off'
                }`,
                wrapArrow: `Mark where a wrapped line continues, ${
                  stored ? 'on' : 'off'
                }`,
                braceMatching: `Highlight the matching bracket, ${
                  stored ? 'on' : 'off'
                }`,
                autocomplete: `Autocomplete as you type, ${
                  stored ? 'on' : 'off'
                }`,
              };
              if (spoken[name]) announceImmediate(spoken[name]);
              return stored;
            },
            getZoomToCursor: () => previewManager?.zoomToCursorEnabled ?? false,
            onZoomToCursorChange: (enabled) => {
              previewManager?.toggleZoomToCursor(enabled);
              announceImmediate(
                enabled
                  ? 'Zoom toward the mouse pointer, on'
                  : 'Zoom toward the mouse pointer, off'
              );
            },
            // The grid is per-interface, and Preferences
            // is Classic's home for its own copy. Reads fall back to the
            // scoped preference before any model exists (the facade's
            // Classic default is grid-off).
            getShowGrid: () =>
              previewManager
                ? previewManager.gridEnabled
                : readScopedPref(STORAGE_KEY_GRID) !== 'false',
            onShowGridChange: (enabled) => {
              if (previewManager) previewManager.toggleGrid(enabled);
              else {
                writeScopedPref(STORAGE_KEY_GRID, enabled ? 'true' : 'false');
              }
              announceImmediate(enabled ? 'Grid shown' : 'Grid hidden');
            },
            getGridSizeOptions: () => {
              // The drawer select is the canonical preset list (built-ins
              // plus the user's saved presets); mirror it minus the
              // custom-size editor, which stays the drawer's job.
              const drawer = document.getElementById('gridPresetSelect');
              const options = drawer
                ? Array.from(drawer.querySelectorAll('option'))
                    .filter((o) => o.value !== 'custom')
                    .map((o) => ({
                      value: o.value,
                      label: o.textContent.trim(),
                    }))
                : [];
              let size = previewManager?.getGridSize?.() ?? null;
              if (!size) {
                try {
                  size = JSON.parse(readScopedPref(STORAGE_KEY_GRID_SIZE));
                } catch {
                  size = null;
                }
              }
              const current = size ? `${size.widthMm}x${size.heightMm}` : null;
              return {
                options,
                current,
                currentLabel: size
                  ? `Current (${size.widthMm} × ${size.heightMm} mm)`
                  : null,
              };
            },
            onGridSizeChange: (value) => {
              // Drive the canonical control so the drawer's handler applies,
              // persists and reports the change exactly once — two controls,
              // one code path.
              const drawer = document.getElementById('gridPresetSelect');
              if (!drawer) return;
              drawer.value = value;
              drawer.dispatchEvent(new Event('change', { bubbles: true }));
            },
          });
          openPreferencesDialog({
            returnFocusTo: document.getElementById('editMenuBtn'),
          });
        },
      },
    ];
  });

  // Initialize design panel controller (Flush Caches, Display Parameters, Check Validity, Geometry Info)
  const designPanelController = getDesignPanelController({
    getPreviewManager: () => previewManager,
    getWorker: () => renderController?.worker || null,
    getScadContent: () => stateManager.getState()?.uploadedFile?.content || '',
    extractParameters,
    // The caches are flushed, not the project, as on the desktop: the same
    // project is previewed again from scratch.
    onFlushComplete: () => {
      const state = stateManager.getState();
      if (!autoPreviewController || !state.uploadedFile) return;
      autoPreviewController.clearPreviewCache();
      autoPreviewController.forcePreview(state.parameters).catch((error) => {
        console.error('[Flush] Preview after flushing failed:', error);
        showErrorToast({ title: 'Preview Failed', message: error.message });
      });
    },
  });
  designPanelController.init();

  const PRINT_UNAVAILABLE_REASON =
    'Sending a model straight to a printer is not built yet. Export the model and open it in your slicer.';
  const MEASURE_UNAVAILABLE_REASON =
    'Measuring on the model is not built yet. Show measurements reports the overall size.';
  const AST_UNAVAILABLE_REASON =
    'The syntax tree is not available in this browser build. Display Parameters shows what the file declares.';
  const CSG_UNAVAILABLE_REASON =
    'The CSG tree is not available in this browser build. Export as CSG saves the flattened CSG source instead.';

  // -- Toolbar: Design menu -------------------------------------------------
  // Order and labels transcribed from upstream MainWindow.ui. Four items
  // ship visibly disabled with a reason rather than absent, so the menu
  // still tells the truth about what desktop OpenSCAD offers: 3D Print,
  // Measure Distance / Angle and the two CSG dumps. Cancel Render is a
  // Forge extra, kept next to the render it cancels.
  getToolbarMenuController().registerMenuBuilder('design', () => {
    const state = stateManager.getState();
    const hasFile = Boolean(state.uploadedFile);

    /** An upstream action this build genuinely cannot perform yet. */
    function unavailable(label, reason) {
      return { type: 'action', label, disabled: true, tooltip: reason };
    }

    return [
      {
        type: 'toggle',
        label: 'Automatic Reload and Preview',
        checked: Boolean(document.getElementById('autoPreviewToggle')?.checked),
        handler: () => {
          // Single source: the existing auto-preview checkbox drives the
          // controller; this item (and the Classic Customizer checkbox)
          // proxy it so all three stay in sync.
          const toggle = document.getElementById('autoPreviewToggle');
          if (!toggle) return;
          toggle.checked = !toggle.checked;
          toggle.dispatchEvent(new Event('change'));
        },
      },
      {
        type: 'action',
        label: 'Reload and Preview',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => {
          fileActionsController.onReload();
          setTimeout(() => {
            if (autoPreviewController) {
              autoPreviewController.onParameterChange(
                stateManager.getState().parameters
              );
            }
          }, 200);
        },
      },
      {
        type: 'action',
        label: 'Preview',
        shortcutAction: 'preview',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: previewNow,
      },
      {
        type: 'action',
        label: 'Render',
        shortcutAction: 'render',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => {
          // Never the transformer button: with a cached full render its
          // action is 'download', and Render must not mean download.
          const btn = document.getElementById('primaryActionBtn');
          if (btn && !btn.disabled) runFullRender();
        },
      },
      {
        type: 'action',
        label: 'Cancel Render',
        shortcutAction: 'cancelRender',
        enabled: renderController?.isBusy?.(),
        tooltip: renderController?.isBusy?.()
          ? undefined
          : 'No render in progress',
        handler: () => {
          if (renderController?.isBusy?.()) renderController.cancel();
        },
      },
      unavailable('3D Print', PRINT_UNAVAILABLE_REASON),
      unavailable('Measure Distance', MEASURE_UNAVAILABLE_REASON),
      unavailable('Measure Angle', MEASURE_UNAVAILABLE_REASON),
      { type: 'separator' },
      {
        type: 'action',
        label: 'Check Validity',
        shortcutAction: 'checkValidity',
        enabled: hasFile,
        tooltip: hasFile ? undefined : 'Open a file first',
        handler: () => designPanelController.checkValidity(),
      },
      unavailable('Display AST…', AST_UNAVAILABLE_REASON),
      unavailable('Display CSG Tree…', CSG_UNAVAILABLE_REASON),
      unavailable('Display CSG Products…', CSG_UNAVAILABLE_REASON),
      {
        type: 'action',
        label: 'Display Parameters…',
        shortcutAction: 'showAST',
        enabled: hasFile,
        tooltip: hasFile
          ? 'Shows the customizer parameters this file declares'
          : 'Open a file first',
        handler: () => designPanelController.showAST(),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'Flush Caches',
        shortcutAction: 'flushCaches',
        handler: () => designPanelController.flushCaches(),
      },
      // Forge-only: Classic's Design menu keeps its upstream shape.
      ...(document.body.dataset.uiMode !== 'classic'
        ? [
            { type: 'separator' },
            {
              type: 'action',
              label: 'Open a drawing in the editor\u2026',
              tooltip:
                'An SVG drawing or a photo, opened in the drawing editor',
              handler: () => openDrawingPicker(),
            },
          ]
        : []),
    ];
  });

  // Initialize display options controller (Axes, Edges, Crosshairs, Wireframe)
  const displayOptionsController = getDisplayOptionsController({
    getPreviewManager: () => previewManager,
    getThree: getThreeModule,
  });
  displayOptionsController.init();

  // ── Toolbar: View menu ───────────────────────────────────────────────────
  getToolbarMenuController().registerMenuBuilder('view', () => {
    const state = stateManager.getState();
    // Center and View All fit the camera to previewManager.mesh, which a
    // preview already provides; state.stl needs a full Generate.
    const hasViewportModel =
      Boolean(state.stl) || Boolean(previewManager?.mesh);
    const projMode = previewManager?.getProjectionMode?.() ?? 'perspective';
    const uiCtrl = getUIModeController();
    const uiModeNow = uiCtrl.getMode();

    function interfaceModeRadio(label, value) {
      return {
        type: 'radio',
        label,
        group: 'interfaceMode',
        value,
        checked: uiModeNow === value,
        onChange: () => {
          if (uiModeNow !== value) {
            uiCtrl.switchMode(value);
          }
        },
      };
    }

    function cameraViewHandler(view) {
      return () => {
        if (previewManager) {
          previewManager.setCameraView(view);
          announceCameraAction(`${view} view`);
        }
      };
    }

    return [
      // -- Display Toggles --
      {
        type: 'toggle',
        label: 'Show Edges',
        shortcutAction: 'toggleEdges',
        checked: displayOptionsController.get('edges'),
        handler: () => displayOptionsController.toggle('edges'),
      },
      {
        type: 'toggle',
        label: 'Show Axes',
        shortcutAction: 'toggleAxes',
        checked: displayOptionsController.get('axes'),
        handler: () => displayOptionsController.toggle('axes'),
      },
      {
        // Upstream's label. This app's mm tick overlay is the scale-marker
        // overlay, and the toolbar button carries the same name.
        type: 'toggle',
        label: 'Show Scale Markers',
        checked: displayOptionsController.get('axisMarks'),
        handler: () => displayOptionsController.toggle('axisMarks'),
      },
      {
        type: 'toggle',
        label: 'Show Crosshairs',
        shortcutAction: 'toggleCrosshairs',
        checked: displayOptionsController.get('crosshairs'),
        handler: () => displayOptionsController.toggle('crosshairs'),
      },
      // The grid, measurements and status-bar toggles. Forge-only: Classic's
      // View menu keeps its desktop shape.
      ...(document.body.dataset.uiMode !== 'classic'
        ? [
            {
              type: 'toggle',
              label: 'Show Grid',
              checked: Boolean(previewManager?.gridEnabled),
              enabled: Boolean(previewManager),
              tooltip: previewManager
                ? undefined
                : 'Preview or render a model first',
              handler: () => {
                if (!previewManager) return;
                const next = !previewManager.gridEnabled;
                previewManager.toggleGrid(next);
                announceImmediate(`Grid ${next ? 'shown' : 'hidden'}`);
              },
            },
            {
              type: 'toggle',
              label: 'Show Measurements',
              checked: Boolean(previewManager?.measurementsEnabled),
              enabled: Boolean(previewManager),
              tooltip: previewManager
                ? undefined
                : 'Preview or render a model first',
              handler: () => {
                if (!previewManager) return;
                const next = !previewManager.measurementsEnabled;
                previewManager.toggleMeasurements(next);
                updateDimensionsDisplay();
                announceImmediate(`Measurements ${next ? 'shown' : 'hidden'}`);
              },
            },
            {
              type: 'toggle',
              label: 'Show Status Bar',
              checked: !document
                .getElementById('previewStatusBar')
                ?.classList.contains('user-hidden'),
              handler: () => {
                const bar = document.getElementById('previewStatusBar');
                if (!bar) return;
                setPreviewStatusBarShown(bar.classList.contains('user-hidden'));
              },
            },
            // Mirrors the Auto-rotate switch in Preview Settings & Info, which
            // owns the state; the item flips the switch so both stay one.
            {
              type: 'toggle',
              label: 'Auto-rotate',
              checked: Boolean(
                document.getElementById('autoRotateSetting')?.checked
              ),
              enabled: Boolean(previewManager),
              tooltip: previewManager
                ? undefined
                : 'Preview or render a model first',
              handler: () => {
                const toggle = document.getElementById('autoRotateSetting');
                if (!toggle) return;
                toggle.checked = !toggle.checked;
                toggle.dispatchEvent(new Event('change'));
              },
            },
          ]
        : []),
      { type: 'separator' },
      // -- Camera Views --
      {
        type: 'action',
        label: 'Top',
        shortcutAction: 'viewTop',
        handler: cameraViewHandler('top'),
      },
      {
        type: 'action',
        label: 'Bottom',
        shortcutAction: 'viewBottom',
        handler: cameraViewHandler('bottom'),
      },
      {
        type: 'action',
        label: 'Left',
        shortcutAction: 'viewLeft',
        handler: cameraViewHandler('left'),
      },
      {
        type: 'action',
        label: 'Right',
        shortcutAction: 'viewRight',
        handler: cameraViewHandler('right'),
      },
      {
        type: 'action',
        label: 'Front',
        shortcutAction: 'viewFront',
        handler: cameraViewHandler('front'),
      },
      {
        type: 'action',
        label: 'Back',
        shortcutAction: 'viewBack',
        handler: cameraViewHandler('back'),
      },
      {
        type: 'action',
        label: 'Diagonal',
        shortcutAction: 'viewDiagonal',
        handler: cameraViewHandler('diagonal'),
      },
      // Center, View All and Reset View are three different commands upstream,
      // and three different commands here.
      {
        type: 'action',
        label: 'Center',
        shortcutAction: 'viewCenter',
        enabled: hasViewportModel,
        tooltip: hasViewportModel
          ? undefined
          : 'Preview or render a model first',
        handler: () => {
          if (previewManager) {
            previewManager.centerCamera();
            announceCameraAction('View centered on the model');
          }
        },
      },
      {
        type: 'action',
        label: 'View All',
        shortcutAction: 'viewAll',
        enabled: hasViewportModel,
        tooltip: hasViewportModel
          ? undefined
          : 'Preview or render a model first',
        handler: () => {
          if (previewManager) {
            previewManager.viewAllCamera();
            announceCameraAction('View fitted to model');
          }
        },
      },
      {
        // No render needed: this one restores the default pose rather than
        // framing anything, so it is the way back from a lost camera.
        type: 'action',
        label: 'Reset View',
        shortcutAction: 'resetView',
        handler: () => {
          if (previewManager) {
            previewManager.resetCamera();
            announceCameraAction('reset');
          }
        },
      },
      { type: 'separator' },
      // -- Zoom --
      {
        type: 'action',
        label: 'Zoom In',
        shortcutAction: 'zoomIn',
        handler: () => {
          if (previewManager) {
            previewManager.zoomCamera(CAMERA_ZOOM_STEP);
            announceCameraAction('zoom-in');
          }
        },
      },
      {
        type: 'action',
        label: 'Zoom Out',
        shortcutAction: 'zoomOut',
        handler: () => {
          if (previewManager) {
            previewManager.zoomCamera(-CAMERA_ZOOM_STEP);
            announceCameraAction('zoom-out');
          }
        },
      },
      { type: 'separator' },
      // -- Projection Radio Group --
      {
        type: 'radio',
        label: 'Perspective',
        group: 'projection',
        value: 'perspective',
        checked: projMode === 'perspective',
        onChange: () => {
          if (previewManager && projMode !== 'perspective') {
            previewManager.toggleProjection();
          }
        },
      },
      {
        type: 'radio',
        label: 'Orthogonal',
        group: 'projection',
        value: 'orthographic',
        checked: projMode === 'orthographic',
        onChange: () => {
          if (previewManager && projMode !== 'orthographic') {
            previewManager.toggleProjection();
          }
        },
      },
      // -- Per-toolbar hide toggles (Classic-only markup) --
      ...(document.body.dataset.uiMode === 'classic'
        ? [
            { type: 'separator' },
            ...Object.entries(CLASSIC_HIDEABLE_TOOLBARS).map(([bar, def]) => ({
              type: 'toggle',
              label: def.label,
              checked: isClassicToolbarHidden(bar),
              handler: () => toggleClassicToolbar(bar),
            })),
          ]
        : []),
      { type: 'separator' },
      // -- Preview Quality (proxies #previewQualitySelect) --
      (() => {
        const select = document.getElementById('previewQualitySelect');
        const options = select ? Array.from(select.options) : [];
        return {
          type: 'submenu',
          label: 'Preview Quality',
          items: options.map((opt) => ({
            type: 'radio',
            label: opt.textContent.trim(),
            group: 'previewQuality',
            value: opt.value,
            checked: select?.value === opt.value,
            onChange: () => {
              if (!select) return;
              select.value = opt.value;
              select.dispatchEvent(new Event('change'));
            },
          })),
        };
      })(),
      // The edge budget: this list is the setting's one home. setEdgeBudget
      // persists, rebuilds the overlay and announces the stats.
      ...(document.body.dataset.uiMode !== 'classic'
        ? [
            (() => {
              const EDGE_DETAIL_OPTIONS = [
                { label: 'Low (25,000 edges)', value: 25000 },
                { label: 'Balanced (75,000 edges)', value: 75000 },
                { label: 'High (250,000 edges)', value: 250000 },
                { label: 'Unlimited', value: 0 },
              ];
              const current = displayOptionsController.getEdgeBudget();
              return {
                type: 'submenu',
                label: 'Edge Detail Limit',
                items: EDGE_DETAIL_OPTIONS.map((opt) => ({
                  type: 'radio',
                  label: opt.label,
                  group: 'edgeDetail',
                  value: String(opt.value),
                  checked: current === opt.value,
                  onChange: () =>
                    displayOptionsController.setEdgeBudget(opt.value),
                })),
              };
            })(),
          ]
        : []),
      ...(document.body.dataset.uiMode === 'classic'
        ? [
            {
              // Keyboard/menu home for the header Simplified/Standard switch
              type: 'toggle',
              label: 'Simplified view',
              checked:
                getUIModeController().getClassicDensity() === 'simplified',
              handler: () => {
                getUIModeController().toggleClassicDensity();
              },
            },
            {
              // The way back from any arrangement the title-bar menus can produce.
              label: 'Reset Panel Layout',
              handler: () => {
                getClassicLayoutController()?.resetPanelLayout();
              },
            },
          ]
        : []),
      { type: 'separator' },
      // -- Interface Mode Radio Group (Classic gated on classic_mode flag) --
      // The radios name the layout they go to: Classic's own "Simplified
      // view" sits in this same menu.
      interfaceModeRadio('Assistive Forge, Simplified', 'simplified'),
      interfaceModeRadio('Assistive Forge, Standard', 'standard'),
      ...(_isEnabled('classic_mode')
        ? [interfaceModeRadio('Classic (Desktop Layout)', 'classic')]
        : []),
    ];
  });

  // ── Toolbar: Window menu ─────────────────────────────────────────────────
  getToolbarMenuController().registerMenuBuilder('window', () => {
    const uiCtrl = getUIModeController();
    /**
     * The tick reads the DOM, not the Simplified-view preference: Classic's
     * dock adopts the Console and shows it whatever that preference says. And
     * togglePanelVisibility announces the change itself, so this does not.
     *
     * @param {string} panelId
     * @param {string} label
     * @param {string|undefined} shortcutAction
     */
    function panelToggle(panelId, label, shortcutAction) {
      return {
        type: 'toggle',
        label,
        checked: uiCtrl.isPanelShowing(panelId),
        ...(shortcutAction ? { shortcutAction } : {}),
        handler: () => uiCtrl.togglePanelVisibility(panelId),
      };
    }

    const classicLayout = getClassicLayoutController();
    const inClassic =
      document.body.dataset.uiMode === 'classic' && Boolean(classicLayout);

    /**
     * A Forge panel that Classic keeps out of the Customizer column.
     * classic.css hides the row while its <details> is closed, so `open`
     * is both the visibility and the tick — one state, so the two cannot
     * disagree.
     *
     * Not panelToggle() for these, even though four of them are in
     * PANEL_REGISTRY: for Libraries and Companion Files the registry names the
     * WRAPPER div, so its tick would report a closed panel as showing and its
     * handler would flip a class the Classic rule does not read. Rather than
     * two helpers in one list for reasons a reader cannot see, all five of the
     * set go through this one.
     *
     * @param {string} selector - the row's <details>
     * @param {string} label - the panel's name, as the Window menu lists it
     */
    function forgeExtraToggle(selector, label) {
      const row = document.querySelector(selector);
      return {
        type: 'toggle',
        label,
        checked: Boolean(row?.open),
        handler: () => {
          const el = document.querySelector(selector);
          if (!el) return;
          el.open = !el.open;
          // Same sentence as UIModeController.togglePanelVisibility uses for
          // every other disclosure, and once per toggle.
          announceImmediate(`${label} ${el.open ? 'opened' : 'closed'}`, {
            clearDelayMs: 1500,
          });
          if (el.open) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        },
      };
    }

    // Upstream builds this menu from the docks themselves, so its order is the
    // dock order: Editor, Console, Customizer, Error-Log, Animate, Font List,
    // Viewport-Control. Next/Previous Window are omitted: one window.
    const editorAvailable = _isEnabled('expert_mode');

    return [
      {
        // Forge's tick asks the toggle button's own pressed state — the DOM
        // truth for "is the editor open". The registry's codeEditor entry
        // tracks whether the button is shown, which is a different question.
        type: 'toggle',
        label: 'Editor',
        shortcutAction: 'toggleCodeEditor',
        checked: inClassic
          ? classicLayout.isEditorVisible()
          : editorAvailable &&
            document
              .getElementById('expertModeToggle')
              ?.getAttribute('aria-pressed') === 'true',
        enabled: inClassic || editorAvailable,
        tooltip:
          inClassic || editorAvailable
            ? undefined
            : CODE_EDITOR_UNAVAILABLE_REASON,
        handler: () => toggleEditorPanel(),
      },
      {
        type: 'toggle',
        label: 'Console',
        shortcutAction: 'toggleConsole',
        checked: inClassic
          ? isConsoleShowingClassic()
          : uiCtrl.isPanelShowing('consoleOutput'),
        handler: () => toggleConsole(),
      },
      {
        type: 'toggle',
        label: 'Customizer',
        shortcutAction: 'toggleCustomizer',
        checked: inClassic
          ? classicLayout.isCustomizerVisible()
          : !document
              .getElementById('paramPanel')
              ?.classList.contains('collapsed'),
        handler: () => toggleCustomizerPanel(),
      },
      // Error-Log gets a custom handler rather than a panelToggle: it is a
      // console tab in Forge and an always-present strip pane in Classic, so
      // PANEL_REGISTRY's show/hide semantics fit neither host.
      {
        type: 'toggle',
        label: 'Error-Log',
        shortcutAction: 'toggleErrorLog',
        checked: isErrorLogShowing(),
        handler: () => toggleErrorLog(),
      },
      // Three panels are Classic-only, so each is a real dock toggle in Classic
      // and keeps its Forge behavior outside it.
      ...(inClassic
        ? [
            {
              type: 'toggle',
              label: 'Animate',
              checked: classicLayout.isAnimateVisible(),
              handler: () => classicLayout.toggleAnimate(),
            },
            {
              type: 'toggle',
              label: 'Font List',
              checked: classicLayout.isFontListVisible(),
              handler: () => classicLayout.toggleFontList(),
            },
            {
              type: 'toggle',
              label: 'Viewport-Control',
              checked: classicLayout.isViewportControlVisible(),
              handler: () => classicLayout.toggleViewportControl(),
            },
            // The five Forge panels Classic keeps out of the Customizer column.
            // Listed after the dock panels and in the column order
            // they had, so a user who knows where they used to be finds them
            // in that order here. Outside Classic these stay where they were,
            // in the web-only group at the foot of this menu.
            { type: 'separator' },
            forgeExtraToggle('#measureSection', 'Image Measurement'),
            forgeExtraToggle('#overlaySection', 'Reference Image'),
            forgeExtraToggle('#libraryControls > details', 'Libraries'),
            // A .forge-disclosure-row sits between the two, so this cannot be a
            // direct-child selector; the class is unique inside the wrapper either
            // way.
            forgeExtraToggle(
              '#projectFilesControls .project-files-details',
              'Companion Files'
            ),
            forgeExtraToggle('#advancedMenu', 'Advanced'),
          ]
        : [
            {
              type: 'action',
              label: 'Viewport-Control',
              handler: () => {
                const panel = document.getElementById('cameraPanel');
                if (panel) {
                  panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  const focusable = panel.querySelector(
                    'button, input, select'
                  );
                  if (focusable) focusable.focus();
                }
              },
            },
          ]),
      { type: 'separator' },
      // Upstream's Ctrl+J opens a jump-to-dock popup. The web reading is a
      // picker of the panels that are on screen right now; choosing one moves
      // focus into it.
      (() => {
        const targets = jumpTargets();
        if (targets.length === 0) {
          return {
            type: 'action',
            label: JUMP_TO_LABEL,
            disabled: true,
            tooltip: JUMP_TO_EMPTY_REASON,
          };
        }
        return {
          type: 'submenu',
          label: JUMP_TO_LABEL,
          shortcutAction: 'jumpToPanel',
          items: targets.map((target) => ({
            type: 'action',
            label: target.label,
            handler: () => {
              // Immediate, not debounced: a render reporting in
              // within 350ms cancels a pending announcement outright, so the
              // user hears nothing about the jump they just made.
              if (target.focus())
                announceImmediate(`Jumped to ${target.label}`);
            },
          })),
        };
      })(),
      // -- Web-only panel toggles --
      // Classic lists these above instead, keyed on each row's own [open].
      // Listing them here as well would put two items with the same name in
      // one menu.
      ...(inClassic
        ? []
        : [
            { type: 'separator' },
            panelToggle('libraries', 'Libraries'),
            panelToggle('companionFileManagement', 'Companion Files'),
            panelToggle('imageMeasurement', 'Image Measurement'),
            panelToggle('referenceOverlay', 'Reference Image'),
            // The same tail slot as Classic's Window menu; the announcement uses the
            // registry label "Advanced Menu".
            panelToggle('advancedMenu', 'Advanced'),
          ]),
    ];
  });

  // ── Toolbar: Help menu ───────────────────────────────────────────────────
  getToolbarMenuController().registerMenuBuilder('help', () => {
    function _openFeaturesTab(tabId) {
      const modal = document.getElementById('featuresGuideModal');
      if (!modal) return;
      openModal(modal);
      const tab = document.getElementById(tabId);
      if (tab) tab.click();
    }

    return [
      {
        type: 'action',
        label: 'About',
        handler: () => openAboutModal(),
      },
      {
        type: 'action',
        label: 'OpenSCAD Homepage',
        tooltip: 'Opens in a new window',
        handler: () =>
          window.open('https://openscad.org', '_blank', 'noopener,noreferrer'),
      },
      {
        type: 'action',
        label: 'Documentation',
        tooltip: 'Opens in a new window',
        handler: () =>
          window.open(
            'https://openscad.org/documentation.html',
            '_blank',
            'noopener,noreferrer'
          ),
      },
      // Both offline items keep upstream's position and say why they cannot
      // work rather than being hidden: nothing third-party is fetched or
      // vendored here.
      {
        type: 'action',
        label: 'Offline Documentation',
        disabled: true,
        tooltip: OFFLINE_DOCUMENTATION_REASON,
      },
      {
        type: 'action',
        label: 'Cheat Sheet',
        tooltip: 'Opens in a new window',
        handler: () =>
          window.open(
            'https://openscad.org/cheatsheet/',
            '_blank',
            'noopener,noreferrer'
          ),
      },
      {
        type: 'action',
        label: 'Offline Cheat Sheet',
        disabled: true,
        tooltip: OFFLINE_CHEAT_SHEET_REASON,
      },
      {
        // Upstream's sentence case. The switches themselves are where
        // File > Show Library Folder goes.
        type: 'action',
        label: 'Library info',
        handler: () => void showLibraryInfoDialog(),
      },
      { type: 'separator' },
      {
        type: 'action',
        label: 'Features Guide',
        handler: () => _openFeaturesTab('tab-workflow'),
      },
      {
        type: 'action',
        label: 'Keyboard Shortcuts\u2026',
        shortcutAction: 'showShortcutsModal',
        handler: () => openShortcutsDialog(),
      },
      {
        type: 'action',
        label: 'Report Issue',
        tooltip: 'Opens in a new window',
        handler: () =>
          window.open(
            'https://github.com/BrennenJohnston/openscad-assistive-forge/issues',
            '_blank',
            'noopener,noreferrer'
          ),
      },
    ];
  });

  // Listen for "Save to Project" events from UI preferences panel
  document.addEventListener('ui-mode-save-to-project', (e) => {
    const prefs = e.detail?.uiPreferences;
    if (!prefs) return;

    const state = stateManager.getState();
    const modelName = state.uploadedFile?.name;
    if (modelName) {
      // Primary path: persist uiPreferences into the IndexedDB project record
      if (currentSavedProjectId) {
        updateProject({ id: currentSavedProjectId, uiPreferences: prefs })
          .then((result) => {
            if (result.success) {
              console.log(
                `[App] UI preferences saved to project record: ${modelName}`
              );
            } else {
              console.warn(
                '[App] Could not save UI preferences to project record:',
                result.error
              );
            }
          })
          .catch((err) => {
            console.error('[App] Project update failed:', err);
          });
      }

      // Fallback path: keep the legacy localStorage key in sync for one release
      // so that projects loaded before this change still have preferences available.
      const uiPrefsKey = `openscad-forge-ui-prefs-${modelName}`;
      if (safeSetItem(uiPrefsKey, JSON.stringify(prefs))) {
        console.log(`[App] UI preferences saved for project: ${modelName}`);
        updateStatus('UI preferences saved to project');
      }
    } else {
      updateStatus('Load a project first to save preferences');
    }
  });

  // Initialize theme toggle button
  initThemeToggle('themeToggle', (theme, activeTheme, message) => {
    console.log(`[App] ${message}`);
    // Optional: Show brief toast notification
    updateStatus(message);
    setTimeout(() => {
      const state = stateManager.getState();
      if (state.uploadedFile) {
        updateStatus('Ready');
      }
    }, 2000);
  });

  // ============================================================================
  // Preset migration check: detect legacy presets for versioned format migration
  // Check for legacy presets that can be migrated to the new versioned format
  // ============================================================================
  const checkPresetMigration = async () => {
    try {
      const migrationInfo = checkMigrationAvailable();

      if (migrationInfo.available && !migrationInfo.alreadyOffered) {
        console.log('[App] Legacy presets detected:', {
          presets: migrationInfo.legacyPresetCount,
          models: migrationInfo.legacyModelCount,
        });

        // Show migration prompt (non-blocking, user can dismiss)
        const message =
          `Found ${migrationInfo.legacyPresetCount} preset(s) from a previous version. ` +
          `Would you like to migrate them to preserve your work?`;

        // Opens on Migrate: Skip ends the offer for good, and Enter on the
        // browser's own question used to migrate.
        const shouldMigrate = await showConfirmDialog(
          message,
          'Presets From an Earlier Version',
          'Migrate',
          'Skip',
          { focusConfirm: true }
        );

        if (shouldMigrate) {
          const result = migrateFromLegacyStorage({ createBackup: true });

          if (result.success) {
            updateStatus(
              `Migrated ${result.migratedPresets} preset(s) successfully!`,
              'success'
            );
            console.log('[App] Migration complete:', result);
          } else {
            updateStatus(
              'Migration encountered issues. Your original presets are preserved.',
              'warning'
            );
            console.warn('[App] Migration issues:', result.errors);
          }
        } else {
          // User declined - don't ask again
          dismissMigrationOffer();
          console.log('[App] User declined preset migration');
        }
      }
    } catch (error) {
      console.warn('[App] Error checking preset migration:', error);
    }
  };

  // Check migration after a short delay (after first-visit modal if present)
  setTimeout(checkPresetMigration, 1500);

  // Initialize high contrast toggle button
  const contrastBtn = document.getElementById('contrastToggle');
  if (contrastBtn) {
    // The label is synced from the state, not written only inside this
    // handler: Ctrl+H and the City Walk's in-game toggle change the contrast
    // too, and the label must not say the opposite of the truth to the people
    // who cannot see the button change.
    const syncContrastLabel = () => {
      const on = themeManager.highContrast;
      // The name keeps its visible word; on or off is the pressed state.
      contrastBtn.setAttribute('aria-label', 'HC: high contrast');
      contrastBtn.setAttribute('aria-pressed', String(on));
      contrastBtn.title = on ? 'High contrast is on' : 'High contrast';
    };

    contrastBtn.addEventListener('click', () => {
      const enabled = themeManager.toggleHighContrast();
      const message = enabled ? 'High Contrast: ON' : 'High Contrast: OFF';
      console.log(`[App] ${message}`);
      updateStatus(message);

      setTimeout(() => {
        const state = stateManager.getState();
        if (state.uploadedFile) {
          updateStatus('Ready');
        }
      }, 2000);
    });

    themeManager.addListener(syncContrastLabel);
    syncContrastLabel();
  }

  // Initialize keyboard shortcuts toggle button
  const shortcutsBtn = document.getElementById('shortcutsToggle');
  if (shortcutsBtn) {
    shortcutsBtn.addEventListener('click', () => openShortcutsDialog());
  }

  // ── The drawing editor takes the preview area ─────────────────────────
  // The editor says it is opening; what that means for the 3D canvas is the
  // preview's business, and this is the one place that knows both.
  // Looked up at the moment, not at boot: the preview's init() rebuilds its
  // container, and the element that was there before it is not the one that
  // is there after.
  const drawingEditorSurface = () =>
    document.getElementById('drawingEditorSurface');
  window.addEventListener('drawing-editor:open', () => {
    previewManager?.showEditorSurface?.(drawingEditorSurface());
    // Cheaper previews for as long as the session lasts.
    editorDraftQuality = true;
    applyPreviewQualityMode();
  });
  window.addEventListener('drawing-editor:close', () => {
    previewManager?.hideEditorSurface?.(drawingEditorSurface());
    editorDraftQuality = false;
    // Whatever draft stood, the committed design comes back below.
    draftPreviewHash = null;
    applyPreviewQualityMode();
    // Changing the quality marks the preview stale, which is honest and, on
    // its own, useless: the person gets their own quality back as a LABEL on
    // a picture still drawn at draft, and has to ask for it again. The same
    // pair the quality select itself uses - apply the mode, then redraw.
    if (autoPreviewController) {
      const state = stateManager.getState();
      if (state?.uploadedFile) {
        autoPreviewController.onParameterChange(state.parameters);
      }
    }
  });
  // A draft of the charm with the drawing as it is now, asked for from
  // the editor's charm view. Drawn through the preview alone: the state, the
  // undo history and the project are not touched, so nothing is written
  // until Apply, and closing without it leaves the committed design standing
  // (the close handler above re-renders it, from the cache when it can).
  // A DXF chosen for a design parameter converts through the same
  // engine the standalone door uses. Asked at the moment of choosing, so a
  // control built before the controller exists still gets it.
  setDxfRenderProvider(() =>
    renderController
      ? (scad, params, options) =>
          renderController.render(scad, params, options)
      : null
  );

  setDraftRenderer((paramName, value, extra) => {
    if (!autoPreviewController) return;
    const state = stateManager.getState();
    if (!state?.parameters) return;
    const draft = { ...state.parameters, [paramName]: value, ...(extra || {}) };
    draftPreviewHash = hashParams(draft);
    autoPreviewController.forcePreview(draft);
  });

  // The editor's Charm view is this preview, seen through an editor
  // that has stopped painting over it.
  window.addEventListener('drawing-editor:view', (event) => {
    previewManager?.setEditorCharmVisible?.(
      event.detail?.view === 'charm',
      event.detail?.host || null
    );
  });

  // Declare format selector elements
  const outputFormatSelect = document.getElementById('outputFormat');

  // ── Export the whole stencil set ──────────────────────────────────────
  // A six-colour stencil is seven printed parts, and the one thing that must
  // not go wrong is which plate is which. The button renders them in order and
  // hands back a zip whose names say what each file is.
  const stencilSetExport = document.getElementById('stencilSetExport');
  const exportStencilSetBtn = document.getElementById('exportStencilSetBtn');
  const stencilSetExportInfo = document.getElementById('stencilSetExportInfo');

  /** How many plates the app has actually written for the current design. */
  function stencilPlateCount(parameters) {
    let n = 0;
    for (let i = 1; i <= 8; i++) {
      const v = parameters?.[`stencil_plate_${i}`];
      const has = v && (typeof v === 'string' ? v !== '' : !!v.data);
      if (!has) break;
      n += 1;
    }
    return n;
  }

  function updateStencilSetExport() {
    if (!stencilSetExport) return;
    const state = stateManager.getState();
    const params = state.parameters || {};
    const isStencil = 'stencil_plate_1' in params && 'plate_number' in params;
    const count = isStencil ? stencilPlateCount(params) : 0;
    const show = isStencil && params.stencil_mode === 'layered' && count > 0;
    stencilSetExport.hidden = !show;
    if (!show) return;
    const pegs =
      params.registration === 'pegs' || params.registration === 'both';
    const parts = count + (pegs ? 1 : 0);
    stencilSetExportInfo.textContent = pegs
      ? `${count} plates and the jig base, plus the paint order.`
      : `${count} plates, plus the paint order.`;
    exportStencilSetBtn.setAttribute(
      'aria-label',
      `Export all plates: ${parts} files as a zip`
    );
  }

  // Which plates exist changes when the design changes and when the plate
  // params are rewritten, both of which land as a parameters update.
  stateManager.subscribe((state, prevState) => {
    if (state.parameters !== prevState.parameters) updateStencilSetExport();
  });
  updateStencilSetExport();

  exportStencilSetBtn?.addEventListener('click', async () => {
    if (isBusy(exportStencilSetBtn)) return;
    const state = stateManager.getState();
    const params = state.parameters || {};
    const count = stencilPlateCount(params);
    const pegs =
      params.registration === 'pegs' || params.registration === 'both';
    const format = outputFormatSelect ? outputFormatSelect.value : 'stl';
    const designName = String(params.design_file?.name || 'stencil').replace(
      /\.[^.]+$/,
      ''
    );
    const { stencilSetJobs, exportStencilSet, EXPORT_STRINGS } =
      await import('./js/stencil-export.js');
    const colourNames = Array.isArray(state.stencilColourNames)
      ? state.stencilColourNames
      : [];
    const jobs = stencilSetJobs({
      parameters: params,
      plateCount: count,
      colourNames,
      includeJig: pegs,
      format,
    });

    setBusy(exportStencilSetBtn, true);
    const restore = exportStencilSetBtn.textContent;
    exportStencilSetBtn.textContent = EXPORT_STRINGS.busy;
    announceImmediate(EXPORT_STRINGS.start(jobs.length + 1));
    try {
      const libsForRender = getEnabledLibrariesForRender();
      const { blob, filename, files } = await exportStencilSet({
        jobs,
        designName,
        colourNames,
        onProgress: (done, total, label) => {
          if (!label) return;
          const line = EXPORT_STRINGS.step(done, total, label);
          updateStatus(line);
          announceImmediate(line);
        },
        render: (parameters) =>
          renderController.renderFull(state.uploadedFile.content, parameters, {
            outputFormat: format,
            paramTypes: state.paramTypes || {},
            files: state.projectFiles,
            mainFile: state.mainFilePath,
            libraries: libsForRender,
          }),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const done = EXPORT_STRINGS.done(files.length, filename);
      updateStatus(done);
      announceImmediate(done);
    } catch (error) {
      console.error('Stencil set export failed:', error);
      const said = EXPORT_STRINGS.failed(error.message);
      updateStatus(said);
      announceImmediate(said);
      showErrorToast({ title: 'Export stopped', message: error.message });
    } finally {
      setBusy(exportStencilSetBtn, false);
      exportStencilSetBtn.textContent = restore;
    }
  });

  const formatInfo = document.getElementById('formatInfo');
  const format2dGuidance = document.getElementById('format2dGuidance');

  // Initialize output format selector
  if (outputFormatSelect && formatInfo) {
    outputFormatSelect.addEventListener('change', () => {
      const format = outputFormatSelect.value;
      const formatDef = OUTPUT_FORMATS[format];

      if (formatDef) {
        formatInfo.textContent = formatDef.description;

        // Update button text
        const formatName = formatDef.name;
        if (primaryActionBtn.dataset.action === 'generate') {
          primaryActionBtn.textContent = `Generate ${formatName}`;
          primaryActionBtn.setAttribute(
            'aria-label',
            `Generate ${formatName} file from current parameters`
          );
        } else {
          setButtonTextWithIcon(
            primaryActionBtn,
            'download',
            `Download ${formatName}`
          );
          primaryActionBtn.setAttribute(
            'aria-label',
            `Download generated ${formatName} file`
          );
        }

        // Show/hide 2D format guidance for SVG/DXF laser cutting workflows
        if (format2dGuidance) {
          if (formatDef.is2D) {
            const wasHidden = format2dGuidance.classList.contains('hidden');
            format2dGuidance.classList.remove('hidden');
            if (wasHidden) {
              format2dGuidance.classList.remove('guidance-enter');
              void format2dGuidance.offsetWidth; // reflow to restart animation
              format2dGuidance.classList.add('guidance-enter');
              format2dGuidance.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
            announceImmediate(
              `${formatName} is a 2D format. See guidance below the format selector.`
            );

            // Show "What will be auto-adjusted" indicator
            const state = stateManager.getState();
            const autoAdjustDiv = document.getElementById('format2dAutoAdjust');
            const autoAdjustList = document.getElementById(
              'format2dAutoAdjustList'
            );
            if (
              autoAdjustDiv &&
              autoAdjustList &&
              state?.parameters &&
              state?.schema
            ) {
              const proposal = propose2DExportChanges(
                state.parameters,
                state.schema,
                format,
                state.projectFiles
              );
              if (proposal.changes.length > 0) {
                autoAdjustList.innerHTML = proposal.changes
                  .map(
                    (c) =>
                      `<li><code>${escapeHtml(c.name)}</code>: currently <em>${escapeHtml(String(c.from))}</em> → will use <strong>${escapeHtml(String(c.to))}</strong></li>`
                  )
                  .join('');
                // Fresh consent per export intent: re-check the box each
                // time the proposal list is (re)shown.
                const applyCheckbox = document.getElementById(
                  'format2dAutoAdjustApply'
                );
                if (applyCheckbox) applyCheckbox.checked = true;
                autoAdjustDiv.classList.remove('hidden');
              } else {
                autoAdjustDiv.classList.add('hidden');
              }
            }
          } else {
            format2dGuidance.classList.add('hidden');
          }
        }

        // Ensure primary action button reflects selected format
        updatePrimaryActionButton();
      }
    });

    // Set initial format info
    const initialFormat = outputFormatSelect.value;
    formatInfo.textContent = OUTPUT_FORMATS[initialFormat]?.description || '';

    // Hide 2D guidance initially (STL is default)
    if (format2dGuidance && !OUTPUT_FORMATS[initialFormat]?.is2D) {
      format2dGuidance.classList.add('hidden');
    }
  }

  // Check browser support
  const support = checkBrowserSupport();
  if (!support.supported) {
    showUnsupportedBrowser(support.missing);
    return;
  }

  // Track WASM initialization state
  let wasmInitialized = false;
  // The download question while it is open, shared with any caller that
  // arrives meanwhile: the browser's own question held them back, the app's
  // does not.
  let downloadQuestion = null;

  /**
   * Ensure WASM is initialized before operations that need it
   * @returns {Promise<boolean>} True if initialized successfully
   */
  async function ensureWasmInitialized() {
    if (!hasUserAcceptedDownload) {
      if (firstVisitModal && firstVisitModal.classList.contains('hidden')) {
        setFirstVisitBlocking(true);
        openModal(firstVisitModal);
      }
      updateStatus(
        'Please review and accept the welcome notice before continuing.',
        'info'
      );
      return false;
    }
    if (wasmInitialized) return true;

    // On a slow or metered connection the download is asked about: once per
    // device (a Download is remembered), and not for files the browser holds.
    if (
      shouldDeferLargeDownloads() &&
      !(await engineFilesCached(DEFAULT_ENGINE_PATH))
    ) {
      downloadQuestion ??= showConfirmDialog(
        'This app needs about 15 MB of files, once. You seem to be on a slow or metered connection. Download them now?',
        'Download the engine?',
        'Download',
        'Not now'
      ).finally(() => {
        downloadQuestion = null;
      });
      const proceed = await downloadQuestion;
      if (!proceed) {
        updateStatus('WASM download deferred', 'info');
        return false;
      }
      rememberEngineDownload();
    }

    // Initialize if not yet done
    if (!renderController) {
      renderController = new RenderController();

      // Only parameters the user actually changed travel as -D.
      // Everything else follows the SCAD source's own declarations, which is
      // what lets an edited default take effect at all.
      renderController.setWithheldDefineKeyResolver(() => {
        const defineState = stateManager.getState();
        return collectWithheldDefineKeys({
          parameters: defineState.parameters || {},
          defaults: defineState.defaults || {},
          schemaNames: Object.keys(defineState.schema?.parameters || {}),
        });
      });
      renderController.setProjectFileNameResolver(
        () => stateManager.getState().uploadedFile?.name || null
      );

      // Set up memory warning callback
      renderController.setMemoryWarningCallback((memoryInfo) => {
        console.warn(
          `[Memory] High usage: ${memoryInfo.usedMB} MB allocated to the OpenSCAD engine`
        );
        // Update memory indicator
        updateMemoryIndicator(memoryInfo);
        // Feed into MemoryMonitor for badge updates
        const monitor = _getMemoryMonitor();
        if (monitor) {
          monitor.updateFromWorker(memoryInfo);
        }
        showMemoryWarning(memoryInfo);
        if (previewQualityMode === 'auto') {
          autoPreviewHints.forceFastUntil =
            Date.now() + AUTO_PREVIEW_FORCE_FAST_MS;
          adaptivePreviewMemo = { key: null, info: null };
          if (autoPreviewController) {
            autoPreviewController.clearPreviewCache();
            const state = stateManager.getState();
            if (state?.uploadedFile) {
              autoPreviewController.onParameterChange(state.parameters);
            }
          }
        }
      });

      // Handle capability detection - notify user about performance limitations
      renderController.setCapabilitiesCallback((capabilities) => {
        console.log('[Main] OpenSCAD capabilities detected:', capabilities);

        // Store for debugging/display
        window.__openscadCapabilities = capabilities;

        // Show warning if Manifold is not available
        if (!capabilities.hasManifold) {
          const warningMessage =
            'Advanced rendering optimization (Manifold) is not available in this OpenSCAD build. ' +
            'Complex models may render slower than expected.';

          // Announce warning to screen readers
          announceImmediate(warningMessage);

          // Also log to console with helpful context
          console.warn(
            '[Performance] Manifold not detected. Expected speedups:\n' +
              '- With Manifold: 5-30x faster for complex boolean operations\n' +
              '- Current: Using slower CGAL/nef backend\n' +
              'Check that official OpenSCAD WASM is loading from /wasm/openscad-official/'
          );
        }

        // Show info about binary STL support
        if (!capabilities.hasBinarySTL) {
          console.warn(
            '[Performance] Binary STL export may not be supported. ' +
              'ASCII STL is ~18x slower.'
          );
        }
      });

      // Show WASM loading progress indicator
      const wasmLoadingOverlay = showWasmLoadingIndicator();

      try {
        // Set crash detection flag BEFORE WASM init.
        // If the page crashes during init, the flag remains set and
        // recovery mode will auto-activate on next load.
        localStorage.setItem(STORAGE_KEY_WASM_INIT_STARTED, 'true');
        localStorage.removeItem(STORAGE_KEY_WASM_INIT_COMPLETED);

        const assetBaseUrl = new URL(
          import.meta.env.BASE_URL,
          window.location.origin
        )
          .toString()
          .replace(/\/$/, '');
        await renderController.init({
          assetBaseUrl,
          onProgress: (percent, message) => {
            console.log(`[WASM Init] ${message}`);
            updateWasmLoadingProgress(wasmLoadingOverlay, percent, message);
          },
        });
        console.log('OpenSCAD WASM ready');
        hideWasmLoadingIndicator(wasmLoadingOverlay);
        wasmInitialized = true;

        // Clear crash detection flag — WASM init succeeded
        localStorage.setItem(STORAGE_KEY_WASM_INIT_COMPLETED, 'true');

        // Start worker health monitoring
        renderController.startHealthMonitoring();
        // Expose WASM readiness as a DOM attribute so E2E tests
        // can wait for it without race-prone overlay checks.
        document.body.setAttribute('data-wasm-ready', 'true');
        // Start memory usage polling
        startMemoryPolling();
        return true;
      } catch (error) {
        console.error('Failed to initialize OpenSCAD WASM:', error);
        hideWasmLoadingIndicator(wasmLoadingOverlay);
        // Said by its dialog alone.
        updateStatus('OpenSCAD engine failed to initialize', 'error', {
          announce: false,
        });
        await showEngineStartFailure(error, startEngineAgain);
        return false;
      }
    }

    return wasmInitialized;
  }

  /**
   * After a failed start, the engine the person picked: a new controller,
   * since the failed one has stopped its worker, then the open file's
   * preview. Another failure shows its own dialog and offer.
   * @param {string} id
   */
  async function startEngineAgain(id) {
    setChosenEngineId(id);
    renderController = null;
    if (await ensureWasmInitialized()) await previewOpenFileAfterStart();
  }

  // Initialize render controller immediately for now (future: can be deferred)
  if (hasUserAcceptedDownload) {
    console.log('Initializing OpenSCAD WASM...');
    await ensureWasmInitialized();
  } else {
    pendingWasmInit = true;
    console.log('WASM init deferred until user consent.');
  }

  /**
   * Show WASM loading progress indicator
   * @returns {HTMLElement} The loading overlay element
   */
  function showWasmLoadingIndicator() {
    const overlay = document.createElement('div');
    overlay.id = 'wasmLoadingOverlay';
    overlay.className = 'wasm-loading-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-label', 'Loading OpenSCAD engine');

    overlay.setAttribute('aria-busy', 'true');

    // Init progress is indeterminate — stage messages only, no percentages
    // (the worker has no real measurement to report).
    overlay.innerHTML = `
      <div class="wasm-loading-content">
        <div class="wasm-loading-spinner">
          <div class="spinner spinner-large"></div>
        </div>
        <h2 class="wasm-loading-title">Loading OpenSCAD Engine</h2>
        <p class="wasm-loading-message">Initializing...</p>
        <div class="wasm-loading-progress-container">
          <div class="wasm-loading-progress-bar">
            <div class="wasm-loading-progress-fill indeterminate"></div>
          </div>
          <span class="wasm-loading-progress-text"></span>
        </div>
        <p class="wasm-loading-hint">This may take a moment on first load (~15-30MB download)</p>
      </div>
    `;

    document.body.appendChild(overlay);
    return overlay;
  }

  /**
   * Update WASM loading progress indicator
   * @param {HTMLElement} overlay - The loading overlay element
   * @param {number} percent - Progress percentage (-1 for indeterminate)
   * @param {string} message - Progress message
   */
  function updateWasmLoadingProgress(overlay, percent, message) {
    if (!overlay) return;

    const messageEl = overlay.querySelector('.wasm-loading-message');
    const progressFill = overlay.querySelector('.wasm-loading-progress-fill');
    const progressText = overlay.querySelector('.wasm-loading-progress-text');

    if (messageEl) messageEl.textContent = message;

    if (percent < 0) {
      // Indeterminate progress
      if (progressFill) {
        progressFill.classList.add('indeterminate');
        progressFill.style.width = '100%';
      }
      if (progressText) progressText.textContent = '';
    } else {
      if (progressFill) {
        progressFill.classList.remove('indeterminate');
        progressFill.style.width = `${percent}%`;
      }
      if (progressText) progressText.textContent = `${percent}%`;
    }
  }

  /**
   * Hide WASM loading indicator
   * @param {HTMLElement} overlay - The loading overlay element
   */
  function hideWasmLoadingIndicator(overlay) {
    if (!overlay) return;

    // Fade out animation
    overlay.classList.add('fade-out');
    setTimeout(() => {
      if (overlay.parentElement) {
        overlay.remove();
      }
    }, 300);
  }

  /**
   * Show memory usage warning notification
   * @param {Object} memoryInfo - Memory usage info from worker
   */
  function showMemoryWarning(memoryInfo) {
    // Remove any existing warning
    const existingWarning = document.getElementById('memoryWarning');
    if (existingWarning) {
      existingWarning.remove();
    }

    const warning = document.createElement('div');
    warning.id = 'memoryWarning';
    warning.className = 'memory-warning';
    warning.setAttribute('role', 'alert');
    warning.innerHTML = `
      <div class="memory-warning-content">
        <span class="memory-warning-icon">⚠️</span>
        <div class="memory-warning-text">
          <strong>High Memory Usage</strong>
          <p>Memory allocated to the OpenSCAD engine: ${memoryInfo.usedMB} MB</p>
          <p class="memory-warning-hint">
            This warning is about the OpenSCAD engine’s allocated memory (it may stay high until the engine is restarted).
            If you also see an error like “produces no geometry”, fix that first. Memory may not be the cause.
          </p>
          <div class="memory-warning-actions" role="group" aria-label="Memory warning actions">
            <button type="button" class="btn btn-sm btn-outline" data-action="preview-fast">
              Use Fast preview
            </button>
            <button type="button" class="btn btn-sm btn-outline" data-action="export-low">
              Set Export quality: Low
            </button>
            <button type="button" class="btn btn-sm btn-outline" data-action="focus-resolution">
              Find resolution setting
            </button>
            <button type="button" class="btn btn-sm btn-outline" data-action="restart-engine">
              Restart engine
            </button>
          </div>
        </div>
        <button class="btn btn-sm btn-outline memory-warning-dismiss" aria-label="Dismiss warning">×</button>
      </div>
    `;

    document.body.appendChild(warning);

    // Handle dismiss
    warning
      .querySelector('.memory-warning-dismiss')
      .addEventListener('click', () => {
        warning.remove();
      });

    // Action buttons
    warning.addEventListener('click', async (e) => {
      const btn = e.target?.closest?.('button[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;

      if (action === 'preview-fast') {
        const select = document.getElementById('previewQualitySelect');
        if (select) {
          select.value = 'fast';
          select.dispatchEvent(new Event('change', { bubbles: true }));
          updateStatus('Preview quality set to Fast', 'success');
        }
      } else if (action === 'export-low') {
        setExportQualityMode('low');
        updateStatus('Export quality set to Low', 'success');
      } else if (action === 'focus-resolution') {
        const candidates = [
          '$fn',
          'smoothness_of_circles_and_arcs',
          '$fa',
          '$fs',
        ];
        let found = false;
        for (const name of candidates) {
          const res = focusParameter(name);
          if (res.found) {
            updateStatus(`Adjust "${name}" to reduce resolution`, 'info');
            found = true;
            break;
          }
        }
        if (!found) {
          updateStatus(
            'Try searching parameters for “$fn”, “smoothness”, “resolution”, or “quality”.',
            'info'
          );
        }
      } else if (action === 'restart-engine') {
        try {
          if (renderController) {
            updateStatus('Restarting engine...', 'info');
            await renderController.restart();
            updateStatus('Engine restarted. Try generating again.', 'success');
          }
        } catch (err) {
          console.error('Failed to restart engine:', err);
          updateStatus(
            'Could not restart engine. Try refreshing the page.',
            'error'
          );
        }
      }
    });

    // Auto-dismiss after 15 seconds
    setTimeout(() => {
      if (warning.parentElement) {
        warning.remove();
      }
    }, 15000);
  }

  /**
   * Provide actionable guidance for configuration-dependent “no geometry” errors.
   * Returns true if it handled the error.
   */
  function updateMemoryIndicator(memoryInfo) {
    const indicator = document.getElementById('memoryIndicator');
    const text = document.getElementById('memoryText');

    if (!indicator || !memoryInfo) return;

    // The indicator is a polite live region and NVDA says its tooltip
    // whenever it is written, so a poll that finds the same value must leave
    // the text, the classes and the tooltip untouched.
    if (indicator.classList.contains('hidden')) {
      indicator.classList.remove('hidden');
    }

    const usedMB = memoryInfo.usedMB || 0;
    const label = `${usedMB} MB`;
    if (text && text.textContent !== label) {
      text.textContent = label;
    }

    // No fictional percent. Warning state is driven by an absolute-MB
    // threshold so the indicator turns "warning" only when the WASM heap
    // buffer is genuinely large. The MemoryMonitor decides the badge
    // separately via memoryInfo.usedMB.
    indicator.classList.toggle('critical', usedMB >= 950);
    indicator.classList.toggle('warning', usedMB >= 819 && usedMB < 950);

    const tips = [`${usedMB} MB allocated to the OpenSCAD engine`];
    if (usedMB >= 950) {
      tips.unshift('Memory very high. Consider refreshing');
    } else if (usedMB >= 819) {
      tips.unshift('Memory usage elevated');
    }
    const tip = tips.join('\n');
    if (indicator.title !== tip) {
      indicator.title = tip;
    }
  }

  // memoryPollInterval is declared at the top of initApp() to avoid TDZ
  function startMemoryPolling() {
    if (memoryPollInterval) return;

    memoryPollInterval = setInterval(async () => {
      if (renderController && renderController.ready) {
        try {
          const memoryInfo = await renderController.getMemoryUsage();
          if (memoryInfo && memoryInfo.available !== false) {
            updateMemoryIndicator(memoryInfo);
            // Feed worker memory data into the MemoryMonitor so the
            // badge reflects actual WASM heap, not main-thread JS heap.
            const monitor = _getMemoryMonitor();
            if (monitor) {
              monitor.updateFromWorker(memoryInfo);
            }
          }
        } catch (_e) {
          // Silently ignore polling errors
        }
      }
    }, 10000);
  }

  function handleConfigDependencyError(error) {
    const code = error?.code;
    const msg = error?.message || '';
    const details = error?.details || '';
    const detailsStr = String(details || '');

    // Handle NO_GEOMETRY — emitted by isNonPreviewableParameters() when
    // generate=Customizer Settings (or similar non-previewable mode). The previous mesh
    // must be cleared so the 3D canvas is empty, matching the expectation that
    // "Customizer Settings" produces no visible geometry.
    if (code === 'NO_GEOMETRY') {
      if (previewManager) {
        previewManager.clear();
      }
      updateStatus(
        "No geometry in this mode. Adjust 'generate' to see a 3D preview.",
        'success'
      );
      previewStateIndicator.className = 'preview-state-indicator state-current';
      previewStateIndicator.textContent = 'No geometry (Customizer mode)';
      previewContainer.classList.remove('preview-error');
      previewContainer.classList.add('preview-current');
      return true;
    }

    // Handle 2D model case — applies to any project producing 2D output
    const is2DModel =
      code === 'MODEL_IS_2D' ||
      /MODEL_IS_2D|not a 3D object|Top level object is a 2D object/i.test(
        msg
      ) ||
      /not a 3D object|2D object/i.test(detailsStr);

    if (is2DModel) {
      if (previewManager) {
        previewManager.clear();
      }
      // Show guidance for 2D model — this is informational, not an error
      // Use 'success' not 'error' to avoid alarming red warnings on a correct workflow path
      updateStatus(
        'Your model produces 2D geometry. Select SVG or DXF output format to export.',
        'success'
      );

      // Override the preview state badge: auto-preview-controller already set it to ERROR
      // before this handler fired. Replace with a non-alarming "2D Model" indicator.
      previewStateIndicator.className = 'preview-state-indicator state-current';
      previewStateIndicator.textContent = '✓ 2D Model: use SVG/DXF';
      previewContainer.classList.remove('preview-error');
      previewContainer.classList.add('preview-current');

      // Dismiss any memory warning that may have been triggered by the failed 2D→STL render.
      // The high memory is a side effect of the expected 2D path, not a real memory issue.
      const memWarning = document.getElementById('memoryWarning');
      if (memWarning) memWarning.remove();

      return true;
    }

    // A library the model needs did not resolve. Say that, because the empty
    // geometry handled below is its consequence, and its guidance would send
    // the user hunting through parameters for a cause that is a checkbox.
    if (findMissingLibrary(`${msg}\n${detailsStr}`)) {
      if (previewManager) {
        previewManager.clear();
      }
      const friendly = translateError(detailsStr || msg);
      const sentence = `${friendly.explanation} ${friendly.suggestion}`;
      updateStatus(sentence, 'error');
      _announceError(sentence);
      return true;
    }

    // One of the model's own checks (an assert()) stopped it. Its message
    // is the cause; the empty geometry handled below is only its
    // consequence, whose guidance would send people looking for an option.
    // Announced once, assertively, as an error.
    const failedCheck = findFailedCheck(`${msg}\n${detailsStr}`);
    if (failedCheck) {
      if (previewManager) {
        previewManager.clear();
      }
      const sentence = failedCheck.message
        ? `The model stopped: ${failedCheck.message}`
        : `The model stopped at one of its checks: ${failedCheck.condition}`;
      updateStatus(sentence, 'error', { announce: false });
      _announceError(sentence);
      return true;
    }

    // A module the engine does not know (or does not switch on) leaves the
    // top level empty too, but the advice to look for a required option
    // would point at the wrong cause: the error dialog names the module.
    if (code === 'UNKNOWN_MODULE') return false;

    const hasDependencyHint =
      /'[^']+?'\s+is set to\s+'(no|off)'/i.test(detailsStr) ||
      /Current top[ -]?level object is empty|top-level object is empty/i.test(
        detailsStr
      );
    const isEmpty =
      code === 'EMPTY_GEOMETRY' ||
      /produces no geometry|top level object is empty/i.test(msg) ||
      hasDependencyHint;

    if (!isEmpty) return false;

    // Clear the 3D preview when geometry is empty so no stale mesh is shown.
    if (previewManager) {
      previewManager.clear();
    }

    // Hide memory warning so the real root cause is not obscured
    const existingWarning = document.getElementById('memoryWarning');
    if (existingWarning) existingWarning.remove();

    // A library: it defines modules or functions and draws nothing itself.
    // No option is missing, so no guidance to look for one, and nothing
    // failed: the badge the preview controller set to its error state is
    // the plain one a preview has before anything is drawn.
    if (error?.libraryFile) {
      updateStatus(
        'This file defines modules or functions and draws nothing on its own. Open a file that uses it to see a shape.'
      );
      previewStateIndicator.className = 'preview-state-indicator state-idle';
      previewStateIndicator.textContent = 'No preview: a library file';
      previewContainer.classList.remove('preview-error');
      return true;
    }

    // Extract all toggle hints from OpenSCAD output (there can be multiple).
    const matches = Array.from(
      detailsStr.matchAll(/'([^']+?)'\s+is set to\s+'([^']+?)'/gi)
    ).map((m) => ({
      label: m?.[1] ? m[1].trim() : null,
      current: m?.[2] ? m[2].trim() : null,
    }));

    const invertToggleValue = (value) => {
      const v = String(value || '')
        .trim()
        .toLowerCase();
      if (v === 'no') return 'yes';
      if (v === 'yes') return 'no';
      if (v === 'off') return 'on';
      if (v === 'on') return 'off';
      return null;
    };

    let chosen =
      matches.length > 0 ? matches[0] : { label: null, current: null };
    let targetKey = null;

    // Prefer a match we can actually find in the UI (prevents “wrong toggle” guidance).
    for (const candidate of matches) {
      if (!candidate.label) continue;
      const keyGuess = candidate.label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      const foundKey = locateParameterKey(keyGuess, {
        labelHint: candidate.label,
      });
      if (foundKey) {
        chosen = candidate;
        targetKey = foundKey;
        break;
      }
    }

    const label = chosen.label;
    const current = chosen.current;
    const suggested = invertToggleValue(current);

    const headline = label
      ? `This selection is blocked because "${label}" is currently "${current ?? 'unknown'}".`
      : 'This selection produces no geometry with the current settings.';

    const nextStep = label
      ? suggested
        ? `Change it to "${suggested}" and try again.`
        : `Change that option (toggle it) and try again.`
      : 'Look for a required option (often “enable/show/include/has…”) and try again.';

    const findHint = label
      ? `Tip: use the “Search the Customizer” box and type "${label}".`
      : '';

    updateStatus(`${headline} ${nextStep} ${findHint}`.trim(), 'error');
    showDependencyGuidanceModal({
      label,
      current,
      suggested,
      targetKey,
    });
    return true;
  }

  /**
   * Show an accessible modal that guides the user to a blocking toggle/setting.
   * @param {Object} info
   * @param {string|null} info.label
   * @param {string|null} info.current
   * @param {string|null} info.suggested
   * @param {string|null} info.targetKey - Param key to focus/highlight
   */
  function showDependencyGuidanceModal(info) {
    if (isAnyModalOpen()) {
      console.log('[DependencyGuidance] Suppressed — another modal is active');
      return;
    }

    const { label, current, suggested, targetKey } = info || {};

    // Reuse a single modal instance
    let modal = document.getElementById('dependencyGuidanceModal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'dependencyGuidanceModal';
      modal.className =
        'preset-modal confirm-modal dependency-guidance-modal hidden';
      modal.setAttribute('role', 'alertdialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'dependencyGuidanceTitle');
      modal.setAttribute('aria-describedby', 'dependencyGuidanceMessage');
      modal.style.zIndex = '10005';
      modal.innerHTML = `
        <div class="preset-modal-content confirm-modal-content">
          <div class="preset-modal-header">
            <h3 id="dependencyGuidanceTitle" class="preset-modal-title">Action needed</h3>
          </div>
          <div class="confirm-modal-body">
            <p id="dependencyGuidanceMessage"></p>
          </div>
          <div class="preset-form-actions">
            <button type="button" class="btn btn-primary" data-action="goto">Take me to the setting</button>
            <button type="button" class="btn btn-secondary" data-action="search">Search for it</button>
            <button type="button" class="btn btn-outline" data-action="close">Close</button>
          </div>
        </div>
      `;
      document.body.appendChild(modal);

      modal.addEventListener('click', (e) => {
        const btn = e.target?.closest?.('button[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        if (action === 'close') {
          closeModal(modal);
          return;
        }
        if (action === 'goto') {
          closeModal(modal);
          if (modal._targetKey) {
            focusParameter(modal._targetKey);
          }
          return;
        }
        if (action === 'search') {
          closeModal(modal);
          const searchInput = document.getElementById('paramSearchInput');
          if (searchInput && modal._label) {
            searchInput.value = modal._label;
            searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            searchInput.focus();
          }
          return;
        }
      });

      // Click outside closes
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal(modal);
      });
    }

    const messageEl = modal.querySelector('#dependencyGuidanceMessage');
    const gotoBtn = modal.querySelector('button[data-action="goto"]');
    const searchBtn = modal.querySelector('button[data-action="search"]');

    const hasTarget = Boolean(targetKey);
    if (gotoBtn) gotoBtn.disabled = !hasTarget;
    if (searchBtn) searchBtn.disabled = !label;

    modal._targetKey = targetKey || null;
    modal._label = label || null;

    const parts = [];
    if (label) {
      parts.push(`"${label}" is currently "${current ?? 'unknown'}".`);
      if (suggested) {
        parts.push(`Change it to "${suggested}" to continue.`);
      } else {
        parts.push('Change that option (toggle it) to continue.');
      }
    } else {
      parts.push(
        'This selection produces no geometry with the current settings. A required option may be off/on.'
      );
    }
    parts.push('Then try again.');

    if (messageEl) {
      messageEl.textContent = parts.join(' ');
    }

    openModal(modal, { focusTarget: gotoBtn || searchBtn || undefined });
  }

  /**
   * Show render time estimate to user.
   * Low-confidence estimates suppress the number — complex models are
   * too unpredictable for a specific figure to be honest.
   * @param {Object} estimate - Result from estimateRenderTime()
   */
  function showRenderEstimate(estimate) {
    if (!estimate || estimate.seconds < 5) return; // Only show for longer renders

    let message =
      estimate.confidence === 'low'
        ? 'Complex model: rendering may take a while'
        : `Estimated render time: ~${estimate.seconds}s`;
    if (estimate.warning) {
      message += ` ⚠️ ${estimate.warning}`;
    }
    updateStatus(message);
  }
  // Export for potential future use (avoids unused warning)
  window._showRenderEstimate = showRenderEstimate;

  // Get DOM elements
  const welcomeScreen = document.getElementById('welcomeScreen');
  const mainInterface = document.getElementById('mainInterface');
  const uploadZone = document.getElementById('uploadZone');
  const fileInput = document.getElementById('fileInput');
  const clearFileBtn = document.getElementById('clearFileBtn');
  statusArea = document.getElementById('statusArea');
  // previewStatusBar, previewStatusText, previewStatusStats are declared at top of initApp() to avoid TDZ
  previewStatusBar = document.getElementById('previewStatusBar');
  previewStatusText = document.getElementById('previewStatusText');
  previewStatusStats = document.getElementById('previewStatusStats');
  const primaryActionBtn = document.getElementById('primaryActionBtn');
  const cancelRenderBtn = document.getElementById('cancelRenderBtn');
  const downloadFallbackLink = document.getElementById('downloadFallbackLink');
  const statsArea = document.getElementById('stats');
  const previewContainer = document.getElementById('previewContainer');
  const autoPreviewToggle = document.getElementById('autoPreviewToggle');
  const previewQualitySelect = document.getElementById('previewQualitySelect');
  const autoBedToggle = document.getElementById('autoBedToggle');
  const dimensionsDisplay = document.getElementById('dimensionsDisplay');
  // Note: outputFormatSelect and formatInfo already declared above

  // Reference overlay controls (used by preset load, back-to-welcome reset, etc.)
  const overlaySourceSelect = document.getElementById('overlaySourceSelect');
  const overlayToggle = document.getElementById('overlayToggle');

  // Create preview state indicator element
  // Not live: the badge's states pass by on every change ("Changes
  // pending", "Generating...", "Preview ready"), and the announcer already
  // says the one that is news.
  const previewStateIndicator = document.createElement('div');
  previewStateIndicator.className = 'preview-state-indicator state-idle';
  previewStateIndicator.textContent = 'No preview';

  // Create rendering overlay
  const renderingOverlay = document.createElement('div');
  renderingOverlay.className = 'preview-rendering-overlay';
  renderingOverlay.innerHTML =
    '<div class="spinner"></div><span class="rendering-text">Generating preview\u2026</span>';

  // Track last generated parameters for comparison
  let lastGeneratedParamsHash = null;

  // Auto-preview enabled by default (values initialized earlier)
  const getSelectedPreviewQualityMode = () => {
    return previewQualitySelect?.value || PREVIEW_QUALITY_DEFAULT;
  };

  // A function declaration so the File-menu builder, the memory banner and
  // the error-recovery action can all reach it regardless of where they sit
  // in this scope; the mode itself is module-scope for the debug hook.
  function setExportQualityMode(mode) {
    exportQualityMode = mode;
    exportQualityPreset = getExportQualityPreset(mode);
  }

  const getManualPreviewQuality = (mode) => {
    switch (mode) {
      case 'fast':
        return RENDER_QUALITY.DRAFT;
      case 'fidelity':
        // Use desktop-equivalent quality - matches OpenSCAD F6 render
        // Respects model's tessellation settings while ensuring OpenSCAD defaults
        return RENDER_QUALITY.DESKTOP_DEFAULT;
      case 'balanced':
      default:
        return RENDER_QUALITY.PREVIEW;
    }
  };

  /**
   * Get export quality preset using adaptive tier system
   * @param {string} mode - Quality mode (low, medium, high, model)
   * @returns {Object|null} Quality preset or null for model default
   */
  const getExportQualityPreset = (mode) => {
    if (mode === 'model') {
      // null = use model's own quality settings (FULL quality with no overrides)
      return null;
    }

    // Get current complexity tier from state
    const state = stateManager.getState();
    const tier = state?.complexityTier || COMPLEXITY_TIER.STANDARD;
    const hardware = state?.adaptiveQualityConfig?.hardware || {
      level: 'medium',
    };

    // Get tier-appropriate preset
    return getQualityPreset(tier, hardware.level, mode, 'export');
  };

  /**
   * Get adaptive preview info using tier system
   * @param {Object} parameters - Current parameters
   * @returns {Object} { quality, qualityKey }
   */
  const getAdaptivePreviewInfo = (parameters) => {
    const state = stateManager.getState();
    const scadContent = state?.uploadedFile?.content || '';
    const tier = state?.complexityTier || COMPLEXITY_TIER.STANDARD;
    const hardware = state?.adaptiveQualityConfig?.hardware || {
      level: 'medium',
    };

    const scadSignature = state?.uploadedFile
      ? `${state.uploadedFile.name}|${scadContent.length}|${tier}`
      : 'none';
    const memoKey = `${hashParams(parameters)}|${scadSignature}|${autoPreviewHints.forceFastUntil}|${autoPreviewHints.lastPreviewDurationMs}|${autoPreviewHints.lastPreviewTriangles}`;
    if (adaptivePreviewMemo.key === memoKey) {
      return adaptivePreviewMemo.info;
    }

    const now = Date.now();
    const forceFast = now < autoPreviewHints.forceFastUntil;
    const slowRender =
      autoPreviewHints.lastPreviewDurationMs &&
      autoPreviewHints.lastPreviewDurationMs >= AUTO_PREVIEW_SLOW_RENDER_MS;
    const heavyTriangles =
      autoPreviewHints.lastPreviewTriangles &&
      autoPreviewHints.lastPreviewTriangles >= AUTO_PREVIEW_TRIANGLE_THRESHOLD;

    let estimatedSlow = false;
    if (scadContent) {
      const estimate = estimateRenderTime(scadContent, parameters);
      // Lower thresholds to trigger auto-fast more promptly for heavy models
      // Also consider file size as a signal (large SCAD files often correlate with complexity)
      const fileSizeHeavy = scadContent.length > 15000; // 15KB+ SCAD file
      estimatedSlow =
        estimate.warning ||
        estimate.seconds >= 8 ||
        estimate.complexity >= 80 ||
        fileSizeHeavy;
    }

    // Determine preview quality level based on conditions
    const useFast = forceFast || slowRender || heavyTriangles || estimatedSlow;
    const qualityLevel = useFast ? 'low' : 'medium';

    // Get tier-appropriate preview preset
    const quality = getQualityPreset(
      tier,
      hardware.level,
      qualityLevel,
      'preview'
    );
    const qualityKey = useFast ? `auto-fast-${tier}` : `auto-balanced-${tier}`;

    const info = { quality, qualityKey };
    adaptivePreviewMemo = { key: memoKey, info };
    return info;
  };

  const applyAutoPreviewOverrides = (parameters, qualityKey) => {
    if (!qualityKey?.startsWith('auto-fast')) {
      return parameters;
    }

    const parityBypass = isDebugPrefEnabled('previewParity');
    if (parityBypass) {
      console.log(
        '[PreviewParity] Bypassing auto-preview overrides — ' +
          'preview will use identical parameters to full render'
      );
      return parameters;
    }

    // Per-project forge.project.json wins; projects without one get the
    // historical builtin keyguard/braille overrides (identical behavior).
    const manifest =
      loadProjectManifest(stateManager.getState()?.projectFiles) ??
      getBuiltinManifest();
    return applyPreviewOverrides(manifest, parameters, qualityKey);
  };

  const resolveAdaptiveQuality = (parameters) =>
    getAdaptivePreviewInfo(parameters).quality;
  const resolveAdaptiveCacheKey = (parameters) =>
    getAdaptivePreviewInfo(parameters).qualityKey;
  const resolveAdaptiveParameters = (parameters, qualityKey) =>
    applyAutoPreviewOverrides(parameters, qualityKey);

  const applyPreviewQualityMode = () => {
    previewQualityMode = getSelectedPreviewQualityMode();
    adaptivePreviewMemo = { key: null, info: null };

    // Read here rather than written into the select, so the
    // person's own choice is untouched and comes back by itself the moment
    // the session ends.
    if (editorDraftQuality && previewQualityMode !== 'auto') {
      previewQuality = RENDER_QUALITY.DRAFT;
      if (autoPreviewController) {
        autoPreviewController.setPreviewQualityResolver(null);
        autoPreviewController.setPreviewCacheKeyResolver(null);
        autoPreviewController.setPreviewParametersResolver(null);
        autoPreviewController.setPreviewQuality(previewQuality);
      }
      return;
    }

    if (previewQualityMode === 'auto') {
      previewQuality = null;
      if (autoPreviewController) {
        autoPreviewController.setPreviewQualityResolver(resolveAdaptiveQuality);
        autoPreviewController.setPreviewCacheKeyResolver(
          resolveAdaptiveCacheKey
        );
        autoPreviewController.setPreviewParametersResolver(
          resolveAdaptiveParameters
        );
        autoPreviewController.setPreviewQuality(null);
      }
      return;
    }

    previewQuality = getManualPreviewQuality(previewQualityMode);
    if (autoPreviewController) {
      autoPreviewController.setPreviewQualityResolver(null);
      autoPreviewController.setPreviewCacheKeyResolver(null);
      autoPreviewController.setPreviewParametersResolver(null);
      autoPreviewController.setPreviewQuality(previewQuality);
    }
  };

  let exportQualityPreset = getExportQualityPreset(exportQualityMode);

  // Wire preview settings UI
  if (autoPreviewToggle) {
    autoPreviewToggle.checked = autoPreviewEnabled;
    autoPreviewToggle.addEventListener('change', () => {
      autoPreviewUserEnabled = autoPreviewToggle.checked;
      autoPreviewEnabled = autoPreviewUserEnabled;
      writeAutoPreviewChoice(autoPreviewEnabled);
      if (autoPreviewController) {
        autoPreviewController.setEnabled(
          autoPreviewEnabled,
          autoPreviewEnabled ? null : 'user'
        );
        // setEnabled() catches up a preview gone out of date; a file opened
        // while it was off has never been drawn, so it is drawn now.
        const state = stateManager.getState();
        if (
          autoPreviewEnabled &&
          state.uploadedFile &&
          autoPreviewController.state === PREVIEW_STATE.IDLE
        ) {
          autoPreviewController
            .forcePreview(state.parameters)
            .catch((error) => {
              console.error(
                '[AutoPreview] Preview on turning on failed:',
                error
              );
              showErrorToast({
                title: 'Preview Failed',
                message: error.message,
              });
            });
        }
      }
    });
  }

  // Recovery Mode's notice stays on screen until it is dismissed. It shows
  // only now that the automatic preview control is wired, so a press of its
  // button is never lost, and the press goes through that control like
  // every other way of turning it on.
  const recoveryBanner = document.getElementById('recoveryBanner');
  if (isRecoveryMode && recoveryBanner && autoPreviewToggle) {
    const closeRecoveryBanner = () => {
      const hadFocus = recoveryBanner.contains(document.activeElement);
      recoveryBanner.classList.add('hidden');
      if (!hadFocus) return;
      // On to the next control rather than the page body.
      getFocusableElements(document.body)
        .find(
          (el) =>
            recoveryBanner.compareDocumentPosition(el) &
            Node.DOCUMENT_POSITION_FOLLOWING
        )
        ?.focus();
    };
    document
      .getElementById('recoveryBannerAutoPreview')
      ?.addEventListener('click', () => {
        autoPreviewToggle.checked = true;
        autoPreviewToggle.dispatchEvent(new Event('change'));
        announceImmediate('Automatic preview is on.');
        closeRecoveryBanner();
      });
    document
      .getElementById('recoveryBannerDismiss')
      ?.addEventListener('click', closeRecoveryBanner);
    recoveryBanner.classList.remove('hidden');
    const recoveryText = document.getElementById('recoveryBannerText');
    if (recoveryText) {
      // The start-up says its own status in this same moment and would
      // write over the sentence before a screen reader read it, so the
      // sentence waits past it (and is not said once the banner is gone).
      const sentence = recoveryText.textContent.replace(/\s+/g, ' ').trim();
      setTimeout(() => {
        if (!recoveryBanner.classList.contains('hidden')) {
          announceImmediate(sentence);
        }
      }, 600);
    }
  }

  if (previewQualitySelect) {
    // Restore the persisted choice; fall back to the shared default
    // (PREVIEW_QUALITY_DEFAULT is the single source; index.html's `selected`
    // only covers pre-JS paint). Recovery mode writes 'fast' to this key so a
    // crashed session reboots at low cost — honoring it here is intended.
    let savedQualityMode = null;
    try {
      savedQualityMode = localStorage.getItem(STORAGE_KEY_PREVIEW_QUALITY);
    } catch {
      // Private browsing / storage disabled — use the default.
    }
    const validQualityMode =
      savedQualityMode &&
      previewQualitySelect.querySelector(`option[value="${savedQualityMode}"]`)
        ? savedQualityMode
        : PREVIEW_QUALITY_DEFAULT;
    if (
      previewQualitySelect.querySelector(`option[value="${validQualityMode}"]`)
    ) {
      previewQualitySelect.value = validQualityMode;
    }
    applyPreviewQualityMode();
    previewQualitySelect.addEventListener('change', () => {
      try {
        localStorage.setItem(
          STORAGE_KEY_PREVIEW_QUALITY,
          previewQualitySelect.value
        );
      } catch {
        // Persistence is best-effort; the in-session mode still applies.
      }
      applyPreviewQualityMode();
      if (autoPreviewController) {
        const state = stateManager.getState();
        if (state?.uploadedFile) {
          autoPreviewController.onParameterChange(state.parameters);
        }
      }
    });
  }

  // Advisory instead of a silent downgrade: with the desktop-fidelity default,
  // heavy models preview at full model quality — surface a one-shot hint that
  // Performance (auto) exists. Auto mode adapts on its own and needs none.
  let lastComplexityAdvisedFile = null;
  function maybeShowComplexityAdvisory(state) {
    if (previewQualityMode === 'auto') return;
    const fileName = state.uploadedFile?.name || null;
    if (!fileName || fileName === lastComplexityAdvisedFile) return;
    const isComplex =
      state.complexityTier === COMPLEXITY_TIER.COMPLEX ||
      (state.complexityAnalysis?.warnings?.length ?? 0) > 0;
    if (!isComplex) return;
    lastComplexityAdvisedFile = fileName;
    // The advisory fires before the first preview, so it cannot know the
    // preview will be slow, and must not say so of a model that draws in 2 s.
    const advisoryMsg =
      'This model has many parts. If previews are slow, switch Preview ' +
      'quality to "Performance (auto)".';
    // updateStatus announces on its own; a second call here would say the
    // whole advisory twice.
    updateStatus(advisoryMsg, 'info');
  }

  // A fresh complexityAnalysis lands once per file load (file-handler sets it
  // after the file is in state), so keying on it avoids advising a new file
  // from the previous file's stale analysis.
  stateManager.subscribe((state, prevState) => {
    if (
      state.uploadedFile &&
      state.complexityAnalysis &&
      state.complexityAnalysis !== prevState.complexityAnalysis
    ) {
      maybeShowComplexityAdvisory(state);
    }
  });

  // Initialize overlay/grid/auto-rotate controller (extracted module)
  const overlayGridCtrl = initOverlayGridController({
    getPreviewManager: () => previewManager,
    updateStatus,
  });

  // Initialize companion files controller (extracted module)
  const companionFilesCtrl = initCompanionFilesController({
    getPreviewManager: () => previewManager,
    getAutoPreviewController: () => autoPreviewController,
    overlayGridCtrl,
    updateStatus,
    getCurrentSavedProjectId: () => currentSavedProjectId,
    setCanonicalProjectFiles,
  });

  // Wire auto-bed toggle
  if (autoBedToggle) {
    autoBedToggle.addEventListener('change', () => {
      const enabled = autoBedToggle.checked;
      if (previewManager) {
        const needsRerender = previewManager.toggleAutoBed(enabled);
        // If model is loaded and setting changed, trigger re-render
        const currentStl = stateManager.getState()?.stl;
        if (needsRerender && currentStl) {
          // Re-render to apply the new auto-bed setting
          // Preserve camera position since user is just toggling a display setting
          previewManager.loadSTL(currentStl, { preserveCamera: true });
          updateDimensionsDisplay();
        }
      }
      console.log(`[App] Auto-bed ${enabled ? 'enabled' : 'disabled'}`);
    });
  }

  // Status bar visibility. The control is View > Show Status Bar;
  // boot only restores the persisted choice here.
  function setPreviewStatusBarShown(shown) {
    const bar = document.getElementById('previewStatusBar');
    if (!bar) return;
    bar.classList.toggle('user-hidden', !shown);
    writeScopedPref(STORAGE_KEY_STATUS_BAR, shown ? 'true' : 'false');
    announceImmediate(`Status bar ${shown ? 'shown' : 'hidden'}`);
    console.log(`[App] Status bar ${shown ? 'shown' : 'hidden'}`);
  }
  if (previewStatusBar) {
    const savedStatusBarPref = readScopedPref(STORAGE_KEY_STATUS_BAR);
    if (savedStatusBarPref === 'false') {
      previewStatusBar.classList.add('user-hidden');
    }
  }

  // ============================================================================
  // Engine toggle: switch between Manifold (fast) and CGAL (stable, max compatibility)
  // Toggle between Manifold (fast, 5-30x speedup) and CGAL (stable, maximum compatibility)
  // ============================================================================
  const manifoldEngineToggle = document.getElementById('manifoldEngineToggle');
  const manifoldEngineHint = document.getElementById('manifoldEngineHint');

  if (manifoldEngineToggle) {
    // Initialize from localStorage (default to true for performance)
    const savedManifoldPref = localStorage.getItem(STORAGE_KEY_MANIFOLD_ENGINE);
    const manifoldEnabled =
      savedManifoldPref === null ? true : savedManifoldPref !== 'false';
    manifoldEngineToggle.checked = manifoldEnabled;

    // Update hint text based on initial state
    if (manifoldEngineHint) {
      manifoldEngineHint.textContent = manifoldEnabled
        ? '5-30× faster. Disable if models fail to render.'
        : 'Using stable engine. Enable for faster rendering.';
    }

    manifoldEngineToggle.addEventListener('change', () => {
      const enabled = manifoldEngineToggle.checked;
      localStorage.setItem(
        STORAGE_KEY_MANIFOLD_ENGINE,
        enabled ? 'true' : 'false'
      );

      // Update hint text
      if (manifoldEngineHint) {
        manifoldEngineHint.textContent = enabled
          ? '5-30× faster. Disable if models fail to render.'
          : 'Using stable engine. Enable for faster rendering.';
      }

      // Announce change for screen readers
      announceImmediate(
        enabled
          ? 'Manifold engine enabled. Faster rendering with good compatibility.'
          : 'Stable engine enabled. Maximum compatibility, slower rendering.'
      );

      console.log(
        `[App] Render engine: ${enabled ? 'Manifold (fast)' : 'CGAL (stable)'}`
      );

      // Note: Changes take effect on next render - no need to re-render current model
      // Show a subtle status message
      updateStatus(
        enabled
          ? 'Fast engine enabled - changes apply to next render'
          : 'Stable engine enabled - changes apply to next render',
        'success'
      );
    });
  }

  // Play sounds: on unless turned off. The moments that play one read the
  // stored choice when they happen. The Alt View's sounds button is the same
  // setting, so each follows the other.
  const soundsToggle = document.getElementById('soundsToggle');
  if (soundsToggle) {
    soundsToggle.checked = localStorage.getItem(STORAGE_KEY_SOUNDS) !== 'false';
    soundsToggle.addEventListener('change', () => {
      setSoundsOn(soundsToggle.checked);
    });
    document.addEventListener('sounds-setting-change', (event) => {
      soundsToggle.checked = event.detail.on;
    });
  }
  // Each kind of Alt View sound on its own, under Play sounds.
  const kindBoxes = document.querySelectorAll('input[data-sound-kind]');
  if (kindBoxes.length) {
    const off = new Set(soundKindsOff());
    for (const box of kindBoxes) {
      box.checked = !off.has(box.dataset.soundKind);
      box.addEventListener('change', () => {
        setSoundKindOff(box.dataset.soundKind, !box.checked);
      });
    }
    document.addEventListener('sound-kinds-change', (event) => {
      const now = new Set(event.detail.off);
      for (const box of kindBoxes)
        box.checked = !now.has(box.dataset.soundKind);
    });
  }

  // Wire model color picker and override toggle
  const modelColorPicker = document.getElementById('modelColorPicker');
  const modelColorReset = document.getElementById('modelColorReset');
  const modelColorEnabled = document.getElementById('modelColorEnabled');
  const modelColorPickerWrapper = modelColorPicker?.closest(
    '.model-color-picker'
  );

  // Load saved state
  const savedModelColor = readScopedPref(STORAGE_KEY_MODEL_COLOR);
  const savedColorEnabled =
    readScopedPref(STORAGE_KEY_MODEL_COLOR_ENABLED) === 'true';

  if (savedModelColor && modelColorPicker) {
    modelColorPicker.value = savedModelColor;
  }

  const updatePickerDisabledState = (enabled) => {
    if (modelColorPickerWrapper) {
      modelColorPickerWrapper.classList.toggle('disabled', !enabled);
    }
  };

  const getSelectedModelColor = () =>
    modelColorPicker?.value ||
    readScopedPref(STORAGE_KEY_MODEL_COLOR) ||
    getThemeDefaultColor();

  const syncPreviewModelColorOverride = () => {
    if (!previewManager) return;

    const enabled = modelColorEnabled?.checked === true;
    const selectedColor = getSelectedModelColor();

    if (enabled) {
      previewManager.setColorOverride(selectedColor);
      previewManager.setColorOverrideEnabled(true);
    } else {
      previewManager.setColorOverrideEnabled(false);
      previewManager.setColorOverride(selectedColor);
    }
  };

  if (modelColorEnabled) {
    modelColorEnabled.checked = savedColorEnabled;
    updatePickerDisabledState(savedColorEnabled);
    if (previewManager) {
      syncPreviewModelColorOverride();
    }

    modelColorEnabled.addEventListener('change', () => {
      const enabled = modelColorEnabled.checked;
      writeScopedPref(STORAGE_KEY_MODEL_COLOR_ENABLED, String(enabled));
      updatePickerDisabledState(enabled);
      if (previewManager) {
        syncPreviewModelColorOverride();
      }
      console.log(
        `[App] Model color override ${enabled ? 'enabled' : 'disabled'}`
      );
    });
  }

  let colorChangeTimeout;

  if (modelColorPicker) {
    modelColorPicker.addEventListener('input', () => {
      const color = modelColorPicker.value;
      clearTimeout(colorChangeTimeout);

      colorChangeTimeout = setTimeout(() => {
        if (previewManager && modelColorEnabled?.checked) {
          previewManager.setColorOverride(color);
        }
        writeScopedPref(STORAGE_KEY_MODEL_COLOR, color);
        console.log(`[App] Model color changed to ${color}`);
      }, 150);
    });
  }

  if (modelColorReset) {
    modelColorReset.addEventListener('click', () => {
      if (previewManager) {
        previewManager.setColorOverride(null);
      }
      if (modelColorPicker) {
        const themeDefault = getThemeDefaultColor();
        modelColorPicker.value = themeDefault;
      }
      removeScopedPref(STORAGE_KEY_MODEL_COLOR);
      console.log('[App] Model color reset to theme default');
    });
  }

  // --- Model Appearance Controls (Opacity, Brightness, Contrast) ---
  const modelOpacityInput = document.getElementById('modelOpacityInput');
  const modelOpacityValue = document.getElementById('modelOpacityValue');
  const brightnessInput = document.getElementById('brightnessInput');
  const brightnessValue = document.getElementById('brightnessValue');
  const contrastInput = document.getElementById('contrastInput');
  const contrastValue = document.getElementById('contrastValue');
  const resetAppearanceBtn = document.getElementById('resetAppearanceBtn');
  const modelAppearanceEnabled = document.getElementById(
    'modelAppearanceEnabled'
  );
  const modelAppearanceSlidersWrapper = document.querySelector(
    '.model-appearance-sliders'
  );

  // Restore persisted values
  const savedOpacity = readScopedPref(STORAGE_KEY_MODEL_OPACITY);
  const savedBrightness = readScopedPref(STORAGE_KEY_BRIGHTNESS);
  const savedContrast = readScopedPref(STORAGE_KEY_CONTRAST);
  const savedAppearanceEnabled =
    readScopedPref(STORAGE_KEY_MODEL_APPEARANCE_ENABLED) === 'true';
  if (savedOpacity && modelOpacityInput) {
    modelOpacityInput.value = savedOpacity;
    if (modelOpacityValue) modelOpacityValue.textContent = `${savedOpacity}%`;
    modelOpacityInput.setAttribute('aria-valuetext', `${savedOpacity}%`);
  }
  if (savedBrightness && brightnessInput) {
    brightnessInput.value = savedBrightness;
    if (brightnessValue) brightnessValue.textContent = `${savedBrightness}%`;
    brightnessInput.setAttribute('aria-valuetext', `${savedBrightness}%`);
  }
  if (savedContrast && contrastInput) {
    contrastInput.value = savedContrast;
    if (contrastValue) contrastValue.textContent = `${savedContrast}%`;
    contrastInput.setAttribute('aria-valuetext', `${savedContrast}%`);
  }

  const updateAppearanceSlidersDisabledState = (enabled) => {
    if (modelAppearanceSlidersWrapper) {
      modelAppearanceSlidersWrapper.classList.toggle('disabled', !enabled);
    }
  };

  function applyAppearanceToPreview() {
    if (!previewManager) return;
    previewManager.setModelOpacity(
      parseInt(modelOpacityInput?.value || '100', 10)
    );
    previewManager.setBrightness(parseInt(brightnessInput?.value || '100', 10));
    previewManager.setContrast(parseInt(contrastInput?.value || '100', 10));
  }

  const syncPreviewAppearanceOverride = () => {
    if (!previewManager) return;

    const enabled = modelAppearanceEnabled?.checked === true;

    if (enabled) {
      applyAppearanceToPreview();
      previewManager.setAppearanceOverrideEnabled(true);
    } else {
      previewManager.setAppearanceOverrideEnabled(false);
    }
  };

  if (modelAppearanceEnabled) {
    modelAppearanceEnabled.checked = savedAppearanceEnabled;
    updateAppearanceSlidersDisabledState(savedAppearanceEnabled);

    modelAppearanceEnabled.addEventListener('change', () => {
      const enabled = modelAppearanceEnabled.checked;
      writeScopedPref(STORAGE_KEY_MODEL_APPEARANCE_ENABLED, String(enabled));
      updateAppearanceSlidersDisabledState(enabled);
      syncPreviewAppearanceOverride();
    });
  }

  if (modelOpacityInput) {
    modelOpacityInput.addEventListener('input', () => {
      const v = modelOpacityInput.value;
      if (modelOpacityValue) modelOpacityValue.textContent = `${v}%`;
      modelOpacityInput.setAttribute('aria-valuetext', `${v}%`);
      writeScopedPref(STORAGE_KEY_MODEL_OPACITY, v);
      if (previewManager && modelAppearanceEnabled?.checked) {
        previewManager.setModelOpacity(parseInt(v, 10));
      }
    });
  }
  if (brightnessInput) {
    brightnessInput.addEventListener('input', () => {
      const v = brightnessInput.value;
      if (brightnessValue) brightnessValue.textContent = `${v}%`;
      brightnessInput.setAttribute('aria-valuetext', `${v}%`);
      writeScopedPref(STORAGE_KEY_BRIGHTNESS, v);
      if (previewManager && modelAppearanceEnabled?.checked) {
        previewManager.setBrightness(parseInt(v, 10));
      }
    });
  }
  if (contrastInput) {
    contrastInput.addEventListener('input', () => {
      const v = contrastInput.value;
      if (contrastValue) contrastValue.textContent = `${v}%`;
      contrastInput.setAttribute('aria-valuetext', `${v}%`);
      writeScopedPref(STORAGE_KEY_CONTRAST, v);
      if (previewManager && modelAppearanceEnabled?.checked) {
        previewManager.setContrast(parseInt(v, 10));
      }
    });
  }
  if (resetAppearanceBtn) {
    resetAppearanceBtn.addEventListener('click', () => {
      if (modelOpacityInput) {
        modelOpacityInput.value = '100';
        if (modelOpacityValue) modelOpacityValue.textContent = '100%';
      }
      if (brightnessInput) {
        brightnessInput.value = '100';
        if (brightnessValue) brightnessValue.textContent = '100%';
      }
      if (contrastInput) {
        contrastInput.value = '100';
        if (contrastValue) contrastValue.textContent = '100%';
      }
      removeScopedPref(STORAGE_KEY_MODEL_OPACITY);
      removeScopedPref(STORAGE_KEY_BRIGHTNESS);
      removeScopedPref(STORAGE_KEY_CONTRAST);
      if (previewManager && modelAppearanceEnabled?.checked) {
        previewManager.resetAppearance();
      }
    });
  }

  /**
   * The live swap: re-read the main.js-owned per-UI surfaces
   * from the newly active namespace and re-apply them — status-bar
   * visibility, model color override, the appearance sliders, and
   * auto-rotate (via the overlay/grid controller). Runs on every
   * Forge<->Classic flip through syncPreviewSceneToMode; no announcements,
   * the mode switch already speaks.
   */
  function reloadScopedUiSurfaces() {
    const statusBarNode = document.getElementById('previewStatusBar');
    if (statusBarNode) {
      statusBarNode.classList.toggle(
        'user-hidden',
        readScopedPref(STORAGE_KEY_STATUS_BAR) === 'false'
      );
    }

    const scopedColor = readScopedPref(STORAGE_KEY_MODEL_COLOR);
    if (modelColorPicker) {
      modelColorPicker.value = scopedColor || getThemeDefaultColor();
    }
    const scopedColorEnabled =
      readScopedPref(STORAGE_KEY_MODEL_COLOR_ENABLED) === 'true';
    if (modelColorEnabled) modelColorEnabled.checked = scopedColorEnabled;
    updatePickerDisabledState(scopedColorEnabled);
    syncPreviewModelColorOverride();

    const scopedOpacity = readScopedPref(STORAGE_KEY_MODEL_OPACITY) || '100';
    const scopedBrightness = readScopedPref(STORAGE_KEY_BRIGHTNESS) || '100';
    const scopedContrast = readScopedPref(STORAGE_KEY_CONTRAST) || '100';
    if (modelOpacityInput) {
      modelOpacityInput.value = scopedOpacity;
      if (modelOpacityValue)
        modelOpacityValue.textContent = `${scopedOpacity}%`;
    }
    if (brightnessInput) {
      brightnessInput.value = scopedBrightness;
      if (brightnessValue) brightnessValue.textContent = `${scopedBrightness}%`;
    }
    if (contrastInput) {
      contrastInput.value = scopedContrast;
      if (contrastValue) contrastValue.textContent = `${scopedContrast}%`;
    }
    const scopedAppearanceEnabled =
      readScopedPref(STORAGE_KEY_MODEL_APPEARANCE_ENABLED) === 'true';
    if (modelAppearanceEnabled) {
      modelAppearanceEnabled.checked = scopedAppearanceEnabled;
    }
    updateAppearanceSlidersDisabledState(scopedAppearanceEnabled);
    syncPreviewAppearanceOverride();

    overlayGridCtrl.reapplyScopedAutoRotate();
  }

  /**
   * Get the theme default model color
   */
  function getThemeDefaultColor() {
    const root = document.documentElement;
    const uiVariant = root.getAttribute('data-ui-variant');
    // themeManager exposes highContrast as a property; there is no
    // isHighContrastEnabled method.
    const highContrast = themeManager.highContrast === true;

    // Check for mono variant first
    if (uiVariant === 'mono') {
      // Light theme = amber, dark theme = green
      return hfmCtrl.isLightThemeActive() ? '#ffb000' : '#00ff00';
    }

    const activeTheme = themeManager.getActiveTheme();
    const themeKey = highContrast ? `${activeTheme}-hc` : activeTheme;
    const colorHex = (PREVIEW_COLORS[themeKey] || PREVIEW_COLORS.light).model;
    return '#' + colorHex.toString(16).padStart(6, '0');
  }

  /**
   * Update the dimensions display panel
   */
  function updateDimensionsDisplay() {
    if (!previewManager || !dimensionsDisplay) return;

    const dimensions = previewManager.calculateDimensions();

    if (dimensions && previewManager.measurementsEnabled) {
      // Show dimensions panel
      dimensionsDisplay.classList.remove('hidden');

      // Update values
      const dimXEl = document.getElementById('dimX');
      const dimYEl = document.getElementById('dimY');
      const dimZEl = document.getElementById('dimZ');
      const dimVolumeEl = document.getElementById('dimVolume');
      if (dimXEl) dimXEl.textContent = `${dimensions.x} mm`;
      if (dimYEl) dimYEl.textContent = `${dimensions.y} mm`;
      if (dimZEl) dimZEl.textContent = `${dimensions.z} mm`;
      if (dimVolumeEl)
        dimVolumeEl.textContent = `${dimensions.volume.toLocaleString()} mm³`;
    } else {
      // Hide dimensions panel
      dimensionsDisplay.classList.add('hidden');
    }
  }

  /**
   * Simple hash function for parameter comparison
   */
  function hashParams(params) {
    return JSON.stringify(params);
  }

  /**
   * Update preview state UI indicator
   * @param {string} state - PREVIEW_STATE value
   * @param {Object} extra - Extra data (stats, etc.)
   */
  function updatePreviewStateUI(state, extra = {}) {
    // Update indicator badge
    previewStateIndicator.className = `preview-state-indicator state-${state}`;

    // Update indicator text
    const stateMessages = {
      [PREVIEW_STATE.IDLE]: 'No preview',
      [PREVIEW_STATE.CURRENT]: extra.cached
        ? '✓ Preview (cached)'
        : '✓ Preview ready',
      [PREVIEW_STATE.PENDING]: 'Changes pending...',
      [PREVIEW_STATE.RENDERING]: 'Generating...',
      [PREVIEW_STATE.STALE]: '⚠ Preview outdated',
      [PREVIEW_STATE.ERROR]: '✗ Preview failed',
    };
    // The hourglass and the spinner are emoji where the system draws them in
    // color, so they ride in a span of their own, hidden from assistive
    // tech, that the alternative look draws as text (variant.css); the
    // words are the badge's text.
    const glyphs = {
      [PREVIEW_STATE.PENDING]: 'wait',
      [PREVIEW_STATE.RENDERING]: 'spin',
    };
    if (glyphs[state]) {
      setButtonTextWithIcon(
        previewStateIndicator,
        glyphs[state],
        stateMessages[state]
      );
    } else {
      previewStateIndicator.textContent = stateMessages[state] || state;
    }
    // A failed preview leaves no model, so the size and count of the last
    // one go with it.
    if (state === PREVIEW_STATE.ERROR) {
      const stats = document.getElementById('stats');
      if (stats) stats.textContent = '';
      clearPreviewStats();
    }
    // A draft of the drawing is not the design, and the badge says so
    // for as long as it is what is on screen.
    const draftStands =
      state === PREVIEW_STATE.CURRENT &&
      draftPreviewHash !== null &&
      autoPreviewController?.previewParamHash === draftPreviewHash;
    if (draftStands) {
      previewStateIndicator.textContent =
        'Draft of the drawing, not yet applied';
    }
    previewStateIndicator.classList.toggle('preview-draft', draftStands);

    // Update preview container border state
    previewContainer.classList.remove(
      'preview-pending',
      'preview-stale',
      'preview-rendering',
      'preview-current',
      'preview-error'
    );
    previewContainer.classList.add(`preview-${state}`);

    // Show/hide rendering overlay
    if (state === PREVIEW_STATE.RENDERING) {
      renderingOverlay.classList.add('visible');
    } else {
      renderingOverlay.classList.remove('visible');
    }

    // Update stats if provided
    if (extra.stats && state === PREVIEW_STATE.CURRENT) {
      let previewPercentText = '';
      if (!extra.fullQuality && autoPreviewController) {
        const currentParams = stateManager.getState()?.parameters;
        const fullStats =
          autoPreviewController.getCurrentFullSTL(currentParams)?.stats;
        if (
          typeof fullStats?.triangles === 'number' &&
          fullStats.triangles > 0 &&
          typeof extra.stats.triangles === 'number'
        ) {
          const ratio = Math.max(
            0,
            Math.min(1, extra.stats.triangles / fullStats.triangles)
          );
          previewPercentText = ` (${Math.round(ratio * 100)}% of full)`;
        }
      }

      const qualityLabel = extra.fullQuality
        ? '<span class="stats-quality full">Full Quality</span>'
        : `<span class="stats-quality preview">Preview Quality${previewPercentText}</span>`;
      statsArea.innerHTML = `${qualityLabel} Size: ${formatFileSize(extra.stats.size)} | Triangles: ${extra.stats.triangles.toLocaleString()}`;

      // Also update the preview status bar stats with timing breakdown
      updatePreviewStats(
        extra.stats,
        extra.fullQuality,
        previewPercentText,
        extra.timing
      );
    }
  }

  /**
   * Format timing duration for display
   * @param {number} ms - Duration in milliseconds
   * @returns {string} Formatted duration string
   */
  function _formatTimingMs(ms) {
    if (typeof ms !== 'number' || ms <= 0) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  /**
   * Update the preview status bar stats display
   * Simplified: only shows essential info (file size and triangle count)
   * @param {Object} stats - Stats object with size and triangles
   * @param {boolean} fullQuality - Whether this is full quality render
   * @param {string} percentText - Unused, kept for API compatibility
   * @param {Object} timing - Unused, kept for API compatibility
   */
  function updatePreviewStats(
    stats,
    _fullQuality = false,
    _percentText = '',
    _timing = null
  ) {
    if (!previewStatusStats || !previewStatusBar) return;

    if (!stats) {
      previewStatusStats.textContent = '';
      previewStatusBar.classList.add('no-stats');
      return;
    }

    // Simplified stats: just size and triangle count
    previewStatusStats.textContent = formatPreviewStats(stats);
    previewStatusBar.classList.remove('no-stats');
  }

  /**
   * Clear the preview status bar stats
   */
  function clearPreviewStats() {
    if (previewStatusStats) {
      previewStatusStats.textContent = '';
    }
    if (previewStatusBar) {
      previewStatusBar.classList.add('no-stats');
    }
  }

  /**
   * Initialize or reinitialize the AutoPreviewController
   * @param {boolean} deferIfNotReady - If true, will attempt to init WASM first if not ready
   */
  async function initAutoPreviewController(deferIfNotReady = false) {
    // renderController exists from the first line of
    // ensureWasmInitialized, long before its worker can render. A controller
    // made in that window previews at once, fails ("Worker not ready") and
    // tells the person "Preview failed" about a project that never had a
    // chance. The code that awaits the engine makes the controller instead:
    // handleFirstVisitClose's deferred block, once the engine is ready. Not
    // ensureWasmInitialized() here: a second call during the download
    // returns false at once, and on a metered connection asks again.
    if (renderController && !wasmInitialized) {
      console.log(
        '[AutoPreview] Engine still starting; the first preview waits for it'
      );
      return;
    }
    if (!renderController || !previewManager) {
      if (deferIfNotReady && previewManager) {
        // WASM not ready yet - try to initialize it first
        console.log('[AutoPreview] Deferring init until WASM is ready...');
        const wasmReady = await ensureWasmInitialized();
        if (!wasmReady || !renderController) {
          console.warn(
            '[AutoPreview] Cannot init - WASM initialization failed or was declined'
          );
          return;
        }
      } else {
        console.warn(
          '[AutoPreview] Cannot init - missing controller or preview manager'
        );
        return;
      }
    }

    autoPreviewController = new AutoPreviewController(
      renderController,
      previewManager,
      {
        // Lower debounce to reduce perceived "delay" after slider changes.
        // Scheduling logic in AutoPreviewController avoids overlapping renders.
        debounceMs: 350,
        maxCacheSize: 10,
        enabled: autoPreviewEnabled,
        pauseReason: autoPreviewUserEnabled ? null : 'user',
        pausedDebounceMs: 2000,
        previewQuality: previewQualityMode === 'auto' ? null : previewQuality,
        resolvePreviewQuality:
          previewQualityMode === 'auto' ? resolveAdaptiveQuality : null,
        resolvePreviewCacheKey:
          previewQualityMode === 'auto' ? resolveAdaptiveCacheKey : null,
        resolvePreviewParameters:
          previewQualityMode === 'auto' ? resolveAdaptiveParameters : null,
        // Render / Generate / export. Playback stops and stays stopped:
        // two render requests would queue behind each other on the one
        // blocking worker and make both slow.
        onFullRenderStart: () => getAnimatePanel()?.pauseForExternalRender(),
        onStateChange: (newState, prevState, extra) => {
          console.log(
            `[AutoPreview] State: ${prevState} -> ${newState}`,
            extra
          );
          // The other half of the same rule: a preview render started by
          // something other than the animation. Animation frames never reach
          // here — renderAnimationFrame sets no preview state.
          if (newState === PREVIEW_STATE.RENDERING) {
            getAnimatePanel()?.pauseForExternalRender();
          }
          // The status bars said "Preview ready" under a badge saying the
          // preview is outdated. With Automatic preview on, the state passes
          // through STALE to PENDING in the same task; only a preview that
          // stays outdated says so, and only over a line that still calls a
          // result ready (another message written with the change, "Quality
          // reduced" say, stands). Not announced: the change was the
          // person's own.
          if (newState === PREVIEW_STATE.STALE) {
            queueMicrotask(() => {
              const line =
                document.getElementById('statusArea')?.textContent.trim() || '';
              if (
                autoPreviewController?.state === PREVIEW_STATE.STALE &&
                /^(Preview|[A-Z0-9]+) ready$/.test(line)
              ) {
                updateStatus('Preview outdated', 'default', {
                  announce: false,
                });
              }
            });
          }
          if (newState === PREVIEW_STATE.CURRENT) {
            if (typeof extra?.renderDurationMs === 'number') {
              autoPreviewHints.lastPreviewDurationMs = extra.renderDurationMs;
              adaptivePreviewMemo = { key: null, info: null };
            }
            if (typeof extra?.stats?.triangles === 'number') {
              autoPreviewHints.lastPreviewTriangles = extra.stats.triangles;
              adaptivePreviewMemo = { key: null, info: null };
            }
            hfmCtrl.clearPersistence();
          }
          updatePreviewStateUI(newState, extra);
        },
        onPreviewReady: (
          stl,
          stats,
          cached,
          _durationMs,
          _timing,
          consoleOutput
        ) => {
          console.log('[AutoPreview] Preview ready, cached:', cached);
          // Update status to ready (use 'success' type to keep visible)
          updateStatus('Preview ready', 'success');
          // Update button state - preview available but may need full render for download
          updatePrimaryActionButton();
          // Update dimensions display
          updateDimensionsDisplay();
          // Console fidelity: preview runs surface their echo()/WARNING
          // output too, not just full renders (a cache hit brings its own)
          if (
            consoleOutput &&
            typeof window.updateConsoleOutput === 'function'
          ) {
            window.updateConsoleOutput(consoleOutput);
          }
          // A model that says how wide it fits a design tells
          // the file control, so the drawing editor measures against the size
          // the design really prints at.
          const fit = consoleOutput
            ? /design fit box mm: w=([\d.]+) h=([\d.]+)/.exec(consoleOutput)
            : null;
          if (fit) {
            setDesignFitBoxMm({ w: Number(fit[1]), h: Number(fit[2]) });
          }
        },
        onProgress: (percent, message, type) => {
          // Simplified status: just show what's happening, no confusing percentages
          if (type === 'preview') {
            // One action, one announcement: an auto-preview fires on every parameter
            // change and this progress callback repeats, so one change would speak
            // "Rendering preview..." several times before "Preview ready". The
            // progress line stays visible on the status surfaces; the completion (or
            // the error) is the news and still speaks.
            updateStatus('Rendering preview...', 'default', {
              announce: false,
            });
          } else {
            // Get current output format from selector for correct progress text
            const outputFormatSelect = document.getElementById('outputFormat');
            const outputFormat = outputFormatSelect?.value || 'stl';
            const formatName =
              OUTPUT_FORMATS[outputFormat]?.name || outputFormat.toUpperCase();
            updateStatus(`Generating ${formatName}...`);
          }
        },
        onError: (error, type) => {
          if (type === 'preview') {
            console.error('[AutoPreview] Preview error:', error);
            // If the backend indicates a blocked/empty-geometry configuration,
            // guide the user to the required toggle instead of a generic failure.
            if (handleConfigDependencyError(error)) {
              return;
            }

            const sentence = previewFailureSentence(error);
            // Said once, assertively; through updateStatus as well it was
            // said twice.
            updateStatus(sentence, 'error', { announce: false });
            _announceError(sentence);
          }
        },
      }
    );

    console.log('[AutoPreview] Controller initialized');

    // Subscribe to library manager changes
    libraryManager.subscribe((action, libraryId) => {
      console.log(`[Library] ${action}: ${libraryId}`);
      // Update auto-preview controller with new library list
      if (autoPreviewController) {
        autoPreviewController.setEnabledLibraries(
          getEnabledLibrariesForRender()
        );
      }
    });
  }

  /**
   * Update the primary action button based on current state.
   * With auto-preview, the button has two states:
   * - "Download" when full-quality output is ready for current params
   * - "Generate" when no full render exists yet or params changed
   * Also shows/hides the fallback download link.
   */
  function updatePrimaryActionButton() {
    // STL view-only mode: nothing is renderable, so Generate must not
    // present itself as available.
    if (isStlViewActive()) {
      primaryActionBtn.textContent = 'Generate';
      primaryActionBtn.dataset.action = 'generate';
      primaryActionBtn.disabled = true;
      primaryActionBtn.dataset.stlViewDisabled = 'true';
      primaryActionBtn.setAttribute(
        'aria-label',
        'Generate is unavailable while viewing an STL file. Open a .scad model to generate designs.'
      );
      downloadFallbackLink.classList.add('hidden');
      _dispatchRenderStateChange(false);
      return;
    }
    if (primaryActionBtn.dataset.stlViewDisabled) {
      delete primaryActionBtn.dataset.stlViewDisabled;
      primaryActionBtn.disabled = false;
    }
    const state = stateManager.getState();
    const hasGeneratedFile = !!state.stl;
    const currentParamsHash = hashParams(state.parameters);
    const paramsChanged = currentParamsHash !== lastGeneratedParamsHash;
    const outputFormatSelect = document.getElementById('outputFormat');
    const selectedFormat = (
      outputFormatSelect?.value ||
      state.outputFormat ||
      'stl'
    ).toLowerCase();

    const formatName =
      OUTPUT_FORMATS[selectedFormat]?.name || selectedFormat.toUpperCase();
    const isStlFormat = selectedFormat === 'stl';

    // Check auto-preview controller state (works for any 3D format routed
    // through the controller; 2D formats bypass it). Same shared helper the
    // File menu and the Classic editor toolbar consult, so the three cannot
    // disagree about whether a full render exists.
    const hasFullQualityStl = hasFullQualitySTLFor(state.parameters);

    const stateOutputFormat = (state.outputFormat || '').toLowerCase();
    const hasMatchingOutput =
      hasGeneratedFile &&
      stateOutputFormat === selectedFormat &&
      !paramsChanged;

    if (isStlFormat && hasFullQualityStl) {
      setButtonTextWithIcon(primaryActionBtn, 'download', 'Download');
      primaryActionBtn.dataset.action = 'download';
      primaryActionBtn.classList.remove('btn-primary');
      primaryActionBtn.classList.add('btn-success');
      primaryActionBtn.setAttribute(
        'aria-label',
        `Download generated ${formatName} file (full quality)`
      );
      downloadFallbackLink.classList.add('hidden');
    } else if (hasMatchingOutput) {
      setButtonTextWithIcon(primaryActionBtn, 'download', 'Download');
      primaryActionBtn.dataset.action = 'download';
      primaryActionBtn.classList.remove('btn-primary');
      primaryActionBtn.classList.add('btn-success');
      primaryActionBtn.setAttribute(
        'aria-label',
        `Download generated ${formatName} file`
      );
      downloadFallbackLink.classList.add('hidden');
    } else {
      primaryActionBtn.textContent = 'Generate';
      primaryActionBtn.dataset.action = 'generate';
      primaryActionBtn.classList.remove('btn-success');
      primaryActionBtn.classList.add('btn-primary');
      primaryActionBtn.setAttribute(
        'aria-label',
        `Generate ${formatName} file from current parameters`
      );

      if (isStlFormat && hasGeneratedFile && paramsChanged) {
        downloadFallbackLink.classList.remove('hidden');
      } else {
        downloadFallbackLink.classList.add('hidden');
      }
    }

    _dispatchRenderStateChange(isStlFormat && hasFullQualityStl);
  }

  /**
   * Announce render-state transitions to every surface that gates on them
   * (the Classic STL buttons). Dispatched from inside
   * updatePrimaryActionButton() — the one place that computes the state —
   * so the event can never drift from what the transformer shows.
   * @param {boolean} hasFullRender
   */
  function _dispatchRenderStateChange(hasFullRender) {
    document.dispatchEvent(
      new CustomEvent('render-state-change', { detail: { hasFullRender } })
    );
  }

  // Import shared validation schemas (FILE_SIZE_LIMITS is imported at top of initApp() to avoid TDZ)
  ({ validateFileUpload } = await import('./js/validation-schemas.js'));

  // A file's offer of a place in Saved Projects waits for the kept-draft
  // question, so the two never open at once. A draft restored in answer has
  // replaced the file the offer was for, and then nothing is offered.
  const savedProjectsUIForFiles = {
    ...savedProjectsUI,
    showSaveProjectPrompt: async (fileData, options) => {
      await draftQuestionSettled;
      if (stateManager.getState().uploadedFile !== fileData.uploadedFile) {
        return { saved: false };
      }
      return savedProjectsUI.showSaveProjectPrompt(fileData, options);
    },
  };

  // Initialize file handler controller
  fileHandler = initFileHandler({
    getPreviewManager: () => previewManager,
    setPreviewManager: (pm) => {
      previewManager = pm;
    },
    getAutoPreviewController: () => autoPreviewController,
    getAutoPreviewEnabled: () => autoPreviewEnabled,
    setCurrentSavedProjectId: (id) => {
      currentSavedProjectId = id;
    },
    getCurrentSavedProjectId: () => currentSavedProjectId,
    setPresetCompanionMap: (map) => {
      presetCompanionMap = map;
      if (_isEnabled('project_presets')) {
        stateManager.setState({ projectCompanionMap: map });
      }
    },
    getFileSizeLimits: () => FILE_SIZE_LIMITS,
    getValidateFileUpload: () => validateFileUpload,
    getCameraPanelController: () => cameraPanelController,
    getOverlayGridCtrl: () => overlayGridCtrl,
    getDisplayOptionsCtrl: getDisplayOptionsController,
    getCompanionFilesCtrl: () => companionFilesCtrl,
    getHfmCtrl: () => hfmCtrl,
    getSavedProjectsUI: () => savedProjectsUIForFiles,
    getFileActionsController: () => fileActionsController,
    getLibraryManager: () => libraryManager,
    getPreviewContainer: () => previewContainer,
    getPreviewStateIndicator: () => previewStateIndicator,
    getRenderingOverlay: () => renderingOverlay,
    updateStatus,
    updatePreviewDrawer,
    updatePrimaryActionButton,
    updateColorLegend: _updateColorLegend,
    updatePreviewStateUI,
    clearPresetSelection,
    forceClearPresetSelection,
    updatePresetDropdown,
    syncPreviewModelColorOverride,
    syncPreviewAppearanceOverride,
    initAutoPreviewController,
    setCanonicalProjectFiles,
    renderLibraryUI,
    getEnabledLibrariesForRender,
    resetProjectUiState,
  });

  // Check for saved draft - but only if first-visit modal is not blocking
  // If first-visit is blocking, defer draft restoration until user accepts
  // IMPORTANT: Skip draft restoration if a manifest or project URL is specified --
  // the URL intent takes priority over any cached draft
  const hasManifestParam = urlParams.get('manifest');
  const hasProjectParam = urlParams.get('project') || urlParams.get('scad');
  const draft =
    !hasManifestParam && !hasProjectParam
      ? await stateManager.loadFromLocalStorage()
      : null;

  if (draft) {
    // Restored by restorePendingDraft(): at the end of start-up, or, while
    // the first-visit modal blocks, once it is dismissed.
    pendingDraft = draft;
  }

  /**
   * Load embedded model from scaffolded app HTML
   * Scaffolded apps embed the schema and scad source in script tags
   * @returns {boolean} True if embedded model was loaded
   */
  function loadEmbeddedModel() {
    const schemaEl = document.getElementById('param-schema');
    const scadEl = document.getElementById('scad-source');

    // Check if both elements exist and have content
    if (!schemaEl || !scadEl) {
      return false;
    }

    const schemaText = schemaEl.textContent?.trim();
    const scadContent = scadEl.textContent?.trim();

    if (!schemaText || !scadContent) {
      return false;
    }

    try {
      // Parse the embedded schema (it's JSON)
      const schema = JSON.parse(schemaText);

      // Validate basic schema structure
      if (!schema.properties || typeof schema.properties !== 'object') {
        console.warn(
          '[Embedded] Invalid schema structure, falling back to file upload'
        );
        return false;
      }

      // Derive filename from schema or default
      const fileName =
        (schema.title || 'embedded-model')
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '_') + '.scad';

      console.log(`[Embedded] Loading embedded model: ${fileName}`);
      console.log(
        `[Embedded] Found ${Object.keys(schema.properties).length} parameters`
      );

      // Process the embedded content using handleFile
      fileHandler.handleFile(
        { name: fileName },
        scadContent,
        null,
        null,
        'example'
      );

      return true;
    } catch (e) {
      console.warn('[Embedded] Failed to load embedded model:', e.message);
      return false;
    }
  }

  // Try to load embedded model (for scaffolded apps)
  // Only attempt if no draft was restored
  if (!draft || !stateManager.getState()?.uploadedFile) {
    const embeddedLoaded = loadEmbeddedModel();
    if (embeddedLoaded) {
      console.log('[App] Loaded embedded model from scaffolded app');
    }
  }

  /**
   * Log render performance metrics to console
   * @param {Object} result - Render result with timing, stats, and data
   */
  function logRenderPerformance(result) {
    if (!result) return;

    const timing = result.timing || {};
    const stats = result.stats || {};
    const capabilities = renderController?.getCapabilities() || {};

    // Calculate bytes per triangle (indicator of ASCII vs Binary)
    const dataSize = result.data?.byteLength || result.stl?.byteLength || 0;
    const bytesPerTri =
      stats.triangles > 0 ? Math.round(dataSize / stats.triangles) : 0;

    const isLikelyBinary = bytesPerTri > 0 && bytesPerTri < 80;
    const isLikelyASCII = bytesPerTri > 100;

    // Detect SVG/DXF by inspecting the first bytes of the output buffer
    let detectedFormat = null;
    const rawData = result.data || result.stl;
    if (rawData && rawData.byteLength > 0) {
      const header = new Uint8Array(
        rawData,
        0,
        Math.min(16, rawData.byteLength)
      );
      const prefix = String.fromCharCode(...header).toLowerCase();
      if (prefix.startsWith('<?xml') || prefix.startsWith('<svg')) {
        detectedFormat = 'SVG';
      } else if (
        prefix.startsWith('0\nsection') ||
        prefix.startsWith('0\r\nsection')
      ) {
        detectedFormat = 'DXF';
      }
    }

    const formatLabel = detectedFormat
      ? detectedFormat
      : isLikelyBinary
        ? 'Binary STL ✓'
        : isLikelyASCII
          ? 'ASCII STL ⚠️'
          : 'Unknown';

    console.log(
      `[Render Stats] ` +
        `Time: ${timing.renderMs || 0}ms | ` +
        `Triangles: ${stats.triangles?.toLocaleString() || 0} | ` +
        `Size: ${(dataSize / 1024).toFixed(1)}KB | ` +
        `Format: ${formatLabel}`
    );

    // Warn if ASCII STL detected
    if (isLikelyASCII && stats.triangles > 1000) {
      console.warn(
        '[Performance Warning] ASCII STL detected! ' +
          'Add --export-format=binstl for ~18x faster exports.'
      );
    }

    // Log Manifold status for slow renders
    if (!capabilities.hasManifold && timing.renderMs > 5000) {
      console.warn(
        `[Performance Warning] Render took ${timing.renderMs}ms without Manifold. ` +
          'With Manifold enabled, complex models can be 5-30x faster.'
      );
    }

    // Log overall performance status
    if (isLikelyBinary && capabilities.hasManifold) {
      console.log(
        '[Performance] ✓ Optimal settings: Binary STL + Manifold enabled'
      );
    } else if (!isLikelyBinary || !capabilities.hasManifold) {
      const issues = [];
      if (!isLikelyBinary) issues.push('Binary STL not active');
      if (!capabilities.hasManifold) issues.push('Manifold not available');
      console.log(`[Performance] ⚠️ Suboptimal settings: ${issues.join(', ')}`);
    }
  }

  let _activeColorParamNames = [];

  function _updateColorLegend(colorNames) {
    if (colorNames !== undefined) _activeColorParamNames = colorNames || [];
    if (!previewManager) return;
    if (_activeColorParamNames.length < 2) {
      previewManager.hideColorLegend();
      return;
    }
    const state = stateManager.getState();
    const params = state?.parameters || {};
    const entries = _activeColorParamNames.map((name) => ({
      name,
      value: normalizeHexColor(params[name]) || '#888888',
    }));
    previewManager.showColorLegend(entries);
  }

  /**
   * Update the visible status surfaces and, by default, announce the message.
   *
   * `announce: false` is for the callers that already announce the same thing
   * themselves — an error toast, say, which speaks assertively with its title
   * attached. Without the opt-out those callers say everything to a
   * screen-reader user twice, because this function is not a silent setter:
   * it routes through stateManager.announceChange.
   *
   * @param {string} message
   * @param {string} [statusType] 'default' | 'info' | 'success' | 'error' | 'warning'
   * @param {{announce?: boolean}} [options]
   */
  function updateStatus(
    message,
    statusType = 'default',
    { announce = true } = {}
  ) {
    // Update the drawer status area (hidden but kept for screen readers)
    if (statusArea) {
      statusArea.textContent = message;

      // Add/remove idle class
      if (message === 'Ready' || message === '') {
        statusArea.classList.add('idle');
      } else {
        statusArea.classList.remove('idle');
      }
    }

    // Update the preview status bar overlay
    if (previewStatusBar && previewStatusText) {
      previewStatusText.textContent = message;

      // Reset all state classes
      previewStatusBar.classList.remove(
        'idle',
        'processing',
        'success',
        'error'
      );

      // Determine state class based on message content or explicit type
      const isIdle = message === 'Ready' || message === '';
      const isProcessing =
        /processing|generating|rendering|loading|compiling|\d+%/i.test(message);
      const isError =
        /error|failed|invalid/i.test(message) || statusType === 'error';
      const isSuccess =
        (/complete|success|ready|generated/i.test(message) && !isIdle) ||
        statusType === 'success';

      if (isIdle) {
        previewStatusBar.classList.add('idle');
      } else if (isError) {
        previewStatusBar.classList.add('error');
      } else if (isProcessing) {
        previewStatusBar.classList.add('processing');
      } else if (isSuccess) {
        previewStatusBar.classList.add('success');
      }
    }

    // Announce status changes via dedicated SR live region.
    // Debounce progress-style updates (percent text) to avoid announcement spam.
    if (announce) {
      const shouldDebounce = /\d+%/.test(message);
      stateManager.announceChange(message, shouldDebounce, {
        source: 'status',
      });
    }
  }

  /**
   * Announce message to screen readers (for Welcome screen example loading, etc.)
   * @param {string} message - Message to announce
   */
  function announceToScreenReader(message) {
    stateManager.announceChange(message);
  }

  // ── Unified upload routing (welcome zone + file picker) ─────────────────

  function withRelativePath(file, relPath) {
    if (file.webkitRelativePath) return file;
    try {
      Object.defineProperty(file, 'webkitRelativePath', { value: relPath });
    } catch (defineErr) {
      // Instance property was non-configurable in this browser; the folder
      // import falls back to file.name-based paths for this file.
      console.warn(
        `[Upload] Could not attach relative path to ${file.name}:`,
        defineErr
      );
    }
    return file;
  }

  function readAllDirectoryEntries(reader) {
    return new Promise((resolve, reject) => {
      const all = [];
      const readBatch = () =>
        reader.readEntries((batch) => {
          if (batch.length === 0) return resolve(all);
          all.push(...batch);
          readBatch();
        }, reject);
      readBatch();
    });
  }

  async function collectFilesFromDirectoryEntry(dirEntry, basePath, out) {
    const entries = await readAllDirectoryEntries(dirEntry.createReader());
    for (const entry of entries) {
      if (entry.isFile) {
        const file = await new Promise((resolve, reject) =>
          entry.file(resolve, reject)
        );
        out.push(withRelativePath(file, `${basePath}/${entry.name}`));
      } else if (entry.isDirectory) {
        await collectFilesFromDirectoryEntry(
          entry,
          `${basePath}/${entry.name}`,
          out
        );
      }
    }
  }

  async function routeUploadSelection(selection) {
    switch (selection.kind) {
      case DROP_KIND.SCAD:
      case DROP_KIND.ZIP:
        fileHandler.handleFile(selection.files[0]);
        break;

      case DROP_KIND.FOLDER: {
        try {
          const files = [];
          for (const entry of selection.directoryEntries) {
            await collectFilesFromDirectoryEntry(entry, entry.name, files);
          }
          if (files.length === 0) {
            showErrorToast({
              title: 'Empty Folder',
              message: 'No files found in the dropped folder.',
            });
            return;
          }
          await fileHandler.handleFolderImport(files);
        } catch (err) {
          showErrorToast({
            title: 'Folder Import Error',
            message: err.message,
          });
        }
        break;
      }

      case DROP_KIND.MULTI: {
        // Loose files with a .scad among them behave like a flat folder:
        // the folder import prompts for the main file and keeps the rest
        // as companions.
        const files = selection.files.map((f) => withRelativePath(f, f.name));
        await fileHandler.handleFolderImport(files);
        break;
      }

      case DROP_KIND.STL:
        await fileHandler.handleStlView(selection.files[0]);
        break;

      case DROP_KIND.PRESET_JSON: {
        const currentState = stateManager.getState();
        if (!currentState.uploadedFile) {
          // Never guess which model a preset belongs to.
          showErrorToast({
            title: 'Open a Model First',
            message:
              'This looks like a preset file. Open the matching .scad model first, then drop the preset file again to import it.',
          });
          return;
        }
        try {
          const fileText = await selection.files[0].text();
          // Merge without overwriting: duplicates by name are skipped, so
          // a stray drop can never clobber existing designs.
          const result = presetManager.importAndMergePresets(
            [fileText],
            currentState.uploadedFile?.name || null,
            currentState.schema?.parameters || {},
            'keep'
          );
          _handleImportResult(result, currentState.uploadedFile?.name || null);
        } catch (error) {
          showErrorToast({ title: 'Import Failed', message: error.message });
        }
        break;
      }

      default:
        showErrorToast({
          title: 'Unsupported File Type',
          message: `Supported: ${describeAccepted()}.`,
        });
    }
  }

  // File input change
  fileInput.addEventListener('change', async (e) => {
    const selection = classifyDrop(e.target.files);
    // Allow re-selecting the same file if needed
    e.target.value = '';
    await routeUploadSelection(selection);
  });

  // Companion file input (Project Files Manager)
  const addCompanionFileInput = document.getElementById(
    'addCompanionFileInput'
  );
  if (addCompanionFileInput) {
    addCompanionFileInput.addEventListener('change', async (e) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      for (const file of files) {
        await companionFilesCtrl.handleAddCompanionFile(file);
      }

      // Reset input for potential re-selection
      e.target.value = '';
    });
  }

  // Missing files warning button: direct action to add missing dependency files
  // Provides a direct action to add missing dependency files
  const addMissingFilesBtn = document.getElementById('addMissingFilesBtn');
  if (addMissingFilesBtn) {
    addMissingFilesBtn.addEventListener('click', () => {
      // Trigger the companion file input
      if (addCompanionFileInput) {
        addCompanionFileInput.click();
      }
    });
  }

  // Companion files Save/Update Project button
  const companionSaveBtn = document.getElementById('companionSaveBtn');
  if (companionSaveBtn) {
    companionSaveBtn.addEventListener('click', async () => {
      if (currentSavedProjectId) {
        // Update existing saved project
        await companionFilesCtrl.autoSaveCompanionFiles();
      } else {
        // Route to save prompt for new projects
        const state = stateManager.getState();
        if (state.uploadedFile) {
          await savedProjectsUI.showSaveProjectPrompt(state);
        }
      }
    });
  }

  const companionSaveToFolderBtn = document.getElementById(
    'companionSaveToFolderBtn'
  );
  if (companionSaveToFolderBtn) {
    companionSaveToFolderBtn.addEventListener('click', async () => {
      if (isBusy(companionSaveToFolderBtn)) return;
      const state = stateManager.getState();
      setBusy(companionSaveToFolderBtn, true);
      try {
        await folderSaveActions.saveCompanions({
          projectFiles: state.projectFiles,
          mainFilePath: state.mainFilePath,
        });
      } finally {
        setBusy(companionSaveToFolderBtn, false);
      }
    });
  }

  // Text File Editor Modal handlers
  const textFileEditorModal = document.getElementById('textFileEditorModal');
  const textFileEditorApply = document.getElementById('textFileEditorApply');
  const textFileEditorCancel = document.getElementById('textFileEditorCancel');
  const textFileEditorClose = document.getElementById('textFileEditorClose');
  const textFileEditorOverlay = document.getElementById(
    'textFileEditorOverlay'
  );

  if (textFileEditorApply) {
    textFileEditorApply.addEventListener('click', () =>
      companionFilesCtrl.applyTextFileEditorChanges()
    );
  }

  // Ctrl+S / Cmd+S keyboard shortcut to save and apply changes
  const textFileEditorContent = document.getElementById(
    'textFileEditorContent'
  );
  if (textFileEditorContent && textFileEditorModal) {
    textFileEditorContent.addEventListener('keydown', (e) => {
      // Ctrl+S or Cmd+S to save and apply
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        companionFilesCtrl.applyTextFileEditorChanges();
      }
    });
  }

  // Escape belongs to the dialog, not to the textarea: bound to the textarea
  // it would die the moment a keyboard user tabbed to Cancel or the X, which
  // is exactly the path they take.
  if (textFileEditorModal) {
    textFileEditorModal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal(textFileEditorModal);
      }
    });
  }

  if (textFileEditorCancel && textFileEditorModal) {
    textFileEditorCancel.addEventListener('click', () =>
      closeModal(textFileEditorModal)
    );
  }

  if (textFileEditorClose && textFileEditorModal) {
    textFileEditorClose.addEventListener('click', () =>
      closeModal(textFileEditorModal)
    );
  }

  if (textFileEditorOverlay && textFileEditorModal) {
    textFileEditorOverlay.addEventListener('click', () =>
      closeModal(textFileEditorModal)
    );
  }

  // Close the project and return to the welcome screen. Split out of the Back
  // button's listener so File > Close Project can ask its own dirty-aware
  // question and still reach this one path without a second dialog.
  async function closeProjectToWelcome() {
    resetProjectUiState();
    // The name of a closed project is not the page's any more.
    const fileInfoSummary = document.getElementById('fileInfoSummary');
    if (fileInfoSummary) fileInfoSummary.textContent = '';

    // Reset file input
    fileInput.value = '';

    // Leave STL view-only mode if it was active
    setStlViewActive(false);
    document
      .getElementById('parametersContainer')
      ?.querySelector('.stl-view-notice')
      ?.remove();

    // Clear state (including preset selection so it doesn't survive reload)
    stateManager.setState({
      uploadedFile: null,
      projectFiles: null,
      mainFilePath: null,
      schema: null,
      parameters: {},
      defaults: {},
      stl: null,
      outputFormat: 'stl',
      stlStats: null,
      detectedLibraries: [],
      currentPresetId: null,
      currentPresetName: null,
    });

    // Sync the dropdown element and fire change so the format info panel,
    // 2D guidance, and button labels all reset to their STL defaults.
    if (outputFormatSelect) {
      outputFormatSelect.value = 'stl';
      outputFormatSelect.dispatchEvent(new Event('change'));
    }

    // Clear history
    stateManager.clearHistory();

    // Hide main interface, show welcome screen
    mainInterface.classList.add('hidden');
    welcomeScreen.classList.remove('hidden');
    setAppSurface('welcome');
    updateStorageDisplay();

    // Refresh saved projects list when returning to welcome screen
    await savedProjectsUI.renderSavedProjectsList();

    // Reset workflow step state, then re-apply slot visibility.
    // applyToolbarModeVisibility sees mainInterface.hidden=true and hides both
    // the toolbar and the workflow progress (welcome-screen branch).
    applyToolbarModeVisibility(getUIModeController().getMode());

    // Exit focus mode if active
    const focusModeBtn = document.getElementById('focusModeBtn');
    if (
      focusModeBtn &&
      mainInterface &&
      mainInterface.classList.contains('focus-mode')
    ) {
      mainInterface.classList.remove('focus-mode');
      focusModeBtn.setAttribute('aria-pressed', 'false');
    }

    // Close Features Guide modal if open
    const featuresGuideModal = document.getElementById('featuresGuideModal');
    if (
      featuresGuideModal &&
      !featuresGuideModal.classList.contains('hidden')
    ) {
      closeFeaturesGuide();
    }

    // Clear preview and remove any loaded overlay from the previous project
    if (previewManager) {
      previewManager.clear();
      previewManager.setReferenceOverlaySource({
        kind: null,
        name: null,
        dataUrlOrText: null,
      });
      previewManager.setOverlayEnabled(false);
    }

    // Reset overlay UI controls so the previous project's state doesn't linger
    if (overlayToggle) overlayToggle.checked = false;
    if (overlaySourceSelect) overlaySourceSelect.value = '';
    overlayGridCtrl.updateOverlaySourceDropdown();
    overlayGridCtrl.updateOverlayStatus();

    // Reset echo drawer so stale warnings don't persist into the
    // next project load (prevents layout shift from expanded drawer).
    updatePreviewDrawer([]);
    if (typeof window.clearConsoleState === 'function') {
      window.clearConsoleState();
    }

    // Reset status
    updateStatus('Ready');
    statsArea.textContent = '';
    clearPreviewStats();

    // Remove compact header
    const appHeader = document.querySelector('.app-header');
    if (appHeader) {
      appHeader.classList.remove('compact');
    }

    // The button that brought the person here is hidden with the project, so
    // focus would fall to the page body (or a control of the closed view):
    // the Main Page's heading says where they are.
    document.getElementById('features-heading')?.focus();

    console.log('[App] File cleared, returned to welcome screen');
  }

  // Back button - returns to welcome screen
  if (clearFileBtn) {
    clearFileBtn.addEventListener('click', async () => {
      // Ask only when there is something to lose: a changed parameter or an
      // edit in the code editor. A project only looked at closes at once.
      const somethingToLose =
        stateManager.canUndo() ||
        getEditorStateManager()?.getIsDirty?.() === true;
      if (!somethingToLose) {
        await closeProjectToWelcome();
        return;
      }
      const confirmed = await showConfirmDialog(
        'Any unsaved changes to your current project will be lost.',
        'Go back to the Main Page?',
        'Confirm',
        'Cancel'
      );
      if (confirmed) await closeProjectToWelcome();
    });
  }

  // Drag and drop
  uploadZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    uploadZone.classList.add('drag-over');
  });

  uploadZone.addEventListener('dragleave', () => {
    uploadZone.classList.remove('drag-over');
  });

  uploadZone.addEventListener('drop', async (e) => {
    e.preventDefault();
    uploadZone.classList.remove('drag-over');
    // Prefer items (enables folder detection); fall back to plain files.
    const input =
      e.dataTransfer.items?.length > 0
        ? e.dataTransfer.items
        : e.dataTransfer.files;
    await routeUploadSelection(classifyDrop(input));
  });

  // The file input is the area's one Tab stop: its own Enter and Space open
  // the picker, and the label wrapping it takes the click.

  // ========== Start new project ==========
  // A way to start a new project from scratch.
  /**
   * A new project from the starter template: the Main Page's Start New
   * Project and File > New File. From the menu a project can be open, so
   * that asks first, as loading an example does.
   */
  async function startNewProject() {
    if (stateManager.getState().uploadedFile) {
      const confirmed = await showConfirmDialog(
        'This will replace the current file. Continue?',
        'New File'
      );
      if (!confirmed) return;
    }

    // Create a starter template
    const starterTemplate = `// New OpenSCAD Project
// Created with OpenSCAD Assistive Forge
// https://github.com/BrennenJohnston/openscad-assistive-forge

/* [Basic Settings] */
// Width of the object
width = 50; // [10:200]

// Height of the object
height = 30; // [10:200]

// Depth of the object
depth = 20; // [10:200]

/* [Advanced] */
// Enable rounded corners
rounded = true;

// Corner radius (when rounded is enabled)
corner_radius = 5; // [1:20]

// Main shape
if (rounded) {
    minkowski() {
        cube([width - corner_radius*2, depth - corner_radius*2, height - corner_radius*2]);
        sphere(r = corner_radius, $fn = 32);
    }
} else {
    cube([width, depth, height]);
}
`;

    try {
      const fileName = 'new_project.scad';
      // Process it like a regular file upload, but pass content directly.
      // `handleFile()` uses FileReader for `File`/Blob inputs; passing a plain object
      // without content will throw. This path intentionally avoids FileReader.
      await fileHandler.handleFile(
        { name: fileName },
        starterTemplate,
        null,
        null,
        'user',
        fileName
      );

      // Announce to screen readers
      announceImmediate(
        'New project created. You can customize the parameters or edit the code.'
      );

      console.log('[App] New project created from template');
    } catch (error) {
      console.error('[App] Failed to create new project:', error);
      updateStatus('Failed to create new project', 'error');
    }
  }

  const startNewProjectBtn = document.getElementById('startNewProjectBtn');
  startNewProjectBtn?.addEventListener('click', () => void startNewProject());

  // Load examples - unified handler
  // IMPORTANT: Keep this as the single click handler for all example buttons.
  // Having multiple click handlers (e.g. role-specific + unified) causes duplicate example loads,
  // which can interrupt auto-preview and leave the preview in a pending/blank state.
  // NOTE: Exclude Features Guide example buttons (`data-feature-example`) because the
  // Features Guide has its own click handler that loads examples and closes the modal.
  // If we attach here too, the same example loads twice and can interrupt preview.
  const exampleButtons = document.querySelectorAll(
    '[data-example]:not([data-feature-example])'
  );
  exampleButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const exampleType = button.dataset.example;
      const tutorialId = button.dataset.tutorial;

      // Load the example first
      await fileHandler.loadExampleByKey(exampleType);

      if (exampleType) {
        // Screen reader confirmation that an example was loaded
        announceToScreenReader(
          `${EXAMPLE_DEFINITIONS[exampleType]?.name || 'Example'} loaded and ready to customize`
        );
      }

      // Launch tutorial if specified (after a short delay to let example load)
      if (tutorialId) {
        setTimeout(() => {
          startTutorial(tutorialId, { triggerEl: button });
        }, 500);
      }
    });
  });

  // The welcome-tour card starts a tour of the surface it
  // sits on. No example loads, so the unified [data-example] handler
  // above never sees this button.
  const welcomeTourBtn = document.getElementById('startWelcomeTourBtn');
  if (welcomeTourBtn) {
    welcomeTourBtn.addEventListener('click', () => {
      startTutorial('welcome', { triggerEl: welcomeTourBtn });
    });
  }

  // Ask about the welcome tour once per load, after the gate.
  const tourNudgeSettled = initTourNudge({
    waitForFirstVisitAcceptance,
    startTutorial,
  });

  // The Beginners-card spotlight waits for the first-visit gate — inside
  // the inert #app it would be unreachable and unannounced. It also waits
  // for the nudge, so the card's tip takes over when the dialog is
  // answered instead of competing with it.
  void initWelcomeSpotlight({
    waitForFirstVisitAcceptance,
    waitForTourNudge: () => tourNudgeSettled,
    waitForDraftQuestion: () => draftQuestionSettled,
  });

  // Wire charm variant selector to update the single Open button
  const charmVariantSelect = document.getElementById('charmVariantSelect');
  const openCharmMakerBtn = document.getElementById('openCharmMakerBtn');
  if (charmVariantSelect && openCharmMakerBtn) {
    charmVariantSelect.addEventListener('change', () => {
      openCharmMakerBtn.dataset.example = charmVariantSelect.value;
    });
  }

  // Wire braille variant selector the same way (card / charm / sign)
  const brailleVariantSelect = document.getElementById('brailleVariantSelect');
  const openBrailleCardBtn = document.getElementById('openBrailleCardBtn');
  if (brailleVariantSelect && openBrailleCardBtn) {
    brailleVariantSelect.addEventListener('change', () => {
      openBrailleCardBtn.dataset.example = brailleVariantSelect.value;
    });
  }

  // =========================================
  // Deep-linking: URL parameter support for external website integration
  // Allows external sites to link directly to Forge with a specific example loaded
  // Usage: ?example=simple-box or ?load=colored-box
  // Note: ?load= is an alias for ?example= (for website embedding convenience)
  // =========================================
  const initUrlParams = new URLSearchParams(window.location.search);
  /**
   * Compose the post-load URL from the surviving query parameters. The
   * fragment is carried over untouched: it holds the shared parameter payload
   * (`#v=1&params=`, state.js) and anything else the sender put there, and a
   * cleanup that drops it destroys the link it just opened.
   * @returns {string}
   */
  const cleanUrlKeepingFragment = () =>
    initUrlParams.toString()
      ? `${window.location.pathname}?${initUrlParams}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`;
  // A drawing sent by a link. `?drawing=<url>` fetches a PNG, JPG,
  // SVG or DXF from a host a project may come from and hands it to the design
  // parameter of whatever the link opened, as if the person had chosen it:
  // the conversion starts behind the dialog and its Cancel, and the editor
  // opens on the result. With nothing else in the link, the standalone
  // editor opens on it instead.
  const drawingParam = initUrlParams.get('drawing');
  async function applyLinkedDrawing({ door = false } = {}) {
    if (!drawingParam) return;
    initUrlParams.delete('drawing');
    history.replaceState(null, '', cleanUrlKeepingFragment());
    const {
      parseDrawingLink,
      fetchDrawingFile,
      waitForDrawingTarget,
      waitForNoModal,
      waitForDoorReady,
      deliverDrawing,
    } = await import('./js/linked-drawing.js');
    try {
      const link = parseDrawingLink(drawingParam, {
        origin: window.location.origin,
      });
      updateStatus(`Fetching ${link.name} from the link\u2026`);
      const file = await fetchDrawingFile(link);
      if (door) {
        const doorInput = document.getElementById('svgEditFileInput');
        if (!doorInput || !(await waitForDoorReady())) {
          throw new Error('The drawing editor is not available on this page.');
        }
        await waitForNoModal();
        deliverDrawing(doorInput, file);
        announceImmediate(
          `${link.name} from the link is opening in the drawing editor.`
        );
        return;
      }
      const target = await waitForDrawingTarget(link.ext);
      if (!target) {
        const message = `This design has no picture setting that takes a .${link.ext} file, so ${link.name} was not loaded.`;
        updateStatus(message, 'error');
        announceImmediate(message);
        return;
      }
      // The example's own "Save this file for quick access?" comes up right
      // after the controls do. The drawing waits for it (an editor
      // opened behind an inert page can neither take focus nor say so).
      await waitForNoModal();
      deliverDrawing(target, file);
      announceImmediate(`${link.name} from the link is being loaded.`);
    } catch (error) {
      console.error('[DeepLink] Failed to load the drawing:', error);
      const message = `Couldn't load the drawing from the link. ${error.message}`;
      updateStatus(message, 'error');
      announceImmediate(message);
    }
  }

  // Support both ?example= and ?load= (alias for website embedding)
  const exampleParam =
    initUrlParams.get('example') || initUrlParams.get('load');

  if (exampleParam) {
    console.log(`[DeepLink] Loading example from URL: ${exampleParam}`);

    // Check if example exists
    if (EXAMPLE_DEFINITIONS[exampleParam]) {
      // Load the example after a short delay to ensure UI is ready
      setTimeout(async () => {
        try {
          const loadedBefore = stateManager.getState().uploadedFile;
          await fileHandler.loadExampleByKey(exampleParam);

          // Clean up URL to avoid reloading on refresh
          initUrlParams.delete('example');
          initUrlParams.delete('load'); // Also remove ?load= alias
          const cleanUrl = cleanUrlKeepingFragment();
          history.replaceState(null, '', cleanUrl);

          // loadExampleByKey reports its own failure (the "Example Load
          // Failed" dialog and the status "Error loading example") and
          // returns without loading anything. Only a project that changed
          // is a success worth announcing.
          if (stateManager.getState().uploadedFile === loadedBefore) {
            console.warn(`[DeepLink] Example not loaded: ${exampleParam}`);
            return;
          }

          console.log(`[DeepLink] Successfully loaded: ${exampleParam}`);
          announceImmediate(
            `${EXAMPLE_DEFINITIONS[exampleParam]?.name || 'Example'} loaded from URL link`
          );
          await applyLinkedDrawing();
        } catch (error) {
          console.error('[DeepLink] Failed to load example:', error);
          updateStatus(`Failed to load example: ${exampleParam}`);
        }
      }, 500);
    } else {
      console.warn(`[DeepLink] Unknown example: ${exampleParam}`);
      console.log(
        '[DeepLink] Available examples:',
        Object.keys(EXAMPLE_DEFINITIONS)
      );
      updateStatus(`Unknown example: ${exampleParam}`);
    }
  }

  // --- Manifest info banner helpers ---
  function showManifestInfoBanner(name, author) {
    const banner = document.getElementById('manifestInfoBanner');
    const text = document.getElementById('manifestInfoText');
    if (!banner) return;
    // The name and author come from whoever hosts the manifest: text only.
    if (text) {
      text.textContent = name ? 'Shared project: ' : 'Shared project';
      if (name) {
        const strong = document.createElement('strong');
        strong.textContent = name;
        text.append(strong);
      }
      if (author) text.append(` by ${author}`);
    }
    const saveBtn = document.getElementById('manifestSaveCopyBtn');
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save My Copy';
    }
    banner.classList.remove('hidden');
  }

  /**
   * Show an inline overwrite confirmation when saving a manifest copy
   * that conflicts with an existing project name.
   * Returns a Promise resolving to 'overwrite', 'new-copy', or 'cancel'.
   */
  function showManifestOverwriteConfirm(projectName) {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'preset-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-labelledby', 'manifestOverwriteTitle');
      modal.setAttribute('aria-modal', 'true');

      modal.innerHTML = `
        <div class="preset-modal-content">
          <div class="preset-modal-header">
            <h3 id="manifestOverwriteTitle" class="preset-modal-title">Project Already Exists</h3>
            <button class="preset-modal-close" aria-label="Close dialog">&times;</button>
          </div>
          <div class="modal-body">
            <div style="margin-top: var(--space-md); padding: var(--space-sm) var(--space-md); border-radius: var(--radius-sm); background: color-mix(in srgb, var(--color-warning, #f59e0b) 15%, transparent); border: 1px solid var(--color-warning, #f59e0b);">
              <p style="margin: 0 0 var(--space-sm); font-weight: 600; color: var(--color-text-primary);">
                &#9888; A project named &ldquo;${escapeHtml(projectName)}&rdquo; already exists.
              </p>
              <p style="margin: 0; color: var(--color-text-secondary); font-size: var(--text-sm);">
                Do you want to overwrite it, or save this as a new copy?
              </p>
            </div>
          </div>
          <div class="preset-modal-footer">
            <button class="btn btn-secondary" id="manifestOverwriteCancel">Cancel</button>
            <button class="btn btn-secondary" id="manifestOverwriteNewCopy">Save as New Copy</button>
            <button class="btn btn-danger" id="manifestOverwriteReplace">Overwrite Existing</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      const cleanup = (result) => {
        closeModal(modal);
        modal.remove();
        resolve(result);
      };

      modal
        .querySelector('.preset-modal-close')
        .addEventListener('click', () => cleanup('cancel'));
      modal
        .querySelector('#manifestOverwriteCancel')
        .addEventListener('click', () => cleanup('cancel'));
      modal
        .querySelector('#manifestOverwriteNewCopy')
        .addEventListener('click', () => cleanup('new-copy'));
      modal
        .querySelector('#manifestOverwriteReplace')
        .addEventListener('click', () => cleanup('overwrite'));

      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          cleanup('cancel');
        }
      });

      openModal(modal);
    });
  }

  /**
   * Show the manifest save-copy modal after a shared project loads.
   * Returns a Promise that resolves to 'save' or 'skip'.
   *
   * If the first-visit modal is still blocking, this function polls until
   * it closes before showing the save-copy modal. In the normal manifest
   * deep-link flow this should never happen because step 1 of the lifecycle
   * already awaits waitForFirstVisitAcceptance(), but the guard is kept as
   * a defensive fallback for any future code paths that call this directly.
   */
  function showManifestSaveCopyModal(projectName, author) {
    return new Promise((resolve) => {
      const doShow = () => {
        const modal = document.getElementById('manifest-save-copy-modal');
        if (!modal) {
          resolve('skip');
          return;
        }

        const nameEl = document.getElementById('manifestSaveCopyProjectName');
        const authorLine = document.getElementById(
          'manifestSaveCopyAuthorLine'
        );
        const authorEl = document.getElementById('manifestSaveCopyAuthor');
        const saveBtn = document.getElementById('manifestSaveCopySave');
        const skipBtn = document.getElementById('manifestSaveCopySkip');

        if (nameEl) nameEl.textContent = projectName || 'Untitled Project';
        if (author && authorEl && authorLine) {
          authorEl.textContent = author;
          authorLine.classList.remove('hidden');
        }

        const cleanup = (result) => {
          closeModal(modal);
          resolve(result);
        };

        saveBtn.addEventListener('click', () => cleanup('save'), {
          once: true,
        });
        skipBtn.addEventListener('click', () => cleanup('skip'), {
          once: true,
        });

        modal.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cleanup('skip');
          }
        });

        openModal(modal, { focusTarget: saveBtn });
      };

      if (firstVisitBlocking) {
        const waitForFirstVisit = setInterval(() => {
          if (!firstVisitBlocking) {
            clearInterval(waitForFirstVisit);
            setTimeout(doShow, 300);
          }
        }, 200);
      } else {
        doShow();
      }
    });
  }

  // Wire manifest banner buttons
  const manifestBanner = document.getElementById('manifestInfoBanner');
  const manifestSaveCopyBtn = document.getElementById('manifestSaveCopyBtn');
  const manifestResetBtn = document.getElementById('manifestResetBtn');
  const manifestDismissBanner = document.getElementById(
    'manifestDismissBanner'
  );

  if (manifestDismissBanner) {
    manifestDismissBanner.addEventListener('click', () => {
      if (manifestBanner) manifestBanner.classList.add('hidden');
    });
  }

  if (manifestSaveCopyBtn) {
    manifestSaveCopyBtn.addEventListener('click', async () => {
      const state = stateManager.getState();
      if (!state.uploadedFile) return;

      if (isBusy(manifestSaveCopyBtn)) return;
      setBusy(manifestSaveCopyBtn, true);

      const origin = state.manifestOrigin;
      const projectName = state.uploadedFile.name.replace('.scad', '');
      const forkedFrom = origin
        ? {
            manifestUrl: origin.url,
            originalName: origin.name,
            originalAuthor: origin.author,
            forkDate: Date.now(),
          }
        : null;
      try {
        const { saveProject, listSavedProjects, updateProject } =
          await import('./js/saved-projects-manager.js');
        const projectFilesObj =
          state.projectFiles instanceof Map
            ? Object.fromEntries(state.projectFiles)
            : state.projectFiles || null;

        const existingProjects = await listSavedProjects();
        const duplicate = existingProjects.find((p) => p.name === projectName);

        if (duplicate) {
          const overwrite = await showManifestOverwriteConfirm(projectName);
          if (overwrite === 'cancel') {
            setBusy(manifestSaveCopyBtn, false);
            return;
          }
          if (overwrite === 'overwrite') {
            const result = await updateProject({
              id: duplicate.id,
              content: state.uploadedFile.content,
              projectFiles:
                projectFilesObj !== null
                  ? JSON.stringify(projectFilesObj)
                  : undefined,
            });
            if (result.success) {
              currentSavedProjectId = duplicate.id;
            } else {
              updateStatus(`Save failed: ${result.error}`, 'error');
              setBusy(manifestSaveCopyBtn, false);
              return;
            }
          } else {
            const result = await saveProject({
              name: projectName,
              originalName: state.uploadedFile.name,
              kind: state.projectFiles ? 'zip' : 'scad',
              mainFilePath: state.mainFilePath || state.uploadedFile.name,
              content: state.uploadedFile.content,
              projectFiles: projectFilesObj,
              forkedFrom,
            });
            if (result.success) {
              currentSavedProjectId = result.id;
            } else {
              updateStatus(`Save failed: ${result.error}`, 'error');
              setBusy(manifestSaveCopyBtn, false);
              return;
            }
          }
        } else {
          const result = await saveProject({
            name: projectName,
            originalName: state.uploadedFile.name,
            kind: state.projectFiles ? 'zip' : 'scad',
            mainFilePath: state.mainFilePath || state.uploadedFile.name,
            content: state.uploadedFile.content,
            projectFiles: projectFilesObj,
            forkedFrom,
          });
          if (result.success) {
            currentSavedProjectId = result.id;
          } else {
            updateStatus(`Save failed: ${result.error}`, 'error');
            setBusy(manifestSaveCopyBtn, false);
            return;
          }
        }

        manifestSaveCopyBtn.textContent = 'Saved!';
        updateStatus('Local copy saved');
        announceImmediate('Local copy saved to browser storage');
        await savedProjectsUI.renderSavedProjectsList();

        if (manifestBanner) manifestBanner.classList.add('hidden');
      } catch (err) {
        console.error('[Manifest] Save copy failed:', err);
        updateStatus('Failed to save local copy', 'error');
        setBusy(manifestSaveCopyBtn, false);
      }
    });
  }

  if (manifestResetBtn) {
    manifestResetBtn.addEventListener('click', async () => {
      const origin = stateManager.getState().manifestOrigin;
      if (!origin?.url) return;
      const confirmed = await showConfirmDialog(
        'Reset to the original shared project? Your unsaved changes will be lost.',
        'Reset Project',
        'Reset',
        'Cancel',
        { destructive: true }
      );
      if (!confirmed) return;
      try {
        updateStatus('Reloading original project...');
        const result = await loadManifest(origin.url, {
          onProgress: ({ message }) => updateStatus(message),
        });
        await fileHandler.handleFile(
          null,
          result.mainContent,
          result.projectFiles,
          result.mainFile,
          'manifest',
          result.manifest.name
        );
        stateManager.setState({
          manifestOrigin: { ...origin, loadedAt: Date.now() },
        });
        updateStatus(`Reset to original: ${origin.name || 'project'}`);
      } catch (err) {
        console.error('[Manifest] Reset failed:', err);
        updateStatus('Failed to reload original project', 'error');
      }
    });
  }

  // =========================================
  // A shared link that failed says so on the Main Page. The link handlers
  // below return to the Main Page when a download fails and strip the link
  // from the address bar, so a sentence in the visually hidden status
  // region alone would leave a sighted person with the Main Page and
  // nothing else. The notice stands at the top of "Open or start a
  // project" until dismissed, gives the loader's own reason, and offers the
  // same link again.
  // =========================================
  const LINK_FAILURE_HEADING = 'The shared project could not be opened.';
  let linkFailureRetryUrl = null;

  async function showLinkFailureNotice({ reason, retryUrl }) {
    const notice = document.getElementById('linkFailureNotice');
    const message = document.getElementById('linkFailureNoticeMessage');
    if (!notice || !message) {
      console.warn(
        '[DeepLink] The failed-link notice is missing from the page'
      );
      return;
    }
    // #app is inert and aria-hidden while the welcome dialog blocks, and an
    // alert filled in there is never heard; a ?project= link can fail before
    // the dialog is answered.
    await waitForFirstVisitAcceptance();
    linkFailureRetryUrl = retryUrl;
    message.replaceChildren();
    notice.hidden = false;
    // Filled once the region is in the accessibility tree, so the alert is
    // announced, and announced once (the welcome dialog's choice error does
    // the same). A second failure replaces the message; nothing stacks.
    setTimeout(() => {
      const heading = document.createElement('h4');
      heading.className = 'link-failure-notice-title';
      heading.textContent = LINK_FAILURE_HEADING;
      const why = document.createElement('p');
      why.className = 'link-failure-notice-reason';
      why.textContent = reason;
      message.replaceChildren(heading, why);
    }, 50);
  }

  document.getElementById('linkFailureRetry')?.addEventListener('click', () => {
    if (linkFailureRetryUrl) {
      window.location.assign(linkFailureRetryUrl);
    }
  });
  document
    .getElementById('linkFailureDismiss')
    ?.addEventListener('click', () => {
      const notice = document.getElementById('linkFailureNotice');
      if (notice) notice.hidden = true;
      document.getElementById('linkFailureNoticeMessage')?.replaceChildren();
      document.getElementById('fileInput')?.focus();
    });

  // =========================================
  // Manifest deep-link: ?manifest=<url> support
  // Loads a full project from a forge-manifest.json hosted externally.
  // This is the primary "one-link sharing" path for external project authors
  // Usage: ?manifest=https://raw.githubusercontent.com/user/repo/main/forge-manifest.json
  // Optional companions: ?preset=<name>, ?skipWelcome=true
  // =========================================
  const manifestParam = initUrlParams.get('manifest');

  if (manifestParam && !exampleParam) {
    // Kept before the handler strips the query, for the notice's Try again.
    const manifestLinkHref = window.location.href;
    console.log(`[DeepLink] Loading project from manifest: ${manifestParam}`);
    updateStatus('Loading project from manifest...');

    // Skip the welcome screen immediately when loading from a manifest
    const shouldSkipWelcome =
      initUrlParams.get('skipWelcome') === 'true' ||
      initUrlParams.get('skipwelcome') === 'true';

    if (shouldSkipWelcome || manifestParam) {
      // Hide welcome screen early so the user sees a loading state, not the landing page
      welcomeScreen.classList.add('hidden');
      mainInterface.classList.remove('hidden');
      setAppSurface('project');
    }

    // ─────────────────────────────────────────────────────────────────────
    // Manifest deep-link lifecycle: the order of operations
    //
    // The steps below must execute in this exact order. Reordering them
    // causes hard-to-diagnose bugs (e.g. the processing overlay covering
    // the first-visit modal, trapping the user in an infinite spinner).
    //
    //  1. GATE: Wait for first-visit acceptance (if needed).
    //     The first-visit modal (z-index 1000) must be resolved before
    //     anything else. No overlay or download may start while it is open.
    //
    //  2. OVERLAY: Show the processing overlay (z-index 10000).
    //     Only shown AFTER the first-visit gate clears. This prevents the
    //     overlay from stacking on top of the first-visit modal and making
    //     the "Download & Continue" button unreachable.
    //
    //  3. DOWNLOAD: Fetch manifest and project files via loadManifest().
    //     Progress messages update both the status bar and the overlay.
    //
    //  4. PROCESS: Call handleFile() to parse and load the project.
    //
    //  5. DISMISS OVERLAY: Remove the processing overlay so the editor
    //     is visible before the save-copy modal appears.
    //
    //  6. SAVE-COPY MODAL: Prompt the user to save a local copy.
    //     This modal has its own first-visit guard, but by this point
    //     the gate has already been cleared in step 1.
    //
    // ERROR PATH: If any step after the overlay is shown throws, the
    // catch block dismisses the overlay to prevent it from getting stuck.
    // ─────────────────────────────────────────────────────────────────────
    setTimeout(async () => {
      let dismissOverlay = null;
      try {
        // Step 1 — GATE: first-visit acceptance must complete before we
        // show any overlay or start any network requests. The first-visit
        // modal sits at z-index 1000; the processing overlay at z-index
        // 10000. Showing the overlay first would bury the modal and trap
        // the user in an infinite spinner.
        if (firstVisitBlocking || !hasUserAcceptedDownload) {
          updateStatus('Waiting for download acceptance...');
          await waitForFirstVisitAcceptance();
        }

        // Step 2 — OVERLAY: safe to show now that no blocking modal is open
        dismissOverlay = showProcessingOverlay(
          'Loading project from manifest...',
          {
            hint: 'Downloading project files. Please do not close or refresh the page.',
          }
        );

        // Step 3 — DOWNLOAD
        const result = await loadManifest(manifestParam, {
          onProgress: ({ message }) => {
            updateStatus(message);
            const msgEl = document.querySelector(
              '#processingOverlay .processing-message'
            );
            if (msgEl) msgEl.textContent = message;
          },
        });

        const { projectFiles, mainFile, mainContent, manifest, defaults } =
          result;
        const projectName = manifest.name || mainFile;

        console.log(
          `[DeepLink] Manifest loaded: "${projectName}" (${projectFiles.size} files)`
        );
        announceImmediate(`Loading project: ${projectName}`);

        // A manifest can name the handful of parameters a beginner should
        // meet first. Set BEFORE handleFile, because handleFile is what
        // renders - setting it afterwards would show every control once and
        // then take most of them away again, which is worse than either state.
        setStarterParameters(defaults?.starterParameters);

        // ?preset=<name> or manifest defaults.preset. With one, the
        // first preview waits until the preset is applied below, so the
        // design's own values are not rendered and thrown away first.
        const presetName = initUrlParams.get('preset') || defaults?.preset;

        // Step 4 — PROCESS: parse and load the project into the editor
        await fileHandler.handleFile(
          null,
          mainContent,
          projectFiles,
          mainFile,
          'manifest',
          projectName,
          { deferInitialPreview: Boolean(presetName) }
        );

        // A name in that list this design does not have is worth saying out
        // loud - to the person, once, and to the console for the author. It is
        // never fatal: the rest of the list still works.
        const starterNames = normalizeStarterList(defaults?.starterParameters);
        if (starterNames.length > 0) {
          const { unknown } = resolveStarterParameters(
            stateManager.getState().schema,
            starterNames
          );
          const message = unknownStarterMessage(unknown);
          if (message) {
            console.warn(`[DeepLink] ${message}`);
            // The notice, not the status line: a status message stands for about
            // 660 ms before the render replaces it - long enough to exist, not long
            // enough to read.
            const { createParameterNotices } =
              await import('./js/parameter-notices.js');
            createParameterNotices(
              document.getElementById('parameterNotices'),
              { announce: (text) => announceImmediate(text) }
            ).show(describeUnknownStarter(unknown));
          }
        }

        // Step 5 — DISMISS OVERLAY before showing the save-copy modal
        if (dismissOverlay) dismissOverlay();

        // Store manifest origin in state for provenance tracking
        stateManager.setState({
          manifestOrigin: {
            url: manifestParam,
            name: manifest.name || null,
            author: manifest.author || null,
            loadedAt: Date.now(),
          },
        });

        // Show the manifest info banner
        showManifestInfoBanner(manifest.name, manifest.author);

        await applyLinkedDrawing();

        // --- ?uiMode= or manifest defaults.uiMode / defaults.hiddenPanels ---
        const manifestUiMode = initUrlParams.get('uiMode') || defaults?.uiMode;
        const manifestHiddenPanels = defaults?.hiddenPanels;
        if (manifestUiMode || manifestHiddenPanels) {
          getUIModeController().importPreferences({
            defaultMode: manifestUiMode || undefined,
            hiddenPanelsInBasic: manifestHiddenPanels || undefined,
          });
          console.log(`[DeepLink] Applied UI preferences from manifest:`, {
            manifestUiMode,
            manifestHiddenPanels,
          });
        }

        // --- ?preset=<name> or manifest defaults.preset -----------------
        if (presetName) {
          // After handleFile, presets have been auto-imported from JSON files.
          // Find the matching preset by name and programmatically select it.
          const state = stateManager.getState();
          const modelName = state.uploadedFile ? presetModelKey(state) : null;
          if (modelName) {
            const presets = presetManager.getPresetsForModel(modelName);
            const match = presets.find(
              (p) => p.name.toLowerCase() === presetName.toLowerCase()
            );
            if (match) {
              console.log(
                `[DeepLink] Applying preset: "${match.name}" (${match.id})`
              );

              // Merge preset parameters onto current state (desktop OpenSCAD parity)
              const mergedParams = { ...state.parameters, ...match.parameters };
              stateManager.setState({ parameters: mergedParams });

              // Re-render UI with preset values
              const parametersContainer = document.getElementById(
                'parametersContainer'
              );
              renderParameterUI(
                state.schema,
                parametersContainer,
                (values) => {
                  // A committed change ends whatever draft stood.
                  draftPreviewHash = null;
                  stateManager.setState({ parameters: values });
                  if (autoPreviewController) {
                    autoPreviewController.onParameterChange(values);
                  }
                  updatePrimaryActionButton();
                },
                mergedParams
              );

              // The selection a person makes from the list (state,
              // hidden select, the preset's signature, nothing changed yet),
              // and the searchable list a person sees says so as well.
              setCurrentPresetSelection(match);
              _presetCombobox?.setValue(match.id);

              // Trigger auto-preview with preset parameters
              if (autoPreviewController) {
                autoPreviewController.onParameterChange(mergedParams);
              }
              updatePrimaryActionButton();

              // The sentence the announcement below says, so what is
              // seen and heard match, and no em dash in the status line.
              updateStatus(`${projectName} loaded with preset ${match.name}`);
              announceImmediate(
                `${projectName} loaded with preset ${match.name}`
              );
            } else {
              console.warn(
                `[DeepLink] Preset not found: "${presetName}". Available:`,
                presets.map((p) => p.name)
              );
              // A notice, not the status line: the status line stands about 300 ms
              // before the render replaces it, and the announcer replaces it at once,
              // so nobody would learn no preset was applied. add(), not show(): what
              // else this link reported stays.
              updateStatus(`${projectName} loaded from manifest`);
              const { createParameterNotices, describeMissingPreset } =
                await import('./js/parameter-notices.js');
              createParameterNotices(
                document.getElementById('parameterNotices'),
                { announce: (text) => announceImmediate(text) }
              ).add(describeMissingPreset(presetName));
            }
          }
        } else {
          updateStatus(`${projectName} loaded from manifest`);
          announceImmediate(`${projectName} loaded from manifest`);
        }

        // The first preview handleFile left to this handler, with the
        // preset's values, or the design's own when the preset was not found.
        // It joins the debounced request above rather than adding a render.
        if (presetName && autoPreviewController) {
          autoPreviewController.onParameterChange(
            stateManager.getState().parameters
          );
        }

        // Auto-preview if manifest requests it
        if (defaults?.autoPreview && autoPreviewController) {
          autoPreviewController.onParameterChange(
            stateManager.getState().parameters
          );
        }

        // Clean up URL parameters
        initUrlParams.delete('manifest');
        initUrlParams.delete('preset');
        initUrlParams.delete('skipWelcome');
        initUrlParams.delete('skipwelcome');
        const cleanUrl = cleanUrlKeepingFragment();
        history.replaceState(null, '', cleanUrl);

        console.log(`[DeepLink] Manifest load complete: ${projectName}`);

        // Step 6 — SAVE-COPY MODAL: prompt user to save a local copy
        const saveCopyChoice = await showManifestSaveCopyModal(
          projectName,
          manifest.author
        );
        if (saveCopyChoice === 'save' && manifestSaveCopyBtn) {
          manifestSaveCopyBtn.click();
        }
      } catch (error) {
        // ERROR PATH: dismiss overlay if it was shown (it's null if the
        // error occurred before step 2, e.g. during first-visit wait)
        if (dismissOverlay) dismissOverlay();
        // A starter list armed for a load that never happened must not be
        // waiting for whatever project this person opens next.
        setStarterParameters(null);
        console.error('[DeepLink] Manifest load failed:', error);

        let friendlyMsg;
        if (error instanceof ManifestError) {
          switch (error.code) {
            case 'INVALID_URL':
              friendlyMsg =
                error.message +
                ' Open the Manifest Sharing Guide for step-by-step instructions.';
              break;
            case 'CORS_ERROR':
              friendlyMsg =
                "Couldn't reach the file server. The manifest or its files may not be publicly " +
                "accessible, or the server doesn't support CORS. Try hosting on GitHub.";
              break;
            case 'VALIDATION_ERROR':
              friendlyMsg = `The manifest file has errors: ${error.details?.errors?.join('; ') || error.message}`;
              break;
            case 'TIMEOUT':
              friendlyMsg =
                'The request timed out. Check your internet connection and try again.';
              break;
            default:
              friendlyMsg = error.message;
          }
        } else {
          friendlyMsg =
            error.name === 'TypeError'
              ? "Couldn't reach the server. The manifest may not be publicly accessible, or CORS may be blocking the request."
              : error.message;
        }

        updateStatus(
          `Couldn't load the project from manifest. ${friendlyMsg} You can still upload a file manually.`,
          'error'
        );

        // Clean up URL so the user isn't stuck in a reload loop
        initUrlParams.delete('manifest');
        initUrlParams.delete('preset');
        initUrlParams.delete('skipWelcome');
        initUrlParams.delete('skipwelcome');
        const failCleanUrl = cleanUrlKeepingFragment();
        history.replaceState(null, '', failCleanUrl);

        // Show welcome screen again on failure so the user isn't stuck
        welcomeScreen.classList.remove('hidden');
        mainInterface.classList.add('hidden');
        setAppSurface('welcome');
        void showLinkFailureNotice({
          reason: friendlyMsg,
          retryUrl: manifestLinkHref,
        });
      }
    }, 500);
  }

  // =========================================
  // Direct launch link: ?project=<url> support
  // Allows linking directly to any .scad or .zip file hosted on the web
  // Usage: ?project=https://example.com/keyguard.zip or ?scad=https://example.com/box.scad
  // =========================================
  const projectParam =
    initUrlParams.get('project') || initUrlParams.get('scad');

  if (projectParam && !exampleParam && !manifestParam) {
    // Kept before the handler strips the query, for the notice's Try again.
    const projectLinkHref = window.location.href;
    console.log(`[DeepLink] Loading project from URL: ${projectParam}`);
    updateStatus('Loading project from URL...');

    setTimeout(async () => {
      try {
        // The manifest handler's step 1, for the same reason. The
        // file handler shows the processing overlay and then asks to save
        // the file, and neither may stand over the welcome dialog.
        if (firstVisitBlocking || !hasUserAcceptedDownload) {
          updateStatus('Waiting for download acceptance...');
          await waitForFirstVisitAcceptance();
          updateStatus('Loading project from URL...');
        }

        // Validate URL
        const projectUrl = new URL(projectParam);
        const urlFileName =
          projectUrl.pathname.split('/').pop() || 'project.scad';
        const isZipUrl = urlFileName.toLowerCase().endsWith('.zip');

        console.log(
          `[DeepLink] Fetching: ${projectParam} (type: ${isZipUrl ? 'ZIP' : 'SCAD'})`
        );

        const loadedBefore = stateManager.getState().uploadedFile;
        if (isZipUrl) {
          // The manifest lane's download, which follows a Git LFS
          // pointer on raw.githubusercontent.com to the archive itself
          // instead of handing the 130-byte pointer to the unzipper.
          const blob = await fetchProjectBlob(projectParam, urlFileName);
          const file = new File([blob], urlFileName, {
            type: 'application/zip',
          });
          await fileHandler.handleFile(file, null, null, null, 'user');
        } else {
          // Handle single .scad file
          const response = await fetch(projectParam);
          if (!response.ok) {
            throw new Error(
              `Server returned ${response.status}: ${response.statusText}`
            );
          }
          const scadContent = await response.text();
          await fileHandler.handleFile(
            { name: urlFileName },
            scadContent,
            null,
            null,
            'user'
          );
        }

        // Clean up URL after loading
        initUrlParams.delete('project');
        initUrlParams.delete('scad');
        const cleanUrl = cleanUrlKeepingFragment();
        history.replaceState(null, '', cleanUrl);

        // handleFile reports its own failures (an archive it cannot
        // open gets the "ZIP Extraction Failed" dialog and the status "Failed
        // to extract ZIP file") and returns without loading anything. Only a
        // project that changed is a success worth announcing.
        if (stateManager.getState().uploadedFile === loadedBefore) {
          console.warn(`[DeepLink] Project not loaded: ${urlFileName}`);
          return;
        }

        console.log(`[DeepLink] Successfully loaded project: ${urlFileName}`);
        updateStatus(`Loaded ${urlFileName} from URL`);
        announceImmediate(`${urlFileName} loaded from URL link`);
        await applyLinkedDrawing();
      } catch (error) {
        console.error('[DeepLink] Failed to load project:', error);
        const friendlyMsg =
          error.name === 'TypeError'
            ? "Couldn't reach the server. The file may not be publicly accessible, or CORS may be blocking the request."
            : `${error.message}`;
        updateStatus(
          `Couldn't load the project from URL. ${friendlyMsg} You can still upload a file manually.`,
          'error'
        );
        void showLinkFailureNotice({
          reason: friendlyMsg,
          retryUrl: projectLinkHref,
        });
      }
    }, 500);
  }

  // A drawing with no design to put it on opens the standalone editor.
  if (drawingParam && !exampleParam && !manifestParam && !projectParam) {
    setTimeout(() => applyLinkedDrawing({ door: true }), 500);
  }

  /**
   * Draw the Customizer for the open project, wired the way every redraw is:
   * each change is recorded for Undo, clears the preset selection and asks
   * for a preview.
   * @param {Object|null} [values] - The values to show; null shows the state's
   */
  function renderCustomizer(values = null) {
    renderParameterUI(
      stateManager.getState().schema,
      document.getElementById('parametersContainer'),
      (changed) => {
        stateManager.recordParameterState();
        stateManager.setState({ parameters: changed });
        clearPresetSelection(changed);
        if (autoPreviewController && stateManager.getState().uploadedFile) {
          autoPreviewController.onParameterChange(changed);
        }
        updatePrimaryActionButton();
      },
      values
    );
  }

  /**
   * Perform undo: restores previous parameter state, re-renders UI, and
   * triggers auto-preview.  Called by Edit toolbar menu, Undo button,
   * and keyboard shortcut.
   */
  function performUndo() {
    const previousParams = stateManager.undo();
    if (previousParams) {
      const state = stateManager.getState();
      renderCustomizer(previousParams);

      if (autoPreviewController && state.uploadedFile) {
        autoPreviewController.onParameterChange(previousParams);
      }

      updatePrimaryActionButton();
    }
  }

  /**
   * Perform redo: restores next parameter state, re-renders UI, and
   * triggers auto-preview.  Called by Edit toolbar menu, Redo button,
   * and keyboard shortcut.
   */
  function performRedo() {
    const nextParams = stateManager.redo();
    if (nextParams) {
      const state = stateManager.getState();
      renderCustomizer(nextParams);

      if (autoPreviewController && state.uploadedFile) {
        autoPreviewController.onParameterChange(nextParams);
      }

      updatePrimaryActionButton();
    }
  }

  // Undo/Redo buttons in Parameters header — delegates to shared logic
  const undoBtn = document.getElementById('undoBtn');
  const redoBtn = document.getElementById('redoBtn');

  undoBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    if (state.uploadedFile && stateManager.canUndo()) {
      performUndo();
    }
  });

  redoBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    if (state.uploadedFile && stateManager.canRedo()) {
      performRedo();
    }
  });

  // Reset button - performs the actual reset (used internally)
  const performReset = () => {
    const state = stateManager.getState();
    if (state.defaults) {
      // Record current state before reset for undo
      stateManager.recordParameterState();

      stateManager.setState({ parameters: { ...state.defaults } });

      // Clear preset selection when resetting to defaults
      clearPresetSelection(state.defaults);

      // Re-render UI with defaults
      renderCustomizer();

      // Trigger auto-preview with reset params
      if (autoPreviewController && state.uploadedFile) {
        autoPreviewController.onParameterChange(state.defaults);
      }

      updateStatus('Customizer reset to defaults');
      // Update button state after reset
      updatePrimaryActionButton();
    }
  };

  // Reset All, the Customizer's one reset button, asks first (COGA)
  const resetAllBtn = document.getElementById('resetAllBtn');
  resetAllBtn.addEventListener('click', async () => {
    const state = stateManager.getState();
    if (!state.defaults) return;

    // Check if there are unsaved changes (parameters differ from defaults)
    const hasChanges = Object.keys(state.parameters).some(
      (key) => state.parameters[key] !== state.defaults[key]
    );

    if (hasChanges) {
      // Show confirmation dialog for COGA compliance
      const confirmed = await showConfirmDialog(
        'This will reset all parameters to their default values. Any unsaved changes will be lost. You can undo this action.',
        'Reset the Customizer?',
        'Reset',
        'Keep Changes'
      );

      if (!confirmed) return;
    }

    performReset();
  });

  // Collapsible Parameter Panel (Desktop only)
  const collapseParamPanelBtn = document.getElementById(
    'collapseParamPanelBtn'
  );
  const paramPanel = document.getElementById('paramPanel');
  const paramPanelBody = document.getElementById('paramPanelBody');

  // Declare toggleParamPanel at module scope so it can be referenced by Split.js code
  let toggleParamPanel = null;
  // The splitter's part in the panel opening and closing, set where the
  // splitter is built: built before an opening panel is measured, taken down
  // once a closing one has slid shut, so its width and gutter never jump.
  let paramPanelLayout = null;

  if (collapseParamPanelBtn && paramPanel && paramPanelBody) {
    // Load saved collapsed state (desktop only)
    // (Storage key defined at module level as STORAGE_KEY_PARAM_PANEL_COLLAPSED)
    let isCollapsed = false;

    // A phone held sideways starts with the column closed, so the preview
    // has the width; a choice made with the button is kept as everywhere.
    const savedState = safeGetItem(STORAGE_KEY_PARAM_PANEL_COLLAPSED);
    if (
      window.innerWidth >= 768 &&
      (savedState === 'true' || (savedState === null && isSidewaysPhone()))
    ) {
      isCollapsed = true;
    }

    // Apply initial state
    if (isCollapsed) {
      paramPanel.classList.add('collapsed');
      collapseParamPanelBtn.setAttribute('aria-expanded', 'false');
      collapseParamPanelBtn.setAttribute(
        'aria-label',
        'Expand customizer panel'
      );
      collapseParamPanelBtn.title = 'Expand panel';
    }

    /**
     * Close or open the panel (desktop only, >= 768px).
     * @param {boolean} collapsed
     * @param {import('./js/drawer-motion.js').DrawerHow} [how]
     * @returns {Promise<boolean>|null|undefined} its slide
     */
    const setParamPanelCollapsed = (
      collapsed,
      { instant = false, remember = true } = {}
    ) => {
      if (window.innerWidth < 768 || collapsed === isCollapsed) {
        return;
      }

      isCollapsed = collapsed;
      let slide;

      if (isCollapsed) {
        // Check if focus is inside the panel body
        const activeElement = document.activeElement;
        const isFocusInBody = paramPanelBody.contains(activeElement);

        // Collapse panel
        collapseParamPanelBtn.setAttribute('aria-expanded', 'false');
        collapseParamPanelBtn.setAttribute(
          'aria-label',
          'Expand customizer panel'
        );
        collapseParamPanelBtn.title = 'Expand panel';

        // If focus was inside body, move it to the toggle button
        if (isFocusInBody) {
          collapseParamPanelBtn.focus();
        }
        slide = slideDrawer(sidePanelParts(paramPanel, 'left'), {
          from: 'left',
          open: false,
          keep: stripWidth(),
          instant,
          name: 'customizer',
          inert: [paramPanelBody],
          whenClosed: () => {
            if (!isCollapsed) return;
            paramPanel.classList.add('collapsed');
            paramPanelLayout?.(false);
          },
        });
      } else {
        // Expand panel
        paramPanel.classList.remove('collapsed');
        paramPanelLayout?.(true);
        collapseParamPanelBtn.setAttribute('aria-expanded', 'true');
        collapseParamPanelBtn.setAttribute(
          'aria-label',
          'Collapse customizer panel'
        );
        collapseParamPanelBtn.title = 'Collapse panel';
        slide = slideDrawer(sidePanelParts(paramPanel, 'left'), {
          from: 'left',
          open: true,
          keep: stripWidth(),
          instant,
          name: 'customizer',
        });
      }

      // Persist state
      if (remember) {
        safeSetItem(STORAGE_KEY_PARAM_PANEL_COLLAPSED, String(isCollapsed));
      }

      // Trigger preview resize after transition
      setTimeout(() => {
        if (previewManager) {
          previewManager.handleResize();
        }
      }, 300); // Match CSS transition duration
      return slide;
    };

    // Toggle function (assigned to outer scope variable)
    toggleParamPanel = function () {
      // On a phone the same arrow closes the sheet (drawer-controller.js).
      if (isPhoneLayout()) return;
      setParamPanelCollapsed(!isCollapsed);
    };

    registerLookDrawer('customizer', {
      place: () => paramPanel,
      isOpen: () =>
        !isCollapsed &&
        window.innerWidth >= 768 &&
        document.body.dataset.uiMode !== 'classic',
      open: (how) => setParamPanelCollapsed(false, how),
      close: (how) => setParamPanelCollapsed(true, how),
    });

    // Add click listener
    collapseParamPanelBtn.addEventListener('click', toggleParamPanel);

    // Handle window resize - reset collapsed state on mobile
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth < 768 && isCollapsed) {
          // Reset to expanded on mobile
          isCollapsed = false;
          paramPanel.classList.remove('collapsed');
          collapseParamPanelBtn.setAttribute('aria-expanded', 'true');
          collapseParamPanelBtn.setAttribute(
            'aria-label',
            'Collapse customizer panel'
          );
          collapseParamPanelBtn.title = 'Collapse panel';
        } else if (!isPhoneLayout()) {
          // Back from a phone, where the arrow carried the sheet's state.
          collapseParamPanelBtn.setAttribute(
            'aria-expanded',
            String(!isCollapsed)
          );
          collapseParamPanelBtn.setAttribute(
            'aria-label',
            isCollapsed
              ? 'Expand customizer panel'
              : 'Collapse customizer panel'
          );
          collapseParamPanelBtn.title = isCollapsed
            ? 'Expand panel'
            : 'Collapse panel';
        }
      }, 150);
    });
  }

  // =========================================================================
  // Expert Mode Integration
  // =========================================================================
  const expertModeToggle = document.getElementById('expertModeToggle');
  const expertModePanel = document.getElementById('expertModePanel');
  const expertModeBody = document.getElementById('expertModeBody');
  const expertRunPreviewBtn = document.getElementById('expertRunPreviewBtn');
  const expertModeCloseBtn = document.getElementById('expertModeCloseBtn');
  const editorDirtyIndicator = document.getElementById('editorDirtyIndicator');

  // Check if Expert Mode feature flag is enabled
  const isExpertModeEnabled = _isEnabled('expert_mode');
  let currentEditor = null;
  let modeManager = null;
  let editorStateManager = null;
  /** Pending "focus the editor" timer, so a later claim on focus can win. */
  let editorFocusTimer;

  // True while an editor edit is being written into stateManager. The push
  // channel below subscribes to the same store, so without this it would
  // echo the edit straight back into the buffer the user is typing in.
  let isApplyingEditorEdit = false;

  /** @type {ReturnType<typeof setTimeout>|null} */
  let editorWriteBackTimer = null;

  /**
   * Drop a queued write-back. Called before the push channel replaces the
   * buffer: a timer armed against the previous project must never fire
   * afterwards, or it would write the old project's text back over the new
   * one while the editor already shows the new file.
   */
  function cancelEditorWriteBack() {
    if (editorWriteBackTimer) {
      clearTimeout(editorWriteBackTimer);
      editorWriteBackTimer = null;
    }
  }

  // Long enough that a burst of typing writes back once, short enough that
  // Export/Generate right after a pause still sees the edit.
  const EDITOR_WRITE_BACK_DELAY_MS = 500;

  // Names the control and its shortcut so the next step is not left to be
  // guessed.
  const EDITED_PENDING_PREVIEW_MESSAGE =
    'Edited. Press Preview (F5) to update the model.';

  /**
   * Publish an editor edit into the app's single source of truth, so render,
   * export and save all see it. Mirrors the folder-watch writer
   * (`main.js` folder-change handler) minus its re-render: typing must not
   * start a render — Preview/F5 does that.
   * @param {string} code
   * @param {Object} [options]
   * @param {boolean} [options.announcePending=true] - False when something is
   *   about to render or save anyway, so the pending-edit line is neither
   *   shown for a frame nor spoken over the action the user just took.
   */
  function applyEditorEdit(code, { announcePending = true } = {}) {
    const state = stateManager.getState();
    if (!state?.uploadedFile) return;
    if (state.uploadedFile.content === code) return;

    isApplyingEditorEdit = true;
    try {
      stateManager.setState({
        uploadedFile: { ...state.uploadedFile, content: code },
        sourceEdited: true,
        // The generated output belongs to the source it was rendered from.
        // Keeping it would let Download and the Export menu hand back the
        // pre-edit model, since their staleness check keys on parameters.
        stl: null,
      });
    } finally {
      isApplyingEditorEdit = false;
    }

    if (autoPreviewController) {
      const cameraSettled = autoPreviewController.initialPreviewDone;
      autoPreviewController.setScadContent(code);
      // An edit is not a new file — keep the user's viewpoint instead
      // of re-fitting to the model. setScadContent clears the preview cache
      // (which is keyed on parameters, not source) but also resets the
      // camera-settled flag, which would snap a zoomed-in user back out.
      autoPreviewController.initialPreviewDone = cameraSettled;
    }

    // Typing deliberately does not render (desktop parity), so this says so:
    // without it there is no affordance anywhere on screen after an edit.
    // Fires once per edit burst, not per keystroke, and the next render's own
    // status replaces it.
    if (announcePending) updateStatus(EDITED_PENDING_PREVIEW_MESSAGE);

    updatePrimaryActionButton();
  }

  /** Queue a write-back for the current edit burst. */
  function scheduleEditorWriteBack() {
    cancelEditorWriteBack();
    editorWriteBackTimer = setTimeout(() => {
      editorWriteBackTimer = null;
      // Read the buffer at fire time. A snapshot captured when the timer was
      // armed could belong to a project the user has since navigated away
      // from, and would be written back over the current one.
      const code = currentEditor?.getValue?.();
      if (typeof code === 'string') applyEditorEdit(code);
    }, EDITOR_WRITE_BACK_DELAY_MS);
  }

  /** Write a queued edit through immediately (save, preview, mode switch). */
  function flushEditorWriteBack() {
    if (!editorWriteBackTimer) return;
    cancelEditorWriteBack();
    const code = currentEditor?.getValue?.();
    // Every caller is on its way to render, export, save or leave the editor,
    // so telling the user to press Preview here would speak over the action
    // they just took.
    if (typeof code === 'string')
      applyEditorEdit(code, { announcePending: false });
  }

  // Reconciliation bookkeeping. `retiredParameterValues` holds
  // values the user had set on parameters the code has since removed, so
  // editing a declaration away and back does not reset their choice.
  let lastReconciledSource = null;
  let lastReconciledFileName = null;
  let retiredParameterValues = {};

  /**
   * Re-read the parameter schema from the edited source and fold it into the
   * live values.
   *
   * A schema parsed once at load would pass `-D` for every parameter in it,
   * so an edited default would reach the worker but the stale `-D` would
   * override it and the model would never move. This runs on Preview, Render
   * and Save rather than on a typing-pause timer, so the Customizer is never
   * rebuilt under a user's hand or mid-sentence for a screen reader.
   *
   * @returns {{added: string[], removed: string[]}|null} null when nothing moved
   */
  function reconcileEditedParameters() {
    const state = stateManager.getState();
    const source = state?.uploadedFile?.content;
    if (typeof source !== 'string' || !state.schema) return null;

    if (state.uploadedFile.name !== lastReconciledFileName) {
      lastReconciledFileName = state.uploadedFile.name;
      lastReconciledSource = null;
      retiredParameterValues = {};
    }
    if (source === lastReconciledSource) return null;
    lastReconciledSource = source;

    let nextSchema;
    try {
      nextSchema = extractParameters(source);
    } catch (error) {
      console.warn(
        '[Reconcile] Could not re-read parameters from the edited code:',
        error
      );
      return null;
    }

    const result = reconcileParameters({
      nextSchema,
      previousSchema: state.schema,
      parameters: state.parameters || {},
      defaults: state.defaults || {},
      retiredValues: retiredParameterValues,
    });

    if (!result.ok) {
      console.warn(
        `[Reconcile] Skipped: ${result.reason}. Keeping the parameters already on screen.`
      );
      return null;
    }
    retiredParameterValues = result.retiredValues;

    const paramTypes = {};
    for (const [name, def] of Object.entries(nextSchema.parameters || {})) {
      paramTypes[name] = def.type || 'string';
    }

    // Ranges, groups and descriptions can move without any value moving, and
    // the panel has to follow those too.
    const schemaMoved =
      JSON.stringify(state.schema?.parameters || {}) !==
      JSON.stringify(nextSchema.parameters || {});

    stateManager.setState({
      schema: nextSchema,
      paramTypes,
      parameters: result.parameters,
      defaults: result.defaults,
    });

    if (!result.changed && !schemaMoved) return null;

    const parametersContainer = document.getElementById('parametersContainer');
    if (parametersContainer) {
      renderParameterUI(
        nextSchema,
        parametersContainer,
        (values) => {
          stateManager.recordParameterState();
          stateManager.setState({ parameters: values });
          clearPresetSelection(values);
          if (autoPreviewController) {
            autoPreviewController.onParameterChange(values);
          }
          updatePrimaryActionButton();
          companionFilesCtrl?.syncOverlayWithScreenshotParam?.(values);
        },
        result.parameters
      );
    }

    return { added: result.added, removed: result.removed };
  }

  /**
   * Publish what the editor holds before something reads the model: flush the
   * pending write-back, then reconcile the schema. Every path that renders,
   * exports or saves goes through here, so none of them can act on a source
   * the user has already changed.
   */
  function publishEditorEdits() {
    flushEditorWriteBack();
    return reconcileEditedParameters();
  }

  if (
    isExpertModeEnabled &&
    expertModeToggle &&
    expertModePanel &&
    expertModeBody
  ) {
    console.log('[Expert Mode] Feature enabled, initializing...');

    // Show the toggle button
    expertModeToggle.classList.remove('hidden');

    // Initialize managers
    modeManager = getModeManager({
      announceToScreenReader: (msg) => announceToScreenReader(msg),
      onModeChange: handleModeChange,
    });
    editorStateManager = getEditorStateManager();

    // Expose modeManager globally for keyboard shortcut handler
    window._modeManager = modeManager;

    // Classic-mode editor co-existence: the desktop shell shows the
    // editor pane ALONGSIDE the customizer, so entering classic must never
    // route through modeManager's exclusive expert view (which hides
    // #paramPanelBody). If expert mode was active, unwind it first so the
    // param body is restored, then light the editor up inside its slot.
    document.addEventListener('classic-editor-activate', () => {
      // Reloading straight into Classic fires this from the controller's
      // init while the welcome screen is up — there is no project and
      // #mainInterface is display:none. Creating CodeMirror there produces
      // a zero-size, empty instance whose '' value then poisons the
      // editor-state capture on exit. Wait for a real project: the
      // file-handler re-fires via syncEditorPane() after every load.
      if (
        document.getElementById('mainInterface')?.classList.contains('hidden')
      ) {
        return;
      }
      if (
        modeManager?.isExpertMode?.() &&
        typeof modeManager.toggleMode === 'function'
      ) {
        modeManager.toggleMode();
      }
      if (!currentEditor) {
        initExpertEditor();
      } else if (currentEditor.setValue) {
        const code = resolveEditorSource();
        // Only write when it actually differs — setValue resets the caret.
        // Unsaved edits win: re-entering Classic must never discard them.
        if (
          code &&
          !editorStateManager.getIsDirty() &&
          currentEditor.getValue?.() !== code
        ) {
          currentEditor.setValue(code);
        }
      }
      expertModePanel.classList.add('classic-editor-active');
      // The slot is a different width than the custom-mode panel; CodeMirror
      // paints with its cached geometry until told to re-measure.
      currentEditor?.refreshLayout?.();
    });

    document.addEventListener('classic-editor-deactivate', () => {
      expertModePanel.classList.remove('classic-editor-active');
    });

    /**
     * Handle mode change between Standard and Expert
     * @param {string} newMode - 'standard' or 'expert'
     * @param {string} oldMode - Previous mode
     */
    function handleModeChange(newMode, oldMode) {
      console.log(`[Expert Mode] Switching from ${oldMode} to ${newMode}`);

      if (newMode === 'expert') {
        // Show Expert Mode panel, hide standard param body
        if (paramPanelBody) paramPanelBody.classList.add('hidden');
        expertModePanel.classList.add('active');
        expertModeToggle.setAttribute('aria-pressed', 'true');

        // Initialize editor if not already done
        if (!currentEditor) {
          initExpertEditor();
        } else {
          // Re-sync from the loaded project, unless the user has unsaved
          // edits in the buffer — those are newer than anything in state
          // that has not been written back yet.
          const currentCode = resolveEditorSource();
          if (
            currentCode &&
            !editorStateManager.getIsDirty() &&
            currentEditor.getValue() !== currentCode
          ) {
            currentEditor.setValue(currentCode);
          }
          currentEditor.refreshLayout?.();
        }

        // Focus the editor, unless something else has claimed focus in the
        // 100 ms since: by then the user may have opened a menu, tabbed onward or
        // clicked something else.
        //
        // This does not stop every steal: pressing the toggle and moving focus in
        // the same task still ends with the editor focused, so at least one other
        // path focuses it - most likely initExpertEditor's own first-run focus.
        if (currentEditor && currentEditor.focus) {
          clearTimeout(editorFocusTimer);
          editorFocusTimer = setTimeout(() => {
            editorFocusTimer = undefined;
            const active = document.activeElement;
            const unclaimed =
              !active ||
              active === document.body ||
              active === document.documentElement ||
              active === expertModeToggle;
            if (unclaimed) currentEditor.focus();
          }, 100);
        }
      } else {
        // Hide Expert Mode panel, show standard param body
        expertModePanel.classList.remove('active');
        if (paramPanelBody) paramPanelBody.classList.remove('hidden');
        expertModeToggle.setAttribute('aria-pressed', 'false');

        // Capture state from editor before switching. An empty value from a
        // never-shown editor must not overwrite a real stored source —
        // capturing '' here is only meaningful when the user actually
        // cleared the document (the buffer is dirty then).
        flushEditorWriteBack();
        if (currentEditor) {
          const code = currentEditor.getValue();
          if (code !== '' || editorStateManager.getIsDirty()) {
            editorStateManager.setSource(code, { markDirty: false });
          }
        }

        // Clear editor instance so Edit menu items disable in Standard Mode
        modeManager.setEditorInstance(null);
      }
    }

    /**
     * The source the editor should show. `uploadedFile.content` is the single
     * source of truth — it is what render, export, save and auto-preview all
     * read, and what every loader writes. Reading anything else first would
     * let a stale cache win forever, so a second project load could never
     * reach the editor.
     * @returns {string}
     */
    function resolveEditorSource() {
      return stateManager.getState()?.uploadedFile?.content || '';
    }

    /**
     * Initialize the Expert Mode code editor
     */
    function initExpertEditor() {
      const editorType = modeManager.resolveEditorType();
      console.log(`[Expert Mode] Using ${editorType} editor`);

      const editorOptions = {
        container: expertModeBody,
        onChange: (code) => {
          editorStateManager.setSource(code, { markDirty: true });
          updateDirtyIndicator();
          scheduleEditorWriteBack();
          // The first keystroke is what makes Undo available, and nothing
          // else re-checks the editor toolbar's enablement.
          getClassicEditorToolbar()?.refresh();
        },
        onSave: () => {
          // The editor's Ctrl+S goes to the same handler the File menu uses; it
          // flushes the write-back first.
          fileActionsController.onSave();
        },
        onRun: () => {
          triggerPreviewFromEditor();
        },
        announce: (msg) => announceToScreenReader(msg),
      };

      if (editorType === 'codemirror') {
        currentEditor = new CodeMirrorEditor(editorOptions);
      } else {
        currentEditor = new TextareaEditor(editorOptions);
      }

      currentEditor.initialize();

      const initialCode = resolveEditorSource();
      if (initialCode) {
        currentEditor.setValue(initialCode);
      }

      editorStateManager.setEditorInstance(currentEditor);

      if (currentEditor.textarea) {
        editorStateManager.setTextareaElement(currentEditor.textarea);
      }

      modeManager.setEditorInstance(currentEditor);
    }

    /**
     * Update the dirty indicator visibility
     */
    function updateDirtyIndicator() {
      if (editorDirtyIndicator && editorStateManager) {
        const isDirty = editorStateManager.getIsDirty();
        editorDirtyIndicator.classList.toggle('visible', isDirty);
        // The dot is hidden via opacity, which does NOT remove it from the
        // accessibility tree — keep aria-hidden in sync so screen readers
        // only encounter "Unsaved changes" when it is actually shown.
        editorDirtyIndicator.setAttribute('aria-hidden', String(!isDirty));
      }
    }

    /**
     * Render a preview of the editor's current code. Explicit user action
     * (▶ Preview, Ctrl+Enter) — typing alone never renders. The
     * dirty flag is untouched: only saving clears it.
     */
    function triggerPreviewFromEditor() {
      if (!currentEditor) return;

      const code = currentEditor.getValue();
      if (!code || code.trim() === '') {
        announceToScreenReader('No code to preview');
        return;
      }

      // Publish the edit before rendering — forcePreview renders whatever
      // content the controller was last given, not the editor buffer — and
      // reconcile, so an edited default is not overridden by a stale -D.
      publishEditorEdits();

      if (!autoPreviewController) {
        announceToScreenReader('Preview is not ready yet');
        return;
      }

      autoPreviewController
        .forcePreview(stateManager.getState().parameters)
        .catch((error) => {
          console.error('[Expert Mode] Preview failed:', error);
          showErrorToast({
            title: 'Preview Failed',
            message: error.message,
          });
          announceToScreenReader('Preview failed. See the error message.');
        });
    }

    // The Classic editor toolbar's Preview must be this exact handler — it
    // flushes the pending write-back first, so it previews what is typed
    // rather than the last published content. It is a closure in this block,
    // so it is published here rather than reached by clicking a hidden
    // button, which is the element-to-element side channel this file avoids.
    editorPreviewTrigger = triggerPreviewFromEditor;

    // Toggle button click handler
    expertModeToggle.addEventListener('click', () => {
      modeManager.toggleMode();
    });

    // Run preview button handler
    if (expertRunPreviewBtn) {
      expertRunPreviewBtn.addEventListener('click', triggerPreviewFromEditor);
    }

    // Close/exit button handler
    if (expertModeCloseBtn) {
      expertModeCloseBtn.addEventListener('click', () => {
        modeManager.switchMode('standard');
      });
    }

    // Mobile: collapse toggle for bottom-sheet expert panel
    const expertCollapseBtn = document.getElementById('expertModeCollapseBtn');
    if (expertCollapseBtn) {
      expertCollapseBtn.addEventListener('click', () => {
        const isCollapsed = expertModePanel.classList.toggle(
          'expert-mode-collapsed'
        );
        expertCollapseBtn.setAttribute('aria-expanded', String(!isCollapsed));
        expertCollapseBtn.setAttribute(
          'aria-label',
          isCollapsed ? 'Expand code editor' : 'Collapse code editor'
        );
        // Clear any inline height set by drag resize
        const paramPanel = expertModePanel.closest('.param-panel');
        if (paramPanel) paramPanel.style.maxHeight = '';
      });
    }

    // Mobile: touch-drag resize on the bottom-sheet handle
    const dragHandle = expertModePanel.querySelector(
      '.expert-mode-drag-handle'
    );
    if (dragHandle) {
      let dragStartY = 0;
      let dragStartHeight = 0;

      dragHandle.addEventListener(
        'touchstart',
        (e) => {
          const paramPanel = expertModePanel.closest('.param-panel');
          if (!paramPanel) return;
          dragStartY = e.touches[0].clientY;
          dragStartHeight = paramPanel.offsetHeight;
          paramPanel.style.transition = 'none';
          e.preventDefault();
        },
        { passive: false }
      );

      dragHandle.addEventListener(
        'touchmove',
        (e) => {
          const paramPanel = expertModePanel.closest('.param-panel');
          if (!paramPanel) return;
          const deltaY = dragStartY - e.touches[0].clientY;
          const maxH = window.innerHeight * 0.7;
          const minH = 48;
          const newHeight = Math.min(
            maxH,
            Math.max(minH, dragStartHeight + deltaY)
          );
          paramPanel.style.maxHeight = `${newHeight}px`;
          // Auto-uncollapse when dragging past minimum
          if (
            newHeight > 80 &&
            expertModePanel.classList.contains('expert-mode-collapsed')
          ) {
            expertModePanel.classList.remove('expert-mode-collapsed');
            if (expertCollapseBtn) {
              expertCollapseBtn.setAttribute('aria-expanded', 'true');
              expertCollapseBtn.setAttribute(
                'aria-label',
                'Collapse code editor'
              );
            }
          }
          e.preventDefault();
        },
        { passive: false }
      );

      dragHandle.addEventListener('touchend', () => {
        const paramPanel = expertModePanel.closest('.param-panel');
        if (!paramPanel) return;
        paramPanel.style.transition = '';
        // Snap to collapsed if dragged very small
        if (paramPanel.offsetHeight < 80) {
          expertModePanel.classList.add('expert-mode-collapsed');
          paramPanel.style.maxHeight = '';
          if (expertCollapseBtn) {
            expertCollapseBtn.setAttribute('aria-expanded', 'false');
            expertCollapseBtn.setAttribute('aria-label', 'Expand code editor');
          }
        }
      });
    }

    // Reset mobile bottom-sheet state when switching back to standard mode
    modeManager.subscribe((newMode) => {
      if (newMode === 'standard') {
        expertModePanel.classList.remove('expert-mode-collapsed');
        const paramPanel = expertModePanel.closest('.param-panel');
        if (paramPanel) paramPanel.style.maxHeight = '';
        if (expertCollapseBtn) {
          expertCollapseBtn.setAttribute('aria-expanded', 'true');
          expertCollapseBtn.setAttribute('aria-label', 'Collapse code editor');
        }
      }
    });

    // Keyboard shortcut: Ctrl+E to toggle Expert Mode (registered via keyboard config below)

    // Push channel: loaded source → editor. Every writer of
    // uploadedFile.content (the load funnel, folder-watch, back-to-welcome)
    // reaches the editor through this one subscription, so Standard,
    // Simplified and Classic all refill from the same place.
    stateManager.subscribe((newState, prevState) => {
      if (isApplyingEditorEdit) return;
      const nextFile = newState?.uploadedFile;
      if (nextFile === prevState?.uploadedFile) return;

      const code = nextFile?.content || '';

      cancelEditorWriteBack();

      if (currentEditor?.setValue && currentEditor.getValue?.() !== code) {
        currentEditor.setValue(code);
      }
      editorStateManager.setSource(code, { markDirty: false });
      editorStateManager.markClean();
      updateDirtyIndicator();
    });

    // The dot also has to react to markClean() calls made outside this block
    // — saving a project clears the flag from the save routine.
    editorStateManager.subscribe((_snapshot, change) => {
      if (change?.type === 'dirty') updateDirtyIndicator();
    });

    // Unsaved editor text is not persisted anywhere the user can get it back
    // from, so leaving the page with a dirty buffer asks first. Separate
    // listener from the worker-termination one above; both run.
    window.addEventListener('beforeunload', (event) => {
      if (!editorStateManager.getIsDirty()) return;
      event.preventDefault();
      event.returnValue = '';
    });

    console.log('[Expert Mode] Initialization complete');
  } else if (!isExpertModeEnabled) {
    console.log('[Expert Mode] Feature flag disabled');
  }

  // Resizable Split Panels (Desktop only - horizontal split between params and preview)
  let splitInstance = null;
  const previewPanel = document.querySelector('.preview-panel');

  // The vertical split (preview info vs canvas) is the overlay drawer in
  // preview-settings-drawer.js; no Split.js is needed for that.

  if (paramPanel && previewPanel) {
    // (Storage key defined at module level as STORAGE_KEY_LAYOUT_SIZES)

    // Load saved split sizes
    let initialSizes = [40, 60]; // Default: 40% params, 60% preview
    try {
      const savedSizes = safeGetItem(STORAGE_KEY_LAYOUT_SIZES);
      if (savedSizes) {
        const parsed = JSON.parse(savedSizes);
        if (Array.isArray(parsed) && parsed.length === 2) {
          initialSizes = parsed;
        }
      }
    } catch (e) {
      // JSON.parse failed on a corrupt value — keep defaults
      console.warn('Could not load split sizes:', e);
    }

    const minSizes = [280, 300];
    // The last width chosen with the divider, by drag or by keyboard. The
    // split is rebuilt from it whenever the Customizer opens again.
    let currentSizes = initialSizes;

    // Initialize Split.js (only if not collapsed and not on mobile)
    const initSplit = function () {
      // Don't initialize on mobile (drawer pattern is used instead)
      if (window.innerWidth < 768) {
        return;
      }

      // Classic mode uses a CSS grid; Split.js inline styles would fight it
      if (document.body.dataset.uiMode === 'classic') {
        return;
      }

      if (splitInstance || paramPanel.classList.contains('collapsed')) {
        return;
      }

      let splitResizePending = false;
      splitInstance = Split([paramPanel, previewPanel], {
        sizes: currentSizes,
        minSize: minSizes,
        gutterSize: 8,
        cursor: 'col-resize',
        onDragStart: () => {
          document.body.classList.add('split-dragging');
        },
        onDrag: () => {
          if (previewManager && !splitResizePending) {
            splitResizePending = true;
            requestAnimationFrame(() => {
              splitResizePending = false;
              previewManager.handleResize();
            });
          }
        },
        onDragEnd: (sizes) => {
          document.body.classList.remove('split-dragging');
          currentSizes = sizes;
          safeSetItem(STORAGE_KEY_LAYOUT_SIZES, JSON.stringify(sizes));

          // Final resize after drag
          if (previewManager) {
            previewManager.handleResize();
          }
        },
      });

      // Add keyboard accessibility to gutter
      setTimeout(() => {
        const gutter = document.querySelector('.gutter');
        if (gutter) {
          // Make gutter focusable
          gutter.setAttribute('tabindex', '0');
          gutter.setAttribute('role', 'separator');
          gutter.setAttribute('aria-orientation', 'vertical');
          gutter.setAttribute('aria-label', 'Resize panels');
          const controlIds = [paramPanel.id, previewPanel.id]
            .filter(Boolean)
            .join(' ');
          if (controlIds) {
            gutter.setAttribute('aria-controls', controlIds);
          }

          // The split's own sizes are the width being moved to. The panels'
          // rendered widths lag behind while their width transition runs, so
          // a held arrow key read from them would step from a stale width
          // and the divider would announce it.
          const getCurrentSizes = () => {
            if (splitInstance) {
              return splitInstance.getSizes();
            }
            const paramWidth = paramPanel.offsetWidth;
            const previewWidth = previewPanel.offsetWidth;
            const totalWidth = paramWidth + previewWidth;
            if (!totalWidth) {
              return [50, 50];
            }
            return [
              (paramWidth / totalWidth) * 100,
              (previewWidth / totalWidth) * 100,
            ];
          };

          const getAriaRange = () => {
            const totalWidth =
              paramPanel.offsetWidth + previewPanel.offsetWidth;
            if (!totalWidth) {
              return { min: 0, max: 100 };
            }
            const minParam = Math.round((minSizes[0] / totalWidth) * 100);
            const maxParam = Math.round((1 - minSizes[1] / totalWidth) * 100);
            return {
              min: Math.max(0, Math.min(minParam, maxParam)),
              max: Math.min(100, Math.max(minParam, maxParam)),
            };
          };

          // Set aria-value attributes
          const updateAriaValues = () => {
            const sizes = getCurrentSizes();
            const { min, max } = getAriaRange();
            gutter.setAttribute('aria-valuenow', Math.round(sizes[0]));
            gutter.setAttribute('aria-valuemin', String(min));
            gutter.setAttribute('aria-valuemax', String(max));
            gutter.setAttribute(
              'aria-valuetext',
              `Customizer: ${Math.round(sizes[0])}%, Preview: ${Math.round(sizes[1])}%`
            );
          };

          updateAriaValues();

          // Keyboard navigation
          gutter.addEventListener('keydown', (e) => {
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
              e.preventDefault();

              const sizes = getCurrentSizes();
              let newParamSize = sizes[0];
              const { min, max } = getAriaRange();

              // Calculate step size
              const smallStep = 2; // 2%
              const largeStep = 5; // 5% with Shift
              const step = e.shiftKey ? largeStep : smallStep;

              // Adjust size based on key
              switch (e.key) {
                case 'ArrowLeft':
                  newParamSize = Math.max(min, sizes[0] - step);
                  break;
                case 'ArrowRight':
                  newParamSize = Math.min(max, sizes[0] + step);
                  break;
                case 'Home':
                  newParamSize = min;
                  break;
                case 'End':
                  newParamSize = max;
                  break;
              }

              const newPreviewSize = 100 - newParamSize;

              // Apply new sizes
              if (splitInstance) {
                splitInstance.setSizes([newParamSize, newPreviewSize]);
                currentSizes = [newParamSize, newPreviewSize];

                // Save to localStorage
                safeSetItem(
                  STORAGE_KEY_LAYOUT_SIZES,
                  JSON.stringify([newParamSize, newPreviewSize])
                );

                // Update ARIA values
                updateAriaValues();

                // Trigger preview resize
                if (previewManager) {
                  previewManager.handleResize();
                }
              }
            }
          });

          // Update ARIA values after drag
          gutter.addEventListener('mouseup', updateAriaValues);
          gutter.addEventListener('touchend', updateAriaValues);
        }
      }, 100);
    };

    // Destroy Split.js and clean up
    const destroySplit = function () {
      if (splitInstance) {
        splitInstance.destroy();
        splitInstance = null;
      }

      // Clean up leftover gutters and inline styles
      const gutters = document.querySelectorAll('.gutter-horizontal');
      gutters.forEach((gutter) => gutter.remove());

      // Clear inline styles that Split.js may have applied
      if (paramPanel) {
        paramPanel.style.removeProperty('width');
        paramPanel.style.removeProperty('flex-basis');
      }
      if (previewPanel) {
        previewPanel.style.removeProperty('width');
        previewPanel.style.removeProperty('flex-basis');
      }
    };

    // Initialize if not collapsed
    if (!paramPanel.classList.contains('collapsed')) {
      initSplit();
    }

    // The Classic dock resizers change the 3D view's box without a window
    // resize, so the canvas would keep its old backing store and letterbox
    // or stretch. Same contract Split.js's onDrag fulfills above, same rAF
    // throttle — a keyboard repeat or a drag fires this continuously.
    let classicResizePending = false;
    document.addEventListener('classic-layout-resize', () => {
      if (!previewManager || classicResizePending) return;
      classicResizePending = true;
      requestAnimationFrame(() => {
        classicResizePending = false;
        previewManager.handleResize();
      });
    });

    // Classic desktop-shell layout (moves console/editor into grid slots,
    // presets into the Customizer dock)
    // Classic swaps the WebGL scene to the desktop Cornfield colors, so the
    // viewport re-detects on every entry and exit (detectTheme() returns
    // 'classic' while the mode is active).
    const syncPreviewSceneToMode = () => {
      // The flip crosses a preference-namespace boundary, so the
      // target interface's own saved viewing state is re-applied in one
      // pass. The DOM surfaces swap even before any model exists...
      reloadScopedUiSurfaces();
      if (!previewManager) return;
      // ...and the scene picks up its grid/measurements/scheme state, then
      // re-detects colors (grid rebuilds resolve them from currentTheme,
      // which updateTheme refreshes right here).
      previewManager.reloadScopedViewPreferences();
      previewManager.updateTheme(
        previewManager.detectTheme(),
        document.documentElement.getAttribute('data-high-contrast') === 'true'
      );
    };

    initClassicLayoutController({
      onEnter: () => {
        destroySplit();
        syncPreviewSceneToMode();
        // Startup contract: collapsed customizer groups, and a first
        // preview with current values if nothing has rendered yet
        collapseCustomizerGroups();
        // Mirror the auto-preview state into the dock checkbox on entry
        const classicAutoCheck = document.getElementById(
          'classicAutoPreviewCheck'
        );
        if (classicAutoCheck && autoPreviewToggle) {
          classicAutoCheck.checked = autoPreviewToggle.checked;
        }
        const classicState = stateManager.getState();
        if (
          classicState?.uploadedFile &&
          !classicState.stl &&
          autoPreviewController
        ) {
          autoPreviewController.onParameterChange(classicState.parameters);
        }
        requestAnimationFrame(() => previewManager?.handleResize?.());
      },
      onExit: () => {
        if (!paramPanel.classList.contains('collapsed')) {
          initSplit();
        }
        syncPreviewSceneToMode();
        requestAnimationFrame(() => previewManager?.handleResize?.());
      },
    });

    // Classic window-bottom status bar: mirrors the viewport overlay
    initClassicStatusBar();

    // Classic editor toolbar. Dependencies are injected because they are
    // closures in here; the toolbar owns wiring and enablement only, never a
    // second implementation of any action. The workflow buttons
    // (Preview/Render/STL/DXF) live on the top Classic toolbar, with their
    // render-state deps.
    initClassicEditorToolbar({
      fileActionsController,
      getEditor: () => getModeManager()?.getEditorInstance?.() || null,
      getState: () => stateManager.getState(),
    });

    // Classic Font List panel. Registering the sample faces costs no new
    // bandwidth: the worker fetches these same four files from the same URLs
    // to mount them for text(), so the browser serves these from cache.
    {
      const fontListPanel = initFontListPanel({
        assetBaseUrl: new URL(import.meta.env.BASE_URL, window.location.origin)
          .toString()
          .replace(/\/$/, ''),
      });
      fontListPanel.loadSampleFaces();
    }

    // Classic Viewport-Control panel. The PreviewManager is built lazily
    // once WASM is ready, so the panel binds to its camera later, the same way
    // the display-options and overlay-grid controllers do.
    initViewportControlPanel({ getPreviewManager: () => previewManager });

    // Classic Animate panel. Playback drives real renders through the
    // auto-preview controller's -D $t path.
    initAnimatePanel({
      getAutoPreviewController: () => autoPreviewController,
      getParameters: () => stateManager.getState().parameters || {},
    });

    // Classic Customizer bar: titlebar ✕ + the Automatic Preview mirror.
    // All state flows through the real controls (#autoPreviewToggle), never
    // element-to-element side channels.
    document
      .getElementById('classicCustomizerCloseBtn')
      ?.addEventListener('click', () => {
        getClassicLayoutController()?.toggleCustomizer();
      });
    {
      const classicAutoCheck = document.getElementById(
        'classicAutoPreviewCheck'
      );
      if (classicAutoCheck && autoPreviewToggle) {
        classicAutoCheck.checked = autoPreviewToggle.checked;
        classicAutoCheck.addEventListener('change', () => {
          if (autoPreviewToggle.checked !== classicAutoCheck.checked) {
            autoPreviewToggle.checked = classicAutoCheck.checked;
            autoPreviewToggle.dispatchEvent(new Event('change'));
          }
        });
        autoPreviewToggle.addEventListener('change', () => {
          classicAutoCheck.checked = autoPreviewToggle.checked;
        });
      }
    }

    // Classic display strip: snap views, axes/grid overlays, bed
    // size, Preview/Render — thin wrappers over the existing actions
    // Classic icon toolbar: thin wrappers over the same actions the
    // menus drive — no new state anywhere.
    const classicToolbar = document.getElementById('classicToolbar');
    if (classicToolbar) {
      restoreClassicToolbarPrefs();
      // The snap-view buttons live on the 3D view toolbar. Scoping this
      // to #classicToolbar would silently leave every one of them dead, so it
      // queries the document — both bars are Classic-only markup.
      document.querySelectorAll('[data-classic-view]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!previewManager) return;
          previewManager.setCameraView(btn.dataset.classicView);
          announceCameraAction(`${btn.dataset.classicView} view`);
        });
      });

      // The bar's View All and Reset View carry the same labels as the View
      // menu's items, so they run the same commands.
      document
        .getElementById('classicViewHomeBtn')
        ?.addEventListener('click', () => {
          if (!previewManager) return;
          previewManager.viewAllCamera();
          announceCameraAction('View fitted to model');
        });

      document
        .getElementById('classicResetViewBtn')
        ?.addEventListener('click', () => {
          if (!previewManager) return;
          previewManager.resetCamera();
          announceCameraAction('reset');
        });

      // File / Edit / Render groups proxy the same handlers as the menus
      document
        .getElementById('classicTbNewBtn')
        ?.addEventListener('click', () => fileActionsController.onNew());
      document
        .getElementById('classicTbOpenBtn')
        ?.addEventListener('click', () =>
          document.getElementById('fileInput')?.click()
        );
      document
        .getElementById('classicTbSaveBtn')
        ?.addEventListener('click', () => fileActionsController.onSave());
      document
        .getElementById('classicTbUndoBtn')
        ?.addEventListener('click', () =>
          document.getElementById('undoBtn')?.click()
        );
      document
        .getElementById('classicTbRedoBtn')
        ?.addEventListener('click', () =>
          document.getElementById('redoBtn')?.click()
        );
      // The desktop's Export STL exports a render that exists; ours
      // gates the same way. aria-disabled + reason rather than disabled, so
      // keyboard and screen-reader users can find the button and hear why
      // (the editor toolbar's pattern). Enabled, it downloads the existing
      // full render — never starts a fresh one.
      const classicTbStlBtn = document.getElementById('classicTbExportStlBtn');
      if (classicTbStlBtn) {
        const reasonId = 'classicTbExportStlReason';
        const reasonSpan = document.createElement('span');
        reasonSpan.id = reasonId;
        reasonSpan.className = 'sr-only';
        reasonSpan.textContent = CLASSIC_STL_NEEDS_RENDER_REASON;
        classicTbStlBtn.insertAdjacentElement('afterend', reasonSpan);

        const refreshClassicTbStl = () => {
          const armed = hasFullQualitySTLFor(
            stateManager.getState().parameters
          );
          if (armed) {
            classicTbStlBtn.removeAttribute('aria-disabled');
            classicTbStlBtn.removeAttribute('aria-describedby');
          } else {
            classicTbStlBtn.setAttribute('aria-disabled', 'true');
            classicTbStlBtn.setAttribute('aria-describedby', reasonId);
          }
        };
        refreshClassicTbStl();
        document.addEventListener('render-state-change', refreshClassicTbStl);

        classicTbStlBtn.addEventListener('click', (event) => {
          if (classicTbStlBtn.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
            announceImmediate(
              `Export as STL unavailable. ${CLASSIC_STL_NEEDS_RENDER_REASON}`
            );
            return;
          }
          exportFormatFromMenu('stl', { renderIfNeeded: false });
        });
      }

      // The workflow triad's other members.
      document
        .getElementById('classicTbPreviewBtn')
        ?.addEventListener('click', () => {
          const tbState = stateManager.getState();
          if (!tbState.uploadedFile) return;
          // Prefer the editor's own trigger: it flushes the pending
          // write-back first, so Preview shows what is typed rather than
          // the last published content — the reason the editor toolbar's
          // Preview used it. Null until the expert block initializes
          // (a Simplified-only session), where the plain path is right.
          if (editorPreviewTrigger) {
            editorPreviewTrigger();
          } else if (autoPreviewController) {
            autoPreviewController.onParameterChange(tbState.parameters);
          }
        });
      document
        .getElementById('classicTbRenderBtn')
        ?.addEventListener('click', () => {
          if (primaryActionBtn && !primaryActionBtn.disabled) {
            runFullRender();
          }
        });
      document
        .getElementById('classicTbExportDxfBtn')
        ?.addEventListener('click', () =>
          fileActionsController.onExport2D?.('dxf')
        );

      // Projection pair mirrors the preview manager's actual mode
      const perspBtn = document.getElementById('classicTbPerspectiveBtn');
      const orthoBtn = document.getElementById('classicTbOrthogonalBtn');
      const syncProjectionButtons = () => {
        const mode = previewManager?.getProjectionMode?.() || 'perspective';
        perspBtn?.setAttribute('aria-pressed', String(mode === 'perspective'));
        orthoBtn?.setAttribute('aria-pressed', String(mode === 'orthographic'));
      };
      // Synced from the projection event, not only inside their own click
      // handlers, so changing projection from the View menu or the P shortcut
      // keeps the pair truthful.
      syncProjectionButtons();
      document.addEventListener(
        'preview-projection-change',
        syncProjectionButtons
      );
      perspBtn?.addEventListener('click', () => {
        if (
          previewManager &&
          previewManager.getProjectionMode?.() !== 'perspective'
        ) {
          previewManager.toggleProjection();
        }
        syncProjectionButtons();
      });
      orthoBtn?.addEventListener('click', () => {
        if (
          previewManager &&
          previewManager.getProjectionMode?.() !== 'orthographic'
        ) {
          previewManager.toggleProjection();
        }
        syncProjectionButtons();
      });

      // Overlay toggles share displayOptionsController state. They listen for
      // display-option-change rather than only updating on their own click,
      // so toggling the same flag from the View menu or the camera panel
      // keeps this button's aria-pressed truthful.
      const wireOverlayToggle = (btnId, option) => {
        const btn = document.getElementById(btnId);
        if (!btn) return;
        const syncPressed = () =>
          btn.setAttribute(
            'aria-pressed',
            String(displayOptionsController.get(option))
          );
        syncPressed();
        btn.addEventListener('click', () => {
          displayOptionsController.toggle(option);
        });
        document.addEventListener('display-option-change', (event) => {
          if (event.detail?.option === option) syncPressed();
        });
      };
      wireOverlayToggle('classicEdgesToggle', 'edges');
      // One flag per button: a combined "Axes (mm)" button driving two display
      // flags at once would let the View menu and the toolbar disagree about
      // either. axisMarks is this app's scale-marker overlay.
      wireOverlayToggle('classicAxesToggle', 'axes');
      wireOverlayToggle('classicScaleMarkersToggle', 'axisMarks');

      // Bed grid and its size select are dropped from Classic entirely; both
      // remain in Simplified and Standard, where the preview settings drawer
      // owns them.

      // Zoom uses the one shared step so the bar, the View menu and the
      // camera panel all move the camera by the same amount.
      const wireZoom = (btnId, direction) => {
        document.getElementById(btnId)?.addEventListener('click', () => {
          if (!previewManager) return;
          previewManager.zoomCamera(direction * CAMERA_ZOOM_STEP);
          announceCameraAction(direction > 0 ? 'Zoomed in' : 'Zoomed out');
        });
      };
      wireZoom('classicZoomInBtn', 1);
      wireZoom('classicZoomOutBtn', -1);

      // Measurement has no engine yet. The buttons are aria-disabled
      // rather than disabled so they stay discoverable; activating one says
      // why instead of doing nothing.
      for (const id of ['classicMeasureDistBtn', 'classicMeasureAngleBtn']) {
        const btn = document.getElementById(id);
        btn?.addEventListener('click', (event) => {
          if (btn.getAttribute('aria-disabled') !== 'true') return;
          event.preventDefault();
          const name = btn.querySelector('.sr-only')?.textContent.trim() || '';
          const reason = document
            .getElementById('classicMeasureReason')
            ?.textContent.trim();
          announceImmediate(`${name} unavailable. ${reason}`);
        });
      }

      document
        .getElementById('classicPreviewBtn')
        ?.addEventListener('click', () => {
          const stripState = stateManager.getState();
          if (!stripState.uploadedFile) return;
          // The same flushing trigger the top toolbar's Preview uses: the plain
          // path re-serves the cached preview of the last published content, so an
          // edit typed within the write-back debounce would be invisible to exactly
          // this button. Null until the expert block initializes (a Simplified-only
          // session), where the plain path is right.
          if (editorPreviewTrigger) {
            editorPreviewTrigger();
          } else if (autoPreviewController) {
            autoPreviewController.onParameterChange(stripState.parameters);
          }
        });

      document
        .getElementById('classicRenderBtn')
        ?.addEventListener('click', () => {
          if (primaryActionBtn && !primaryActionBtn.disabled) {
            runFullRender();
          }
        });

      const classicTbCustomizerBtn = document.getElementById(
        'classicTbCustomizerBtn'
      );
      classicTbCustomizerBtn?.addEventListener('click', () => {
        const layout = getClassicLayoutController();
        if (!layout) return;
        const visible = layout.toggleCustomizer();
        classicTbCustomizerBtn.setAttribute('aria-pressed', String(visible));
      });

      // APG toolbar keyboard pattern: each toolbar's buttons form ONE tab
      // stop and Arrow keys move within it — Tab-through would cost two dozen
      // presses to cross. Applied to both the top toolbar and the 3D view
      // toolbar from one implementation, so the two cannot drift apart. Any
      // <select> keeps its own tab stop, because Arrow keys change a select's
      // value and hijacking them there would break the control.
      const wireRovingToolbar = (toolbarEl) => {
        if (!toolbarEl) return;
        const buttons = Array.from(toolbarEl.querySelectorAll('button'));
        if (buttons.length === 0) return;

        // aria-disabled buttons stay in the ring on purpose: that is how a
        // keyboard user discovers them and hears why they are unavailable.
        const visible = () =>
          buttons.filter((b) => b.offsetParent !== null && !b.disabled);
        const setStop = (target) => {
          for (const b of buttons) b.tabIndex = b === target ? 0 : -1;
        };
        // The single tab stop must always be a VISIBLE button — if the stop
        // is hidden (Simplified density, phone-width trims), Tab skips it and
        // the whole toolbar drops out of the keyboard order. Re-pick whenever
        // visibility can change.
        const refreshStop = () => {
          const list = visible();
          if (list.length === 0) return;
          const current = buttons.find((b) => b.tabIndex === 0);
          setStop(list.includes(current) ? current : list[0]);
        };

        setStop(buttons[0]);
        refreshStop();
        document.addEventListener('ui-mode-changed', refreshStop);
        document.addEventListener('classic-density-change', refreshStop);
        // The Alt View's ASCII settings button shows in the look only.
        new MutationObserver(refreshStop).observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['data-ui-variant'],
        });
        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(refreshStop, 200);
        });

        toolbarEl.addEventListener('focusin', (event) => {
          const btn = event.target.closest('button');
          if (btn && buttons.includes(btn)) setStop(btn);
        });
        toolbarEl.addEventListener('keydown', (event) => {
          const { key } = event;
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
          const btn = event.target.closest('button');
          if (!btn || !buttons.includes(btn)) return;

          const list = visible();
          if (list.length === 0) return;
          const current = list.indexOf(btn);
          let next;
          if (key === 'Home') next = list[0];
          else if (key === 'End') next = list[list.length - 1];
          else {
            const delta = key === 'ArrowRight' ? 1 : -1;
            next = list[(current + delta + list.length) % list.length];
          }
          event.preventDefault();
          setStop(next);
          next.focus();
        });
      };

      wireRovingToolbar(classicToolbar);
      wireRovingToolbar(document.getElementById('classicCameraBar'));
    }

    // Initialize mobile drawer controller
    initDrawerController();

    // Initialize image measurement tool
    initImageMeasurement({
      onCoordinateCopied: (axis, value) => {
        // Populate focused parameter field with copied coordinate
        const active = document.activeElement;
        if (
          active &&
          active.tagName === 'INPUT' &&
          (active.type === 'number' || active.type === 'text')
        ) {
          active.value = value;
          active.dispatchEvent(new Event('input', { bubbles: true }));
          active.dispatchEvent(new Event('change', { bubbles: true }));
        }
      },
    });

    // Wire measurement fullscreen expand/close buttons
    const measureExpandBtn = document.getElementById('measureExpandBtn');
    const measureFsClose = document.getElementById('measureFullscreenClose');
    const measureFsBackdrop = document.getElementById(
      'measureFullscreenBackdrop'
    );
    if (measureExpandBtn)
      measureExpandBtn.addEventListener('click', measureOpenFullscreen);
    if (measureFsClose)
      measureFsClose.addEventListener('click', measureCloseFullscreen);
    if (measureFsBackdrop)
      measureFsBackdrop.addEventListener('click', measureCloseFullscreen);

    // --- Measurement mode toggle (radiogroup with roving tabindex) ---
    const modeBtns = [
      document.getElementById('measureModePoint'),
      document.getElementById('measureModeRuler'),
      document.getElementById('measureModeCalibrate'),
    ].filter(Boolean);
    const modeNames = ['point', 'ruler', 'calibrate'];
    const measureDistRow = document.getElementById('measureDistRow');
    const measureCalibRow = document.getElementById('measureCalibRow');
    const measureCoordRow = document.querySelector('.measure-coord-row');

    function activateModeBtn(idx) {
      modeBtns.forEach((btn, i) => {
        const isActive = i === idx;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-checked', String(isActive));
        btn.tabIndex = isActive ? 0 : -1;
      });
      setMeasureMode(modeNames[idx]);
      // Show/hide mode-specific rows
      if (measureDistRow)
        measureDistRow.classList.toggle('hidden', modeNames[idx] !== 'ruler');
      if (measureCalibRow)
        measureCalibRow.classList.toggle(
          'hidden',
          modeNames[idx] !== 'calibrate'
        );
      if (measureCoordRow)
        measureCoordRow.classList.toggle(
          'hidden',
          modeNames[idx] === 'calibrate'
        );
    }

    modeBtns.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        activateModeBtn(i);
        btn.focus();
      });
      btn.addEventListener('keydown', (e) => {
        let nextIdx = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          nextIdx = (i + 1) % modeBtns.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          nextIdx = (i - 1 + modeBtns.length) % modeBtns.length;
        }
        if (nextIdx >= 0) {
          e.preventDefault();
          activateModeBtn(nextIdx);
          modeBtns[nextIdx].focus();
        }
      });
    });

    // --- Measurement scale factor input ---
    const measureScaleInput = document.getElementById('measureScaleInput');
    if (measureScaleInput) {
      const currentSf = getScaleFactor();
      measureScaleInput.value = currentSf > 0 ? currentSf : '';
      measureScaleInput.addEventListener('change', () => {
        setScaleFactor(parseFloat(measureScaleInput.value) || 0);
      });
    }

    // --- Calibration Apply handler ---
    const measureCalibApply = document.getElementById('measureCalibApply');
    const measureCalibMm = document.getElementById('measureCalibMm');
    const measureCalibError = document.getElementById('measureCalibError');
    if (measureCalibApply && measureCalibMm) {
      measureCalibApply.addEventListener('click', () => {
        const pixelDist = getCalibDistancePx();
        const mmVal = parseFloat(measureCalibMm.value);

        // Validation
        if (!pixelDist || pixelDist <= 0) {
          showCalibError('Place two points on the canvas first.');
          return;
        }
        if (!Number.isFinite(mmVal) || mmVal <= 0) {
          showCalibError('Enter a valid distance greater than 0.');
          return;
        }

        hideCalibError();
        const sf = pixelDist / mmVal;
        setScaleFactor(sf);
        announceImmediate(
          `Calibration applied: ${sf.toFixed(2)} pixels per millimeter`
        );

        // Switch back to Ruler mode after calibration
        activateModeBtn(1);
        modeBtns[1]?.focus();
      });
    }

    function showCalibError(msg) {
      if (measureCalibError) {
        measureCalibError.textContent = msg;
        measureCalibError.classList.remove('hidden');
      }
    }
    function hideCalibError() {
      if (measureCalibError) {
        measureCalibError.textContent = '';
        measureCalibError.classList.add('hidden');
      }
    }

    // --- Distance copy and clear handlers ---
    const measureCopyDist = document.getElementById('measureCopyDist');
    const measureClearRuler = document.getElementById('measureClearRuler');
    if (measureCopyDist) {
      measureCopyDist.addEventListener('click', () => {
        const el = document.getElementById('measureDistValue');
        if (el && el.textContent !== '--') {
          navigator.clipboard
            .writeText(el.textContent)
            .then(() => {
              announceImmediate(`Distance ${el.textContent} copied`);
            })
            .catch(() => {});
        }
      });
    }
    if (measureClearRuler) {
      measureClearRuler.addEventListener('click', () => clearRulerPoints());
    }

    // --- Shared Image Store: intercept measurement uploads ---
    const measureFileInput = document.getElementById('measureFileInput');
    const measureImageSelect = document.getElementById('measureImageSelect');
    if (measureFileInput) {
      measureFileInput.addEventListener('change', async (e) => {
        try {
          const file = e.target.files?.[0];
          if (!file || !file.type.startsWith('image/')) return;
          const record = await SharedImageStore.addImage(file);

          // Persist screenshot to projectFiles under Screenshots/ folder
          const state = stateManager.getState();
          let { projectFiles, mainFilePath, uploadedFile: uf } = state;

          // Initialize projectFiles Map if needed (single-file → multi-file)
          if (!projectFiles && uf) {
            projectFiles = new Map();
            const mainPath = mainFilePath || uf.name;
            projectFiles.set(mainPath, uf.content);
            mainFilePath = mainPath;
            stateManager.setState({ projectFiles, mainFilePath });
            setCanonicalProjectFiles(projectFiles);
          }

          if (projectFiles) {
            projectFiles.set(`Screenshots/${record.name}`, record.dataUrl);
            stateManager.setState({ projectFiles });
            setCanonicalProjectFiles(projectFiles);

            // Auto-save to IndexedDB so screenshots persist across sessions
            await companionFilesCtrl.autoSaveCompanionFiles();
          }

          // Select the newly uploaded image in the dropdown
          if (measureImageSelect) {
            measureImageSelect.value = String(record.id);
            if (measureImageSelect.value !== String(record.id)) {
              setTimeout(() => {
                if (measureImageSelect)
                  measureImageSelect.value = String(record.id);
              }, 0);
            }
          }

          // Update overlay dropdown with the new screenshot
          overlayGridCtrl.updateOverlaySourceDropdown();
        } catch (err) {
          console.error('[App] Measurement image upload failed:', err);
        }
      });
    }
    // Populate image recall dropdown when store changes
    SharedImageStore.onImagesChange(() => {
      const imgs = SharedImageStore.getImages();
      if (measureImageSelect) {
        const currentVal = measureImageSelect.value;
        measureImageSelect.innerHTML =
          '<option value="">-- No screenshots --</option>';
        for (const [id, rec] of imgs) {
          const opt = document.createElement('option');
          opt.value = id;
          opt.textContent = `${rec.name} (${rec.width}\u00d7${rec.height})`;
          measureImageSelect.appendChild(opt);
        }
        measureImageSelect.value = currentVal;
      }
      // Also add shared images to overlay source dropdown
      if (overlaySourceSelect) {
        // Preserve current selection before removing/re-adding shared options
        const overlayCurrentVal = overlaySourceSelect.value;
        // Remove previous shared-image entries
        for (const opt of [...overlaySourceSelect.options]) {
          if (opt.dataset.shared) opt.remove();
        }
        for (const [, rec] of imgs) {
          const opt = document.createElement('option');
          opt.value = `screenshot:${rec.name}`;
          opt.textContent = `\uD83D\uDCF7 ${rec.name}`;
          opt.dataset.shared = '1';
          overlaySourceSelect.appendChild(opt);
        }
        // Restore selection (removing the old option cleared it)
        if (overlayCurrentVal) {
          overlaySourceSelect.value = overlayCurrentVal;
        }
      }
    });
    if (measureImageSelect) {
      measureImageSelect.addEventListener('change', () => {
        const id = parseInt(measureImageSelect.value, 10);
        if (!id) return;
        const rec = SharedImageStore.getImages().get(id);
        if (rec) loadImageFromDataURL(rec.dataUrl, rec.name);
      });
    }

    // --- Unit Sync: wire both unit selects ---
    const measureUnitSelect = document.getElementById('measureUnitSelect');
    const overlayUnitSelect = document.getElementById('overlayUnitSelect');
    const scaleFactorInput = document.getElementById('scaleFactorInput');
    const overlaySizeUnit = document.getElementById('overlaySizeUnit');
    const overlayOffsetUnit = document.getElementById('overlayOffsetUnit');

    // Set initial state from persisted values
    if (measureUnitSelect) measureUnitSelect.value = getUnit();
    if (overlayUnitSelect) overlayUnitSelect.value = getUnit();
    if (scaleFactorInput && getScaleFactor() > 0)
      scaleFactorInput.value = getScaleFactor();

    function updateUnitLabels(unit) {
      if (overlaySizeUnit) overlaySizeUnit.textContent = unit;
      if (overlayOffsetUnit) overlayOffsetUnit.textContent = unit;
      const overlayWidthInput = document.getElementById('overlayWidthInput');
      const overlayHeightInput = document.getElementById('overlayHeightInput');
      const overlayOffsetXInput = document.getElementById(
        'overlayOffsetXInput'
      );
      const overlayOffsetYInput = document.getElementById(
        'overlayOffsetYInput'
      );
      if (overlayWidthInput)
        overlayWidthInput.setAttribute(
          'aria-label',
          `Overlay width in ${unit === 'mm' ? 'millimeters' : 'pixels'}`
        );
      if (overlayHeightInput)
        overlayHeightInput.setAttribute(
          'aria-label',
          `Overlay height in ${unit === 'mm' ? 'millimeters' : 'pixels'}`
        );
      if (overlayOffsetXInput)
        overlayOffsetXInput.setAttribute(
          'aria-label',
          `Overlay X offset in ${unit === 'mm' ? 'millimeters' : 'pixels'}`
        );
      if (overlayOffsetYInput)
        overlayOffsetYInput.setAttribute(
          'aria-label',
          `Overlay Y offset in ${unit === 'mm' ? 'millimeters' : 'pixels'}`
        );
    }

    function syncBothUnitSelects(unit) {
      if (measureUnitSelect) measureUnitSelect.value = unit;
      if (overlayUnitSelect) overlayUnitSelect.value = unit;
      updateUnitLabels(unit);
    }

    if (measureUnitSelect) {
      measureUnitSelect.addEventListener('change', () =>
        setUnit(measureUnitSelect.value)
      );
    }
    if (overlayUnitSelect) {
      overlayUnitSelect.addEventListener('change', () =>
        setUnit(overlayUnitSelect.value)
      );
    }
    if (scaleFactorInput) {
      scaleFactorInput.addEventListener('change', () => {
        setScaleFactor(parseFloat(scaleFactorInput.value) || 0);
      });
    }

    onUnitChange(({ unit }) => syncBothUnitSelects(unit));
    onScaleChange(({ scaleFactor: sf }) => {
      if (scaleFactorInput && sf > 0) scaleFactorInput.value = sf;
      if (measureScaleInput && sf > 0) measureScaleInput.value = sf;
    });

    // Initialize preview settings drawer (overlay with resize functionality)
    initPreviewSettingsDrawer({
      onResize: () => {
        if (previewManager) {
          previewManager.handleResize();
        }
      },
    });

    // The Alt View's Lens drawer on a desktop, risen from the actions bar.
    initLensDrawer();

    // Initialize camera panel controller (right-side drawer)
    cameraPanelController = initCameraPanelController({
      previewManager: null, // Will be set after preview manager is initialized
      onPanControl: (params) => hfmCtrl.onPanControl(params),
    });

    // Dev bypass: check localStorage or URL param before sequence detector
    const HFM_UNLOCK_KEY = 'openscad-customizer-hfm-unlock';
    const devUnlockFlag = localStorage.getItem(HFM_UNLOCK_KEY) === 'true';
    const urlParams = new URLSearchParams(window.location.search);
    const urlUnlock = urlParams.get('hfm') === 'unlock';

    if (urlUnlock) {
      // Strip param to avoid accidental sharing
      urlParams.delete('hfm');
      /**
       * Keep the fragment: composing the new URL from pathname and query alone
       * would make a link of the form `/?hfm=unlock#v=1&params=...` lose its
       * payload the moment it opened.
       *
       * `cleanUrlKeepingFragment()` is not a drop-in here, which is why this is
       * a line rather than a call. That helper closes over `initUrlParams`, a
       * different URLSearchParams built at module start; this block deletes
       * `hfm` from its own local copy. Calling the helper would compose from a
       * copy that still has `hfm` and put the parameter straight back. Making
       * the helper usable would mean mutating a module-level object other code
       * reads afterwards.
       *
       * The arithmetic lives in two places, and that is the stated cost.
       */
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;
      history.replaceState(null, '', newUrl);
    }

    if (devUnlockFlag || urlUnlock) {
      hfmCtrl.handleUnlock();
    }

    // Initialize input sequence detector (still works for non-dev users).
    // Finding it this way is the moment the key's arrival is for.
    initSequenceDetector(() => hfmCtrl.handleUnlock({ moment: true }));

    // Expose DevTools helper for manual unlock
    window.__unlockAltView = () => {
      localStorage.setItem(HFM_UNLOCK_KEY, 'true');
      hfmCtrl.handleUnlock();
      return 'Alt View unlocked. Refresh to persist.';
    };

    // ASCII City Walk: the gated welcome card's launch button. The
    // game module is lazy-loaded on first press so it costs nothing until
    // someone who found the unlock actually plays.
    const cityWalkLaunchBtn = document.getElementById('cityWalkLaunchBtn');
    if (cityWalkLaunchBtn) {
      // The game is desktop-only, gated on the same viewport shape as Classic.
      // Entry only: a session already running survives any resize, and Escape
      // always leaves.
      const cityWalkGateReason = () =>
        document
          .getElementById('cityWalkGateReason')
          ?.textContent.replace(/\s+/g, ' ')
          .trim();

      const updateCityWalkGate = () => {
        const gated = !isViewportDesktopShaped();
        const reasonEl = document.getElementById('cityWalkGateReason');
        // Shown, not sr-only: on a phone there is no hover tooltip, so the
        // reason has to be on the card for a sighted player to read.
        if (reasonEl) reasonEl.hidden = !gated;
        if (gated) {
          cityWalkLaunchBtn.setAttribute('aria-disabled', 'true');
          cityWalkLaunchBtn.setAttribute(
            'aria-describedby',
            'cityWalkGateReason'
          );
          const reason = cityWalkGateReason();
          if (reason) {
            cityWalkLaunchBtn.setAttribute(
              'title',
              `Enter the City. ${reason}`
            );
          }
        } else {
          cityWalkLaunchBtn.removeAttribute('aria-disabled');
          cityWalkLaunchBtn.removeAttribute('aria-describedby');
          cityWalkLaunchBtn.removeAttribute('title');
        }
      };
      updateCityWalkGate();
      subscribeViewportShape(updateCityWalkGate);

      cityWalkLaunchBtn.addEventListener('click', async (event) => {
        // Gated means aria-disabled, not disabled: the click still arrives, so
        // say why rather than doing nothing (the Classic toggle's pattern).
        if (cityWalkLaunchBtn.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          const reason = cityWalkGateReason();
          announceImmediate(
            reason
              ? `ASCII City Walk unavailable. ${reason}`
              : 'ASCII City Walk unavailable.'
          );
          return;
        }
        // No disabled toggle here: disabling the button would blur it, and
        // the controller restores focus to this trigger on exit. Re-entry is
        // already guarded by the controller's session singleton.
        try {
          const mod = await import('./js/game/city-walk-controller.js');
          await mod.launchCityWalk({
            hfmCtrl,
            triggerEl: cityWalkLaunchBtn,
            walkMapping,
            openControllerSettings: ({ onClose }) =>
              openShortcutsDialog({ tab: 'controller', onClose }),
            joyConChoice: () => joyConChoice,
          });
        } catch (error) {
          console.error('[CityWalk] Failed to launch:', error);
          _announceError('The game could not be started. Please try again.');
        }
      });
    }

    // Expose startTutorial globally for E2E test automation
    window.startTutorial = (tutorialId) => startTutorial(tutorialId);

    // On a phone the bar is fixed over the page; leave it its real height.
    reserveSpaceForActionsBar(document.getElementById('actionsBar'));

    // Collapse details sections on mobile by default
    const initMobileDetailsCollapse = () => {
      if (window.innerWidth >= 768) return;

      const detailsToCollapse = ['.advanced-menu'];

      detailsToCollapse.forEach((selector) => {
        const el = document.querySelector(selector);
        if (el && el.tagName === 'DETAILS') {
          el.removeAttribute('open');
        }
      });
    };

    // Call on load
    initMobileDetailsCollapse();

    // The split is built the moment the panel opens, so it opens straight to
    // the width it had instead of opening wider or narrower and then jumping,
    // and taken down once it has closed.
    paramPanelLayout = (open) => (open ? initSplit() : destroySplit());

    // Handle window resize - destroy/reinit split on mobile
    let splitResizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(splitResizeTimeout);
      splitResizeTimeout = setTimeout(() => {
        if (window.innerWidth < 768) {
          destroySplit();
        } else if (
          !splitInstance &&
          !paramPanel.classList.contains('collapsed')
        ) {
          initSplit();
        }
      }, 150);
    });
  }

  // Focus Mode - Maximize 3D preview
  const focusModeBtn = document.getElementById('focusModeBtn');
  // The phone's bottom drawers: Camera, and the Alt View's ASCII drawer,
  // which opens into the same space.
  const phoneDrawers = ['cameraDrawer', 'asciiDrawer']
    .map((id) => document.getElementById(id))
    .filter(Boolean);

  if (focusModeBtn && mainInterface) {
    let isFocusMode = false;
    let cameraFocusExitBtn = null;
    // Assigned below; used by the camera focus exit button handler.
    let toggleFocusMode = () => {};

    /**
     * Check if we're in mobile portrait mode
     */
    const isMobilePortrait = () => {
      return (
        window.innerWidth <= 480 &&
        window.matchMedia('(orientation: portrait)').matches
      );
    };

    /**
     * Whether a bottom drawer is open (layout.css splits the screen for it)
     */
    const isCameraDrawerExpanded = () =>
      phoneDrawers.some(
        (drawer) => !drawer.hidden && !drawer.classList.contains('collapsed')
      );

    /**
     * Create floating exit button for camera focus mode
     */
    const createCameraFocusExitBtn = () => {
      if (cameraFocusExitBtn) return cameraFocusExitBtn;

      cameraFocusExitBtn = document.createElement('button');
      cameraFocusExitBtn.id = 'cameraFocusExitBtn';
      cameraFocusExitBtn.className = 'btn camera-focus-exit-btn';
      cameraFocusExitBtn.setAttribute('aria-label', 'Exit focus mode');
      cameraFocusExitBtn.title = 'Exit focus mode (Esc)';
      cameraFocusExitBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
        </svg>
        <span>Exit</span>
      `;

      cameraFocusExitBtn.addEventListener('click', () => toggleFocusMode());

      // Insert after the main interface
      document.getElementById('app').appendChild(cameraFocusExitBtn);

      return cameraFocusExitBtn;
    };

    /**
     * Enter camera focus mode (mobile portrait with camera drawer open)
     */
    const enterCameraFocusMode = () => {
      mainInterface.classList.add('camera-focus-mode');
      createCameraFocusExitBtn();
    };

    /**
     * Exit camera focus mode
     */
    const exitCameraFocusMode = () => {
      mainInterface.classList.remove('camera-focus-mode');
    };

    /**
     * Update camera focus mode state based on current conditions
     */
    const updateCameraFocusMode = () => {
      if (isFocusMode && isMobilePortrait() && isCameraDrawerExpanded()) {
        enterCameraFocusMode();
      } else {
        exitCameraFocusMode();
      }
    };

    // Toggle focus mode
    toggleFocusMode = function () {
      isFocusMode = !isFocusMode;

      if (isFocusMode) {
        // Enter focus mode
        mainInterface.classList.add('focus-mode');
        // The name keeps its visible word; the pressed state says on or off.
        focusModeBtn.setAttribute('aria-pressed', 'true');
        focusModeBtn.title = 'Full Screen is on (Esc leaves it)';

        // Check for camera focus mode (mobile portrait + camera drawer open)
        updateCameraFocusMode();
      } else {
        // Exit focus mode
        mainInterface.classList.remove('focus-mode');
        exitCameraFocusMode();
        focusModeBtn.setAttribute('aria-pressed', 'false');
        focusModeBtn.title = 'Full Screen (the preview alone)';
      }

      // Trigger preview resize after mode change
      setTimeout(() => {
        if (previewManager) {
          previewManager.handleResize();
        }
      }, 100);
    };

    // Add click listener
    focusModeBtn.addEventListener('click', toggleFocusMode);

    // A drawer opening or closing in Full Screen changes the split
    if (phoneDrawers.length) {
      const drawerObserver = new MutationObserver((mutations) => {
        if (!mutations.some((m) => m.attributeName === 'class')) return;
        updateCameraFocusMode();
        if (isFocusMode) {
          // The canvas follows its box once the layout has settled
          setTimeout(() => previewManager?.handleResize(), 150);
        }
      });
      phoneDrawers.forEach((drawer) =>
        drawerObserver.observe(drawer, { attributes: true })
      );
    }

    // Watch for window resize/orientation changes
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isFocusMode) {
          updateCameraFocusMode();
        }
      }, 150);
    });

    // Add Escape key listener to exit focus mode
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isFocusMode) {
        // Only exit focus mode if no modals are open
        const modals = document.querySelectorAll('.modal:not(.hidden)');
        if (modals.length === 0) {
          toggleFocusMode();
        }
      }
    });
  }

  // Set by the Cancel button: the next canceled Generate was asked to stop,
  // and is not an error.
  let generateCanceled = false;

  // Primary Action Button (transforms between Generate and Download)
  primaryActionBtn.addEventListener('click', async () => {
    const action = primaryActionBtn.dataset.action;
    const state = stateManager.getState();

    if (action === 'download') {
      // Get selected output format
      const outputFormat =
        outputFormatSelect?.value || state.outputFormat || 'stl';

      // Download action - get full quality file from auto-preview controller
      const fullSTL = autoPreviewController?.getCurrentFullSTL(
        state.parameters
      );

      if (fullSTL && outputFormat === 'stl') {
        // Use cached full quality STL
        const filename = resolveDownloadFilename(
          state.uploadedFile.name,
          state.parameters,
          outputFormat,
          getBrailleDownloadName()
        );
        downloadFile(fullSTL.stl, filename, outputFormat);
        updateStatus(`Downloaded: ${filename}`);
        return;
      }

      // Fallback to state.stl
      if (!state.stl) {
        showErrorToast({
          title: 'Nothing to Download',
          message: 'No file has been generated yet. Click Generate first.',
        });
        return;
      }

      const filename = resolveDownloadFilename(
        state.uploadedFile.name,
        state.parameters,
        outputFormat,
        getBrailleDownloadName()
      );

      downloadFile(state.stl, filename, outputFormat);
      updateStatus(`Downloaded: ${filename}`);
      refreshFolderSaveAffordances();
      return;
    }

    await runFullRender();
    refreshFolderSaveAffordances();
  });

  /**
   * The full-quality render with all of its UI side effects and no download
   * side effect. Every Render surface (Design ▸ Render, the rebindable
   * `render` shortcut and both Classic Render buttons) calls this rather
   * than the primaryActionBtn handler, which means Generate or Download
   * depending on state the user cannot see: through it, pressing Render
   * would trigger an STL save prompt whenever a full render was cached.
   */
  async function runFullRender() {
    // Publish the editor's pending write-back first, or pressing Render
    // within 500 ms of typing would render the content from before the edit.
    // Every Preview button publishes first too.
    publishEditorEdits();
    const state = stateManager.getState();

    if (!state.uploadedFile) {
      showErrorToast({
        title: 'No File Uploaded',
        message: 'Upload a .scad or .zip file first.',
      });
      return;
    }

    if (!renderController) {
      showErrorToast({
        title: 'Engine Not Ready',
        message:
          'The OpenSCAD engine has not initialized. Please wait or refresh the page.',
      });
      return;
    }

    try {
      // Get selected output format
      let outputFormat = outputFormatSelect?.value || 'stl';
      let formatName =
        OUTPUT_FORMATS[outputFormat]?.name || outputFormat.toUpperCase();

      // Show cancel button, and move focus there before this button
      // disables itself: the render can be stopped from where focus is.
      generateCanceled = false;
      cancelRenderBtn.classList.remove('hidden');
      disableKeepingFocus(primaryActionBtn, cancelRenderBtn);
      setButtonTextWithIcon(
        primaryActionBtn,
        'wait',
        `Generating ${formatName}...`
      );
      // The name follows the words while it generates; the state function
      // sets it back with the words when the render ends.
      primaryActionBtn.setAttribute(
        'aria-label',
        `Generating ${formatName} file from current parameters`
      );

      // Disable undo/redo during rendering to prevent state mismatches
      stateManager.setHistoryEnabled(false);

      // Cancel any pending preview renders
      if (autoPreviewController) {
        autoPreviewController.cancelPending();
      }

      updatePreviewStateUI(PREVIEW_STATE.RENDERING);

      // Show render time estimate for complex models. Low-confidence
      // estimates suppress the number (too unpredictable to be honest).
      const estimate = estimateRenderTime(
        state.uploadedFile.content,
        state.parameters
      );
      // "Generating" is said once a Generate: by the estimate when there is
      // one, else by the first progress report.
      let generatingSaid = false;
      if (estimate.seconds >= 5 || estimate.warning) {
        const estimateMsg =
          estimate.confidence === 'low'
            ? `Generating ${formatName}... (complex model, may take a while)`
            : `Generating ${formatName}... (estimated ~${estimate.seconds}s)`;
        if (estimate.warning) {
          console.warn('[Render] Complexity warning:', estimate.warning);
        }
        updateStatus(estimateMsg);
        generatingSaid = true;
      }

      const startTime = Date.now();

      let result;

      // Auto-detect 2D parameters with a 3D format and switch to SVG.
      // When the user has e.g. generate="first layer for SVG/DXF file" but
      // the format dropdown is still STL, rendering will fail with MODEL_IS_2D.
      // Proactively switch to SVG so the render succeeds.
      if (
        !OUTPUT_FORMATS[outputFormat]?.is2D &&
        typeof state.parameters?.generate === 'string' &&
        /svg|dxf|2d|first layer/i.test(state.parameters.generate)
      ) {
        outputFormat = 'svg';
        formatName = 'SVG';
        if (outputFormatSelect) {
          outputFormatSelect.value = 'svg';
          outputFormatSelect.dispatchEvent(new Event('change'));
        }
        stateManager.setState({ outputFormat: 'svg' });
        updateStatus('Generating SVG… (auto-switched from STL for 2D output)');
      }

      // Use auto-preview controller for full render if available (STL only for now)
      if (autoPreviewController && outputFormat === 'stl') {
        result = await autoPreviewController.renderFull(state.parameters, {
          ...(exportQualityPreset ? { quality: exportQualityPreset } : {}),
        });

        if (result.cached) {
          console.log('[Download] Using cached full quality render');
        }
      } else {
        // Direct render with specified format
        // Pass files/mainFile/libraries for multi-file projects
        const libsForRender = getEnabledLibrariesForRender();
        // For 2D formats, propose schema-aware parameter changes so models
        // that require a specific 'generate' (or equivalent) value produce
        // 2D geometry — applied only with the consent checkbox checked.
        const proposal = propose2DExportChanges(
          state.parameters,
          state.schema,
          outputFormat,
          state.projectFiles
        );
        const renderParameters =
          proposal.changes.length > 0 && is2DAdjustmentsConsented()
            ? proposal.resolvedParameters
            : state.parameters;
        const renderOptions = {
          outputFormat,
          paramTypes: state.paramTypes || {},
          files: state.projectFiles,
          mainFile: state.mainFilePath,
          libraries: libsForRender,
          ...(exportQualityPreset ? { quality: exportQualityPreset } : {}),
          onProgress: (_percent, _message) => {
            const fn =
              OUTPUT_FORMATS[outputFormat]?.name || outputFormat.toUpperCase();
            updateStatus(`Generating ${fn}...`, 'default', {
              announce: !generatingSaid,
            });
            generatingSaid = true;
          },
        };
        try {
          result = await renderController.renderFull(
            state.uploadedFile.content,
            renderParameters,
            renderOptions
          );
        } catch (renderErr) {
          if (renderErr.code === 'MODEL_NOT_2D') {
            const proceed = await confirmProjectionFallback(outputFormat);
            if (!proceed) {
              updateStatus(`${formatName} export canceled`);
              updatePreviewStateUI(PREVIEW_STATE.STALE);
              return;
            }
            updateStatus(
              `Projecting 3D mesh to approximate ${outputFormat.toUpperCase()}...`
            );
            result = await renderController.render2DFallback(
              state.uploadedFile.content,
              strip2DGenerateForFallback(renderParameters),
              renderOptions
            );
          } else {
            throw renderErr;
          }
        }
      }

      const duration = ((Date.now() - startTime) / 1000).toFixed(1);

      // Store the hash of parameters used for this generation
      lastGeneratedParamsHash = hashParams(state.parameters);

      const outputData = result.data || result.stl;
      const resolvedFormat = result.format || outputFormat;
      if (resolvedFormat === 'stl' && looksLikeOffGeometry(outputData)) {
        throw new Error(
          'Internal error: the renderer returned OFF geometry where STL was expected. Please try generating again.'
        );
      }
      stateManager.setState({
        generatedOutput: {
          data: outputData,
          format: resolvedFormat,
          stats: result.stats,
          paramsHash: lastGeneratedParamsHash,
        },
        stl: outputData,
        outputFormat: resolvedFormat,
        stlStats: result.stats,
        lastRenderTime: duration,
      });

      // Display the result in the preview panel.
      // 2D formats (SVG) get a native rendered SVG viewer;
      // 3D formats are loaded into the Three.js viewer.
      const is2DFormat = OUTPUT_FORMATS[outputFormat]?.is2D;
      if (is2DFormat && resolvedFormat === 'svg' && previewManager) {
        try {
          let svgText =
            typeof outputData === 'string'
              ? outputData
              : new TextDecoder().decode(outputData);

          // Pre-inject F6-render parity styling (see state-colors.js).
          svgText = svgText.replace(
            /(<svg[^>]*>)/i,
            '$1' + build2DPreviewStyleTag('rendered')
          );

          if (typeof previewManager.show2DPreviewAs3DPlane === 'function') {
            await previewManager.show2DPreviewAs3DPlane(svgText, {
              mode: 'rendered',
            });
          } else {
            previewManager.show2DPreview(svgText, { mode: 'rendered' });
          }
        } catch (previewErr) {
          console.warn('[Generate] Failed to show 2D preview:', previewErr);
        }
      } else if (
        !autoPreviewController &&
        previewManager &&
        outputData &&
        !is2DFormat
      ) {
        try {
          if (previewManager.setRenderState) {
            previewManager.setRenderState(null);
          }
          previewManager.hide2DPreview();
          await previewManager.loadSTL(outputData, { preserveCamera: false });
          hfmCtrl.clearPersistence();
        } catch (loadErr) {
          console.warn('[Generate] Failed to load STL into preview:', loadErr);
        }
      } else if (previewManager && !is2DFormat) {
        previewManager.hide2DPreview();
      }

      // Store console output for the Console panel (echo/warning/error display)
      if (
        result.consoleOutput &&
        typeof window.updateConsoleOutput === 'function'
      ) {
        window.updateConsoleOutput(result.consoleOutput);
      }

      // Update detailed stats in drawer (not in status bar overlay)
      const triangleInfo =
        result.stats.triangles > 0
          ? ` | Triangles: ${result.stats.triangles.toLocaleString()}`
          : '';
      statsArea.innerHTML = `<span class="stats-quality full">Full Quality ${formatName}</span> Size: ${formatFileSize(result.stats.size)}${triangleInfo} | Time: ${duration}s`;

      // Update the preview status bar with minimal stats
      updatePreviewStats(result.stats, true);

      console.log('Full render complete:', result.stats);

      // Log performance metrics
      logRenderPerformance(result);

      // Simple status - ready to download (use 'success' type to keep visible)
      // Use correct format name instead of hardcoded "STL"
      updateStatus(`${formatName} ready`, 'success');

      // Update preview state to show full quality
      updatePreviewStateUI(PREVIEW_STATE.CURRENT, {
        stats: result.stats,
        fullQuality: true,
      });
    } catch (error) {
      if (generateCanceled && error?.code === 'RENDER_CANCELLED') {
        // Stopped on purpose: the status says so, and the preview on screen
        // is still the one from before.
        updatePreviewStateUI(
          autoPreviewController?.state ?? PREVIEW_STATE.STALE
        );
        return;
      }
      console.error('Generation failed:', error);
      updatePreviewStateUI(PREVIEW_STATE.ERROR, {
        error: error.message,
      });
      if (typeof window.addStructuredError === 'function') {
        window.addStructuredError(error?.message || 'Generation failed');
      }

      // Extract OpenSCAD console output embedded in error.details and surface it
      // to the Console panel. This is how missing-include warnings reach the user.
      if (error.details && typeof window.updateConsoleOutput === 'function') {
        const outputMarker = '[OpenSCAD output]';
        const markerIdx = error.details.indexOf(outputMarker);
        if (markerIdx !== -1) {
          const embeddedOutput = error.details
            .slice(markerIdx + outputMarker.length)
            .trim();
          if (embeddedOutput) {
            window.updateConsoleOutput(embeddedOutput);
          }
        }
      }

      // Special-case: configuration dependency / empty geometry guidance
      if (handleConfigDependencyError(error)) {
        return;
      }

      // Special-case: SVG/DXF export failures — 3D geometry produced when 2D is required.
      // Guide the user to the specific 'generate' parameter that controls the output mode.
      const currentFormat = outputFormatSelect?.value || 'stl';
      if (currentFormat === 'svg' || currentFormat === 'dxf') {
        const msg = (error?.message || '').toLowerCase();
        const is2DGeometryError =
          error?.code === 'MODEL_NOT_2D' ||
          msg.includes('not a 2d') ||
          msg.includes('3d geometry but svg') ||
          msg.includes('3d geometry but dxf') ||
          msg.includes('no geometry') ||
          msg.includes('empty') ||
          msg.includes('missing') ||
          msg.includes('svgcontains no geometry') ||
          msg.includes('svg output is empty') ||
          msg.includes('dxf output is empty');
        if (is2DGeometryError) {
          const currentState = stateManager.getState();
          const schemaParams = currentState.schema?.parameters || {};
          const generateParam = schemaParams.generate;
          const currentParams = currentState.parameters || {};

          // Determine the actual generate value from state (more reliable than echo parsing)
          const actualGenerateValue = currentParams.generate ?? null;

          // Locate the generate parameter in the UI for the "Take me to the setting" button.
          const generateTargetKey = locateParameterKey('generate', {
            labelHint: 'generate',
          });

          let twoDDisplayName = null;
          if (generateParam?.enum) {
            const twoDOption = generateParam.enum.find((entry) => {
              const v = String(
                typeof entry === 'object' ? entry.value : entry
              ).toLowerCase();
              const l =
                typeof entry === 'object' && entry.label
                  ? String(entry.label).toLowerCase()
                  : v;
              return (
                v.includes('svg') ||
                v.includes('dxf') ||
                v.includes('first layer') ||
                l.includes('svg') ||
                l.includes('dxf') ||
                l.includes('first layer')
              );
            });
            if (twoDOption) {
              twoDDisplayName =
                typeof twoDOption === 'object' && twoDOption.label
                  ? twoDOption.label
                  : typeof twoDOption === 'object'
                    ? twoDOption.value
                    : twoDOption;
            }
          }

          // Check if generate is already set to a 2D-compatible value.
          // If so, the issue is a rendering engine limitation, not a settings problem.
          const actualLower = String(actualGenerateValue ?? '').toLowerCase();
          const alreadySet2D =
            actualLower.includes('svg') ||
            actualLower.includes('dxf') ||
            actualLower.includes('first layer');

          const statusLine = `Error: ${currentFormat.toUpperCase()} export requires 2D geometry`;
          if (alreadySet2D) {
            // Said by the error dialog alone. The guidance below stands down
            // when another dialog is open, so its line is still said.
            updateStatus(statusLine, 'error', { announce: false });
            showErrorModal({
              title: `${currentFormat.toUpperCase()} Export Issue`,
              message: `The "${actualGenerateValue}" setting is selected, but the rendering engine could not produce 2D geometry. This can happen due to browser-based rendering limitations with complex models.`,
              suggestion:
                'Re-generate, or export the 3D model as STL and use desktop OpenSCAD for SVG/DXF export.',
            });
          } else {
            updateStatus(statusLine);
            showDependencyGuidanceModal({
              label: 'generate',
              current: actualGenerateValue,
              suggested: twoDDisplayName,
              targetKey: generateTargetKey,
            });
          }
          return;
        }
      }

      // Use COGA-compliant friendly error translation (code-first)
      const friendlyError = translateError(error.message, {
        code: error.code,
        details: error.details,
      });
      // Said once, by the dialog as focus moves into it, the engine's own
      // line read with its technical details: the message panels leave
      // error lines to it.
      updateStatus(`Error: ${friendlyError.title}`, 'error', {
        announce: false,
      });

      showErrorModal({
        title: friendlyError.title,
        message: friendlyError.explanation,
        suggestion: friendlyError.suggestion,
        technical: error.message,
        // Cancel had focus while the render ran, and is gone once it fails.
        returnFocus: primaryActionBtn,
        ...(await engineOffer(error.code, renderWithEngine)),
      });
    } finally {
      primaryActionBtn.disabled = false;
      hideCancelRender();
      // Re-enable undo/redo after rendering
      stateManager.setHistoryEnabled(true);
      // Always restore button to correct state based on current conditions
      updatePrimaryActionButton();
    }
  }

  /**
   * After a failed render, the engine the person picked, then the same
   * render again through the button that started it. An engine that does
   * not start shows its own dialog and offer.
   * @param {string} id
   */
  async function renderWithEngine(id) {
    try {
      await switchEngineTo(await loadEnginesManifest(), id, {
        renderController,
        rerun: async () => primaryActionBtn.click(),
      });
    } catch (error) {
      console.error('[Engine] The engine did not start:', error);
      updateStatus('OpenSCAD engine failed to initialize', 'error', {
        announce: false,
      });
      await showEngineStartFailure(error, renderWithEngine);
    }
  }

  /**
   * Hide Cancel when a render ends. Focus on it goes back to the main
   * button first (enabled again by then), not to the page body.
   */
  function hideCancelRender() {
    if (document.activeElement === cancelRenderBtn) primaryActionBtn.focus();
    cancelRenderBtn.classList.add('hidden');
  }

  // Cancel render button
  cancelRenderBtn.addEventListener('click', () => {
    if (renderController) {
      generateCanceled = true;
      renderController.cancel();
      updateStatus('Generation canceled by user');
      primaryActionBtn.disabled = false;
      hideCancelRender();
      // Re-enable undo/redo after cancellation
      stateManager.setHistoryEnabled(true);
      updatePrimaryActionButton();
    }
  });

  // Fallback download link (for when parameters changed but old STL still exists)
  downloadFallbackLink.addEventListener('click', (e) => {
    e.preventDefault();
    const state = stateManager.getState();

    if (!state.stl) {
      return;
    }

    const filename = resolveDownloadFilename(
      state.uploadedFile.name,
      state.parameters,
      'stl',
      getBrailleDownloadName()
    );

    downloadSTL(state.stl, filename);
    updateStatus(`Downloaded (previous STL): ${filename}`);
  });

  // ========== Publish project ==========

  const publishProjectModal = document.getElementById('publishProjectModal');
  const publishModalClose = document.getElementById('publishModalClose');
  const publishModalOverlay = document.getElementById('publishModalOverlay');
  const publishManifestOutput = document.getElementById(
    'publishManifestOutput'
  );
  const copyManifestBtn = document.getElementById('copyManifestBtn');
  const publishRepoUrl = document.getElementById('publishRepoUrl');
  const publishShareLinkContainer = document.getElementById(
    'publishShareLinkContainer'
  );
  const publishShareLink = document.getElementById('publishShareLink');
  const copyShareLinkBtn = document.getElementById('copyShareLinkBtn');

  /**
   * Generate a forge-manifest.json from the current project state.
   * @returns {Object} Manifest object
   */
  function generateManifestFromProject() {
    const state = stateManager.getState();
    const uiModeController = getUIModeController();
    return buildProjectManifest({
      uploadName: state.uploadedFile?.name || 'design.scad',
      mainFilePath: state.mainFilePath,
      projectFiles: state.projectFiles,
      presetName: state.currentPresetName,
      uiModePrefs: uiModeController.getPreferencesForExport(),
      registryHiddenDefaults: uiModeController
        .getRegistry()
        .filter((p) => p.defaultHiddenInBasic)
        .map((p) => p.id),
    });
  }

  const publishIncludeSettings = document.getElementById(
    'publishIncludeSettings'
  );
  const downloadProjectZipBtn = document.getElementById(
    'downloadProjectZipBtn'
  );
  const copySettingsLinkBtn = document.getElementById('copySettingsLinkBtn');

  // ── The drawing editor's own door ───────────────────────────────────────
  //
  // Two triggers, one picker, one lazily-built editor. The module is imported
  // on first use, so nobody pays for it until they open it, and it stays out
  // of the core bundle's size budget.
  const svgEditFileInput = document.getElementById('svgEditFileInput');
  const editDrawingSpotlightBtn = document.getElementById(
    'editDrawingSpotlightBtn'
  );
  let svgEditEntry = null;

  /** The drawing editor's file picker: the Main Page's spotlight and Design > Open a drawing in the editor. */
  function openDrawingPicker() {
    svgEditFileInput?.click();
  }

  async function getSvgEditEntry() {
    if (svgEditEntry) return svgEditEntry;
    const { createSvgEditEntry } = await import('./js/svg-edit-entry.js');
    svgEditEntry = createSvgEditEntry({
      announce: (message) => announceImmediate(message),
      onError: (message) =>
        showErrorToast({ title: 'Cannot Edit That File', message }),
      // The DXF lane uses the app's own engine as its converter. Calls
      // serialize behind the controller's queue, so a conversion cannot
      // collide with a model render already in flight.
      render: renderController
        ? (scad, params, options) =>
            renderController.render(scad, params, options)
        : null,
    });
    return svgEditEntry;
  }

  // "Open with Forge". An installed app can be registered with the
  // operating system for a set of file types, and the files then arrive here.
  //
  // Wired but inert on purpose. The registration lives in the web app
  // manifest's `file_handlers` member, and that member is not in
  // public/manifest.json: it stays out until an "Open with" has been seen to
  // work from an installed Forge on a real machine. Adding that member is
  // the one edit that makes the path below run.
  //
  // Feature-detected, so this does nothing at all in Firefox or Safari, and
  // nothing in Chrome or Edge until the app is installed.
  (async () => {
    const { initLaunchFiles } = await import('./js/launch-files.js');
    initLaunchFiles({
      // A launched file takes exactly the path an uploaded one takes.
      openDesign: (file) => fileHandler.handleFile(file),
      openDrawing: async (file) => {
        announceImmediate(`Opening ${file.name} in the drawing editor.`);
        const entry = await getSvgEditEntry();
        await entry.openFile(file);
      },
      // A launched file arrives earlier than any upload can - the launch IS
      // the page load - so wait for the engine the same way the deep-link
      // lifecycle does.
      waitUntilReady: () =>
        new Promise((resolve) => {
          if (document.body.getAttribute('data-wasm-ready') === 'true') {
            resolve();
            return;
          }
          const observer = new MutationObserver(() => {
            if (document.body.getAttribute('data-wasm-ready') === 'true') {
              observer.disconnect();
              resolve();
            }
          });
          observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['data-wasm-ready'],
          });
        }),
      onUnsupported: (name) =>
        showErrorToast({
          title: 'Cannot Open That File',
          message: `Forge cannot open ${name}. It works with .scad, .zip, .svg and .dxf files.`,
        }),
    });
  })();

  if (svgEditFileInput) {
    svgEditFileInput.addEventListener('change', async (event) => {
      const file = event.target.files?.[0];
      // Chosen and then chosen again: the input must not remember the last
      // file, or picking the same one twice is silently ignored.
      event.target.value = '';
      if (!file) return;
      announceImmediate(`Opening ${file.name} in the drawing editor.`);
      const entry = await getSvgEditEntry();
      await entry.openFile(file);
    });

    // A drawing sent by a link with no design to put it on waits for
    // this listener before it is handed to the input.
    document.body.dataset.drawingDoorReady = '1';

    if (editDrawingSpotlightBtn) {
      editDrawingSpotlightBtn.addEventListener('click', openDrawingPicker);
    }
  }

  /**
   * The address that reopens the loaded project, without any settings.
   * A design opened from a local file has no such address: nothing on the web
   * can fetch it, and saying so is better than composing a link that 404s.
   * @returns {string|null}
   */
  function projectReopenUrl() {
    const state = stateManager.getState();
    const forgeBase = window.location.origin + window.location.pathname;
    const manifestUrl = state.manifestOrigin?.url;
    if (manifestUrl) {
      return `${forgeBase}?manifest=${encodeURIComponent(manifestUrl)}`;
    }
    const exampleKey = fileHandler.getCurrentExampleKey?.();
    if (exampleKey) {
      return `${forgeBase}?example=${encodeURIComponent(exampleKey)}`;
    }
    return null;
  }

  /**
   * Copy text, or hand it over to copy by hand when the browser refuses.
   * @param {string} text
   * @returns {Promise<boolean>} True only when the text reached the
   *   clipboard, so a caller says "copied" only then
   */
  async function copyTextWithFallback(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_err) {
      return showTextToCopyDialog(text);
    }
  }

  const saveToFolderBtn = document.getElementById('saveToFolderBtn');
  if (saveToFolderBtn) {
    saveToFolderBtn.addEventListener('click', async () => {
      if (isBusy(saveToFolderBtn)) return;
      const state = stateManager.getState();
      if (!state.stl || !state.uploadedFile) {
        showErrorToast({
          title: 'Nothing to Save',
          message: 'No file has been generated yet. Click Generate first.',
        });
        return;
      }
      const outputFormat =
        document.getElementById('outputFormat')?.value ||
        state.outputFormat ||
        'stl';
      // The same bytes the Download button would hand over. Saving to the
      // folder must never mean rendering the model a second time.
      const fileName = resolveDownloadFilename(
        state.uploadedFile.name,
        state.parameters,
        outputFormat,
        getBrailleDownloadName()
      );
      setBusy(saveToFolderBtn, true);
      try {
        await folderSaveActions.saveExport({
          fileName,
          data: state.stl,
          mainFilePath: state.mainFilePath,
        });
      } finally {
        setBusy(saveToFolderBtn, false);
      }
    });
  }

  if (copySettingsLinkBtn) {
    copySettingsLinkBtn.addEventListener('click', async () => {
      const state = stateManager.getState();
      if (!state.uploadedFile) {
        showErrorToast({
          title: 'No File Loaded',
          message: 'Upload a .scad or .zip file first.',
        });
        return;
      }

      const fragment = stateManager.getShareFragment();
      const base = projectReopenUrl();
      const link = `${base || window.location.origin + window.location.pathname}${fragment}`;
      if (!(await copyTextWithFallback(link))) return;

      if (!base) {
        updateStatus(
          'Link copied. It carries your settings only, so whoever opens it ' +
            'needs to load this design first.'
        );
      } else if (!fragment) {
        updateStatus(
          'Link copied. Everything is at its default value, so it opens the ' +
            'design as its author left it.'
        );
      } else {
        updateStatus('Link copied. It opens this design with your settings.');
      }
    });
  }

  /** File > Publish: the manifest dialog for the loaded project. */
  function openPublishDialog() {
    if (!publishProjectModal) return;
    const state = stateManager.getState();
    if (!state.uploadedFile) {
      showErrorToast({
        title: 'No File Loaded',
        message: 'Upload a .scad or .zip file first.',
      });
      return;
    }

    const manifest = generateManifestFromProject();

    // Check the emitted manifest against the same validator the loader runs,
    // so the dialog can never hand out a manifest its own loader refuses.
    const validation = validateManifest(manifest);
    if (!validation.valid) {
      showErrorToast({
        title: 'Manifest Not Generated',
        message:
          `This project produced a manifest the loader would refuse: ` +
          `${validation.errors.join(' ')} Nothing was copied. Please report ` +
          `this so it can be fixed.`,
      });
      return;
    }

    const manifestJson = JSON.stringify(manifest, null, 2);

    if (publishManifestOutput) {
      publishManifestOutput.textContent = manifestJson;
    }

    // Reset the shareable link section
    if (publishShareLinkContainer) {
      publishShareLinkContainer.classList.add('hidden');
    }
    if (publishRepoUrl) {
      publishRepoUrl.value = '';
    }
    if (publishIncludeSettings) {
      publishIncludeSettings.checked = false;
    }

    openModal(publishProjectModal);
  }

  if (publishProjectModal) {
    // Close handlers
    if (publishModalClose) {
      publishModalClose.addEventListener('click', () =>
        closeModal(publishProjectModal)
      );
    }
    if (publishModalOverlay) {
      publishModalOverlay.addEventListener('click', () =>
        closeModal(publishProjectModal)
      );
    }
    // Escape closes from anywhere inside, its address field included.
    publishProjectModal?.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeModal(publishProjectModal);
    });

    // Copy manifest button
    if (copyManifestBtn && publishManifestOutput) {
      copyManifestBtn.addEventListener('click', async () => {
        if (!(await copyTextWithFallback(publishManifestOutput.textContent))) {
          return;
        }
        const textSpan =
          copyManifestBtn.querySelector('.btn-text') || copyManifestBtn;
        const original = textSpan.textContent;
        textSpan.textContent = 'Copied!';
        setTimeout(() => {
          textSpan.textContent = original;
        }, 2000);
        updateStatus('Manifest copied to clipboard');
      });
    }

    // Generate shareable link when repo URL changes
    if (publishRepoUrl && publishShareLink && publishShareLinkContainer) {
      const composeShareLink = () => {
        let baseUrl = publishRepoUrl.value.trim();
        if (!baseUrl) {
          publishShareLinkContainer.classList.add('hidden');
          return;
        }

        // Ensure trailing slash
        if (!baseUrl.endsWith('/')) {
          baseUrl += '/';
        }

        const manifestUrl = `${baseUrl}forge-manifest.json`;
        const forgeBase = window.location.origin + window.location.pathname;
        // The same serializer the address bar uses, so a copied link and the
        // address bar can never mean different things.
        const fragment = publishIncludeSettings?.checked
          ? stateManager.getShareFragment()
          : '';
        const shareUrl = `${forgeBase}?manifest=${encodeURIComponent(manifestUrl)}${fragment}`;

        publishShareLink.value = shareUrl;
        publishShareLinkContainer.classList.remove('hidden');
      };

      publishRepoUrl.addEventListener('input', composeShareLink);
      if (publishIncludeSettings) {
        publishIncludeSettings.addEventListener('change', composeShareLink);
      }
    }

    // Download the whole project as one archive
    if (downloadProjectZipBtn) {
      downloadProjectZipBtn.addEventListener('click', async () => {
        const state = stateManager.getState();
        if (!state.uploadedFile) {
          showErrorToast({
            title: 'No File Loaded',
            message: 'Upload a .scad or .zip file first.',
          });
          return;
        }

        try {
          const uiModeController = getUIModeController();
          // asBundle: false - the archive ships the project unpacked beside
          // its manifest, so the manifest has to name loose files even when
          // the project itself arrived as a ZIP.
          const manifest = buildProjectManifest({
            uploadName: state.uploadedFile?.name || 'design.scad',
            mainFilePath: state.mainFilePath,
            projectFiles: state.projectFiles,
            presetName: state.currentPresetName,
            uiModePrefs: uiModeController.getPreferencesForExport(),
            registryHiddenDefaults: uiModeController
              .getRegistry()
              .filter((panel) => panel.defaultHiddenInBasic)
              .map((panel) => panel.id),
            asBundle: false,
          });

          const provenance = buildProvenance({
            manifestUrl: state.manifestOrigin?.url || null,
            projectName: manifest.name,
            author: state.manifestOrigin?.author || null,
            appVersion: __APP_VERSION__,
            presetName: state.currentPresetName,
            parameters: stateManager.collectNonDefaultParameters() || {},
            generatedAt: new Date().toISOString(),
          });

          const entries = buildProjectZipEntries({
            projectFiles: state.projectFiles,
            mainFilePath: manifest.files.main,
            mainContent: state.uploadedFile?.content ?? state.scadContent,
            manifest,
            provenance,
          });

          const { default: JSZip } = await import('jszip');
          const zip = new JSZip();
          for (const entry of entries) {
            zip.file(entry.path, entry.content, { base64: entry.base64 });
          }
          const blob = await zip.generateAsync({ type: 'blob' });

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${manifest.name || 'project'}.zip`;
          link.click();
          URL.revokeObjectURL(url);

          updateStatus(`Project archive downloaded, ${entries.length} files.`);
        } catch (error) {
          console.error('[Publish] Project ZIP failed:', error);
          showErrorToast({
            title: 'Download Failed',
            message: `Could not build the project archive: ${error.message}`,
          });
        }
      });
    }

    // Copy shareable link button
    if (copyShareLinkBtn && publishShareLink) {
      copyShareLinkBtn.addEventListener('click', async () => {
        if (!(await copyTextWithFallback(publishShareLink.value))) return;
        copyShareLinkBtn.textContent = 'Copied!';
        setTimeout(() => {
          copyShareLinkBtn.textContent = 'Copy';
        }, 2000);
        updateStatus('Shareable link copied to clipboard');
      });
    }
  }

  /**
   * What belonged to the project being replaced, cleared as another loads or
   * the project closes: its generated file and the link to download it, and
   * the notices about its values.
   */
  function resetProjectUiState() {
    lastGeneratedParamsHash = null;
    // "Not now" to another engine holds for one file.
    resetEngineOffer();
    stateManager.setState({ stl: null, stlStats: null, generatedOutput: null });
    document.getElementById('downloadFallbackLink')?.classList.add('hidden');
    const notices = document.getElementById('parameterNotices');
    if (notices) {
      notices.replaceChildren();
      notices.hidden = true;
    }
  }

  // ========== Preset system ==========
  // OpenSCAD Customizer-compatible preset management
  // Preset controls: Save = update current, + = new, - = delete

  // "design default values" -- always first in preset dropdown (desktop OpenSCAD parity)
  // Virtual preset ID for the immutable defaults entry (not stored in PresetManager)
  const DESIGN_DEFAULTS_ID = '__design_defaults__';

  // Searchable combobox instance (non-null only when searchable_combobox flag is on)
  let _presetCombobox = null;

  const stableStringify = (value) => {
    const seen = new WeakSet();
    const normalize = (val) => {
      if (Array.isArray(val)) {
        return val.map(normalize);
      }
      if (val && typeof val === 'object') {
        if (seen.has(val)) {
          return null;
        }
        seen.add(val);
        return Object.keys(val)
          .sort()
          .reduce((acc, key) => {
            acc[key] = normalize(val[key]);
            return acc;
          }, {});
      }
      return val;
    };
    return JSON.stringify(normalize(value));
  };

  function buildPresetSignature(params) {
    if (!params) return null;
    const state = stateManager.getState();
    const schemaParams = state.schema?.parameters;
    const normalized = schemaParams
      ? coercePresetValues(params, schemaParams)
      : params;
    return stableStringify(normalized);
  }

  function setCurrentPresetSignature(params) {
    currentPresetSignature = buildPresetSignature(params);
  }

  function doesPresetMatchParams(params) {
    if (!currentPresetSignature || !params) {
      return false;
    }
    return buildPresetSignature(params) === currentPresetSignature;
  }

  function updatePresetDirtyState(currentValues = null) {
    const state = stateManager.getState();
    if (!state.currentPresetId) {
      isPresetDirty = false;
      return;
    }

    const valuesToCheck = currentValues || state.parameters;
    if (!valuesToCheck || !currentPresetSignature) {
      isPresetDirty = true;
      return;
    }

    isPresetDirty = !doesPresetMatchParams(valuesToCheck);
  }

  function forceClearPresetSelection() {
    const state = stateManager.getState();
    const presetSelect = document.getElementById('presetSelect');
    const hasSelection =
      state.currentPresetId || state.currentPresetName || presetSelect?.value;

    // Debug logging to help identify unexpected clears
    if (hasSelection) {
      console.log('[Preset] Clearing selection:', {
        currentPresetId: state.currentPresetId,
        currentPresetName: state.currentPresetName,
        dropdownValue: presetSelect?.value,
        callerStack: new Error().stack?.split('\n').slice(1, 4).join('\n'),
      });
    }

    currentPresetSignature = null;
    isPresetDirty = false;

    if (hasSelection) {
      stateManager.setState({ currentPresetId: null, currentPresetName: null });
      if (presetSelect) {
        presetSelect.value = '';
      }
      updatePresetControlStates();
    }
  }

  /**
   * Update preset control button states based on current selection
   * Implements OpenSCAD Customizer semantics: Save/Delete need selection, Add always works
   */
  function updatePresetControlStates() {
    const state = stateManager.getState();
    const presetSelect = document.getElementById('presetSelect');
    const savePresetBtn = document.getElementById('savePresetBtn');
    const addPresetBtn = document.getElementById('addPresetBtn');
    const deletePresetBtn = document.getElementById('deletePresetBtn');

    const hasPresetSelected = presetSelect && presetSelect.value !== '';
    const hasModel = !!state.uploadedFile;
    // "design default values" is immutable -- save/delete should be disabled
    const isDesignDefaults = presetSelect?.value === DESIGN_DEFAULTS_ID;

    // Save button: enabled when a user preset is selected (not design defaults)
    if (savePresetBtn) {
      savePresetBtn.disabled =
        !hasPresetSelected || !hasModel || isDesignDefaults;
      savePresetBtn.title = isDesignDefaults
        ? 'Design default values cannot be overwritten'
        : !hasPresetSelected
          ? 'Select a preset first to save changes'
          : isPresetDirty
            ? 'Save Preset: overwrites current preset (unsaved changes)'
            : 'Save Preset: overwrites current preset';
      savePresetBtn.dataset.dirty = isPresetDirty ? 'true' : 'false';
    }

    // Add button: always enabled when model is loaded
    if (addPresetBtn) {
      addPresetBtn.disabled = !hasModel;
    }

    // Delete button: enabled when a user preset is selected (not design
    // defaults). It disables itself after a delete, with focus on it, so
    // focus moves to the preset chooser first rather than to the page body.
    if (deletePresetBtn) {
      if (!hasPresetSelected || !hasModel || isDesignDefaults) {
        const chooser = [
          document.querySelector(
            '#presetComboboxContainer .preset-combobox-input'
          ),
          presetSelect,
        ].find(
          (el) =>
            el &&
            (typeof el.checkVisibility !== 'function' || el.checkVisibility())
        );
        disableKeepingFocus(deletePresetBtn, chooser);
      } else {
        deletePresetBtn.disabled = false;
      }
      deletePresetBtn.title = isDesignDefaults
        ? 'Design default values cannot be deleted'
        : hasPresetSelected
          ? 'Delete current preset'
          : 'Select a preset first to delete';
    }

    // Copy Preset button: duplicates any selection, including design
    // defaults (that copies the schema defaults into a new preset)
    const copyPresetBtn = document.getElementById('copyPresetBtn');
    if (copyPresetBtn) {
      copyPresetBtn.disabled = !hasPresetSelected || !hasModel;
      copyPresetBtn.title = hasPresetSelected
        ? 'Copy Preset: duplicate the current preset'
        : 'Select a preset first to copy it';
    }

    // Copy preset name button: enabled whenever something is selected,
    // including the immutable "design default values" entry, whose visible
    // name copies like any other.
    const copyPresetNameBtn = document.getElementById('copyPresetNameBtn');
    if (copyPresetNameBtn) {
      copyPresetNameBtn.disabled = !hasPresetSelected;
      copyPresetNameBtn.title = hasPresetSelected
        ? 'Copy preset name'
        : 'Select a preset first to copy its name';
    }
  }

  function clearPresetSelection(currentValues = null) {
    // OpenSCAD Customizer behavior:
    // Changing parameters does NOT clear the selected preset.
    // We only update whether the current preset has unsaved changes.
    if (isLoadingPreset) {
      return;
    }

    updatePresetDirtyState(currentValues);
    updatePresetControlStates();
  }

  /**
   * Single entry point for applying a preset's parameters and companion
   * files. Called by both the Manage Presets modal and the preset dropdown
   * so the two code paths behave identically.
   *
   * Responsibilities:
   *  1. Merge visible preset parameters onto current state
   *  2. Re-render parameter UI
   *  3. Apply explicit preset.companionFiles (embedded content, legacy path)
   *  4. Alias-mount preset-specific files from presetCompanionMap (ZIP path)
   *  5. Update auto-preview controller
   *  6. Sync screenshot overlay
   *  7. Track current preset selection and update status
   *
   * @param {Object} preset - Loaded preset with .parameters, .name, .id
   */
  /** A preset was written: the tour's "Save a preset" step listens for it. */
  function notePresetSaved(name) {
    document.body.dispatchEvent(
      new CustomEvent('forge:preset-saved', { bubbles: true, detail: { name } })
    );
  }

  function applyPresetParametersAndCompanions(preset) {
    // A value typed but not yet applied joins the current values first, so
    // the preset lands on top of it rather than on the values before it.
    commitPendingParameterEdit(document.getElementById('parametersContainer'));
    isLoadingPreset = true;

    const state = stateManager.getState();
    const hiddenNames = new Set(
      Object.keys(state.schema?.hiddenParameters || {})
    );

    // Merge visible preset params onto current state (desktop OpenSCAD parity)
    const visiblePresetParams = {};
    for (const [k, v] of Object.entries(preset.parameters)) {
      if (!hiddenNames.has(k)) visiblePresetParams[k] = v;
    }
    const mergedParams = { ...state.parameters, ...visiblePresetParams };
    stateManager.setState({ parameters: mergedParams });

    // Re-render UI with merged parameters
    const parametersContainer = document.getElementById('parametersContainer');
    renderParameterUI(
      state.schema,
      parametersContainer,
      (values) => {
        stateManager.setState({ parameters: values });
        clearPresetSelection(values);
        if (autoPreviewController) {
          autoPreviewController.onParameterChange(values);
        }
        updatePrimaryActionButton();
        companionFilesCtrl.syncOverlayWithScreenshotParam(values);
      },
      mergedParams
    );

    // Build updated projectFiles from the canonical project snapshot rather than
    // the currently aliased working set, so preset-specific companion files do
    // not bleed into the next preset selection.
    const curState = stateManager.getState();
    const currentProjectFilesForLog = curState.projectFiles
      ? new Map(curState.projectFiles)
      : new Map();
    const newProjectFiles = canonicalProjectFiles
      ? new Map(canonicalProjectFiles)
      : currentProjectFilesForLog;

    // Merge explicit preset.companionFiles (content a saved preset embeds)
    if (
      preset.companionFiles &&
      Object.keys(preset.companionFiles).length > 0
    ) {
      for (const [filename, content] of Object.entries(preset.companionFiles)) {
        newProjectFiles.set(filename, content);
      }
    }

    let companionMapping = null;
    if (_isEnabled('project_presets') && preset.source === 'project') {
      const projState = stateManager.getState();
      companionMapping =
        projState.projectCompanionMap?.get(preset.name) ?? null;
    } else if (!_isEnabled('project_presets')) {
      companionMapping = presetCompanionMap?.get(preset.name) ?? null;
    }

    const aliasedFiles = applyCompanionAliases(
      newProjectFiles,
      companionMapping
    );

    // Ground-truth diagnostics: which companion files the preset mounted
    const _diagCompanion = {
      presetName: preset.name,
      presetId: preset.id,
      presetSource: preset.source,
      resolution: companionMapping?.resolution ?? 'none',
      aliases: companionMapping?.aliases
        ? { ...companionMapping.aliases }
        : companionMapping?.openingsPath
          ? { 'openings_and_additions.txt': companionMapping.openingsPath }
          : null,
    };
    if (companionMapping?.aliases) {
      for (const [target, source] of Object.entries(companionMapping.aliases)) {
        const content = aliasedFiles.get(target);
        if (content != null) {
          const snippet =
            typeof content === 'string'
              ? content.slice(0, 120).replace(/\n/g, '\\n')
              : `<binary ${content.byteLength ?? content.length} bytes>`;
          _diagCompanion[`mounted:${target}`] = {
            source,
            sizeBytes:
              typeof content === 'string'
                ? content.length
                : (content.byteLength ?? 0),
            preview: snippet,
          };
        }
      }
    } else if (companionMapping?.openingsPath) {
      const content = aliasedFiles.get('openings_and_additions.txt');
      if (content != null) {
        const snippet =
          typeof content === 'string'
            ? content.slice(0, 120).replace(/\n/g, '\\n')
            : `<binary ${content.byteLength ?? content.length} bytes>`;
        _diagCompanion['mounted:openings_and_additions.txt'] = {
          source: companionMapping.openingsPath,
          sizeBytes:
            typeof content === 'string'
              ? content.length
              : (content.byteLength ?? 0),
          preview: snippet,
        };
      }
    }
    console.log('[Preset Diag]', _diagCompanion);
    if (companionMapping?.resolution === 'ancestor-fallback') {
      console.debug(
        `[Preset] "${preset.name}" companion resolved via ancestor fallback`
      );
    } else if (companionMapping?.resolution === 'ambiguous') {
      console.warn(
        `[Preset] "${preset.name}" companion resolution is ambiguous — no alias applied`
      );
    }

    stateManager.setState({ projectFiles: aliasedFiles });

    // Reset output format to STL when loading a preset whose parameters
    // produce 3D geometry.  Check both the dropdown and the state because
    // they can desync (e.g. dropdown shows STL but state still says SVG
    // after a welcome-screen round-trip).
    const _fmtSelect = document.getElementById('outputFormat');
    const _stateNeedsReset =
      (stateManager.getState().outputFormat || 'stl') !== 'stl';
    if (_fmtSelect && (_fmtSelect.value !== 'stl' || _stateNeedsReset)) {
      const is2DPreset =
        isNonPreviewable(mergedParams, state.schema) ||
        (typeof mergedParams.generate === 'string' &&
          /svg|dxf|2d|first layer/i.test(mergedParams.generate));
      if (!is2DPreset) {
        _fmtSelect.value = 'stl';
        _fmtSelect.dispatchEvent(new Event('change'));
        stateManager.setState({ outputFormat: 'stl' });
      }
    }

    if (autoPreviewController) {
      autoPreviewController.setProjectFiles(
        aliasedFiles,
        curState.mainFilePath
      );
      autoPreviewController.onParameterChange(mergedParams);
    }

    updatePrimaryActionButton();
    companionFilesCtrl.updateProjectFilesUI();

    // When the preset has a mapped SVG, force-select the aliased overlay
    // in the dropdown. Without this, the dropdown retains the previous
    // preset's SVG path (which still exists in the Map) and autoSelectOverlaySource
    // returns early, leaving the overlay stale.
    const svgTarget = getOverlaySvgTarget(companionMapping);
    if (svgTarget && aliasedFiles.has(svgTarget)) {
      if (overlaySourceSelect) {
        overlaySourceSelect.value = svgTarget;
      }
      overlayGridCtrl
        .loadOverlayFromProjectFile(svgTarget)
        .then(() => {
          // SVG 96 DPI size applied; SCAD case-opening / screen dims override.
          overlayGridCtrl.autoApplyScreenDimensionsFromParams(mergedParams);
          overlayGridCtrl.updateOverlayUIFromConfig();
        })
        .catch((err) => {
          console.warn('[Preset] Failed to load preset SVG overlay:', err);
        });
    }

    companionFilesCtrl.syncOverlayWithScreenshotParam(mergedParams);
    setCurrentPresetSelection(preset);

    isLoadingPreset = false;

    const applied = Object.keys(visiblePresetParams).length;
    const total = Object.keys(preset.parameters).length;
    if (applied < total) {
      updateStatus(
        `Loaded preset: ${preset.name} (${applied} of ${total} parameters applied)`
      );
    } else {
      updateStatus(`Loaded preset: ${preset.name}`);
    }
  }

  function setCurrentPresetSelection(preset) {
    if (!preset) {
      forceClearPresetSelection();
      return;
    }

    console.log('[Preset] Setting selection:', {
      id: preset.id,
      name: preset.name,
      paramCount: Object.keys(preset.parameters || {}).length,
    });

    setCurrentPresetSignature(preset.parameters);
    isPresetDirty = false;
    stateManager.setState({
      currentPresetId: preset.id,
      currentPresetName: preset.name,
    });
    const presetSelect = document.getElementById('presetSelect');
    if (presetSelect) {
      presetSelect.value = preset.id;
    }
    updatePresetControlStates();
  }

  // User presets are keyed by the project's main file, not the archive
  // that delivered it. A ZIP's filename changes when someone renames or
  // re-downloads it; the main .scad path inside it does not. The desktop keys
  // presets the same way (the sidecar .json sits beside the .scad).
  function presetModelKey(state) {
    return state.mainFilePath || state.uploadedFile?.name;
  }

  // Update preset dropdown based on current model
  // Preserves current selection if the preset still exists
  // Applies the user's saved sort preference (shared with Import/Export modal)
  function updatePresetDropdown() {
    const state = stateManager.getState();
    const presetSelect = document.getElementById('presetSelect');

    if (!state.uploadedFile) {
      presetSelect.disabled = true;
      presetSelect.innerHTML =
        '<option value="">-- No model loaded --</option>';
      if (_presetCombobox) {
        _presetCombobox.update([], null);
        _presetCombobox.setDisabled(true);
      }
      currentPresetSignature = null;
      isPresetDirty = false;
      updatePresetControlStates();
      return;
    }

    const modelName = presetModelKey(state);
    // Older presets sit under the archive's name; move them to
    // the stable key once. No-op on every later call.
    presetManager.adoptLegacyModelKey(state.uploadedFile.name, modelName);
    const currentSortOrder =
      localStorage.getItem(PRESET_SORT_KEY) || 'name-asc';
    const presets = presetManager.getSortedPresets(modelName, currentSortOrder);

    // Remember current selection from state (survives dropdown rebuilds)
    const currentPresetId = state.currentPresetId;

    // Clear and rebuild native select dropdown
    presetSelect.innerHTML = '<option value="">-- Select Preset --</option>';

    // "design default values" is always first in the dropdown (desktop
    // OpenSCAD parity): a virtual preset derived from the .scad source
    // defaults, not stored in PresetManager.
    const defaultsOption = document.createElement('option');
    defaultsOption.value = DESIGN_DEFAULTS_ID;
    defaultsOption.textContent = 'design default values';
    defaultsOption.style.fontStyle = 'italic';
    presetSelect.appendChild(defaultsOption);

    const useProjectPresets =
      _isEnabled('project_presets') && state.projectPresets;

    if (useProjectPresets) {
      const projectGroup = document.createElement('optgroup');
      projectGroup.label = 'Project Presets';
      const projectNames = Object.keys(state.projectPresets).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
      for (const name of projectNames) {
        const option = document.createElement('option');
        option.value = `proj::${name}`;
        option.textContent = name;
        projectGroup.appendChild(option);
      }
      if (projectNames.length > 0) {
        presetSelect.appendChild(projectGroup);
      }

      const userPresets = presets.filter((p) => p.id !== 'design-defaults');
      if (userPresets.length > 0) {
        const savedGroup = document.createElement('optgroup');
        savedGroup.label = 'Saved Presets';
        for (const preset of userPresets) {
          const option = document.createElement('option');
          option.value = preset.id;
          option.textContent = preset.name;
          savedGroup.appendChild(option);
        }
        presetSelect.appendChild(savedGroup);
      }
    } else if (presets.length > 0) {
      presets.forEach((preset) => {
        if (preset.id === 'design-defaults') return;
        const option = document.createElement('option');
        option.value = preset.id;
        option.textContent = preset.name;
        presetSelect.appendChild(option);
      });
    }

    presetSelect.disabled = false;

    // Sync the sort toolbar dropdown to reflect the active sort order
    const sortSelect = document.getElementById('presetDropdownSort');
    if (sortSelect && sortSelect.value !== currentSortOrder) {
      sortSelect.value = currentSortOrder;
    }

    if (_presetCombobox) {
      const defaultComboEntry = {
        id: DESIGN_DEFAULTS_ID,
        label: 'design default values',
        italic: true,
      };
      let comboOptions;
      if (useProjectPresets) {
        const projNames = Object.keys(state.projectPresets).sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
        comboOptions = [
          defaultComboEntry,
          ...projNames.map((name) => ({
            id: `proj::${name}`,
            label: name,
            group: 'Project Presets',
          })),
          ...presets
            .filter((p) => p.id !== 'design-defaults')
            .map((p) => ({ id: p.id, label: p.name, group: 'Saved Presets' })),
        ];
      } else {
        comboOptions = [
          defaultComboEntry,
          ...presets
            .filter((p) => p.id !== 'design-defaults')
            .map((p) => ({ id: p.id, label: p.name })),
        ];
      }
      _presetCombobox.update(comboOptions, currentPresetId || null);
      // At rest the desktop shows the active
      // "design default values", not a search hint. Display only.
      _presetCombobox.setRestingLabel('design default values');
      _presetCombobox.setDisabled(false);
    }

    if (currentPresetId) {
      let restoredSelection = false;
      if (useProjectPresets && currentPresetId.startsWith('proj::')) {
        const pName = currentPresetId.slice(6);
        if (state.projectPresets[pName]) {
          presetSelect.value = currentPresetId;
          setCurrentPresetSignature(state.projectPresets[pName]);
          restoredSelection = true;
        }
      }
      if (!restoredSelection) {
        const currentPreset = presets.find(
          (preset) => preset.id === currentPresetId
        );
        if (currentPreset) {
          presetSelect.value = currentPresetId;
          setCurrentPresetSignature(currentPreset.parameters);
        } else if (currentPresetId !== DESIGN_DEFAULTS_ID) {
          forceClearPresetSelection();
        }
      }
    } else {
      currentPresetSignature = null;
      isPresetDirty = false;
    }

    updatePresetDirtyState();
    updatePresetControlStates();
  }

  // Show save preset modal
  function showSavePresetModal() {
    const state = stateManager.getState();

    if (!state.uploadedFile) {
      showErrorToast({
        title: 'No Model Loaded',
        message: 'Upload a model before saving a preset.',
      });
      return;
    }

    const modal = document.createElement('div');
    modal.className = 'preset-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'savePresetTitle');
    modal.setAttribute('aria-modal', 'true');

    modal.innerHTML = `
      <div class="preset-modal-content">
        <div class="preset-modal-header">
          <h3 id="savePresetTitle" class="preset-modal-title">Save Preset</h3>
          <button class="preset-modal-close" aria-label="Close dialog" data-action="close">&times;</button>
        </div>
        <form class="preset-form" id="savePresetForm">
          <div class="preset-form-group">
            <label for="presetName" class="preset-form-label">Preset Name *</label>
            <input 
              type="text" 
              id="presetName" 
              class="preset-form-input" 
              placeholder="e.g., Large Handle"
              required
              autofocus
            />
            <span class="preset-form-hint">Give this preset a descriptive name</span>
          </div>
          <div class="preset-form-group">
            <label for="presetDescription" class="preset-form-label">Description (Optional)</label>
            <textarea 
              id="presetDescription" 
              class="preset-form-textarea" 
              placeholder="Optional description of this configuration..."
            ></textarea>
          </div>
          <div class="preset-form-actions">
            <button type="button" class="btn btn-secondary" data-action="close">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Preset</button>
          </div>
        </form>
      </div>
    `;

    document.body.appendChild(modal);

    // Close handler for dynamic modal
    const closeSavePresetModal = () => {
      closeModal(modal);
      document.body.removeChild(modal);
    };
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeSavePresetModal();
    });

    // Handle form submission
    const form = modal.querySelector('#savePresetForm');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();

      const name = modal.querySelector('#presetName')?.value.trim();
      const description = modal
        .querySelector('#presetDescription')
        ?.value.trim();

      if (!name) {
        showErrorToast({
          title: 'Name Required',
          message: 'Please enter a preset name.',
        });
        return;
      }

      // Auto-rename duplicates: "test1" → "test1 (1)" → "test1 (2)" etc.
      const existingPresets = presetManager.getPresetsForModel(
        presetModelKey(state)
      );
      let finalName = name;
      const existingNames = new Set(existingPresets.map((p) => p.name));
      if (existingNames.has(name)) {
        let counter = 1;
        while (existingNames.has(`${name} (${counter})`)) {
          counter++;
        }
        finalName = `${name} (${counter})`;
      }

      try {
        // Capture companion files (text-only, exclude images and main file)
        const companionSnapshot = {};
        if (state.projectFiles) {
          for (const [path, content] of state.projectFiles.entries()) {
            if (
              path !== state.mainFilePath &&
              typeof content === 'string' &&
              !content.startsWith('data:')
            ) {
              companionSnapshot[path] = content;
            }
          }
        }

        // Save preset and capture returned object (contains id and name)
        const savedPreset = presetManager.savePreset(
          presetModelKey(state),
          finalName,
          state.parameters,
          {
            description,
            companionFiles:
              Object.keys(companionSnapshot).length > 0
                ? companionSnapshot
                : null,
          }
        );

        updateStatus(`Preset "${finalName}" saved`);
        notePresetSaved(finalName);

        // OpenSCAD Customizer behavior:
        // - "+" creates a new preset AND selects it in the dropdown
        // - Save button immediately becomes available to overwrite that preset
        //
        // In practice, the preset dropdown may be rebuilt by subscribers and other UI
        // events around the save; ensure selection is applied after any rebuilds.
        setCurrentPresetSelection(savedPreset);
        updatePresetDropdown();

        const ensurePresetSelected = (presetId) => {
          const presetSelectEl = document.getElementById('presetSelect');
          if (!presetSelectEl) return;

          presetSelectEl.value = presetId;
          updatePresetControlStates();

          // If the option isn't present yet (or a subsequent rebuild overwrote it),
          // retry on the next frame.
          if (presetSelectEl.value !== presetId) {
            console.warn(
              '[Preset] Auto-select did not stick, retrying after rebuild'
            );
            requestAnimationFrame(() => {
              const el = document.getElementById('presetSelect');
              if (!el) return;
              el.value = presetId;
              updatePresetControlStates();
            });
          }
        };

        ensurePresetSelected(savedPreset.id);

        closeSavePresetModal();
      } catch (error) {
        showErrorToast({ title: 'Preset Save Failed', message: error.message });
      }
    });

    // Handle close buttons
    modal.querySelectorAll('[data-action="close"]').forEach((btn) => {
      btn.addEventListener('click', closeSavePresetModal);
    });

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeSavePresetModal();
      }
    });

    // Open modal with focus management (WCAG 2.2 focus trapping)
    openModal(modal, {
      focusTarget: modal.querySelector('#presetName'),
    });
  }

  // Handle the result from importAndMergePresets and refresh the UI
  function _handleImportResult(result, _modelName) {
    if (result.imported > 0 || result.skipped > 0) {
      let message = `Imported ${result.imported} design${result.imported !== 1 ? 's' : ''}`;
      if (result.skipped > 0) {
        message += ` (${result.skipped} skipped: duplicate names)`;
      }
      if (result.errors?.length > 0) {
        message += `\n\nErrors:\n${result.errors.join('\n')}`;
      }
      showErrorToast({ title: 'Import Complete', message });
      updatePresetDropdown();
      if (result.presets?.length > 0) {
        const last = result.presets[result.presets.length - 1];
        if (last?.id) setCurrentPresetSelection(last);
      }
      // Close and reopen the manage modal to reflect new list
      const existingModal = document.querySelector('.preset-modal');
      if (existingModal) {
        closeModal(existingModal);
        document.body.removeChild(existingModal);
      }
      showManagePresetsModal();
    } else {
      const errorMsg = result.errors?.length
        ? `Import failed: ${result.errors.join('; ')}`
        : 'No valid designs found in the selected file(s).';
      showErrorToast({ title: 'Import Failed', message: errorMsg });
    }
  }

  /**
   * Show the Import / Export designs dialog.
   * @param {Object} [options]
   * @param {number} [options.afterDeleteIndex] - Where a preset was just
   *   deleted from the list: the list opens unfolded, focus on the row now in
   *   its place, so someone clearing out presets keeps their place.
   */
  function showManagePresetsModal(options = {}) {
    const state = stateManager.getState();

    if (!state.uploadedFile) {
      showErrorToast({
        title: 'No Model Loaded',
        message: 'Upload a model before managing presets.',
      });
      return;
    }

    const modelName = presetModelKey(state);
    let currentSortOrder = localStorage.getItem(PRESET_SORT_KEY) || 'name-asc';
    const presets = presetManager.getSortedPresets(modelName, currentSortOrder);

    const modal = document.createElement('div');
    modal.className = 'preset-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'managePresetsTitle');
    modal.setAttribute('aria-modal', 'true');

    const formatDate = (timestamp) => {
      return new Date(timestamp).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const presetsHTML =
      presets.length === 0
        ? '<div class="preset-empty">No presets saved for this model</div>'
        : presets
            .map(
              (preset) => `
          <div class="preset-item" data-preset-id="${preset.id}">
            <div class="preset-item-info">
              <h4 class="preset-item-name">${preset.name}</h4>
              <p class="preset-item-meta">
                ${preset.description || 'No description'} • 
                Created ${formatDate(preset.created)}
              </p>
            </div>
            <div class="preset-item-actions">
              <button class="btn btn-sm btn-primary" data-action="load" data-preset-id="${preset.id}" aria-label="Load preset ${preset.name}">
                Load
              </button>
              <button class="btn btn-sm btn-secondary" data-action="export" data-preset-id="${preset.id}" aria-label="Export preset ${preset.name}">
                Export
              </button>
              <button class="btn btn-sm btn-outline" data-action="delete" data-preset-id="${preset.id}" aria-label="Delete preset ${preset.name}">
                Delete
              </button>
            </div>
          </div>
        `
            )
            .join('');

    modal.innerHTML = `
      <div class="preset-modal-content">
        <div class="preset-modal-header">
          <h3 id="managePresetsTitle" class="preset-modal-title">Import / Export Presets</h3>
          <button class="preset-modal-close" aria-label="Close dialog" data-action="close">&times;</button>
        </div>
        <div class="preset-import-export-actions" style="display:flex;gap:12px;padding:16px;border-bottom:1px solid var(--border-color, #e0e0e0);">
          <button class="btn btn-primary" data-action="import" style="flex:1;padding:12px;font-size:1em;">
            📂 Import Presets
          </button>
          <button class="btn btn-primary" data-action="export-all" style="flex:1;padding:12px;font-size:1em;">
            💾 Export All Presets
          </button>
        </div>
        ${
          presets.length > 0
            ? `
        <details class="preset-list-details" style="padding:0 16px 16px;">
          <summary style="padding:8px 0;cursor:pointer;color:var(--text-secondary, #666);">
            Individual presets (${presets.length})
          </summary>
          <div class="preset-list-toolbar">
            <label class="preset-sort-label" for="presetSortSelect">Sort</label>
            <select id="presetSortSelect" class="preset-sort-select" aria-label="Sort presets by">
              <option value="name-asc"${currentSortOrder === 'name-asc' ? ' selected' : ''}>Name (A–Z)</option>
              <option value="name-desc"${currentSortOrder === 'name-desc' ? ' selected' : ''}>Name (Z–A)</option>
              <option value="date-created"${currentSortOrder === 'date-created' ? ' selected' : ''}>Date created (newest)</option>
              <option value="date-modified"${currentSortOrder === 'date-modified' ? ' selected' : ''}>Date modified (newest)</option>
            </select>
          </div>
          <div class="preset-list" id="presetListContainer">
            ${presetsHTML}
          </div>
        </details>
        `
            : '<div class="preset-empty" style="padding:16px;">No presets saved for this model yet. Import a preset file or use the + button to create one.</div>'
        }
        <div class="preset-modal-footer">
          <button class="btn btn-outline" data-action="close">Close</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Wire sort selector
    const presetSortSelect = modal.querySelector('#presetSortSelect');
    const presetListContainer = modal.querySelector('#presetListContainer');
    if (presetSortSelect && presetListContainer) {
      presetSortSelect.addEventListener('change', () => {
        currentSortOrder = presetSortSelect.value;
        localStorage.setItem(PRESET_SORT_KEY, currentSortOrder);
        const sorted = presetManager.getSortedPresets(
          modelName,
          currentSortOrder
        );
        presetListContainer.innerHTML = sorted
          .map(
            (preset) => `
          <div class="preset-item" data-preset-id="${preset.id}">
            <div class="preset-item-info">
              <h4 class="preset-item-name">${preset.name}</h4>
              <p class="preset-item-meta">
                ${preset.description || 'No description'} •
                Created ${formatDate(preset.created)}
              </p>
            </div>
            <div class="preset-item-actions">
              <button class="btn btn-sm btn-primary" data-action="load" data-preset-id="${preset.id}" aria-label="Load preset ${preset.name}">Load</button>
              <button class="btn btn-sm btn-secondary" data-action="export" data-preset-id="${preset.id}" aria-label="Export preset ${preset.name}">Export</button>
              <button class="btn btn-sm btn-outline" data-action="delete" data-preset-id="${preset.id}" aria-label="Delete preset ${preset.name}">Delete</button>
            </div>
          </div>`
          )
          .join('');
        // Keep the main preset dropdown in sync with the new sort order
        updatePresetDropdown();
        const label =
          presetSortSelect.options[presetSortSelect.selectedIndex]?.text || '';
        announceImmediate(`Presets sorted by ${label}`);
      });
    }

    // Close handler for dynamic modal
    const closeManagePresetsModalHandler = () => {
      closeModal(modal);
      document.body.removeChild(modal);
    };
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeManagePresetsModalHandler();
    });

    // Handle actions
    modal.addEventListener('click', async (e) => {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;

      const action = btn.dataset.action;
      const presetId = btn.dataset.presetId;

      if (action === 'close') {
        closeManagePresetsModalHandler();
      } else if (action === 'load') {
        const preset = presetManager.loadPreset(modelName, presetId);
        if (preset) {
          applyPresetParametersAndCompanions(preset);
          closeManagePresetsModalHandler();
        }
      } else if (action === 'delete') {
        const presetToDelete = presetManager.loadPreset(modelName, presetId);
        const presetLabel = presetToDelete?.name || 'this preset';
        // Plain words: the dialog shows its message as text, so markup here
        // was read out as tags.
        const confirmed = await showConfirmDialog(
          presetToDelete?.name
            ? `Delete the preset "${presetLabel}"? This cannot be undone.`
            : 'Delete this preset? This cannot be undone.',
          'Delete Preset',
          'Delete',
          'Cancel',
          { destructive: true }
        );
        if (confirmed) {
          const index = [
            ...modal.querySelectorAll('button[data-action="delete"]'),
          ].indexOf(btn);
          presetManager.deletePreset(modelName, presetId);
          updatePresetDropdown();
          closeManagePresetsModalHandler();
          showManagePresetsModal({ afterDeleteIndex: index });
        }
      } else if (action === 'export') {
        const json = presetManager.exportPreset(modelName, presetId);
        if (json) {
          const preset = presetManager.loadPreset(modelName, presetId);
          const blob = new Blob([json], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${sanitizeFilename(preset.name)}.json`;
          a.click();
          URL.revokeObjectURL(url);
          updateStatus(`Exported preset: ${preset.name}`);
        }
      } else if (action === 'export-all') {
        // Export in OpenSCAD native format (includes "design default values" as first entry)
        // Pass hidden parameters for desktop parity (included in export but not in UI)
        const currentState = stateManager.getState();
        const hiddenParams = currentState.schema?.hiddenParameters || {};
        const json = presetManager.exportOpenSCADNativeFormat(
          modelName,
          hiddenParams
        );
        if (json) {
          const blob = new Blob([json], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${modelName.replace('.scad', '')}-presets.json`;
          a.click();
          URL.revokeObjectURL(url);
          updateStatus('Exported all presets (OpenSCAD native format)');
        } else {
          updateStatus(
            'No presets to export. Create some presets first using the + button.'
          );
        }
      } else if (action === 'import') {
        // Ask user whether to merge with existing presets or replace them all.
        // "Replace" is destructive and always requires confirmation.
        // Import mode dialog: three options mapped to importAndMergePresets strategies
        const importModeDialog = document.createElement('dialog');
        importModeDialog.className = 'preset-import-mode-dialog';
        importModeDialog.setAttribute('aria-labelledby', 'importModeTitle');
        importModeDialog.innerHTML = `
          <form method="dialog" class="import-mode-form">
            <h3 id="importModeTitle" class="import-mode-title">Import presets</h3>
            <fieldset class="import-mode-fieldset">
              <legend class="import-mode-legend">Import mode</legend>
              <label class="import-mode-option">
                <input type="radio" name="importMode" value="merge" checked />
                <span class="import-mode-label">
                  <strong>Merge</strong>
                  <span class="import-mode-desc">Add imported presets; skip any with the same name as existing ones</span>
                </span>
              </label>
              <label class="import-mode-option">
                <input type="radio" name="importMode" value="replace" />
                <span class="import-mode-label">
                  <strong>Replace</strong>
                  <span class="import-mode-desc">Delete all existing presets for this model, then import</span>
                </span>
              </label>
              <label class="import-mode-option">
                <input type="radio" name="importMode" value="copies" />
                <span class="import-mode-label">
                  <strong>Import as copies</strong>
                  <span class="import-mode-desc">Import all presets; rename duplicates with (2), (3)… suffixes</span>
                </span>
              </label>
            </fieldset>
            <div class="import-mode-actions">
              <button type="submit" value="ok" class="btn btn-primary">Choose files…</button>
              <button type="submit" value="cancel" class="btn btn-outline">Cancel</button>
            </div>
          </form>`;
        document.body.appendChild(importModeDialog);
        importModeDialog.showModal();

        const importMode = await new Promise((resolve) => {
          importModeDialog.addEventListener(
            'close',
            () => {
              const returnValue = importModeDialog.returnValue;
              const mode =
                importModeDialog.querySelector(
                  'input[name="importMode"]:checked'
                )?.value || 'merge';
              document.body.removeChild(importModeDialog);
              resolve(returnValue === 'ok' ? mode : null);
            },
            { once: true }
          );
        });

        if (!importMode) return; // user canceled

        // Create file input for import
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.multiple = true;
        input.onchange = async (e) => {
          const files = e.target.files;
          if (!files || files.length === 0) return;

          try {
            const currentState = stateManager.getState();
            const currentModelName = currentState.uploadedFile?.name || null;
            const paramSchema = currentState.schema?.parameters || {};

            if (!currentModelName) {
              const proceed = await showConfirmDialog(
                'No model is currently loaded. Presets will be saved as "Unknown Model" and may not appear in the dropdown until you load a matching model.\n\nContinue with import?',
                'Import Presets',
                'Import',
                'Cancel'
              );
              if (!proceed) return;
            }

            // Replace mode: confirm and clear existing presets first
            if (importMode === 'replace' && currentModelName) {
              const existing =
                presetManager.getPresetsForModel(currentModelName) || [];
              const realCount = existing.filter(
                (p) => p.id !== 'design-defaults'
              ).length;

              // Reject if import files are empty to avoid silent data loss
              const fileTexts = await Promise.all(
                Array.from(files).map((f) => f.text())
              );
              const hasValidContent = fileTexts.some((t) => {
                try {
                  return !!JSON.parse(t);
                } catch {
                  return false;
                }
              });
              if (!hasValidContent) {
                showErrorToast({
                  title: 'Invalid Import Data',
                  message:
                    'The selected file(s) contain no valid preset data. Import canceled to protect your existing presets.',
                });
                return;
              }

              if (realCount > 0) {
                const confirmed = await showConfirmDialog(
                  `Replace all presets? This will permanently delete ${realCount} existing preset${realCount !== 1 ? 's' : ''} for "${currentModelName}". This cannot be undone.\n\nContinue?`,
                  'Replace All Presets',
                  'Replace all',
                  'Cancel',
                  { destructive: true }
                );
                if (!confirmed) return;
                presetManager.clearPresetsForModel(currentModelName, {
                  preserveDefaults: true,
                });
              }

              // After clearing, import with overwrite strategy
              const result = presetManager.importAndMergePresets(
                fileTexts,
                currentModelName,
                paramSchema,
                'overwrite'
              );
              _handleImportResult(result, currentModelName);
              return;
            }

            // Map UI modes to importAndMergePresets conflictStrategy
            // merge → 'keep' (skip duplicates by name)
            // copies → 'rename' (append (2), (3) suffix)
            const conflictStrategy =
              importMode === 'copies' ? 'rename' : 'keep';

            const fileTexts = await Promise.all(
              Array.from(files).map((f) => f.text())
            );
            const result = presetManager.importAndMergePresets(
              fileTexts,
              currentModelName,
              paramSchema,
              conflictStrategy
            );
            _handleImportResult(result, currentModelName);
          } catch (error) {
            showErrorToast({ title: 'Import Failed', message: error.message });
          }
        };
        input.click();
      }
    });

    // Close on backdrop click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeManagePresetsModalHandler();
      }
    });

    let focusTarget = modal.querySelector('.preset-modal-close');
    const afterDelete = options?.afterDeleteIndex;
    if (Number.isInteger(afterDelete) && afterDelete >= 0) {
      const list = modal.querySelector('.preset-list-details');
      if (list) list.open = true;
      const deletes = modal.querySelectorAll('button[data-action="delete"]');
      focusTarget =
        deletes[afterDelete] ||
        deletes[afterDelete - 1] ||
        list?.querySelector('summary') ||
        focusTarget;
    }

    // Open modal with focus management (WCAG 2.2 focus trapping)
    openModal(modal, { focusTarget });
  }

  // Preset button handlers - OpenSCAD Customizer semantics
  // Save: update selected preset, Add: create new, Delete: remove selected
  const savePresetBtn = document.getElementById('savePresetBtn');
  const addPresetBtn = document.getElementById('addPresetBtn');
  const copyPresetBtn = document.getElementById('copyPresetBtn');
  const deletePresetBtn = document.getElementById('deletePresetBtn');
  const managePresetsBtn = document.getElementById('managePresetsBtn');
  const presetSelect = document.getElementById('presetSelect');

  /**
   * Overwrite the currently selected preset with the current parameter
   * values. Shared by the Save button and the unsaved-changes prompt when
   * switching presets.
   * @returns {boolean} True if the preset was saved
   */
  function overwriteCurrentPreset() {
    const state = stateManager.getState();
    const selectedPresetId = presetSelect?.value || state.currentPresetId;

    if (!state.uploadedFile) {
      updateStatus('No model loaded', 'error');
      return false;
    }

    if (!selectedPresetId) {
      // Fallback: if no preset selected, show dialog (shouldn't happen if button is disabled)
      updateStatus('Select a preset first, or use + to create new', 'warning');
      return false;
    }

    // Block saving over "design default values" (immutable, desktop parity)
    if (selectedPresetId === DESIGN_DEFAULTS_ID) {
      updateStatus(
        'Design default values cannot be overwritten. Use + to create a new preset.',
        'warning'
      );
      return false;
    }

    // Get the preset to update
    const preset = presetManager.loadPreset(
      presetModelKey(state),
      selectedPresetId
    );
    if (!preset) {
      updateStatus('Preset not found', 'error');
      return false;
    }

    try {
      // Capture companion files (text-only, exclude images and main file)
      const companionSnapshot = {};
      if (state.projectFiles) {
        for (const [path, content] of state.projectFiles.entries()) {
          if (
            path !== state.mainFilePath &&
            typeof content === 'string' &&
            !content.startsWith('data:')
          ) {
            companionSnapshot[path] = content;
          }
        }
      }

      // Save/overwrite the current preset with current parameters
      const savedPreset = presetManager.savePreset(
        presetModelKey(state),
        preset.name, // Use existing name - this will overwrite
        state.parameters,
        {
          description: preset.description,
          companionFiles:
            Object.keys(companionSnapshot).length > 0
              ? companionSnapshot
              : null,
        }
      );

      updateStatus(`Preset "${preset.name}" saved`, 'success');
      notePresetSaved(preset.name);
      setCurrentPresetSelection(savedPreset);
      return true;
    } catch (error) {
      updateStatus(`Failed to save preset: ${error.message}`, 'error');
      return false;
    }
  }

  // Save button: update the selected preset; it never creates a new one.
  savePresetBtn.addEventListener('click', () => {
    if (overwriteCurrentPreset()) {
      // Brief visual feedback on button
      savePresetBtn.textContent = '✓';
      setTimeout(() => {
        savePresetBtn.textContent = '💾';
      }, 1500);
    }
  });

  // Add button: create a new preset from the current settings (shows a
  // dialog), like the desktop Customizer's + button.
  addPresetBtn.addEventListener('click', showSavePresetModal);

  // Copy button: duplicate the selected preset's saved values into a new
  // preset named "<name> (copy)" and select it. Copying design defaults
  // creates a preset from the schema defaults.
  copyPresetBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    const selectedPresetId = presetSelect?.value;
    if (!state.uploadedFile || !selectedPresetId) return;

    let baseName;
    let params;
    let description;
    if (selectedPresetId === DESIGN_DEFAULTS_ID) {
      baseName = 'design default values';
      params = { ...state.defaults };
    } else {
      const preset = presetManager.loadPreset(
        presetModelKey(state),
        selectedPresetId
      );
      if (!preset) {
        updateStatus('Preset not found', 'error');
        return;
      }
      baseName = preset.name;
      params = { ...preset.parameters };
      description = preset.description;
    }

    const existingNames = new Set(
      (presetManager.getPresetsForModel(presetModelKey(state)) || []).map(
        (p) => p.name
      )
    );
    let copyName = `${baseName} (copy)`;
    for (let i = 2; existingNames.has(copyName); i++) {
      copyName = `${baseName} (copy ${i})`;
    }

    try {
      const savedPreset = presetManager.savePreset(
        presetModelKey(state),
        copyName,
        params,
        { description }
      );
      updatePresetDropdown();
      setCurrentPresetSelection(savedPreset);
      updateStatus(`Preset copied to "${copyName}"`, 'success');
      notePresetSaved(copyName);
    } catch (error) {
      updateStatus(`Failed to copy preset: ${error.message}`, 'error');
    }
  });

  // Delete button: Delete currently selected preset
  deletePresetBtn.addEventListener('click', async () => {
    const state = stateManager.getState();
    const selectedPresetId = presetSelect?.value;

    if (!state.uploadedFile || !selectedPresetId) {
      return;
    }

    // Block deleting "design default values" (immutable, desktop parity)
    if (selectedPresetId === DESIGN_DEFAULTS_ID) {
      updateStatus('Design default values cannot be deleted.', 'warning');
      return;
    }

    // Get preset info for confirmation
    const preset = presetManager.loadPreset(
      presetModelKey(state),
      selectedPresetId
    );
    if (!preset) {
      updateStatus('Preset not found', 'error');
      return;
    }

    // Show warning modal — deletion is irreversible
    const confirmed = await showConfirmDialog(
      `Delete the preset "${preset.name}"? This cannot be undone.`,
      'Delete Preset',
      'Delete',
      'Cancel',
      { destructive: true }
    );

    if (!confirmed) {
      return;
    }

    try {
      const deleted = presetManager.deletePreset(
        presetModelKey(state),
        selectedPresetId
      );
      if (deleted) {
        updateStatus(`Deleted preset: ${preset.name}`, 'success');
        updatePresetDropdown(); // Refresh dropdown
        forceClearPresetSelection(); // Clear state
      } else {
        updateStatus('Failed to delete preset', 'error');
      }
    } catch (error) {
      updateStatus(`Failed to delete preset: ${error.message}`, 'error');
    }
  });

  // Manage button: Import/export modal
  managePresetsBtn.addEventListener('click', showManagePresetsModal);

  // Copy preset name button: copies the visible label of the
  // currently-selected preset to the clipboard with a polite SR
  // announcement. Works for the immutable "design default values"
  // entry too — copies the exact displayed string verbatim.
  const copyPresetNameBtn = document.getElementById('copyPresetNameBtn');
  if (copyPresetNameBtn) {
    copyPresetNameBtn.addEventListener('click', async () => {
      const selected = presetSelect?.options[presetSelect.selectedIndex];
      const name = selected?.text?.trim();
      if (!presetSelect?.value || !name) {
        updateStatus('Select a preset first to copy its name', 'warning');
        return;
      }

      const result = await copyPresetName(name);
      if (result.ok) {
        announceImmediate(`Copied "${name}" to clipboard`);
        updateStatus(`Copied "${name}" to clipboard`, 'success');
      } else {
        // Non-blocking: leave the name visible in the status bar so the
        // user can select it manually. Avoids a blocking modal for what
        // is otherwise a minor convenience action.
        updateStatus(
          `Could not copy automatically. Preset name: ${name}`,
          'warning'
        );
      }
    });
  }

  // Preset sort control: re-sort dropdown when sort order changes
  const presetDropdownSort = document.getElementById('presetDropdownSort');
  if (presetDropdownSort) {
    presetDropdownSort.addEventListener('change', () => {
      safeSetItem(PRESET_SORT_KEY, presetDropdownSort.value);
      updatePresetDropdown();
      const label =
        presetDropdownSort.options[presetDropdownSort.selectedIndex]?.text ||
        '';
      announceImmediate(`Presets sorted by ${label}`);
    });
  }

  // Preset search/filter
  const presetSearchInput = document.getElementById('presetSearchInput');
  const presetSearchClear = document.getElementById('presetSearchClear');
  const presetSearchStatus = document.getElementById('presetSearchStatus');

  if (presetSearchInput && presetSearchClear && presetSearchStatus) {
    let _searchDebounce = null;

    function _applyPresetFilter() {
      const term = presetSearchInput.value.trim().toLowerCase();
      const options = Array.from(presetSelect.options);
      let visible = 0;

      for (const opt of options) {
        if (!opt.value) {
          // Keep the placeholder option always visible
          opt.hidden = false;
          continue;
        }
        const match = !term || opt.text.toLowerCase().includes(term);
        opt.hidden = !match;
        if (match) visible++;
      }

      presetSearchClear.hidden = !presetSearchInput.value;

      const total = options.filter((o) => o.value).length;
      if (!term) {
        presetSearchStatus.textContent = '';
      } else {
        presetSearchStatus.textContent = `${visible} of ${total} presets match`;
      }
    }

    presetSearchInput.addEventListener('input', () => {
      clearTimeout(_searchDebounce);
      _searchDebounce = setTimeout(_applyPresetFilter, 150);
    });

    presetSearchClear.addEventListener('click', () => {
      presetSearchInput.value = '';
      _applyPresetFilter();
      presetSearchStatus.textContent = 'Search cleared, all presets shown';
      presetSearchInput.focus();
    });
  }

  // Searchable combobox (searchable_combobox feature flag)
  if (_isEnabled('searchable_combobox')) {
    const comboContainer = document.getElementById('presetComboboxContainer');
    const presetSearchLegacy = document.getElementById('presetSearchLegacy');
    const presetSelectorLegacy = document.getElementById('presetSelector');

    if (comboContainer) {
      // Show combobox, hide legacy elements
      comboContainer.hidden = false;
      if (presetSearchLegacy) presetSearchLegacy.hidden = true;
      if (presetSelectorLegacy) presetSelectorLegacy.hidden = true;

      _presetCombobox = initSearchableCombobox({
        container: comboContainer,
        placeholder: 'Search presets…',
        inputId: 'presetComboboxInput',
        ariaLabel: 'Select preset',
        disabled: true,
      });

      // Mirror combobox selection to the hidden native select for shared event handlers
      comboContainer.addEventListener('change', (e) => {
        const id = e.detail?.value;
        if (id != null) {
          // Update the native select value so existing change handlers fire
          if (presetSelect) {
            presetSelect.value = id;
            presetSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  }

  // Update button states when preset selection changes
  presetSelect.addEventListener('change', () => {
    updatePresetControlStates();
  });

  // Initialize button states
  updatePresetControlStates();

  // Library help button handler (bind once, not in renderLibraryUI)
  const libraryHelpBtn = document.getElementById('libraryHelpBtn');
  if (libraryHelpBtn) {
    libraryHelpBtn.addEventListener('click', () => {
      openFeaturesGuide({ tab: 'libraries' });
    });
  }

  // Welcome screen role path "Learn More" buttons — each opens the
  // Features Guide on the tab named in its data-feature-tab attribute.
  const roleLearnButtons = document.querySelectorAll('.btn-role-learn');
  roleLearnButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.dataset.featureTab) {
        openFeaturesGuide({ tab: btn.dataset.featureTab });
      } else {
        openFeaturesGuide();
      }
    });
  });

  // Accessibility spotlight links
  const spotlightLinks = document.querySelectorAll('.spotlight-link');
  spotlightLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();

      if (link.dataset.featureTab) {
        openFeaturesGuide({ tab: link.dataset.featureTab });
      } else if (link.dataset.doc) {
        // A link that names a doc opens the Features Guide at its default tab.
        openFeaturesGuide();
      }
    });
  });

  /**
   * Three-way unsaved-changes prompt shown when switching away from a
   * preset with unsaved parameter changes.
   * @param {string} presetName - Name of the preset with unsaved changes
   * @returns {Promise<'save'|'discard'|'cancel'>}
   */
  function showUnsavedPresetDialog(presetName) {
    return new Promise((resolve) => {
      const dialog = document.createElement('dialog');
      dialog.className = 'preset-import-mode-dialog';
      dialog.setAttribute('aria-labelledby', 'unsavedPresetTitle');
      dialog.innerHTML = `
        <form method="dialog" class="import-mode-form">
          <h3 id="unsavedPresetTitle" class="import-mode-title">Unsaved preset changes</h3>
          <p class="import-mode-desc">The preset &ldquo;${escapeHtml(presetName)}&rdquo; has parameter changes that have not been saved.</p>
          <div class="import-mode-actions">
            <button type="submit" value="save" class="btn btn-primary">Save changes</button>
            <button type="submit" value="discard" class="btn btn-outline">Discard changes</button>
            <button type="submit" value="cancel" class="btn btn-outline">Cancel</button>
          </div>
        </form>`;
      document.body.appendChild(dialog);
      dialog.showModal();
      dialog.addEventListener(
        'close',
        () => {
          const choice = dialog.returnValue;
          document.body.removeChild(dialog);
          resolve(
            choice === 'save' || choice === 'discard' ? choice : 'cancel'
          );
        },
        { once: true }
      );
    });
  }

  // Handle preset selection
  presetSelect.addEventListener('change', async (e) => {
    const presetId = e.target.value;
    if (!presetId) return;

    const state = stateManager.getState();

    // Unsaved-changes guard: switching away from a dirty user preset
    // offers Save / Discard / Cancel instead of silently dropping edits
    const previousPresetId = state.currentPresetId;
    if (
      isPresetDirty &&
      previousPresetId &&
      previousPresetId !== presetId &&
      previousPresetId !== DESIGN_DEFAULTS_ID
    ) {
      const choice = await showUnsavedPresetDialog(
        state.currentPresetName || 'current preset'
      );
      if (choice === 'cancel') {
        presetSelect.value = previousPresetId;
        _presetCombobox?.setValue(previousPresetId);
        updatePresetControlStates();
        return;
      }
      if (choice === 'save') {
        const prevSelectValue = presetSelect.value;
        presetSelect.value = previousPresetId;
        const saved = overwriteCurrentPreset();
        presetSelect.value = prevSelectValue;
        if (!saved) {
          _presetCombobox?.setValue(previousPresetId);
          presetSelect.value = previousPresetId;
          updatePresetControlStates();
          return;
        }
      }
      // 'discard' falls through and loads the newly selected preset
    }

    // Handle "design default values" virtual preset (desktop OpenSCAD parity)
    // The values a preset replaces are one Undo away.
    stateManager.recordParameterState();
    if (presetId === DESIGN_DEFAULTS_ID) {
      isLoadingPreset = true;

      const defaultParams = { ...state.defaults };
      stateManager.setState({ parameters: defaultParams });

      // Re-render UI with default parameters
      const parametersContainer = document.getElementById(
        'parametersContainer'
      );
      renderParameterUI(
        state.schema,
        parametersContainer,
        (values) => {
          stateManager.setState({ parameters: values });
          clearPresetSelection(values);
          if (autoPreviewController) {
            autoPreviewController.onParameterChange(values);
          }
          updatePrimaryActionButton();
        },
        defaultParams
      );

      // Reset output format to STL when loading design defaults (3D preset)
      const _fmtSelectDefaults = document.getElementById('outputFormat');
      if (_fmtSelectDefaults && _fmtSelectDefaults.value !== 'stl') {
        const is2DDefaults =
          isNonPreviewable(defaultParams, state.schema) ||
          (typeof defaultParams.generate === 'string' &&
            /svg|dxf|2d|first layer/i.test(defaultParams.generate));
        if (!is2DDefaults) {
          _fmtSelectDefaults.value = 'stl';
          _fmtSelectDefaults.dispatchEvent(new Event('change'));
          stateManager.setState({ outputFormat: 'stl' });
        }
      }

      if (autoPreviewController) {
        autoPreviewController.onParameterChange(defaultParams);
      }
      updatePrimaryActionButton();

      // Track as current selection (virtual preset)
      currentPresetSignature = null;
      isPresetDirty = false;
      stateManager.setState({
        currentPresetId: DESIGN_DEFAULTS_ID,
        currentPresetName: 'design default values',
      });

      isLoadingPreset = false;
      updatePresetControlStates();
      updateStatus('Loaded design default values');
      return;
    }

    if (_isEnabled('project_presets') && presetId.startsWith('proj::')) {
      const presetName = presetId.slice(6);
      const projPresets = state.projectPresets;
      if (projPresets && projPresets[presetName]) {
        const projPreset = {
          id: presetId,
          name: presetName,
          parameters: projPresets[presetName],
          source: 'project',
        };
        applyPresetParametersAndCompanions(projPreset);
      }
      return;
    }

    const preset = presetManager.loadPreset(presetModelKey(state), presetId);

    if (preset) {
      // Log compatibility info (desktop OpenSCAD parity: silently skip extras)
      const hiddenParamNames = new Set(
        Object.keys(state.schema?.hiddenParameters || {})
      );
      const compatibility = presetManager.analyzePresetCompatibility(
        preset.parameters,
        state.schema?.parameters || state.schema,
        [...hiddenParamNames]
      );
      if (
        compatibility.extraParams.length > 0 ||
        compatibility.missingParams.length > 0
      ) {
        console.info(
          `[Preset] "${preset.name}": ${compatibility.compatibleCount} params applied, ` +
            `${compatibility.extraParams.length} skipped (not in current file), ` +
            `${compatibility.missingParams.length} kept at defaults (not in preset)`
        );
      }

      applyPresetParametersAndCompanions(preset);
      // Keep showing the preset name in dropdown (don't reset)
      // The dropdown will reset when parameters change (handled in onChange callback)
    }
  });

  /**
   * Show preset compatibility warning dialog
   * @param {Object} preset - The preset being loaded
   * @param {Object} compatibility - Compatibility analysis result
   * @param {Object} state - Current app state
   * @returns {Promise<string>} 'apply' or 'cancel'
   */
  function _showPresetCompatibilityWarning(preset, compatibility, state) {
    return new Promise((resolve) => {
      // Check for SCAD version info
      const scadVersion = state.uploadedFile?.content
        ? extractScadVersion(state.uploadedFile.content)
        : null;

      const modal = document.createElement('div');
      modal.className = 'preset-modal';
      modal.setAttribute('role', 'alertdialog');
      modal.setAttribute('aria-labelledby', 'presetCompatTitle');
      modal.setAttribute('aria-describedby', 'presetCompatMessage');
      modal.setAttribute('aria-modal', 'true');

      // Build issue list
      let issueHtml = '';

      if (compatibility.extraParams.length > 0) {
        issueHtml += `
          <div class="preset-compat-section">
            <h4>⚠️ Obsolete parameters (${compatibility.extraParams.length})</h4>
            <p>These preset parameters don't exist in the current file (may have been removed or renamed):</p>
            <ul class="preset-compat-list">
              ${compatibility.extraParams.map((p) => `<li><code>${escapeHtml(p)}</code></li>`).join('')}
            </ul>
          </div>
        `;
      }

      if (compatibility.missingParams.length > 0) {
        issueHtml += `
          <div class="preset-compat-section">
            <h4>ℹ️ New parameters (${compatibility.missingParams.length})</h4>
            <p>These file parameters aren't in the preset (will use defaults):</p>
            <ul class="preset-compat-list">
              ${compatibility.missingParams
                .slice(0, 10)
                .map((p) => `<li><code>${escapeHtml(p)}</code></li>`)
                .join('')}
              ${compatibility.missingParams.length > 10 ? `<li>...and ${compatibility.missingParams.length - 10} more</li>` : ''}
            </ul>
          </div>
        `;
      }

      const versionNote = scadVersion
        ? `<p class="preset-compat-note">Current file version: <strong>${scadVersion.version}</strong></p>`
        : '';

      modal.innerHTML = `
        <div class="preset-modal-content modal-medium">
          <div class="preset-modal-header">
            <h3 id="presetCompatTitle">Preset May Be From Different Version</h3>
            <button class="preset-modal-close" aria-label="Close">&times;</button>
          </div>
          <div class="modal-body">
            <p id="presetCompatMessage">
              The preset "<strong>${escapeHtml(preset.name)}</strong>" may have been created for a different version 
              of this file. Some parameters don't match.
            </p>
            ${versionNote}
            ${issueHtml}
            <p>
              <strong>${compatibility.compatibleCount}</strong> of <strong>${compatibility.totalPresetParams}</strong> 
              preset parameters can be applied.
            </p>
          </div>
          <div class="preset-modal-footer">
            <button type="button" class="btn btn-outline" data-action="cancel">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="apply">Apply Anyway</button>
          </div>
        </div>
      `;

      const handleAction = (action) => {
        document.body.removeChild(modal);
        resolve(action);
      };

      modal.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-action]');
        const closeBtn = e.target.closest('.preset-modal-close');

        if (btn) {
          handleAction(btn.dataset.action);
        } else if (closeBtn || e.target === modal) {
          handleAction('cancel');
        }
      });

      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          handleAction('cancel');
        }
      });

      document.body.appendChild(modal);
      modal.querySelector('button[data-action="apply"]').focus();
    });
  }

  // Subscribe to preset changes
  presetManager.subscribe((action, _preset, _modelName) => {
    // Update the dropdown only when the preset list changes. presetManager
    // emits a 'load' event too, and rebuilding the <select> on 'load' would
    // reset the selection to "-- Select Preset --".
    if (action === 'load') {
      return;
    }

    updatePresetDropdown();
  });

  // Refresh preset dropdown whenever the loaded project changes (including
  // switching from one project to another, not just the initial load).
  stateManager.subscribe((state, prevState) => {
    if (
      state.uploadedFile &&
      state.uploadedFile.name !== prevState.uploadedFile?.name
    ) {
      updatePresetDropdown();
    }
  });

  // ========== End preset system ==========

  // ========== Advanced menu ==========

  // View Source Button
  const viewSourceBtn = document.getElementById('viewSourceBtn');
  const copySourceBtn = document.getElementById('copySourceBtn');
  const sourceViewerModal = document.getElementById('sourceViewerModal');
  const sourceViewerClose = document.getElementById('sourceViewerClose');
  const sourceViewerOverlay = document.getElementById('sourceViewerOverlay');
  const sourceViewerContent = document.getElementById('sourceViewerContent');
  const sourceViewerCopy = document.getElementById('sourceViewerCopy');
  const sourceViewerInfo = document.getElementById('sourceViewerInfo');

  viewSourceBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    if (!state.uploadedFile) {
      announceImmediate('Upload a file first to view source code');
      return;
    }

    sourceViewerContent.value = state.uploadedFile.content;

    const lineCount = state.uploadedFile.content.split('\n').length;
    const charCount = state.uploadedFile.content.length;
    sourceViewerInfo.innerHTML = `
      <span>📄 ${state.uploadedFile.name}</span>
      <span>📏 ${lineCount.toLocaleString()} lines</span>
      <span>📊 ${charCount.toLocaleString()} characters</span>
    `;

    // On the heading, not the code: focus in the text put a screen reader at
    // its end ("blank"), past everything it was opened to read.
    openModal(sourceViewerModal, {
      focusTarget: document.getElementById('sourceViewerTitle'),
    });
  });

  copySourceBtn?.addEventListener('click', async () => {
    const state = stateManager.getState();
    if (!state.uploadedFile) {
      announceImmediate('Upload a file first to copy source code');
      return;
    }

    try {
      await navigator.clipboard.writeText(state.uploadedFile.content);
      copySourceBtn.textContent = '✅ Copied!';
      updateStatus('Source code copied to clipboard');
      setTimeout(() => {
        copySourceBtn.textContent = '📋 Copy Source';
      }, 2000);
    } catch (error) {
      console.error('Failed to copy source:', error);
      const textarea = document.createElement('textarea');
      textarea.value = state.uploadedFile.content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      copySourceBtn.textContent = '✅ Copied!';
      setTimeout(() => {
        copySourceBtn.textContent = '📋 Copy Source';
      }, 2000);
    }
  });

  sourceViewerClose?.addEventListener('click', () => {
    closeModal(sourceViewerModal);
  });

  sourceViewerOverlay?.addEventListener('click', () => {
    closeModal(sourceViewerModal);
  });

  sourceViewerCopy?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(sourceViewerContent.value);
      sourceViewerCopy.textContent = '✅ Copied!';
      setTimeout(() => {
        sourceViewerCopy.textContent = '📋 Copy';
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  });

  // =========================================
  // Console output display for ECHO/WARNING/ERROR messages
  // =========================================
  const consoleOutputModal = document.getElementById('consoleOutputModal');
  const consoleOutputClose = document.getElementById('consoleOutputClose');
  const consoleOutputOverlay = document.getElementById('consoleOutputOverlay');
  const consoleOutput = document.getElementById('consoleOutput');
  const consoleCopyBtn = document.getElementById('consoleCopyBtn');
  const consoleClearBtn = document.getElementById('consoleClearBtn');
  const consoleCloseBtn = document.getElementById('consoleCloseBtn');

  // State for console output
  let lastConsoleOutput = '';

  const BENIGN_OPENSCAD_CONSOLE_PATTERNS = [
    /Could not initialize localization \(application path is '\/'\)\.?/i,
    /WARNING:\s*Viewall and autocenter disabled in favor of \$vp\*/i,
  ];

  function normalizeOpenSCADConsoleOutput(output) {
    if (!output || typeof output !== 'string') return '';

    const normalizedLines = output
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        if (line.startsWith('[ERR] ')) return line.substring(6).trim();
        if (line.startsWith('[ERR]')) return line.substring(5).trim();
        return line;
      })
      .filter(
        (line) =>
          !BENIGN_OPENSCAD_CONSOLE_PATTERNS.some((pattern) =>
            pattern.test(line)
          )
      );

    return normalizedLines.join('\n');
  }

  // Initialize ErrorLogPanel first (renders into structured view tab)
  const errorLogPanel = getErrorLogPanel();
  initAddStructuredError();

  // Initialize unified ConsolePanel with structured sub-panel
  const consolePanel = getConsolePanel({ structuredPanel: errorLogPanel });

  /**
   * Update console output display
   * Display ECHO/WARNING/ERROR messages for user communication
   * @param {string} output - Console output from OpenSCAD render
   */
  function updateConsoleOutput(output, { append = false } = {}) {
    if (!output || output.trim() === '') return;
    const normalizedOutput = normalizeOpenSCADConsoleOutput(output);

    if (!append) {
      // Append-only log (desktop parity): mark a new render section instead
      // of wiping the log. The structured error table still reflects only
      // the latest render so click-to-line never targets stale errors.
      consolePanel.beginRenderSection();
      errorLogPanel.clear();
    }

    if (!normalizedOutput || normalizedOutput.trim() === '') {
      lastConsoleOutput = '';
      updatePreviewDrawer([]);
      if (consoleOutput && !consoleOutputModal?.classList.contains('hidden')) {
        renderConsoleOutput('');
      }
      return;
    }

    lastConsoleOutput = normalizedOutput;

    // If modal is open, update it
    if (consoleOutput && !consoleOutputModal?.classList.contains('hidden')) {
      renderConsoleOutput(normalizedOutput);
    }

    // Feed both panels with parsed console output
    consolePanel.addOutput(normalizedOutput);
    errorLogPanel.addOutput(normalizedOutput);

    // Extract ECHO/WARNING/ERROR messages and display in preview drawer
    const consoleMessages = extractConsoleMessages(normalizedOutput);
    updatePreviewDrawer(consoleMessages);

    const echoCount = consoleMessages.filter((m) => m.type === 'echo').length;
    if (echoCount > 0) {
      console.log(`[Console] ${echoCount} ECHO statement(s) captured`);
    }
  }

  /**
   * Extract ECHO, WARNING, and ERROR messages from console output.
   * @param {string} output - Raw console output
   * @returns {{ type: 'echo'|'warning'|'error', text: string }[]}
   */
  const _ERR_INFO_PATTERNS =
    /^(?:Geometries in cache|Geometry cache size|CGAL Polyhedrons in cache|CGAL cache size|Total rendering time|Top level object is|Status:\s|Genus:\s|Vertices:\s|Facets:\s|Could not initialize localization|Compiling design|Rendering design)/i;

  function extractConsoleMessages(output) {
    if (!output) return [];

    return output
      .split('\n')
      .map((line) => {
        const trimmed = line.trim();
        if (!trimmed) return null;

        if (trimmed.startsWith('ECHO:')) {
          // The whole echo, quotes and every argument: a designer's
          // echo("Width:", w) is read for its value.
          const text = trimmed.replace(/^ECHO:\s*/, '').trim();
          return text.length > 0 ? { type: 'echo', text } : null;
        }

        if (trimmed.includes('WARNING:') || trimmed.includes('Warning:')) {
          const warnText = trimmed.startsWith('[ERR] ')
            ? trimmed.substring(6)
            : trimmed;
          return { type: 'warning', text: warnText };
        }

        if (trimmed.includes('ERROR:') || trimmed.includes('Error:')) {
          const errText = trimmed.startsWith('[ERR] ')
            ? trimmed.substring(6)
            : trimmed;
          return { type: 'error', text: errText };
        }

        if (trimmed.startsWith('[ERR]')) {
          const text = trimmed
            .substring(trimmed.startsWith('[ERR] ') ? 6 : 5)
            .trim();
          if (!text || _ERR_INFO_PATTERNS.test(text)) {
            return null;
          }
          return { type: 'error', text };
        }

        return null;
      })
      .filter(Boolean);
  }

  // Echo drawer fold state: a fold the user chose survives re-renders with
  // the same problems; only new warnings or errors force it back open.
  let echoDrawerUserCollapsed = false;
  let lastEchoImportantCount = 0;

  /**
   * Update the preview drawer to show ECHO, WARNING, and ERROR messages.
   * @param {{ type: 'echo'|'warning'|'error', text: string }[]} messages
   */
  function updatePreviewDrawer(messages) {
    const echoDrawer = document.getElementById('echoDrawer');
    const echoDrawerLabel = document.getElementById('echoDrawerLabel');
    const echoMessagesEl = document.getElementById('echoMessages');

    if (!echoDrawer || !echoDrawerLabel || !echoMessagesEl) return;

    if (messages.length === 0) {
      echoDrawer.classList.remove(
        'visible',
        'echo-drawer--warning',
        'echo-drawer--error'
      );
      echoDrawer.classList.add('collapsed');
      echoDrawerLabel.textContent = 'No messages';
      echoMessagesEl.innerHTML = '';
      echoDrawerUserCollapsed = false;
      lastEchoImportantCount = 0;
      document
        .getElementById('echoDrawerToggle')
        ?.setAttribute('aria-expanded', 'false');
      return;
    }

    const echoCount = messages.filter((m) => m.type === 'echo').length;
    const warnCount = messages.filter((m) => m.type === 'warning').length;
    const errorCount = messages.filter((m) => m.type === 'error').length;

    // Build label describing the mix of message types
    const parts = [];
    if (echoCount > 0) parts.push(`${echoCount} echo`);
    if (warnCount > 0)
      parts.push(`${warnCount} warning${warnCount > 1 ? 's' : ''}`);
    if (errorCount > 0)
      parts.push(`${errorCount} error${errorCount > 1 ? 's' : ''}`);
    echoDrawerLabel.textContent = `OpenSCAD Messages (${parts.join(', ')})`;

    // Set severity class on the drawer for toggle-bar tinting
    echoDrawer.classList.remove('echo-drawer--warning', 'echo-drawer--error');
    if (errorCount > 0) {
      echoDrawer.classList.add('echo-drawer--error');
    } else if (warnCount > 0) {
      echoDrawer.classList.add('echo-drawer--warning');
    }

    // Render each message as a color-coded line (text-only, no user HTML)
    const escHtml = (s) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    echoMessagesEl.innerHTML = messages
      .map((m) => {
        const cls = `echo-msg-line echo-msg-line--${m.type}`;
        const prefix =
          m.type === 'echo' ? 'ECHO: ' : m.type === 'warning' ? '' : '';
        return `<span class="${cls}">${escHtml(prefix + m.text)}</span>`;
      })
      .join('\n');

    // The drawer is 120px tall and holds one render's messages, so without
    // this a design that echoes more than about six lines would show only its
    // first few, every time. Same reasoning as the console modal: the content
    // is replaced wholesale each render, so there is no reader position worth
    // preserving and no scrolled-up pause.
    echoMessagesEl.scrollTop = echoMessagesEl.scrollHeight;

    // Show the drawer: always mark it visible so the badge/label appears.
    // Auto-expand only when problems are present AND either the user has
    // not folded it, or NEW problems arrived since their fold.
    echoDrawer.classList.add('visible');
    const importantCount = warnCount + errorCount;
    if (
      importantCount > 0 &&
      (!echoDrawerUserCollapsed || importantCount > lastEchoImportantCount)
    ) {
      echoDrawer.classList.remove('collapsed');
      echoDrawerUserCollapsed = false;
    }
    lastEchoImportantCount = importantCount;

    // aria mirrors the real state: the class and the attribute are never
    // written from different truths, so echo-only output cannot read as
    // aria-expanded=false while its content stays visible.
    document
      .getElementById('echoDrawerToggle')
      ?.setAttribute(
        'aria-expanded',
        String(!echoDrawer.classList.contains('collapsed'))
      );

    // A run with an error has failed, and its failure is announced with the
    // error; the count would be a second voice for the same news. A single
    // message is said by its own line in the console; the count is for when
    // several arrive at once.
    if (errorCount > 0 || warnCount + echoCount < 2) return;

    // Build accessible announcement
    const summary = [];
    if (warnCount > 0)
      summary.push(`${warnCount} warning${warnCount > 1 ? 's' : ''}`);
    if (echoCount > 0)
      summary.push(`${echoCount} echo message${echoCount > 1 ? 's' : ''}`);
    announceImmediate(`Model has ${summary.join(', ')}`, {
      source: 'messages',
    });
  }

  /**
   * Render console output with highlighted ECHO lines
   * @param {string} output - Raw console output
   */
  function renderConsoleOutput(output) {
    if (!consoleOutput) return;

    if (!output || output.trim() === '') {
      consoleOutput.textContent =
        'No console output yet. Generate a model to see output.';
      return;
    }

    // Split into lines and highlight ECHO lines
    const lines = output.split('\n');
    const highlightedLines = lines.map((line) => {
      if (line.includes('ECHO:')) {
        return `<span class="echo-line">${escapeHtml(line)}</span>`;
      }
      return escapeHtml(line);
    });

    consoleOutput.innerHTML = highlightedLines.join('\n');

    // Land on the newest output, like the desktop console. No scrolled-up
    // pause here, unlike the Log view: this pane shows only the latest
    // render's output rather than a running log, so when it is re-rendered
    // the text a reader was holding their place in is gone.
    consoleOutput.scrollTop = consoleOutput.scrollHeight;
  }

  // Open console modal
  const openConsoleModal = () => {
    if (!consoleOutputModal) return;

    // Render current console output
    renderConsoleOutput(lastConsoleOutput);

    // Show it with focus inside, on its heading: left on the button behind
    // it, Escape had nothing to close.
    openModal(consoleOutputModal, {
      focusTarget: document.getElementById('consoleOutputTitle'),
    });

    // renderConsoleOutput's scroll-to-newest ran against a hidden modal, where
    // scrollHeight is 0, so it did nothing. Now that the modal has layout, put
    // it on the newest output the way the desktop console does.
    if (consoleOutput) {
      consoleOutput.scrollTop = consoleOutput.scrollHeight;
    }
    // No announcement: focus on the heading already says which panel this
    // is, and "Console output panel opened" said it a second time.
  };

  // Echo drawer toggle
  const echoDrawerToggleBtn = document.getElementById('echoDrawerToggle');
  const echoDrawerEl = document.getElementById('echoDrawer');

  echoDrawerToggleBtn?.addEventListener('click', () => {
    if (!echoDrawerEl) return;
    const collapsed = echoDrawerEl.classList.toggle('collapsed');
    echoDrawerUserCollapsed = collapsed;
    echoDrawerToggleBtn.setAttribute('aria-expanded', String(!collapsed));

    // A collapsed drawer has no layout, so the scroll-to-newest that ran when
    // its messages arrived was a no-op against a scrollHeight of 0. Opening it
    // is the first moment the write can land.
    if (!collapsed) {
      const messages = document.getElementById('echoMessages');
      if (messages) messages.scrollTop = messages.scrollHeight;
    }
  });

  // The status line over the preview rests on the echo drawer, whatever its
  // height: closed, open, or gone (layout.css reads the property). Sitting
  // on it, the line hid the drawer's label and messages, and in the Alt View
  // its Characters and Edges row caught the presses meant for the toggle.
  const previewCanvasSection = document.getElementById('previewCanvasSection');
  if (echoDrawerEl && previewCanvasSection && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      previewCanvasSection.style.setProperty(
        '--echo-drawer-height',
        `${echoDrawerEl.offsetHeight}px`
      );
    }).observe(echoDrawerEl);
  }

  // Echo drawer "View Full Console" button
  const echoViewConsoleBtn = document.getElementById('echoViewConsoleBtn');
  echoViewConsoleBtn?.addEventListener('click', openConsoleModal);

  // Close handlers
  const closeConsoleModal = () => {
    if (consoleOutputModal) {
      closeModal(consoleOutputModal);
    }
  };

  consoleOutputClose?.addEventListener('click', closeConsoleModal);
  consoleOutputOverlay?.addEventListener('click', closeConsoleModal);
  consoleCloseBtn?.addEventListener('click', closeConsoleModal);

  // Escape key closes console modal (accessibility)
  consoleOutputModal?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeConsoleModal();
      e.preventDefault();
    }
  });

  // Copy to clipboard
  consoleCopyBtn?.addEventListener('click', async () => {
    if (!lastConsoleOutput) {
      consoleCopyBtn.textContent = 'Nothing to copy';
      setTimeout(() => {
        consoleCopyBtn.innerHTML = `
          <svg class="btn-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          Copy to Clipboard
        `;
      }, 2000);
      return;
    }

    try {
      await navigator.clipboard.writeText(lastConsoleOutput);
      consoleCopyBtn.textContent = '✅ Copied!';
      announceImmediate('Console output copied to clipboard');
      setTimeout(() => {
        consoleCopyBtn.innerHTML = `
          <svg class="btn-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          Copy to Clipboard
        `;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy console output:', error);
      consoleCopyBtn.textContent = 'Copy failed';
    }
  });

  // Download console log for troubleshooting
  const consoleDownloadBtn = document.getElementById('consoleDownloadBtn');
  consoleDownloadBtn?.addEventListener('click', () => {
    if (!lastConsoleOutput) {
      updateStatus('No console output to download', 'warning');
      return;
    }

    // Generate filename with timestamp
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, 19);
    const state = stateManager.getState();
    const modelName =
      state.uploadedFile?.name?.replace('.scad', '') || 'console';
    const filename = `${modelName}-console-${timestamp}.txt`;

    // Create blob and download
    const blob = new Blob([lastConsoleOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    updateStatus(`Downloaded: ${filename}`, 'success');
    announceImmediate('Console log downloaded');
  });

  // Clear console
  consoleClearBtn?.addEventListener('click', () => {
    clearConsoleState();
    announceImmediate('Console output cleared');
  });

  /**
   * Reset all console/warning display state to a clean slate.
   * Called on project switch and by the manual clear button.
   */
  function clearConsoleState() {
    lastConsoleOutput = '';
    renderConsoleOutput('');
    consolePanel.clear();
    const consolePanelDetails = document.getElementById('consolePanel');
    if (consolePanelDetails) {
      consolePanelDetails.open = false;
    }
  }

  // Make updateConsoleOutput available globally for the render result handler
  window.updateConsoleOutput = updateConsoleOutput;
  window.clearConsoleState = clearConsoleState;

  // Unlock Limits Toggle
  const unlockLimitsToggle = document.getElementById('unlockLimitsToggle');
  unlockLimitsToggle?.addEventListener('change', (e) => {
    const unlocked = e.target.checked;
    setLimitsUnlocked(unlocked);

    if (unlocked) {
      updateStatus(
        '⚠️ Parameter limits unlocked - values outside normal range allowed'
      );
    } else {
      updateStatus('Parameter limits restored to defaults');
    }
  });

  // Expand all / Collapse all.
  // Setting `.open` programmatically fires the <details> 'toggle' event
  // which is already wired in ui-generator.js to persist per-file state,
  // so these handlers stay deliberately tiny.
  const expandAllGroupsBtn = document.getElementById('expandAllGroupsBtn');
  const collapseAllGroupsBtn = document.getElementById('collapseAllGroupsBtn');
  /** @param {boolean} open */
  const setAllParamGroupsOpen = (open) => {
    const parametersContainer = document.getElementById('parametersContainer');
    if (!parametersContainer) return;
    const groups = parametersContainer.querySelectorAll('details.param-group');
    if (groups.length === 0) {
      announceImmediate('No parameter groups to update');
      return;
    }
    groups.forEach((d) => {
      if (d.open !== open) d.open = open;
    });
    if (open) {
      // Focus the first parameter when Expand all is activated, to give
      // keyboard users a clear next landing spot.
      const firstControl = /** @type {HTMLElement|null} */ (
        parametersContainer.querySelector(
          '.param-control input, .param-control select, .param-control textarea, .param-control button'
        )
      );
      if (firstControl && typeof firstControl.focus === 'function') {
        firstControl.focus();
      }
    }
    announceImmediate(
      open
        ? `Expanded ${groups.length} parameter groups`
        : `Collapsed ${groups.length} parameter groups`
    );
  };
  expandAllGroupsBtn?.addEventListener('click', () =>
    setAllParamGroupsOpen(true)
  );
  collapseAllGroupsBtn?.addEventListener('click', () =>
    setAllParamGroupsOpen(false)
  );

  // Reset Group Button
  const resetGroupBtn = document.getElementById('resetGroupBtn');
  const resetGroupSelector = document.getElementById('resetGroupSelector');
  const resetGroupSelect = document.getElementById('resetGroupSelect');
  const confirmResetGroupBtn = document.getElementById('confirmResetGroupBtn');

  resetGroupBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    if (!state.schema || !state.schema.groups) {
      showErrorToast({
        title: 'No Model Loaded',
        message: 'Upload a model with parameter groups first.',
      });
      return;
    }

    resetGroupSelect.innerHTML = '';
    state.schema.groups.forEach((group) => {
      const option = document.createElement('option');
      option.value = group.id;
      option.textContent = group.label;
      resetGroupSelect.appendChild(option);
    });

    resetGroupSelector.classList.remove('hidden');
    // To the picker it just revealed, which names itself: otherwise the
    // press sounded as if nothing had happened.
    resetGroupSelect.focus();
  });

  confirmResetGroupBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    const groupId = resetGroupSelect.value;

    if (!groupId || !state.schema) return;

    stateManager.recordParameterState();

    const defaults = getAllDefaults();
    const newParams = { ...state.parameters };
    let resetCount = 0;

    Object.values(state.schema.parameters).forEach((param) => {
      if (param.group === groupId && defaults[param.name] !== undefined) {
        newParams[param.name] = defaults[param.name];
        resetCount++;
      }
    });

    stateManager.setState({ parameters: newParams });

    const parametersContainer = document.getElementById('parametersContainer');
    renderParameterUI(
      state.schema,
      parametersContainer,
      (values) => {
        stateManager.recordParameterState();
        stateManager.setState({ parameters: values });
        clearPresetSelection(values);
        if (autoPreviewController && state.uploadedFile) {
          autoPreviewController.onParameterChange(values);
        }
        updatePrimaryActionButton();
      },
      newParams
    );

    if (autoPreviewController && state.uploadedFile) {
      autoPreviewController.onParameterChange(newParams);
    }

    // Back to Reset Group: the picker and its button are hidden now.
    if (resetGroupSelector.contains(document.activeElement)) {
      resetGroupBtn?.focus();
    }
    resetGroupSelector.classList.add('hidden');
    const groupLabel =
      state.schema.groups.find((g) => g.id === groupId)?.label || groupId;
    updateStatus(
      `Reset ${resetCount} parameters in "${groupLabel}" to defaults`
    );
    updatePrimaryActionButton();
  });

  // View Params JSON Button
  const viewParamsJsonBtn = document.getElementById('viewParamsJsonBtn');
  const paramsJsonModal = document.getElementById('paramsJsonModal');
  const paramsJsonClose = document.getElementById('paramsJsonClose');
  const paramsJsonOverlay = document.getElementById('paramsJsonOverlay');
  const paramsJsonContent = document.getElementById('paramsJsonContent');
  const paramsJsonCopy = document.getElementById('paramsJsonCopy');

  viewParamsJsonBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    if (!state.uploadedFile) {
      showErrorToast({
        title: 'No File Loaded',
        message: 'Upload a .scad or .zip file first.',
      });
      return;
    }

    const json = JSON.stringify(state.parameters, null, 2);
    paramsJsonContent.value = json;
    // On the heading, as View Source: focus in the text started at its end.
    openModal(paramsJsonModal, {
      focusTarget: document.getElementById('paramsJsonTitle'),
    });
  });

  paramsJsonClose?.addEventListener('click', () => {
    closeModal(paramsJsonModal);
  });

  paramsJsonOverlay?.addEventListener('click', () => {
    closeModal(paramsJsonModal);
  });

  paramsJsonCopy?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(paramsJsonContent.value);
      paramsJsonCopy.textContent = '✅ Copied!';
      updateStatus('Customizer JSON copied to clipboard');
      setTimeout(() => {
        paramsJsonCopy.textContent = '📋 Copy';
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  });

  // =========================================
  // Export Changed Settings (troubleshooting and sharing support)
  // =========================================
  const exportChangedBtn = document.getElementById('exportChangedBtn');

  exportChangedBtn?.addEventListener('click', () => {
    const state = stateManager.getState();
    if (!state.uploadedFile || !state.schema) {
      showErrorToast({
        title: 'No File Loaded',
        message: 'Upload a .scad or .zip file first.',
      });
      return;
    }

    const defaultParams = state.schema.parameters || {};

    // The file names the project it belongs to; currentModelName is not
    // always set, and the file then said "Unknown Model".
    const modelName =
      state.uploadedFile?.name || state.currentModelName || 'model';
    const changedJson = presetManager.exportChangedParametersJSON(
      state.parameters,
      defaultParams,
      modelName
    );

    const parsed = JSON.parse(changedJson);

    if (parsed.message && parsed.changeCount === undefined) {
      updateStatus('All parameters are at default values');
      announceImmediate(
        'All parameters are at default values. Nothing to export.'
      );
      return;
    }

    const blob = new Blob([changedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `${projectStem(modelName)}-${todayStamp()}-changed-settings.json`;
    a.click();
    URL.revokeObjectURL(url);

    const changeCount = parsed.changeCount || 0;
    updateStatus(`Exported ${changeCount} changed parameter(s)`);
    announceImmediate(
      `Downloaded ${changeCount} changed parameters as JSON file`
    );
  });

  // Close modals on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const featuresGuideModal = document.getElementById('featuresGuideModal');
      if (!sourceViewerModal.classList.contains('hidden')) {
        closeModal(sourceViewerModal);
      }
      if (!paramsJsonModal.classList.contains('hidden')) {
        closeModal(paramsJsonModal);
      }
      if (
        featuresGuideModal &&
        !featuresGuideModal.classList.contains('hidden')
      ) {
        closeFeaturesGuide();
      }
    }
  });

  // ========== End advanced menu ==========

  // ========== Features guide modal ==========

  // Open Features Guide modal with optional tab selection
  function openFeaturesGuide({ tab = 'libraries' } = {}) {
    const featuresGuideModal = document.getElementById('featuresGuideModal');
    if (!featuresGuideModal) return;

    // Show modal with focus trap + automatic focus restoration
    openModal(featuresGuideModal, {
      // Focus will be moved to the requested tab (or first focusable)
      focusTarget: document.getElementById(`tab-${tab}`) || undefined,
    });

    // Switch to requested tab
    const tabId = `tab-${tab}`;
    const tabButton = document.getElementById(tabId);
    if (tabButton) {
      switchFeaturesTab(tabId);
      // A second try, for a browser that dropped the dialog's first focus. It
      // must not pull focus back from a tab the person has already moved to.
      setTimeout(() => {
        if (!featuresGuideModal.contains(document.activeElement)) {
          tabButton.focus();
        }
      }, 100);
    }
  }

  // Expose openFeaturesGuide to window for module-level functions
  if (typeof window !== 'undefined') {
    window.openFeaturesGuide = openFeaturesGuide;
  }

  // Close Features Guide modal
  function closeFeaturesGuide() {
    const featuresGuideModal = document.getElementById('featuresGuideModal');
    if (!featuresGuideModal) return;

    closeModal(featuresGuideModal);
  }

  // Switch between tabs
  function switchFeaturesTab(tabId) {
    const allTabs = document.querySelectorAll('.features-tab');
    const allPanels = document.querySelectorAll('.features-panel');

    allTabs.forEach((tab) => {
      const isActive = tab.id === tabId;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    allPanels.forEach((panel) => {
      const panelId = panel.id;
      const associatedTab = document.querySelector(
        `[aria-controls="${panelId}"]`
      );
      if (associatedTab && associatedTab.id === tabId) {
        panel.hidden = false;
      } else {
        panel.hidden = true;
      }
    });
  }

  // Features Guide close button
  const featuresGuideClose = document.getElementById('featuresGuideClose');
  featuresGuideClose?.addEventListener('click', closeFeaturesGuide);

  // Features Guide overlay click
  const featuresGuideOverlay = document.getElementById('featuresGuideOverlay');
  featuresGuideOverlay?.addEventListener('click', closeFeaturesGuide);

  // Features Guide main button handler
  const featuresGuideBtn = document.getElementById('featuresGuideBtn');
  if (featuresGuideBtn) {
    featuresGuideBtn.addEventListener('click', () => {
      openFeaturesGuide();
    });
  }

  // Tab keyboard navigation
  const featuresTabs = document.querySelectorAll('.features-tab');
  featuresTabs.forEach((tab, _index) => {
    // Click to activate tab
    tab.addEventListener('click', () => {
      switchFeaturesTab(tab.id);
    });

    // Keyboard navigation
    tab.addEventListener('keydown', (e) => {
      // Filter out hidden tabs (e.g., gated Alt View tab)
      const tabs = Array.from(featuresTabs).filter((t) => !t.hidden);
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex === -1) return; // Current tab is hidden, skip navigation
      let nextIndex = currentIndex;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
          tabs[nextIndex].focus();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
          tabs[nextIndex].focus();
          break;
        case 'Home':
          e.preventDefault();
          tabs[0].focus();
          break;
        case 'End':
          e.preventDefault();
          tabs[tabs.length - 1].focus();
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          switchFeaturesTab(tab.id);
          break;
      }
    });
  });

  // Example buttons within Features Guide
  document.addEventListener('click', (e) => {
    const exampleBtn = e.target.closest('[data-feature-example]');
    if (exampleBtn && exampleBtn.dataset.example) {
      e.preventDefault();
      const exampleKey = exampleBtn.dataset.example;
      fileHandler.loadExampleByKey(exampleKey, {
        closeFeaturesGuideModal: true,
      });
    }
  });

  // ========== End features guide modal ==========

  // ========== Configurable keyboard shortcuts ==========
  // Handlers for the configurable actions defined in keyboard-config.js.

  keyboardConfig.on('render', () => {
    const state = stateManager.getState();
    if (state.uploadedFile && !primaryActionBtn.disabled) {
      runFullRender();
    }
  });

  // Registered here rather than in a document-level keydown listener, so
  // these show in the shortcuts modal, can be rebound, and stay out of text
  // entry: the registry skips every non-global shortcut inside an input or
  // a contenteditable, so Ctrl+Z in the code editor runs the editor's own
  // undo, not the parameter undo, and nothing fires twice.
  keyboardConfig.on('undo', () => {
    const state = stateManager.getState();
    if (state.uploadedFile && stateManager.canUndo()) {
      performUndo();
    }
  });

  const redoParameterChange = () => {
    const state = stateManager.getState();
    if (state.uploadedFile && stateManager.canRedo()) {
      performRedo();
    }
  };
  keyboardConfig.on('redo', redoParameterChange);
  keyboardConfig.on('redoAlt', redoParameterChange);

  keyboardConfig.on('renderAlt', () => {
    const state = stateManager.getState();
    if (state.uploadedFile && !primaryActionBtn.disabled) {
      primaryActionBtn.click();
    }
  });

  keyboardConfig.on('generateShortcut', () => {
    const state = stateManager.getState();
    if (
      state.uploadedFile &&
      primaryActionBtn.dataset.action === 'generate' &&
      !primaryActionBtn.disabled
    ) {
      primaryActionBtn.click();
    }
  });

  keyboardConfig.on('downloadShortcut', () => {
    const state = stateManager.getState();
    if (state.stl && primaryActionBtn.dataset.action === 'download') {
      primaryActionBtn.click();
    }
  });

  /**
   * Design > Preview and F5: preview now, whether or not Automatic preview is
   * on. With nothing changed there is nothing to render, and the status line
   * says so.
   */
  function previewNow() {
    if (!stateManager.getState().uploadedFile || !autoPreviewController) {
      return;
    }
    publishEditorEdits();
    const { parameters } = stateManager.getState();
    if (autoPreviewController.isPreviewCurrentFor(parameters)) {
      updateStatus('Preview is current');
      return;
    }
    autoPreviewController.forcePreview(parameters).catch((error) => {
      console.error('[Preview] Preview failed:', error);
      showErrorToast({ title: 'Preview Failed', message: error.message });
    });
  }

  keyboardConfig.on('preview', previewNow);

  keyboardConfig.on('reloadAndPreview', () => {
    const state = stateManager.getState();
    if (state.uploadedFile) {
      fileActionsController.onReload();
      if (autoPreviewController) {
        autoPreviewController.onParameterChange(
          stateManager.getState().parameters
        );
      }
    }
  });

  // The same stop as the Cancel button, which only a Generate shows. A
  // preview has no Cancel and nothing to fall back to: stopped, it would say
  // "Generating..." for good.
  const cancelGenerate = () => {
    if (!cancelRenderBtn.classList.contains('hidden')) cancelRenderBtn.click();
  };
  keyboardConfig.on('cancelRender', cancelGenerate);

  keyboardConfig.on('download', () => {
    const state = stateManager.getState();
    if (state.stl) {
      primaryActionBtn.click();
    }
  });

  keyboardConfig.on('focusMode', () => {
    const focusModeBtn = document.getElementById('focusModeBtn');
    focusModeBtn?.click();
  });

  keyboardConfig.on('altViewSmaller', () => hfmCtrl.stepCharacters(-1));
  keyboardConfig.on('altViewLarger', () => hfmCtrl.stepCharacters(1));

  // Ctrl+B and Ctrl+Alt+4 carry the same description in the shortcuts
  // modal, "Toggle Customizer panel", and run the same command.
  keyboardConfig.on('toggleParameters', () => toggleCustomizerPanel());

  const resetViewCommand = () => {
    if (previewManager) {
      previewManager.resetCamera();
      announceImmediate('View reset to default');
    }
  };
  keyboardConfig.on('resetView', resetViewCommand);

  // Camera view presets (Ctrl+numpad to match OpenSCAD desktop)
  keyboardConfig.on('viewTop', () => {
    if (previewManager) previewManager.setCameraView('top');
  });

  keyboardConfig.on('viewBottom', () => {
    if (previewManager) previewManager.setCameraView('bottom');
  });

  keyboardConfig.on('viewFront', () => {
    if (previewManager) previewManager.setCameraView('front');
  });

  keyboardConfig.on('viewBack', () => {
    if (previewManager) previewManager.setCameraView('back');
  });

  keyboardConfig.on('viewLeft', () => {
    if (previewManager) previewManager.setCameraView('left');
  });

  keyboardConfig.on('viewRight', () => {
    if (previewManager) previewManager.setCameraView('right');
  });

  keyboardConfig.on('viewDiagonal', () => {
    if (previewManager) previewManager.setCameraView('diagonal');
  });

  keyboardConfig.on('viewCenter', () => {
    if (previewManager) {
      previewManager.centerCamera();
      announceImmediate('View centered on the model');
    }
  });

  keyboardConfig.on('toggleProjection', () => {
    if (previewManager) {
      const newMode = previewManager.toggleProjection();
      const isPerspective = newMode === 'perspective';

      // Update desktop toggle button state
      const projToggle = document.getElementById('projectionToggle');
      if (projToggle) {
        projToggle.setAttribute(
          'aria-pressed',
          isPerspective ? 'false' : 'true'
        );
        projToggle.title = isPerspective
          ? 'Switch to Orthographic (P)'
          : 'Switch to Perspective (P)';
        const labelSpan = projToggle.querySelector('span');
        if (labelSpan) {
          labelSpan.textContent = isPerspective
            ? 'Perspective'
            : 'Orthographic';
        }
      }

      // Update mobile toggle button state
      const mobileProjToggle = document.getElementById(
        'mobileProjectionToggle'
      );
      if (mobileProjToggle) {
        mobileProjToggle.setAttribute(
          'aria-pressed',
          isPerspective ? 'false' : 'true'
        );
        mobileProjToggle.title = isPerspective
          ? 'Switch to Orthographic'
          : 'Switch to Perspective';
        const mobileLabelSpan = mobileProjToggle.querySelector('span');
        if (mobileLabelSpan) {
          mobileLabelSpan.textContent = isPerspective
            ? 'Perspective'
            : 'Orthographic';
        }
      }
    }
  });

  keyboardConfig.on('focusSavedProjects', () => {
    const savedProjectsList = document.getElementById('savedProjectsList');
    const welcomeScreen = document.getElementById('welcomeScreen');

    // Only focus if on welcome screen
    if (welcomeScreen && !welcomeScreen.classList.contains('hidden')) {
      if (savedProjectsList) {
        // Scroll to saved projects section
        savedProjectsList.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });

        // Focus first project card if available
        const firstCard = savedProjectsList.querySelector(
          '.saved-project-card'
        );
        if (firstCard) {
          firstCard.focus();
        } else {
          savedProjectsList.focus();
        }
      }
    }
  });

  keyboardConfig.on('resetAllParams', () => {
    const state = stateManager.getState();
    if (state.uploadedFile) {
      resetAllBtn.click();
    }
  });

  // Classic renders one fixed desktop appearance, so these shortcuts would
  // flip a setting with no visible effect. Say so instead of no-opping.
  const APPEARANCE_UNAVAILABLE_IN_CLASSIC =
    'Not available in Classic mode. Classic uses the desktop light appearance. Leave Classic to change the theme or high contrast.';

  keyboardConfig.on('toggleHighContrast', () => {
    if (document.body.dataset.uiMode === 'classic') {
      announceImmediate(APPEARANCE_UNAVAILABLE_IN_CLASSIC);
      updateStatus('High contrast is not available in Classic mode');
      return;
    }
    const enabled = themeManager.toggleHighContrast();
    announceImmediate(`High contrast mode ${enabled ? 'enabled' : 'disabled'}`);
  });
  keyboardConfig.on('searchParams', () => {
    const searchInput = document.getElementById('paramSearchInput');
    if (searchInput) {
      searchInput.scrollIntoView({ block: 'nearest' });
      searchInput.focus();
    }
  });
  keyboardConfig.on('toggleTheme', () => {
    if (document.body.dataset.uiMode === 'classic') {
      announceImmediate(APPEARANCE_UNAVAILABLE_IN_CLASSIC);
      updateStatus('Theme switching is not available in Classic mode');
      return;
    }
    themeManager.cycleTheme();
  });

  keyboardConfig.on('showShortcutsModal', () => openShortcutsDialog());

  // File action shortcuts
  keyboardConfig.on('newFile', () => fileActionsController.onNew());
  keyboardConfig.on('saveFile', () => fileActionsController.onSave());
  keyboardConfig.on('saveFileAs', () => fileActionsController.onSaveAs());
  keyboardConfig.on('reloadFile', () => fileActionsController.onReload());
  keyboardConfig.on('exportImage', () => fileActionsController.onExportImage());

  // Edit action shortcuts
  keyboardConfig.on('copyViewportImage', () =>
    editActionsController.copyViewportImage()
  );
  keyboardConfig.on('jumpNextError', () =>
    editActionsController.jumpToNextError()
  );
  keyboardConfig.on('jumpPrevError', () =>
    editActionsController.jumpToPrevError()
  );
  keyboardConfig.on('increaseFontSize', () =>
    editActionsController.increaseFontSize()
  );
  keyboardConfig.on('decreaseFontSize', () =>
    editActionsController.decreaseFontSize()
  );
  // Design action shortcuts
  keyboardConfig.on('flushCaches', () => designPanelController.flushCaches());
  keyboardConfig.on('showAST', () => designPanelController.showAST());
  keyboardConfig.on('checkValidity', () =>
    designPanelController.checkValidity()
  );

  // Display action shortcuts
  const viewAllCommand = () => {
    if (previewManager?.mesh) {
      previewManager.viewAllCamera();
      announceImmediate('View fitted to model');
    }
  };
  keyboardConfig.on('viewAll', viewAllCommand);
  // Ctrl+] / Ctrl+[. The menu, the camera bar and these share one step, so
  // every surface moves the camera by the same amount.
  keyboardConfig.on('zoomIn', () => {
    if (previewManager) {
      previewManager.zoomCamera(CAMERA_ZOOM_STEP);
      announceCameraAction('zoom-in');
    }
  });
  keyboardConfig.on('zoomOut', () => {
    if (previewManager) {
      previewManager.zoomCamera(-CAMERA_ZOOM_STEP);
      announceCameraAction('zoom-out');
    }
  });
  keyboardConfig.on('toggleAxes', () =>
    displayOptionsController.toggle('axes')
  );
  keyboardConfig.on('toggleEdges', () =>
    displayOptionsController.toggle('edges')
  );
  keyboardConfig.on('toggleCrosshairs', () =>
    displayOptionsController.toggle('crosshairs')
  );
  keyboardConfig.on('toggleConsole', () => toggleConsole());
  // 'errorLog' is not in PANEL_REGISTRY, so togglePanelVisibility would
  // return early and Ctrl+Alt+2 would do nothing. The Error-Log is a console
  // tab in Forge and a strip pane in Classic; registry semantics fit
  // neither, so it gets its own per-host handler.
  keyboardConfig.on('toggleErrorLog', () => toggleErrorLog());
  // These two run the same command as their Window-menu items: in Classic
  // the Editor lives in the dock, which the panel registry cannot reach.
  keyboardConfig.on('toggleCodeEditor', () => toggleEditorPanel());
  keyboardConfig.on('toggleCustomizer', () => toggleCustomizerPanel());
  keyboardConfig.on('jumpToPanel', () => openJumpToPicker());
  keyboardConfig.on('nextPanel', () => getUIModeController().cyclePanel(1));
  keyboardConfig.on('prevPanel', () => getUIModeController().cyclePanel(-1));

  const runEditorAction = (actionId) => {
    const modeManager = getModeManager();
    if (modeManager?.isExpertMode?.() && modeManager.getEditorInstance?.()) {
      modeManager.getEditorInstance().performAction?.(actionId);
    }
  };
  keyboardConfig.on('find', () => runEditorAction('find'));
  keyboardConfig.on('findNext', () => runEditorAction('findNext'));
  keyboardConfig.on('findPrevious', () => runEditorAction('findPrevious'));
  keyboardConfig.on('findReplace', () => runEditorAction('findReplace'));

  // Ctrl+E is Window ▸ Editor's command as well, Simplified's answer included.
  keyboardConfig.on('toggleExpertMode', () => toggleEditorPanel());

  // ========== Gamepad controller integration ==========
  if (gamepadController) {
    // The controller says how far to move in each step: degrees to orbit,
    // and shares of the view to pan and zoom, so the view moves at the same
    // speed on a large model and a small one. A stick moves the camera the
    // way it is pushed: right takes the camera right around the model (a
    // positive angle here), up takes it up.
    const orthographicCamera = () =>
      previewManager?.projectionMode === 'orthographic'
        ? previewManager.orthoCamera
        : null;
    // A share of 1, in model millimeters: the camera's distance from what
    // it looks at, or the height an orthographic view shows.
    const viewSpan = () => {
      const ortho = orthographicCamera();
      if (ortho) return (ortho.top - ortho.bottom) / ortho.zoom;
      const camera = previewManager?.getActiveCamera?.();
      const target = previewManager?.controls?.target;
      return camera && target ? camera.position.distanceTo(target) : 0;
    };
    // While the City Walk runs it reads the controller itself; the preview
    // behind it must not move.
    const cityWalkUp = () => {
      const layer = document.getElementById('cityWalkLayer');
      return !!layer && !layer.hidden;
    };
    // Each move the controller makes is the view's own to the Alt View,
    // which ticks for it as it would for a drag.
    gamepadController.on('camera:rotate', ({ x, y }) => {
      if (previewManager && !cityWalkUp()) {
        noteControllerMove();
        previewManager.rotateHorizontal((x * Math.PI) / 180);
        previewManager.rotateVertical((y * Math.PI) / 180);
      }
    });

    gamepadController.on('camera:zoom', ({ delta }) => {
      if (!previewManager || cityWalkUp()) return;
      // An orthographic view zooms by a factor of 1 + 0.02 for each unit
      // zoomCamera() takes, rather than by moving: the same share becomes
      // that factor there.
      const amount = orthographicCamera()
        ? (Math.sign(delta) * Math.expm1(Math.abs(delta))) / 0.02
        : delta * viewSpan();
      noteControllerMove();
      previewManager.zoomCamera(amount);
    });

    gamepadController.on('camera:pan', ({ x, y }) => {
      if (previewManager && !cityWalkUp()) {
        const span = viewSpan();
        noteControllerMove();
        previewManager.panCamera(x * span, y * span);
      }
    });

    // The buttons drive the interface by the preset: in Menus, focus steps
    // as Tab does and the D-pad sends the arrow keys; in Camera, the D-pad
    // turns and tilts the view a step (as the camera panel's rotate buttons
    // do) and the bumpers walk the standard views; in Customizer, the
    // bumpers step through the parameters and the triggers through the
    // groups. In each, A presses, B does what Escape does (a render's
    // Cancel included), and View steps to the next preset. The navigator
    // stands down while the City Walk runs. In the Alt View a press that
    // acts with no control on screen plays a key (the rest press a control
    // or move the view, which have their own sounds).
    // The triggers' parts are the Classic interface's own in Classic; the
    // standard list covers a desktop and a phone (a part not shown is
    // passed by).
    const gamepadNavigator = createGamepadNavigator({
      getRegions: () =>
        document.body.dataset.uiMode === 'classic'
          ? CLASSIC_REGIONS
          : DEFAULT_REGIONS,
      onUnhandledBack: () => getToolbarMenuController().closeAll(),
    });
    const PAD_TURN_STEP = 0.1;
    const turnView = (horizontal, vertical) => {
      if (!previewManager || cityWalkUp()) return;
      if (horizontal) previewManager.rotateHorizontal(horizontal);
      if (vertical) previewManager.rotateVertical(vertical);
    };
    // The standard views as the bumpers walk them: round the model, then
    // over and under it.
    const STANDARD_VIEWS = ['front', 'right', 'back', 'left', 'top', 'bottom'];
    let standardViewAt = -1;
    const walkViews = (by) => {
      if (!previewManager || cityWalkUp()) return;
      standardViewAt =
        (standardViewAt + by + STANDARD_VIEWS.length) % STANDARD_VIEWS.length;
      const view = STANDARD_VIEWS[standardViewAt];
      playPadKey();
      previewManager.setCameraView(view);
      announceCameraAction(`${view} view`);
    };
    const PRESET_SPOKEN = {
      camera: 'Camera controls',
      customizer: 'Customizer controls',
      menus: 'Menu controls',
    };
    const gamepadCommands = {
      previous: gamepadNavigator.previous,
      next: gamepadNavigator.next,
      previousRegion: gamepadNavigator.previousRegion,
      nextRegion: gamepadNavigator.nextRegion,
      activate: gamepadNavigator.activate,
      back: gamepadNavigator.back,
      up: () => gamepadNavigator.arrow('up'),
      down: () => gamepadNavigator.arrow('down'),
      left: () => gamepadNavigator.arrow('left'),
      right: () => gamepadNavigator.arrow('right'),
      fullScreen: gamepadNavigator.fullScreen,
      // The controller's own button opens Keys on its own tab.
      keys: () => {
        if (!cityWalkUp()) openShortcutsDialog({ tab: 'controller' });
      },
      resetView: () => {
        playPadKey();
        resetViewCommand();
      },
      viewAll: () => {
        playPadKey();
        viewAllCommand();
      },
      turnLeft: () => turnView(PAD_TURN_STEP, 0),
      turnRight: () => turnView(-PAD_TURN_STEP, 0),
      tiltUp: () => turnView(0, PAD_TURN_STEP),
      tiltDown: () => turnView(0, -PAD_TURN_STEP),
      previousView: () => walkViews(-1),
      nextView: () => walkViews(1),
      previousParameter: () => gamepadNavigator.parameter(-1),
      nextParameter: () => gamepadNavigator.parameter(1),
      previousGroup: () => gamepadNavigator.group(-1),
      nextGroup: () => gamepadNavigator.group(1),
      resetParameter: gamepadNavigator.resetParameter,
      generate: gamepadNavigator.generate,
      nextPreset: () => {
        playPadKey();
        const preset = gamepadMapping.nextPreset();
        announceImmediate(
          PRESET_SPOKEN[preset] ?? gamepadMapping.presetName(preset)
        );
      },
    };
    const repeatingActions = new Set(
      GAMEPAD_ACTIONS.filter((a) => a.repeats).map((a) => a.id)
    );
    // Each input's action, from the saved assignments; read again whenever
    // they change, and when a pad is plugged in (its layout may differ).
    let actionByInput = {};
    const applyGamepadMapping = () => {
      const { assignments } = gamepadMapping.get();
      gamepadController.setAssignments(assignments);
      actionByInput = {};
      for (const [action, input] of Object.entries(assignments)) {
        if (input) actionByInput[input] = action;
      }
    };
    applyGamepadMapping();
    gamepadMapping.subscribe(applyGamepadMapping);
    gamepadController.on('connected', applyGamepadMapping);
    gamepadController.on('input', ({ name, repeat }) => {
      if (gamepadNavigator.isPaused()) return;
      // A Joy-Con's layout gives its buttons their own jobs.
      const action = (gamepadController.layoutInputs() ?? actionByInput)[name];
      if (!action || (repeat && !repeatingActions.has(action))) return;
      gamepadCommands[action]?.();
    });

    // Gamepad connection feedback
    gamepadController.on('connected', (info) => {
      updateStatus(`Gamepad connected: ${info.id.split(' (')[0]}`);
    });

    gamepadController.on('disconnected', () => {
      updateStatus('Gamepad disconnected');
    });
  }

  updateStatus('Ready - Upload a file to begin');

  startupDone = true;
  if (!firstVisitBlocking) restorePendingDraft();
}

// Library UI Rendering
function renderLibraryUI(detectedLibraries) {
  const libraryControls = document.getElementById('libraryControls');
  const libraryList = document.getElementById('libraryList');
  const libraryBadge = document.getElementById('libraryBadge');
  const libraryDetails = libraryControls?.querySelector('.library-details');
  const libraryHelp = libraryControls?.querySelector('.library-help');

  if (!libraryControls || !libraryList || !libraryBadge) {
    console.warn('Library UI elements not found');
    return;
  }

  // Always show library controls
  libraryControls.classList.remove('hidden');

  // Update badge count
  libraryBadge.textContent = libraryManager.getEnabled().length;

  // Update help text based on whether libraries were detected
  if (libraryHelp) {
    if (detectedLibraries.length === 0) {
      libraryHelp.textContent =
        'No libraries detected in this model. You can still enable library bundles to use external functions and modules.';
    } else {
      libraryHelp.textContent = 'Enable libraries used by this model:';
    }
  }

  // Auto-expand only when libraries are detected
  if (libraryDetails) {
    if (detectedLibraries.length > 0) {
      libraryDetails.open = true;
    } else {
      libraryDetails.open = false;
    }
  }

  // Clear existing list
  libraryList.innerHTML = '';

  // Get all libraries
  const allLibraries = Object.values(LIBRARY_DEFINITIONS);

  // Render library checkboxes
  allLibraries.forEach((lib) => {
    const isDetected = detectedLibraries.includes(lib.id);
    const isEnabled = libraryManager.isEnabled(lib.id);

    const libraryItem = document.createElement('label');
    libraryItem.className = 'library-item';
    if (isDetected) {
      libraryItem.classList.add('library-detected');
    }

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = `library-${lib.id}`;
    checkbox.checked = isEnabled;
    checkbox.setAttribute('data-library-id', lib.id);

    const icon = document.createElement('span');
    icon.className = 'library-icon';
    icon.textContent = lib.icon;
    icon.setAttribute('aria-hidden', 'true');

    const info = document.createElement('span');
    info.className = 'library-info';

    const name = document.createElement('strong');
    name.className = 'library-name';
    name.textContent = lib.name;
    if (isDetected) {
      const badge = document.createElement('span');
      badge.className = 'library-required-badge';
      badge.textContent = 'required';
      badge.setAttribute('aria-label', 'Required by this model');
      name.appendChild(badge);
    }

    const desc = document.createElement('span');
    desc.className = 'library-description';
    desc.textContent = lib.description;

    info.appendChild(name);
    info.appendChild(desc);

    libraryItem.appendChild(checkbox);
    libraryItem.appendChild(icon);
    libraryItem.appendChild(info);

    libraryList.appendChild(libraryItem);

    // Add event listener
    checkbox.addEventListener('change', () => {
      if (checkbox.disabled) return;
      if (checkbox.checked) {
        libraryManager.enable(lib.id);
      } else {
        libraryManager.disable(lib.id);
      }
      libraryBadge.textContent = libraryManager.getEnabled().length;
      // Update status area with library toggle feedback
      const statusArea = document.getElementById('statusArea');
      if (statusArea) {
        statusArea.textContent = `${lib.name} ${checkbox.checked ? 'enabled' : 'disabled'}`;
      }
      // A library is a render input: re-preview so a model that needs a
      // switched-off library says so instead of rendering from leftovers. The
      // libraryManager subscription set up alongside the controller already
      // refreshes its enabled-libraries snapshot.
      autoPreviewController.onLibrariesChange(
        stateManager.getState().parameters || {}
      );
    });
  });

  // Probe whether this copy of the app can actually serve each library's
  // files (its own manifest, the file the worker mounts from) and say so on
  // the row. An enabled library stays operable even when unreachable, so it
  // can still be switched off.
  libraryManager.checkAvailability().then((availability) => {
    allLibraries.forEach((lib) => {
      if (availability[lib.id] !== false) return;
      const checkbox = libraryList.querySelector(
        `input[data-library-id="${lib.id}"]`
      );
      if (!checkbox) return; // list re-rendered since the probe started
      const row = checkbox.closest('.library-item');
      if (!row || row.querySelector('.library-unavailable-note')) return;
      if (!checkbox.checked) {
        checkbox.disabled = true;
      }
      row.classList.add('library-unavailable');
      const note = document.createElement('span');
      note.className = 'library-unavailable-note';
      note.textContent =
        'Not available right now: the library’s files could not be reached.';
      row.querySelector('.library-info')?.appendChild(note);
    });
  });
}

// Update auto-preview to include libraries
function getEnabledLibrariesForRender() {
  const paths = libraryManager.getMountPaths();
  return paths;
}

// Desktop reference geometry from CLI extracts (OpenSCAD 2026.01.03
// Nightly, Manifold backend).
//
// Facet counts here are engine-version-specific tessellation bookkeeping:
// volume and bounding box measured identical across engines while facet
// counts differ by up to ~9% (desktop-comparison-results.md, under
// docs/archive/audit/). The authoritative parity check is `npm run parity`
// (scripts/parity/), which compares dimensional metrics with tolerances;
// this debug helper's ±10% triangle comparison is only a quick in-browser
// sanity probe.
const DESKTOP_REFERENCE_GEOMETRY = {
  '3d-printed-keyguard': {
    scenarioId: '3d-printed-keyguard',
    parameters: { generate: 'keyguard', type_of_keyguard: '3D-Printed' },
    geometry: { vertices: 5978, facets: 12016 },
    exports: { stl_bytes: 3394047 },
    openscadVersion: '2026.01.03',
    backend: 'Manifold',
  },
  'laser-cut-keyguard': {
    scenarioId: 'laser-cut-keyguard',
    parameters: { generate: 'keyguard', type_of_keyguard: 'Laser-Cut' },
    geometry: { vertices: 3288, facets: 6636 },
    exports: { stl_bytes: 1912770 },
    openscadVersion: '2026.01.03',
    backend: 'Manifold',
  },
  'keyguard-frame-multicolor': {
    scenarioId: 'keyguard-frame-multicolor',
    parameters: {
      type_of_keyguard: '3D-Printed',
      generate: 'keyguard frame',
      show_keyguard_with_frame: 'yes',
      have_a_keyguard_frame: 'yes',
    },
    geometry: { vertices: 6981, facets: 14118 },
    exports: { stl_bytes: 3940675 },
    openscadVersion: '2026.01.03',
    backend: 'Manifold',
  },
};

function findMatchingReference(params) {
  if (!params) return null;
  for (const ref of Object.values(DESKTOP_REFERENCE_GEOMETRY)) {
    const allMatch = Object.entries(ref.parameters).every(
      ([key, value]) => params[key] === value
    );
    if (allMatch) return ref;
  }
  return null;
}

// Expose key managers to window for testing and debugging
if (typeof window !== 'undefined') {
  window.stateManager = stateManager;
  window.presetManager = presetManager;
  window.themeManager = themeManager;
  window.previewManager = previewManager;
  // For a spec that reads the preview's mesh and its overlays. A
  // getter, because the manager is made after this line runs.
  Object.defineProperty(window, 'previewManager', {
    get: () => previewManager,
    configurable: true,
  });
  window.libraryManager = libraryManager;

  window.__forgeDebug = {
    /**
     * Current 3D viewport color-scheme key (a PREVIEW_COLORS name). Classic
     * mode reports 'classic' — the desktop Cornfield scheme.
     * @returns {string|null}
     */
    previewColorScheme() {
      return previewManager?.currentTheme ?? null;
    },

    /**
     * Where the active camera is. A spec has to be able to prove
     * that a key pressed inside the drawing editor did not move the model
     * behind it, and a screenshot of a hidden canvas cannot say so.
     * @returns {{x: number, y: number, z: number}|null}
     */
    cameraPosition() {
      const camera = previewManager?.getActiveCamera?.();
      if (!camera?.position) return null;
      const { x, y, z } = camera.position;
      return { x, y, z };
    },

    /**
     * The reference overlay's live placement. Where the image sits is
     * saved per project, and a spec has to be able to prove the numbers came
     * back rather than infer it from a picture.
     * @returns {Object|null}
     */
    overlayPlacement() {
      const c = previewManager?.overlayConfig;
      if (!c) return null;
      return {
        enabled: c.enabled,
        offsetX: c.offsetX,
        offsetY: c.offsetY,
        rotationDeg: c.rotationDeg,
        width: c.width,
        height: c.height,
        lockAspect: c.lockAspect,
        zPreset: c.zPreset,
        zCustomMm: c.zCustomMm,
        zPosition: c.zPosition,
      };
    },

    /**
     * Mouse-wheel zoom focal point. The Preferences checkbox is this
     * setting's only control, so specs prove a change against the manager's
     * state rather than a second checkbox.
     * @returns {boolean|null}
     */
    zoomToCursor() {
      return previewManager?.zoomToCursorEnabled ?? null;
    },

    /**
     * Grid scene truth. `visible` reads the helper actually in the
     * scene, not the preference — the preference matrix asserts what is
     * painted, exactly as axisTickOverlay() does for ticks.
     * @returns {{enabled: boolean, visible: boolean|null, size: {widthMm: number, heightMm: number}|null}|null}
     */
    grid() {
      if (!previewManager) return null;
      return {
        enabled: Boolean(previewManager.gridEnabled),
        visible: previewManager.gridHelper?.visible ?? null,
        size: previewManager.getGridSize?.() ?? null,
      };
    },

    /**
     * Export quality mode. File > Export Quality is this setting's only
     * control and it has no DOM element to read, so the memory-banner and
     * recovery specs prove changes here.
     * @returns {string}
     */
    exportQuality() {
      return exportQualityMode;
    },

    /**
     * Camera projection scene truth. Projection is live camera
     * state shared across the interfaces by order — never persisted — so
     * the preference matrix proves flip continuity here rather than
     * through either interface's own toggle button.
     * @returns {string|null}
     */
    projection() {
      return previewManager?.projectionMode ?? null;
    },

    /**
     * What the axis-tick overlay actually put in the scene.
     *
     * `inScene` is read from the scene graph, not from the preference: the
     * defect this exists to catch is the option reading as on while the
     * overlay has thrown and nothing is drawn. Asserting the toggle's own
     * state would report success throughout.
     *
     * `colorHex` is the color the overlay actually baked at build time, the
     * only way a spec can prove the overlay took the active theme's color
     * (screenshots can't read a material). Null until a build succeeded.
     *
     * `distanceMm`/`tickStepMm` expose the zoom-adaptive scale so a spec can
     * prove the overlay re-derived itself after a zoom, and `nodes` names the
     * depth-honest children (ticks, dashed negatives, line-glyph digits)
     * actually present under the group.
     *
     * @returns {{enabled: boolean, inScene: boolean, ticks: number, labels: number, colorHex: number|null, distanceMm: number|null, tickStepMm: number|null, nodes: string[]}|null}
     */
    axisTickOverlay() {
      const controller = getDisplayOptionsController();
      const scene = previewManager?.scene;
      if (!controller || !scene) return null;
      const group = scene.getObjectByName('__axisTickOverlay');
      return {
        enabled: controller.get('axisMarks'),
        inScene: Boolean(group),
        ticks: controller._axisTickOverlay?.tickCount ?? 0,
        labels: controller._axisTickOverlay?.labelCount ?? 0,
        colorHex: controller._axisTickOverlay?.colorHex ?? null,
        distanceMm: controller._axisTickOverlay?.distanceMm ?? null,
        tickStepMm: controller._axisTickOverlay?.tickStepMm ?? null,
        nodes: group ? group.children.map((c) => c.name).sort() : [],
      };
    },

    /**
     * The corner XYZ triad's live state. `present` reads the
     * PreviewManager's own reference — the render pass draws exactly when
     * that reference exists, so a spec asserting on it is asserting on what
     * the second pass will actually paint. `letterColorHex` is the
     * scheme-resolved color the letters were built with.
     * @returns {{enabled: boolean, present: boolean, letterColorHex: number|null}|null}
     */
    axisTriad() {
      const controller = getDisplayOptionsController();
      if (!controller || !previewManager) return null;
      return {
        enabled: controller.get('axes'),
        present: Boolean(previewManager._axisTriad),
        letterColorHex: previewManager._axisTriad?.letterColorHex ?? null,
      };
    },

    /**
     * Where the camera is and what it orbits. Read-only, and the only way a
     * parity test can prove that Center, View All and Reset View each moved
     * the view differently rather than merely that the item was clickable.
     * `target` is null when the browser has no WebGL, so there are no controls.
     * `right` and `up` are the camera's own world axes, which show a roll;
     * the rotation the viewport panel publishes is OpenSCAD's $vpr and
     * cannot.
     * @returns {{position: number[], target: number[]|null, right: number[], up: number[]}|null}
     */
    cameraPose() {
      const camera = previewManager?.getActiveCamera?.();
      if (!camera) return null;
      camera.updateMatrixWorld();
      const m = camera.matrixWorld.elements;
      return {
        position: camera.position.toArray(),
        target: previewManager.controls?.target?.toArray() ?? null,
        right: [m[0], m[1], m[2]],
        up: [m[4], m[5], m[6]],
      };
    },

    async compareGeometry() {
      if (!renderController || !renderController.ready) {
        console.error(
          '[GeomDiag] Render controller not ready. Initialize WASM first.'
        );
        return null;
      }
      const state = stateManager.getState();
      if (!state.uploadedFile?.content) {
        console.error('[GeomDiag] No model loaded.');
        return null;
      }

      console.log(
        '[GeomDiag] Rendering at FULL quality (no $fn capping) for geometry comparison...'
      );
      const startTime = performance.now();

      try {
        const result = await renderController.renderFull(
          state.uploadedFile.content,
          state.parameters,
          {
            quality: RENDER_QUALITY.FULL,
            outputFormat: 'stl',
            paramTypes: state.paramTypes || {},
            files: state.projectFiles,
            mainFile: state.mainFilePath,
            libraries: getEnabledLibrariesForRender(),
          }
        );

        const durationMs = Math.round(performance.now() - startTime);
        const stats = result.stats || {};
        const triangles = stats.triangles || 0;
        const stlBytes = stats.size || 0;

        const browserResult = { triangles, stlBytes, renderMs: durationMs };

        console.log('[GeomDiag] === Browser Geometry Results ===');
        console.log(`  Triangles (facets): ${triangles.toLocaleString()}`);
        console.log(
          `  STL size: ${stlBytes.toLocaleString()} bytes (${(stlBytes / 1024).toFixed(1)} KB)`
        );
        console.log(`  Render time: ${durationMs}ms`);

        const ref = findMatchingReference(state.parameters);

        if (ref) {
          const refTriangles = ref.geometry.facets;
          const refVertices = ref.geometry.vertices;
          const refStlBytes = ref.exports.stl_bytes;

          const triDiff = triangles - refTriangles;
          const triPct =
            refTriangles > 0
              ? ((triDiff / refTriangles) * 100).toFixed(1)
              : 'N/A';
          const sizeDiff = stlBytes - refStlBytes;
          const sizePct =
            refStlBytes > 0
              ? ((sizeDiff / refStlBytes) * 100).toFixed(1)
              : 'N/A';

          console.log(
            `[GeomDiag] === Desktop Reference (${ref.scenarioId}) ===`
          );
          console.log(`  OpenSCAD: ${ref.openscadVersion} (${ref.backend})`);
          console.log(`  Triangles (facets): ${refTriangles.toLocaleString()}`);
          console.log(
            `  Unique vertices (OFF format): ${refVertices.toLocaleString()}`
          );
          console.log(`  STL size: ${refStlBytes.toLocaleString()} bytes`);

          console.log('[GeomDiag] === Comparison ===');
          console.log(
            `  Triangle delta: ${triDiff > 0 ? '+' : ''}${triDiff.toLocaleString()} (${triPct}%)`
          );
          console.log(
            `  STL size delta: ${sizeDiff > 0 ? '+' : ''}${sizeDiff.toLocaleString()} bytes (${sizePct}%)`
          );

          const withinTolerance = Math.abs(parseFloat(triPct)) <= 10;
          console.log(
            `  Within 10% tolerance: ${withinTolerance ? 'YES' : 'NO'}`
          );

          return {
            browser: browserResult,
            reference: {
              scenarioId: ref.scenarioId,
              triangles: refTriangles,
              vertices: refVertices,
              stlBytes: refStlBytes,
            },
            comparison: {
              triangleDelta: triDiff,
              triangleDeltaPct: parseFloat(triPct),
              stlSizeDelta: sizeDiff,
              stlSizeDeltaPct: parseFloat(sizePct),
              withinTolerance,
            },
          };
        }

        console.log(
          '[GeomDiag] No matching desktop reference for current parameters.'
        );
        console.log(
          '[GeomDiag] Known references:',
          Object.keys(DESKTOP_REFERENCE_GEOMETRY).join(', ')
        );
        return { browser: browserResult, reference: null, comparison: null };
      } catch (err) {
        console.error('[GeomDiag] Render failed:', err);
        return { error: err.message };
      }
    },

    getDesktopReferences() {
      return { ...DESKTOP_REFERENCE_GEOMETRY };
    },

    /**
     * Headless render hook for the desktop-parity harness
     * (scripts/parity/render-wasm.mjs). Renders arbitrary SCAD at full
     * quality to binary STL without touching UI state — no project needs
     * to be loaded.
     *
     * @param {Object} job
     * @param {string} job.scadText - Main SCAD source
     * @param {Object} [job.params] - Parameter values
     * @param {Object} [job.paramTypes] - Schema types for formatting
     * @param {Object} [job.files] - Companion files as { path: content }
     * @param {string} [job.mainFile] - Main file path within files
     * @returns {Promise<{base64Stl: string, stats: Object, consoleOutput: string}|{error: string}>}
     */
    async parityRender({
      scadText,
      params = {},
      paramTypes = {},
      files = null,
      mainFile = null,
    } = {}) {
      if (!renderController || !renderController.ready) {
        return { error: 'Render controller not ready. Initialize WASM first.' };
      }
      if (typeof scadText !== 'string' || scadText.length === 0) {
        return { error: 'parityRender requires scadText' };
      }
      try {
        const filesMap = files ? new Map(Object.entries(files)) : undefined;
        const result = await renderController.renderFull(scadText, params, {
          quality: RENDER_QUALITY.FULL,
          outputFormat: 'stl',
          paramTypes,
          ...(filesMap ? { files: filesMap, mainFile } : {}),
          libraries: [],
        });

        const bytes =
          result.stl instanceof ArrayBuffer
            ? new Uint8Array(result.stl)
            : result.stl;
        let binary = '';
        const CHUNK = 0x8000;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
        }

        return {
          base64Stl: btoa(binary),
          stats: result.stats || null,
          consoleOutput: result.consoleOutput || '',
        };
      } catch (err) {
        return { error: err?.message || String(err) };
      }
    },

    toggleCsgBypass(enable) {
      const key = DEBUG_PREFS.noCsgColors;
      const wasEnabled = isDebugPrefEnabled('noCsgColors');
      const nowEnabled = enable !== undefined ? Boolean(enable) : !wasEnabled;

      if (nowEnabled) {
        localStorage.setItem(key, '1');
      } else {
        localStorage.removeItem(key);
      }
      console.log(
        `[ToggleDebug] CSG bypass: ${nowEnabled ? 'ON' : 'OFF'} ` +
          `(was ${wasEnabled ? 'ON' : 'OFF'})`
      );

      if (autoPreviewController) {
        autoPreviewController.clearPreviewCache();
        const state = stateManager.getState();
        if (state?.uploadedFile?.content) {
          autoPreviewController.forcePreview(state.parameters);
        }
      }
      return nowEnabled;
    },

    toggleDesktopQuality(enable) {
      const key = DEBUG_PREFS.desktopQuality;
      const wasEnabled = isDebugPrefEnabled('desktopQuality');
      const nowEnabled = enable !== undefined ? Boolean(enable) : !wasEnabled;

      if (nowEnabled) {
        localStorage.setItem(key, '1');
      } else {
        localStorage.removeItem(key);
      }
      console.log(
        `[ToggleDebug] Desktop quality: ${nowEnabled ? 'ON' : 'OFF'} ` +
          `(was ${wasEnabled ? 'ON' : 'OFF'})`
      );

      if (autoPreviewController) {
        autoPreviewController.clearPreviewCache();
        const state = stateManager.getState();
        if (state?.uploadedFile?.content) {
          autoPreviewController.forcePreview(state.parameters);
        }
      }
      return nowEnabled;
    },

    toggleSourceOverrides(enable) {
      const key = DEBUG_PREFS.sourceOverrides;
      const wasEnabled = isDebugPrefEnabled('sourceOverrides');
      const nowEnabled = enable !== undefined ? Boolean(enable) : !wasEnabled;

      if (nowEnabled) {
        localStorage.setItem(key, '1');
      } else {
        localStorage.removeItem(key);
      }
      console.log(
        `[ToggleDebug] Source overrides: ${nowEnabled ? 'ON' : 'OFF'} ` +
          `(was ${wasEnabled ? 'ON' : 'OFF'}). ` +
          `When ON, parameters are baked into SCAD source instead of using -D flags.`
      );

      if (autoPreviewController) {
        autoPreviewController.clearPreviewCache();
        const state = stateManager.getState();
        if (state?.uploadedFile?.content) {
          autoPreviewController.forcePreview(state.parameters);
        }
      }
      return nowEnabled;
    },

    getToggles() {
      const csgBypass = isDebugPrefEnabled('noCsgColors');
      const desktopQuality = isDebugPrefEnabled('desktopQuality');
      const sourceOverrides = isDebugPrefEnabled('sourceOverrides');
      const toggles = {
        csgBypass,
        desktopQuality,
        sourceOverrides,
      };
      console.log('[ToggleDebug] Current toggles:', toggles);
      return toggles;
    },

    /**
     * Export the SCAD source exactly as it would be sent to the renderer.
     * @param {Object} [options]
     * @param {boolean} [options.download=false] - trigger a browser file download
     * @returns {string|null} The SCAD source text, or null if no model is loaded
     */
    exportScadSource(options = {}) {
      const { download = false } = options;
      const state = stateManager.getState();
      if (!state.uploadedFile?.content) {
        console.error('[ExportDiag] No model loaded.');
        return null;
      }

      // Renders always use the unmodified source, so it is the only source
      // there is to export.
      const source =
        autoPreviewController?.currentScadContent || state.uploadedFile.content;
      const label = 'original';

      const csgBypass = isDebugPrefEnabled('noCsgColors');

      console.log(`[ExportDiag] Source type: ${label}`);
      console.log(`[ExportDiag] CSG bypass active: ${csgBypass}`);
      console.log(`[ExportDiag] Source length: ${source.length} chars`);

      if (download) {
        const blob = new Blob([source], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `forge-export-${label}.scad`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log(`[ExportDiag] Downloaded as forge-export-${label}.scad`);
      }

      return source;
    },

    /**
     * Dump the render arguments that would be passed to the OpenSCAD worker.
     * Logs parameters, paramTypes, output format, and capability flags.
     * @returns {Object|null} Structured diagnostic info, or null if no model loaded
     */
    dumpRenderArgs() {
      const state = stateManager.getState();
      if (!state.uploadedFile?.content) {
        console.error('[RenderArgsDiag] No model loaded.');
        return null;
      }

      const parameters = state.parameters || {};
      const paramTypes = state.paramTypes || {};

      const csgBypass = isDebugPrefEnabled('noCsgColors');
      const rawSource =
        autoPreviewController?.currentScadContent || state.uploadedFile.content;
      const hasColorCalls = AutoPreviewController.scadUsesColor(rawSource);
      const colorPassthroughEnabled = isFlagEnabled('color_passthrough');
      const caps = renderController?.getCapabilities?.() || {};
      const supportsRenderColors = Boolean(
        caps.hasRenderColorsFlag || caps.hasManifold
      );

      let previewOutputFormat;
      if (csgBypass) {
        previewOutputFormat = 'stl';
      } else if (supportsRenderColors) {
        previewOutputFormat = 'off';
      } else {
        previewOutputFormat =
          hasColorCalls && colorPassthroughEnabled ? 'off' : 'stl';
      }

      console.log('[RenderArgsDiag] === Current Render Arguments ===');
      console.log(
        '[RenderArgsDiag] Parameters:',
        JSON.parse(JSON.stringify(parameters))
      );
      console.log(
        '[RenderArgsDiag] Parameter types:',
        JSON.parse(JSON.stringify(paramTypes))
      );
      console.log(
        '[RenderArgsDiag] Preview output format:',
        previewOutputFormat
      );
      const sourceOverrides = isDebugPrefEnabled('sourceOverrides');
      console.log('[RenderArgsDiag] CSG bypass:', csgBypass);
      console.log(
        '[RenderArgsDiag] Source overrides (bake params into SCAD):',
        sourceOverrides
      );
      console.log('[RenderArgsDiag] Has color() calls:', hasColorCalls);
      console.log(
        '[RenderArgsDiag] Color passthrough flag:',
        colorPassthroughEnabled
      );
      console.log(
        '[RenderArgsDiag] Supports render colors:',
        supportsRenderColors
      );
      console.log('[RenderArgsDiag] Engine capabilities:', caps);

      const scadSource =
        autoPreviewController?.currentScadContent || state.uploadedFile.content;
      const exactDefineArgs = formatBuildDefineArgs(
        parameters,
        paramTypes,
        scadSource
      );
      const paramSummary = [];
      for (let i = 0; i < exactDefineArgs.length; i += 2) {
        const assignment = exactDefineArgs[i + 1] || '';
        const key = assignment.split('=')[0];
        const type = paramTypes[key] || 'unknown';
        paramSummary.push(
          `  ${exactDefineArgs[i]} ${assignment}  (type: ${type})`
        );
      }
      if (sourceOverrides) {
        console.log(
          '[RenderArgsDiag] Source overrides ACTIVE — parameters will be ' +
            'baked into SCAD source via _applyOverrides instead of -D flags'
        );
      }
      console.log(
        '[RenderArgsDiag] Exact -D args ' +
          '(same formatting as worker buildDefineArgs):\n' +
          paramSummary.join('\n')
      );

      // Preview quality & auto-preview diagnostics
      const previewInfo = autoPreviewController
        ? autoPreviewController.resolvePreviewQualityInfo(parameters)
        : { quality: null, qualityKey: 'unknown' };
      const previewParams = autoPreviewController
        ? autoPreviewController.resolvePreviewParametersForRender(
            parameters,
            previewInfo.qualityKey,
            previewInfo.quality
          )
        : parameters;
      const previewOverridesActive = previewParams !== parameters;

      console.log(
        '[RenderArgsDiag] Preview quality key:',
        previewInfo.qualityKey
      );
      console.log(
        '[RenderArgsDiag] Preview quality preset:',
        previewInfo.quality
      );
      console.log(
        '[RenderArgsDiag] Auto-preview overrides active:',
        previewOverridesActive
      );
      if (previewOverridesActive) {
        const diffs = {};
        for (const [k, v] of Object.entries(previewParams)) {
          if (parameters[k] !== v)
            diffs[k] = { original: parameters[k], preview: v };
        }
        console.log('[RenderArgsDiag] Preview parameter overrides:', diffs);
      }

      // Backend info
      const manifoldPref = localStorage.getItem(STORAGE_KEY_MANIFOLD_ENGINE);
      const useManifold =
        manifoldPref === null ? true : manifoldPref !== 'false';
      console.log(
        '[RenderArgsDiag] Backend:',
        useManifold ? 'Manifold' : 'CGAL'
      );

      // Companion / project file summary
      const projectFiles = state.projectFiles;
      const companionSummary = [];
      if (projectFiles && projectFiles.size > 0) {
        for (const [name, content] of projectFiles.entries()) {
          const size =
            typeof content === 'string'
              ? content.length
              : (content?.byteLength ?? content?.length ?? 0);
          companionSummary.push({ name, sizeBytes: size });
        }
      }
      console.log('[RenderArgsDiag] Mounted project files:', companionSummary);
      console.log(
        '[RenderArgsDiag] Main file path:',
        state.mainFilePath || '(single file)'
      );

      // Auto-preview controller state
      const autoPreviewInfo = autoPreviewController
        ? autoPreviewController.getStateInfo()
        : null;
      console.log('[RenderArgsDiag] Auto-preview state:', autoPreviewInfo);

      console.log('[RenderArgsDiag] === End Render Arguments ===');

      return {
        parameters,
        paramTypes,
        previewOutputFormat,
        csgBypass,
        sourceOverrides,
        hasColorCalls,
        colorPassthroughEnabled,
        supportsRenderColors,
        capabilities: caps,
        previewQualityKey: previewInfo.qualityKey,
        previewQuality: previewInfo.quality,
        previewOverridesActive,
        backend: useManifold ? 'Manifold' : 'CGAL',
        mountedFiles: companionSummary,
        mainFilePath: state.mainFilePath || null,
        autoPreviewState: autoPreviewInfo,
      };
    },
  };
}

// Global error handlers — catch uncaught exceptions and unhandled promise
// rejections so screen reader users receive audible feedback.
window.onerror = (message) => {
  console.error('[Global]', message);
  _announceError('An unexpected error occurred.');
};

window.addEventListener('unhandledrejection', (event) => {
  console.error('[Global] Unhandled promise rejection:', event.reason);
  _announceError('An unexpected error occurred.');
});

// Start the app
initApp();
